import {
  inferPreferredTimezoneFromLead,
  isValidIanaTimezone,
  normalizePreferredContactTimezone,
} from "../../Utils/DateAndTime";
import { getApplicationUserIdFromTrainerObject, linkedTrainerIdentity } from "../../Utils/emsTrainerUserMatch";
import {
  REVIEW_RATING_OPTIONS,
  formatReviewRatingLabel,
} from "../../Utils/ReviewRatingIcon";
import {
  getInstallmentDueDateOrderError,
  getStandaloneFeeInstallmentDueDateError,
} from "../../Utils/feeInstallmentDueDates";
import {
  getExpectedInstallmentAmount,
  getFeeInstallmentCapacityError,
  moneyMatches,
  validateFeeInstallmentAmounts,
} from "../../Utils/feeInstallmentAmounts";
import { validateFeeInstallmentStatusChange } from "../../Utils/feeInstallmentReceipts";

let FormatDateV1 = (date) => {
  const formattedDate = date
    ? new Date(date).toLocaleDateString("en-GB") // dd/mm/yyyy
    : "-";

  return formattedDate.replace(/\//g, "-");
};

export const formatDateTime = (dateTime) => {
  if (!dateTime) return "";

  const date = new Date(dateTime);
  if (Number.isNaN(date.getTime())) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  const h24 = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 || 12;
  const hours12 = String(h12);
  return `${day}-${month}-${year} ${hours12}:${minutes} ${ampm}`;
};

function readLeadSourceName(item) {
  if (!item || typeof item !== "object") return "";
  const nested = item.lead_source ?? item.leadSource ?? item.LeadSource ?? null;
  const candidates = [
    item.lead_source_name,
    typeof nested === "string" ? nested : "",
    nested?.lead_source_name,
    nested?.lead_source_short_name,
    nested?.name,
  ];
  for (const value of candidates) {
    const text = String(value ?? "").trim();
    if (text && text !== "-" && text !== "—" && !/^\d+$/.test(text)) return text;
  }
  return "";
}

function leadActivityList(item) {
  if (Array.isArray(item?.lead_activities)) return item.lead_activities;
  if (Array.isArray(item?.leadActivities)) return item.leadActivities;
  return [];
}

function activityTimeValue(value) {
  if (!value) return 0;
  const parsed = Date.parse(String(value).trim().replace(" ", "T"));
  return Number.isNaN(parsed) ? 0 : parsed;
}

function latestLeadActivity(item) {
  return leadActivityList(item)
    .filter((activity) => activity && activity.activity_datetime)
    .sort((a, b) => activityTimeValue(b.activity_datetime) - activityTimeValue(a.activity_datetime))[0];
}

function leadActivitySummary(item) {
  const activities = leadActivityList(item);
  const apiCount = Number(item?.lead_activities_count);
  const count = activities.length > 0
    ? activities.length
    : Number.isFinite(apiCount) && apiCount >= 0
      ? apiCount
      : 0;
  const latest = latestLeadActivity(item);
  const rawLast =
    latest?.activity_datetime ||
    item?.last_lead_activity_at ||
    item?.last_lead_activity_datetime ||
    "";
  const lastText = rawLast
    ? formatDateTime(String(rawLast).trim().replace(" ", "T")) || String(rawLast)
    : "-";
  return { count, lastText, rawLast, latest };
}

function leadActivityStatusLabel(activity) {
  if (!activity) return "-";
  const callStatus = String(activity.call_status || "").trim();
  if (["Attended", "Missed", "Rejected", "No Answer"].includes(callStatus)) {
    return callStatus;
  }
  return "-";
}

function leadStageStatusLabel(item) {
  const leadStatusRaw =
    item?.lead_stage?.lead_status ??
    item?.leadStage?.lead_status ??
    item?.lead_status;
  if (
    leadStatusRaw === 1 ||
    leadStatusRaw === "1" ||
    leadStatusRaw === true ||
    String(leadStatusRaw || "").trim().toLowerCase() === "open"
  ) {
    return "Open";
  }
  if (
    leadStatusRaw === 0 ||
    leadStatusRaw === "0" ||
    leadStatusRaw === false ||
    String(leadStatusRaw || "").trim().toLowerCase() === "closed"
  ) {
    return "Closed";
  }
  return "-";
}

/** Formats time strings (e.g. HH:mm or HH:mm:ss) to 12-hour with am/pm for list cells. */
const formatTime12h = (timeStr) => {
  if (timeStr === undefined || timeStr === null || timeStr === "") return "-";
  const s = String(timeStr).trim();
  if (!s || s === "-") return "-";
  if (/am|pm/i.test(s)) return s;
  const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return s;
  const h = parseInt(m[1], 10);
  const min = m[2];
  if (Number.isNaN(h)) return s;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${min} ${ampm}`;
};

/** Class days as M, T, W, TH, F, S, SU. */
const formatClassDayLetters = (days) => {
  let list = [];
  if (Array.isArray(days)) {
    list = days;
  } else if (typeof days === "string" && days.trim()) {
    try {
      const parsed = JSON.parse(days);
      list = Array.isArray(parsed) ? parsed : days.split(",");
    } catch {
      list = days.split(",");
    }
  }
  const letter = (day) => {
    const value = String(day || "").trim().toLowerCase();
    if (value === "monday" || value === "mon") return "M";
    if (value === "tuesday" || value === "tue") return "T";
    if (value === "wednesday" || value === "wed") return "W";
    if (value === "thursday" || value === "thu" || value === "th") return "TH";
    if (value === "friday" || value === "fri") return "F";
    if (value === "saturday" || value === "sat") return "S";
    if (value === "sunday" || value === "sun" || value === "su") return "SU";
    return "";
  };
  return list.map(letter).filter(Boolean).join(", ");
};

/** Unified display for Trainer/Employee dropdown rows across person/user payloads. */
const formatTrainerUserOption = (val) => {
  if (!val) return "";
  const appUser =
    val?.application_user ??
    val?.user ??
    (Array.isArray(val?.applicationuser) ? val.applicationuser[0] : null) ??
    (Array.isArray(val?.application_user) ? val.application_user[0] : null) ??
    null;
  const fullName =
    String(val?.full_name || "").trim() ||
    String(val?.person_full_name || "").trim() ||
    String(val?.person?.full_name || "").trim() ||
    String(val?.organization_person?.person?.full_name || "").trim() ||
    String(val?.organization_person?.full_name || "").trim() ||
    String(appUser?.full_name || "").trim() ||
    [val?.first_name, val?.last_name].filter(Boolean).join(" ").trim() ||
    [appUser?.first_name, appUser?.last_name].filter(Boolean).join(" ").trim();
  const code =
    String(
      val?.organization_person?.person_code ??
        val?.person_code ??
        val?.employee_code ??
        ""
    ).trim();
  const userType = String(
    val?.user_type_label ??
      val?.user_type_name ??
      val?.application_user_type ??
      val?.user_type ??
      val?.application_user?.user_type_name ??
      val?.application_user?.user_type ??
      val?.application_user?.application_user_type ??
      val?.organization_user?.user_type_name ??
      val?.organization_user?.user_type ??
      val?.organization_user?.application_user_type ??
      val?.role_name ??
      ""
  ).trim();
  const userName = String(val?.username ?? appUser?.username ?? "").trim();
  const meta = [code, userType, userName].filter(Boolean);
  return `${fullName || "-"}${meta.length ? ` (${meta.join(", ")})` : ""}`.trim();
};

const sessionTrainerName = (trainer) =>
  String(trainer?.applicationuser?.[0]?.full_name || "").trim() ||
  String(trainer?.application_user?.[0]?.full_name || "").trim() ||
  String(trainer?.application_user?.full_name || "").trim() ||
  String(trainer?.person?.full_name || "").trim() ||
  [trainer?.person?.first_name, trainer?.person?.last_name].filter(Boolean).join(" ").trim();

const classTrainerRowsFrom = (row) => {
  if (!row || typeof row !== "object") return [];
  const nested = row.batchClass ?? row.batch_class ?? null;
  return [
    ...(Array.isArray(row?.class_trainers) ? row.class_trainers : []),
    ...(Array.isArray(row?.classTrainers) ? row.classTrainers : []),
    ...(Array.isArray(nested?.class_trainers) ? nested.class_trainers : []),
    ...(Array.isArray(nested?.classTrainers) ? nested.classTrainers : []),
  ];
};

const isActiveClassTrainerRow = (link) =>
  String(link?.status ?? "Active").trim().toLowerCase() !== "inactive";

/** Current class trainers from class_trainers; do not use stale trainer_employee_id. */
const batchClassTrainerLabel = (row) => {
  const rows = classTrainerRowsFrom(row).filter(isActiveClassTrainerRow);
  const isPrimary = (link) =>
    String(link?.trainer_role || "").trim().toLowerCase() === "primary";
  const ordered = [
    ...rows.filter(isPrimary),
    ...rows.filter((link) => !isPrimary(link)),
  ];
  const names = [];
  const seen = new Set();
  ordered.forEach((link) => {
    const name = sessionTrainerName(link?.trainer);
    if (!name) return;
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    names.push(name);
  });
  if (rows.length) return names.join(", ");
  return sessionTrainerName(
    row?.trainer || row?.batchClass?.trainer || row?.batch_class?.trainer
  );
};

const trainerProfileImageUrl = (trainer) =>
  trainer?.profile_image_url ||
  trainer?.person?.profile_image_url ||
  trainer?.Person?.profile_image_url ||
  trainer?.organization_person?.person?.profile_image_url ||
  trainer?.organization_person?.Person?.profile_image_url ||
  trainer?.organizationPerson?.person?.profile_image_url ||
  trainer?.organizationPerson?.Person?.profile_image_url ||
  "";

const classRecordTrainerPhoto = (item) => {
  const sessionRows = Array.isArray(item?.record_trainers)
    ? item.record_trainers
    : Array.isArray(item?.recordTrainers)
      ? item.recordTrainers
      : [];
  const current = sessionRows.filter((row) => {
    const status = String(row?.assignment_status || "Assigned").trim();
    return status !== "Replaced" && status !== "Cancelled";
  });
  const classTrainers = [
    ...(Array.isArray(item?.batchClass?.class_trainers) ? item.batchClass.class_trainers : []),
    ...(Array.isArray(item?.batchClass?.classTrainers) ? item.batchClass.classTrainers : []),
    ...(Array.isArray(item?.batch_class?.class_trainers) ? item.batch_class.class_trainers : []),
    ...(Array.isArray(item?.batch_class?.classTrainers) ? item.batch_class.classTrainers : []),
  ];
  const candidates = [
    ...current,
    ...sessionRows,
    ...classTrainers,
    ...(Array.isArray(item?.batch?.batch_trainers) ? item.batch.batch_trainers : []),
    ...(Array.isArray(item?.batch?.batchTrainers) ? item.batch.batchTrainers : []),
  ];
  for (const row of candidates) {
    const url = trainerProfileImageUrl(row?.trainer);
    if (url) return url;
  }
  return trainerProfileImageUrl(item?.batchClass?.trainer || item?.batch_class?.trainer);
};

const formatSessionTrainerRows = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return "";
  return rows
    .map((row) => {
      const name = sessionTrainerName(row?.trainer) || "Trainer";
      return [name, row?.trainer_role, row?.assignment_status].filter(Boolean).join(" · ");
    })
    .join(", ");
};

const classRecordCardTrainer = (item) => {
  const sessionRows = Array.isArray(item?.record_trainers)
    ? item.record_trainers
    : Array.isArray(item?.recordTrainers)
      ? item.recordTrainers
      : [];
  const active = sessionRows.filter((row) => {
    const status = String(row?.assignment_status || "").trim();
    return status !== "Replaced" && status !== "Cancelled";
  });
  const pool = active.length ? active : sessionRows;
  const primary =
    pool.find((row) => String(row?.trainer_role || "").trim().toLowerCase() === "primary") ||
    pool[0];
  if (primary) {
    const trainer = primary?.trainer;
    return {
      name: sessionTrainerName(trainer) || "",
      role: String(primary?.trainer_role || "").trim(),
      photo: trainerProfileImageUrl(trainer),
      employeeId: primary?.trainer_employee_id || trainer?.organization_user_id || "",
      appUserId:
        trainer?.application_user_id ||
        trainer?.applicationuser?.[0]?.application_user_id ||
        trainer?.Applicationuser?.[0]?.application_user_id ||
        "",
    };
  }
  const bc = item?.batchClass ?? item?.batch_class ?? {};
  const classTrainerRows = classTrainerRowsFrom(bc).filter(isActiveClassTrainerRow);
  const primaryClassTrainer =
    classTrainerRows.find(
      (link) => String(link?.trainer_role || "").trim().toLowerCase() === "primary"
    ) || classTrainerRows[0];
  if (primaryClassTrainer) {
    const trainer = primaryClassTrainer?.trainer;
    return {
      name: sessionTrainerName(trainer) || batchClassTrainerLabel(bc),
      role: String(primaryClassTrainer?.trainer_role || "Primary").trim(),
      photo: trainerProfileImageUrl(trainer),
      employeeId:
        primaryClassTrainer?.trainer_employee_id ||
        trainer?.organization_user_id ||
        "",
      appUserId:
        trainer?.application_user_id ||
        trainer?.applicationuser?.[0]?.application_user_id ||
        trainer?.Applicationuser?.[0]?.application_user_id ||
        "",
    };
  }
  const trainer = bc?.trainer;
  const name = sessionTrainerName(trainer);
  return {
    name,
    role: name ? "Primary" : "",
    photo: trainerProfileImageUrl(trainer),
    employeeId: bc?.trainer_employee_id || trainer?.organization_user_id || "",
    appUserId:
      trainer?.application_user_id ||
      trainer?.applicationuser?.[0]?.application_user_id ||
      trainer?.Applicationuser?.[0]?.application_user_id ||
      "",
  };
};

/** Formats date-ish input as dd-mm-yyyy (no time). */
const formatDateOnly = (value) => {
  if (!value) return "-";
  const s = String(value).trim();
  if (!s) return "-";
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}-${match[2]}-${match[1]}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "-";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

/** Returns friendly duration (e.g. 40 Minutes) from start/end date-time values. */
const formatDurationMinutes = (start, end) => {
  if (!start || !end) return "-";
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return "-";
  const diffMs = e.getTime() - s.getTime();
  if (diffMs <= 0) return "-";
  const mins = Math.round(diffMs / 60000);
  return `${mins} Minutes`;
};

/** Date and 12-hour time for a class record's actual start or end. */
const formatClassRecordActualWhen = (value) => {
  if (!value) return "-";
  const date = formatDateOnly(value);
  const time = formatTimeOnlyFromDateTime(value);
  if (date === "-" && time === "-") return "-";
  if (date === "-") return time;
  if (time === "-") return date;
  return `${date} ${time}`;
};

/** Extract HH:mm(:ss) from datetime-ish strings and format to 12-hour time. */
const formatTimeOnlyFromDateTime = (value) => {
  if (!value) return "-";
  const s = String(value).trim();
  if (!s) return "-";
  const t = s.match(/(\d{1,2}:\d{2}(?::\d{2})?)/);
  if (t?.[1]) return formatTime12h(t[1]);
  return formatTime12h(s);
};

/** Returns YYYY-MM-DD from date/datetime-ish input, else "". */
const toYmd = (value) => {
  if (value == null) return "";
  const s = String(value).trim();
  if (!s) return "";
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Hide students from class-level active lists after completion date.
 * Historical attendance rows remain available in attendance list APIs.
 */
const isBatchStudentActiveForClassDate = (batchStudent, classDateYmd) => {
  if (!batchStudent) return false;
  const status = String(batchStudent?.batch_status || "")
    .trim()
    .toLowerCase();
  if (status !== "completed") return true;

  const completedYmd = toYmd(batchStudent?.completion_date);
  if (!classDateYmd) {
    // Without a class date, safest behavior for "Completed" is to hide.
    return false;
  }
  if (!completedYmd) return false;
  // Show only through completion date; hide strictly after completion date.
  return classDateYmd <= completedYmd;
};

/** True if nested lead has at least one admission (enrolled). */
const leadHasAdmission = (lead) => {
  if (!lead || typeof lead !== "object") return false;
  const a = lead.admission ?? lead.admissions;
  if (Array.isArray(a)) return a.length > 0;
  if (a && typeof a === "object") return true;
  return false;
};

const countEnrolledDemoParticipants = (participants) => {
  if (!Array.isArray(participants) || participants.length === 0) return 0;
  return participants.filter((p) => leadHasAdmission(p?.lead)).length;
};

/** One line per participant for Demo Sessions list hover (lead name or student name). */
const buildDemoParticipantNamesTooltip = (participants) => {
  if (!Array.isArray(participants) || participants.length === 0) return "";
  return participants
    .map((p) => {
      const lead = p?.lead;
      const fromLead =
        lead?.person_full_name ||
        [lead?.first_name, lead?.middle_name, lead?.last_name]
          .filter((x) => x && String(x).trim() !== "")
          .join(" ")
          .trim();
      let line = "";
      if (fromLead) line = String(fromLead).trim();
      else {
        const st = p?.student;
        if (st) {
          const sn =
            (st.full_name && String(st.full_name).trim()) ||
            [st.first_name, st.middle_name, st.last_name]
              .filter((x) => x && String(x).trim() !== "")
              .join(" ")
              .trim();
          if (sn) line = sn;
          else if (st.student_id != null && st.student_id !== "")
            line = String(st.student_id);
        }
        if (!line && p?.organization_ems_lead_id != null)
          line = `Lead #${p.organization_ems_lead_id}`;
        else if (!line && p?.organization_ems_student_id != null)
          line = `Student #${p.organization_ems_student_id}`;
        else if (!line) line = "—";
      }
      if (leadHasAdmission(lead)) line = `${line} — Enrolled`;
      return line;
    })
    .join("\n");
};

/**
 * Last-resort: find the first non-empty admission_number (or aliases) anywhere in a batch-student row.
 * Payload shapes differ; avoids "-" when the number is nested under an unexpected key.
 */
const deepFindBatchStudentAdmissionNumber = (obj, depth = 0) => {
  if (depth > 8 || obj == null || typeof obj !== "object") return null;
  const keyNames = ["admission_number", "admission_no", "admissionNumber"];
  for (const k of keyNames) {
    const v = obj[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      return String(v).trim();
    }
  }
  if (Array.isArray(obj)) {
    for (const el of obj) {
      const n = deepFindBatchStudentAdmissionNumber(el, depth + 1);
      if (n) return n;
    }
    return null;
  }
  for (const child of Object.values(obj)) {
    if (child != null && typeof child === "object") {
      const n = deepFindBatchStudentAdmissionNumber(child, depth + 1);
      if (n) return n;
    }
  }
  return null;
};

/** Resolve admission_number from batch-student list API shapes (nested relation / flat / aliases). */
const pickBatchStudentAdmissionNumber = (item) => {
  const admissionIdRaw = item?.organization_ems_admission_id;
  const admissionId =
    admissionIdRaw != null && String(admissionIdRaw).trim() !== ""
      ? String(admissionIdRaw).trim()
      : "";

  const numFromAdmissionObj = (adm) => {
    if (!adm || typeof adm !== "object" || Array.isArray(adm)) return null;
    const n =
      adm.admission_number ??
      adm.admission_no ??
      adm.admissionNumber ??
      adm.number;
    if (n === undefined || n === null || n === "") return null;
    const s = String(n).trim();
    return s === "" ? null : s;
  };

  const idsMatch = (row) => {
    if (!row || typeof row !== "object") return false;
    const id =
      row.organization_ems_admission_id ?? row.admission_id ?? row.id;
    return id != null && String(id).trim() === admissionId;
  };

  const pickFromAdmissionList = (list) => {
    if (!Array.isArray(list) || list.length === 0) return null;
    if (admissionId) {
      const match = list.find((row) => {
        if (!row || typeof row !== "object") return false;
        const id =
          row.organization_ems_admission_id ??
          row.id ??
          row.admission_id;
        return id != null && String(id).trim() === admissionId;
      });
      const fromMatch = numFromAdmissionObj(match);
      if (fromMatch) return fromMatch;
    }
    for (const row of list) {
      const n = numFromAdmissionObj(row);
      if (n) return n;
    }
    return null;
  };

  const single =
    item?.admission ??
    item?.Admission ??
    item?.organization_ems_admission ??
    item?.ems_admission ??
    item?.student?.admission ??
    null;

  let num = numFromAdmissionObj(single);
  if (!num) {
    num =
      pickFromAdmissionList(item?.admissions) ||
      pickFromAdmissionList(item?.student?.admissions) ||
      pickFromAdmissionList(item?.student?.admission_list);
  }
  if (!num) {
    num =
      item?.admissions?.[0] != null
        ? numFromAdmissionObj(item.admissions[0])
      : null;
  }
  if (!num) {
    num =
      item?.admission_number ??
      item?.admission_no ??
      item?.admissionNumber ??
      item?.student?.admission_number ??
      null;
  }
  // If `admission` is present but uses a non-standard shape, try matching nested objects by FK.
  if (!num && admissionId && item && typeof item === "object") {
    const candidates = [
      item.admission,
      item.Admission,
      item.organization_ems_admission,
      item.student?.admission,
      item.student?.Admission,
    ].filter((x) => x && typeof x === "object" && !Array.isArray(x));
    for (const c of candidates) {
      if (idsMatch(c)) {
        const n = numFromAdmissionObj(c);
        if (n) {
          num = n;
          break;
        }
      }
    }
  }
  if (!num) {
    num = deepFindBatchStudentAdmissionNumber(item);
  }
  if (num === undefined || num === null || num === "") return "-";
  return String(num).trim() || "-";
};

export const Configurations = [
  {
    route: "/program-monthly-traffic",
    ListProps: {
      title: "Program Monthly Traffic",
      apiEndpoint: "/api/ems/program-monthly-traffic",
      filteredApiEndpoint: "/api/ems/program-monthly-traffic/filtered",
      filterModule: "ems_program_monthly_traffic",
      useMetadataFilters: true,
      addRoute: "/program-monthly-traffic",
      editRouteBase: "/program-monthly-traffic/edit",
      tablePrimaryKey: "organization_ems_training_program_monthly_traffic_id",
      tableName: "ems_program_monthly_traffic",
      datagridKey: "ems_program_monthly_traffic_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "training_program_name",
        "country_name",
        "city_name",
        "traffic_month",
        "monthly_traffic",
      ],
      config: {
        sort: { field: "traffic_month", direction: "desc" },
        defaultVisibleColumns: [
          "training_program_name",
          "country_name",
          "city_name",
          "traffic_month",
          "monthly_traffic",
        ],
        columnLabels: {
          training_program_name: "Training Program",
          country_name: "Country",
          city_name: "City",
          traffic_month: "Month",
          monthly_traffic: "Monthly Traffic",
        },
        columnWidths: {
          training_program_name: 260,
          country_name: 180,
          city_name: 180,
          traffic_month: 140,
          monthly_traffic: 160,
        },
      },
      mapResponseToRows: (data) =>
        (Array.isArray(data) ? data : []).map((item) => ({
          id: item.organization_ems_training_program_monthly_traffic_id,
          organization_ems_training_program_monthly_traffic_id:
            item.organization_ems_training_program_monthly_traffic_id,
          training_program_name: item?.training_program_name || "-",
          country_name: item?.country_name || "-",
          city_name: item?.city_name || "-",
          traffic_month: item?.traffic_month || "-",
          monthly_traffic: item?.monthly_traffic ?? 0,
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        if (!formData.organization_id) {
          errors.organization_id = "Organization is required.";
        }
        if (!formData.training_program_id) {
          errors.training_program_id = "Training program is required.";
        }
        if (!formData.traffic_month) {
          errors.traffic_month = "Month is required.";
        }
        const traffic = String(formData.monthly_traffic ?? "").trim();
        if (traffic === "") {
          errors.monthly_traffic = "Monthly traffic is required.";
        } else if (!/^\d+$/.test(traffic)) {
          errors.monthly_traffic = "Monthly traffic must be a whole number.";
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },
      baseUrl: "/api/ems/program-monthly-traffic",
      initialData: {
        training_program_id: "",
        country_id: "",
        state_id: "",
        city_id: "",
        traffic_month: "",
        monthly_traffic: "",
      },
      successMessages: {
        add: "Monthly traffic added!",
        update: "Monthly traffic updated!",
      },
      redirectPath: "/program-monthly-traffic",
      headerProps: {
        addMessage: "Add Monthly Traffic",
        updateMessage: "Update Monthly Traffic",
        homeLink: "/program-monthly-traffic",
        homeText: "Program Monthly Traffic",
      },
      fieldGridLayout: {
        rows: [
          { variant: "full", mainKeys: ["training_program_id"] },
          {
            variant: "threeEqual",
            mainKeys: ["country_id", "state_id", "city_id"],
          },
          { variant: "twoEqual", mainKeys: ["traffic_month", "monthly_traffic"] },
        ],
      },
      Inputs: [
        {
          type: "Select",
          label: "Training Program *",
          mainKey: "training_program_id",
          OptionMainKey: "TrainingPrograms",
          menuKey: "organization_ems_training_program_id",
          formatValue: (val) => `${val.training_program_name || ""}`,
        },
        {
          type: "Select",
          label: "Country",
          mainKey: "country_id",
          OptionMainKey: "Countries",
          menuKey: "general_country_id",
          formatValue: (val) => `${val.country_name || ""}`,
        },
        {
          type: "Select",
          label: "State",
          mainKey: "state_id",
          OptionMainKey: "States",
          menuKey: "general_state_id",
          formatValue: (val) => `${val.state_name || ""}`,
        },
        {
          type: "Select",
          label: "City",
          mainKey: "city_id",
          OptionMainKey: "Cities",
          menuKey: "general_city_id",
          formatValue: (val) => `${val.city_name || ""}`,
        },
        {
          type: "Month",
          label: "Month *",
          mainKey: "traffic_month",
        },
        {
          type: "Text",
          label: "Monthly Traffic *",
          mainKey: "monthly_traffic",
          digitsOnly: true,
        },
      ],
      DataFetchers: [
        {
          name: "TrainingPrograms",
          Type: "Fetch",
          link: "/api/ems/training-programs",
        },
        { name: "Countries", Type: "Fetch", link: "/api/general/countries/v1" },
        {
          name: "States",
          Type: "FetchDepend",
          dependKey: "country_id",
          link: "/api/general/countries/${country_id}/states/v1",
        },
        {
          name: "Cities",
          Type: "FetchDepend",
          dependKey: "state_id",
          link: "/api/general/countries/${country_id}/states/${state_id}/cities",
        },
      ],
    },
  },
  {
    route: "/training-programs-categories",
    ListProps: {
      title: "Training Program Categories",
      apiEndpoint: "/api/ems/training-programs-categories",
      filteredApiEndpoint: "/api/ems/training-programs-categories/filtered",
      filterModule: "ems_training_program_categories",
      useMetadataFilters: true,
      addRoute: "/training-programs-categories",
      editRouteBase: "/training-programs-categories/edit",
      tablePrimaryKey: "organization_ems_training_program_category_id",
      tableName: "ems_training_program_categories",
      datagridKey: "ems_training_program_categories_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "training_program_category_name",
        "training_program_category_code",
        "description",
      ],
      config: {
        sort: { field: "training_program_category_name", direction: "asc" },
        defaultVisibleColumns: [
          "training_program_category_name",
          "training_program_category_code",
          "description",
        ],
        columnLabels: {
          training_program_category_name: "Category Name",
          training_program_category_code: "Category Code",
          description: "Description",
        },
        columnWidths: {
          training_program_category_name: 280,
          training_program_category_code: 160,
          description: 320,
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => ({
          id: item.organization_ems_training_program_category_id,
          training_program_category_name:
            item.training_program_category_name || "-",
          training_program_category_code:
            item.training_program_category_code || "-",
          description: item.description || "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const isAddMode =
          !formData?.organization_ems_lead_id && !formData?.id;
        if (isAddMode) {
          setFormErrors({});
          return true;
        }

        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          training_program_category_name,
          training_program_category_code,
          description,
        } = formData;

        // --- organization_id ---
        if (!organization_id)
          errors.organization_id = "Organization is required.";
        else if (isNaN(organization_id))
          errors.organization_id = "Organization ID must be a valid number.";

        // --- organization_entity_id (sometimes|nullable|integer) ---
        if (organization_entity_id && isNaN(organization_entity_id))
          errors.organization_entity_id = "Entity ID must be a valid number.";

        // --- training_program_category_name ---
        if (!training_program_category_name?.trim())
          errors.training_program_category_name =
            "Training Program Category name is required.";
        else if (training_program_category_name.length > 100)
          errors.training_program_category_name =
            "Category name cannot exceed 100 characters.";

        // --- training_program_category_code ---
        if (
          training_program_category_code &&
          training_program_category_code.length > 10
        )
          errors.training_program_category_code =
            "Category code cannot exceed 10 characters.";

        // --- description ---
        if (description && description.length > 255)
          errors.description = "Description cannot exceed 255 characters.";

        // --- set and return ---
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/training-programs-categories",
      initialData: {
        organization_entity_id: "",
        training_program_category_name: "",
        training_program_category_code: "",
        description: "",
      },
      successMessages: {
        add: "Training Program Category added!",
        update: "Training Program Category updated!",
      },
      redirectPath: "/training-programs-categories",
      headerProps: {
        addMessage: "Add Training Program Category",
        updateMessage: "Update Training Program Category",
        homeLink: "/training-programs-categories",
        homeText: "Training Program Category",
      },
      Inputs: [
        {
          type: "Text",
          label: "Training Program Category Name *",
          mainKey: "training_program_category_name",
        },
        {
          type: "Text",
          label: "Training Program Category Code ",
          mainKey: "training_program_category_code",
        },
        {
          type: "MultiRow",
          label: "Description ",
          mainKey: "description",
        },
      ],
      DataFetchers: [],
    },
  },
  {
    route: "/training-programs",
    ListProps: {
      title: "Training Programs",
      apiEndpoint: "/api/ems/training-programs",
      filteredApiEndpoint: "/api/ems/training-programs/filtered",
      filterModule: "ems_training_programs",
      useMetadataFilters: true,
      addRoute: "/training-programs",
      editRouteBase: "/training-programs/edit",
      tablePrimaryKey: "organization_ems_training_program_id",
      tableName: "ems_training_programs",
      datagridKey: "ems_training_programs_grid_v3",
      showAddButton: true,
      personalizeColumnKeys: [
        "training_category",
        "training_program",
        "duration_in_hours",
        "duration_in_months",
        "modules",
        "assessments",
        "interview_qa_url",
        "is_active",
      ],
      config: {
        sort: { field: "training_program_name", direction: "asc" },
        defaultVisibleColumns: [
          "training_category",
          "training_program",
          "duration_in_hours",
          "duration_in_months",
          "modules",
          "assessments",
        ],
        columnLabels: {
          training_category: "Category",
          training_program: "Training Program",
          duration_in_hours: "Hours",
          duration_in_months: "Months",
          modules: "Modules",
          assessments: "Assessments",
          interview_qa_url: "Interview Q/A URL",
          is_active: "Active",
        },
        columnWidths: {
          training_category: 200,
          training_program: 320,
          duration_in_hours: 110,
          duration_in_months: 110,
          modules: 110,
          assessments: 130,
          interview_qa_url: 220,
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => ({
          id: item.organization_ems_training_program_id,
          training_category:
            item?.category?.training_program_category_name || "-",
          training_program: item?.training_program_name || "-",
          __training_program_code: item.training_program_code || "",
          modules: Number(
            item?.module_count ?? item?.modules_count ?? 0
          ),
          assessments: Number(item?.assessment_count ?? 0),
          duration_in_hours: item.duration_in_hours || "-",
          duration_in_months: item.duration_in_months || "-",
          interview_qa_url: item.interview_qa_url || "-",
          is_active: item.is_active || "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          organization_ems_training_program_category_id,
          training_program_name,
          training_program_code,
          fee_structures,
          description,
          duration_in_hours,
          duration_in_months,
          notes_url,
          interview_qa_url,
          website_url,
          photo_url,
          is_active,
        } = formData;

        // --- organization_id (required|integer|exists) ---
        if (!organization_id)
          errors.organization_id = "Organization is required.";
        else if (isNaN(organization_id))
          errors.organization_id = "Organization ID must be a valid number.";

        const feeRows = Array.isArray(fee_structures) ? fee_structures : [];
        const inrRow = feeRows.find(
          (row) =>
            String(row?.currency_code || "").trim().toUpperCase() === "INR"
        );
        const hasInrAmount =
          inrRow?.amount !== "" &&
          inrRow?.amount !== null &&
          inrRow?.amount !== undefined;

        if (!hasInrAmount) {
          errors.fee_structures = "INR amount is required.";
        } else if (isNaN(Number(inrRow.amount))) {
          errors.fee_structures = "INR amount must be a valid number.";
        } else if (Number(inrRow.amount) <= 0) {
          errors.fee_structures = "INR amount must be greater than zero.";
        } else if (
          feeRows.some((row) => {
            if (
              row?.amount === "" ||
              row?.amount === null ||
              row?.amount === undefined
            ) {
              return false;
            }
            const n = Number(row.amount);
            return Number.isNaN(n) || n <= 0;
          })
        ) {
          errors.fee_structures =
            "All entered fee amounts must be positive numbers greater than zero.";
        }

        // --- organization_entity_id (sometimes|nullable|integer|exists) ---
        if (organization_entity_id && isNaN(organization_entity_id))
          errors.organization_entity_id = "Entity ID must be a valid number.";

        // --- organization_ems_training_program_category_id (sometimes|nullable|integer|exists) ---
        if (!organization_ems_training_program_category_id)
          errors.organization_ems_training_program_category_id =
            "Training Program Category is required.";
        else if (isNaN(organization_ems_training_program_category_id))
          errors.organization_id =
            "Training Porgram Category ID must be a valid number.";

        // --- training_program_name (required|string|max:150|unique) ---
        if (!training_program_name?.trim())
          errors.training_program_name = "Training Program name is required.";
        else if (training_program_name.length > 150)
          errors.training_program_name =
            "Training Program name cannot exceed 150 characters.";

        // --- training_program_code (nullable|string|max:20) ---
        if (training_program_code && training_program_code.length > 20)
          errors.training_program_code =
            "Training Program code cannot exceed 20 characters.";

        // --- description (nullable|string|max:255) ---
        if (description && description.length > 255)
          errors.description = "Description cannot exceed 255 characters.";

        // --- duration_in_hours (nullable, positive integer) ---
        if (
          duration_in_hours !== "" &&
          duration_in_hours !== null &&
          duration_in_hours !== undefined
        ) {
          const hours = Number(duration_in_hours);
          if (Number.isNaN(hours)) {
            errors.duration_in_hours = "Duration in hours must be a valid number.";
          } else if (hours <= 0 || !Number.isInteger(hours)) {
            errors.duration_in_hours =
              "Duration in hours must be a positive whole number.";
          }
        }

        // --- duration_in_months (nullable, positive number) ---
        if (
          duration_in_months !== "" &&
          duration_in_months !== null &&
          duration_in_months !== undefined
        ) {
          const months = Number(duration_in_months);
          if (Number.isNaN(months)) {
            errors.duration_in_months =
              "Duration in months must be a valid number.";
          } else if (months <= 0) {
            errors.duration_in_months =
              "Duration in months must be greater than zero.";
          }
        }

        const looksLikeHttpUrl = (value) => {
          const text = String(value || "").trim();
          if (!text) return true;
          if (text.length > 1000) return false;
          try {
            const parsed = new URL(text);
            return parsed.protocol === "http:" || parsed.protocol === "https:";
          } catch {
            return false;
          }
        };

        if (notes_url && String(notes_url).trim().length > 1000) {
          errors.notes_url = "Notes URL cannot exceed 1000 characters.";
        } else if (notes_url && !looksLikeHttpUrl(notes_url)) {
          errors.notes_url = "Enter a valid notes URL starting with http:// or https://.";
        }

        if (interview_qa_url && String(interview_qa_url).trim().length > 1000) {
          errors.interview_qa_url =
            "Interview Q/A URL cannot exceed 1000 characters.";
        } else if (interview_qa_url && !looksLikeHttpUrl(interview_qa_url)) {
          errors.interview_qa_url =
            "Enter a valid Interview Q/A URL starting with http:// or https://.";
        }

        if (website_url && String(website_url).trim().length > 1000) {
          errors.website_url = "Website URL cannot exceed 1000 characters.";
        } else if (website_url && !looksLikeHttpUrl(website_url)) {
          errors.website_url =
            "Enter a valid website URL starting with http:// or https://.";
        }

        if (photo_url instanceof File) {
          const maxBytes = 2 * 1024 * 1024;
          if (!String(photo_url.type || "").startsWith("image/")) {
            errors.photo_url = "Photo must be an image file.";
          } else if (photo_url.size > maxBytes) {
            errors.photo_url = "Photo must be 2 MB or smaller.";
          }
        }

        // --- is_active (optional / nullable) ---
        if (
          is_active !== undefined &&
          is_active !== null &&
          is_active !== "" &&
          typeof is_active !== "boolean" &&
          !["true", "false", 1, 0, "1", "0"].includes(is_active)
        )
          errors.is_active = "Active status must be true or false.";

        // --- set and return ---
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/training-programs",
      initialData: {
        organization_entity_id: "",
        organization_ems_training_program_category_id: "",
        training_program_name: "",
        training_program_code: "",
        fee_structures: [],
        description: "",
        duration_in_hours: "",
        duration_in_months: "",
        notes_url: "",
        interview_qa_url: "",
        website_url: "",
        photo_url: "",
        is_active: null,
      },
      successMessages: {
        add: "Training Program added!",
        update: "Training Program updated!",
      },
      redirectPath: "/training-programs",
      headerProps: {
        addMessage: "Add Training Program",
        updateMessage: "Update Training Program",
        homeLink: "/training-programs",
        homeText: "Training Program",
      },
      fieldGridLayout: {
        rows: [
          {
            variant: "full",
            mainKeys: ["organization_ems_training_program_category_id"],
          },
          {
            variant: "twoEqual",
            mainKeys: ["training_program_name", "training_program_code"],
          },
          { variant: "full", mainKeys: ["description"] },
          {
            variant: "twoEqual",
            mainKeys: ["duration_in_months", "duration_in_hours"],
          },
          {
            variant: "twoEqual",
            mainKeys: ["notes_url", "interview_qa_url"],
          },
          { variant: "full", mainKeys: ["website_url"] },
          { variant: "full", mainKeys: ["photo_url"] },
          { variant: "full", mainKeys: ["fee_structures"] },
        ],
      },
      Inputs: [
        {
          type: "Select",
          label: "Training Program Category *",
          mainKey: "organization_ems_training_program_category_id",
          OptionMainKey: "ProgramCategory",
          menuKey: "organization_ems_training_program_category_id",
          formatValue: (val) => `${val.training_program_category_name}`,
        },
        {
          type: "Text",
          label: "Training Program Name *",
          mainKey: "training_program_name",
        },
        {
          type: "Text",
          label: "Training Program Code ",
          mainKey: "training_program_code",
        },
        {
          type: "MultiRow",
          label: "Description ",
          mainKey: "description",
        },
        {
          type: "Number",
          label: "Duration in Months ",
          mainKey: "duration_in_months",
        },
        {
          type: "Number",
          label: "Duration in Hours ",
          mainKey: "duration_in_hours",
        },
        {
          type: "Text",
          label: "Notes URL",
          mainKey: "notes_url",
          placeholder: "https://...",
        },
        {
          type: "Text",
          label: "Interview Q/A URL",
          mainKey: "interview_qa_url",
          placeholder: "https://...",
        },
        {
          type: "Text",
          label: "Website URL",
          mainKey: "website_url",
          placeholder: "https://...",
        },
        {
          type: "Picture",
          label: "Photo",
          mainKey: "photo_url",
          align: "left",
          showFieldLabel: true,
          uploadButtonLabel: "Upload Image",
        },
        {
          type: "CurrencyAmountTable",
          label: "Fee Structure *",
          mainKey: "fee_structures",
          OptionMainKey: "Currencies",
        },
      ],
      DataFetchers: [
        {
          name: "ProgramCategory",
          Type: "Fetch",
          link: "/api/ems/training-programs-categories",
        },
        {
          name: "Currencies",
          Type: "Fetch",
          link: "/api/ems-currencies",
        },
      ],
    },
  },
  {
    route: "/training-program-modules",
    ListProps: {
      title: "Training Program Modules",
      apiEndpoint: "/api/ems/training-program-modules",
      filteredApiEndpoint: "/api/ems/training-program-modules/filtered",
      filterModule: "ems_training_program_modules",
      useMetadataFilters: true,
      addRoute: "/training-program-modules",
      editRouteBase: "/training-program-modules/edit",
      tablePrimaryKey: "organization_ems_training_program_module_id",
      tableName: "ems_training_program_modules",
      datagridKey: "ems_training_program_modules_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "category",
        "training_program",
        "module_count",
      ],
      config: {
        sort: {
          field: "category",
          direction: "asc",
        },
        defaultVisibleColumns: [
          "category",
          "training_program",
          "module_count",
        ],
        columnLabels: {
          category: "Category",
          training_program: "Training Program",
          module_count: "Modules",
        },
        columnWidths: {
          category: 240,
          training_program: 320,
          module_count: 120,
        },
      },
      mapResponseToRows: (data) => {
        const formatDuration = (value) =>
          value === null || value === undefined || value === "" ? "—" : value;
        const mapModule = (mod) => ({
          id: mod.organization_ems_training_program_module_id ?? mod.id,
          organization_ems_training_program_module_id:
            mod.organization_ems_training_program_module_id ?? mod.id,
          module_name: mod.module_name || "-",
          duration_in_hours: formatDuration(mod.duration_in_hours),
          duration_in_days: formatDuration(mod.duration_in_days),
        });
        const rows = Array.isArray(data) ? data : [];
        const grouped = rows.some((item) => Array.isArray(item?.modules))
          ? rows.map((item) => ({
              categoryId: item.organization_ems_training_program_category_id,
              programId: item.organization_ems_training_program_id,
              category:
                item?.category?.training_program_category_name ||
                item?.category ||
                "-",
              training_program:
                item?.training_program?.training_program_name ||
                item?.training_program ||
                "-",
              modules: (item.modules || []).map(mapModule),
            }))
          : (() => {
              const byKey = new Map();
              rows.forEach((item) => {
                const categoryId =
                  item.organization_ems_training_program_category_id;
                const programId = item.organization_ems_training_program_id;
                const key = `${categoryId ?? "0"}:${programId ?? "0"}`;
                if (!byKey.has(key)) {
                  byKey.set(key, {
                    categoryId,
                    programId,
                    category:
                      item?.category?.training_program_category_name || "-",
                    training_program:
                      item?.training_program?.training_program_name || "-",
                    modules: [],
                  });
                }
                byKey.get(key).modules.push(mapModule(item));
              });
              return Array.from(byKey.values());
            })();

        return grouped.map((group) => {
          const id = `${group.categoryId ?? "0"}:${group.programId ?? "0"}`;
          const count = group.modules.length;
          return {
            id,
            organization_ems_training_program_module_id: id,
            category: group.category,
            training_program: group.training_program,
            module_count: count === 1 ? "1 module" : `${count} modules`,
            modules: group.modules,
          };
        });
      },
    },
    FormProps: {
      validateForm: (formData, setFormErrors, mode) => {
        const errors = {};

        if (!formData?.organization_id) {
          errors.organization_id = "Organization is required.";
        }
        if (!formData?.organization_ems_training_program_category_id) {
          errors.organization_ems_training_program_category_id =
            "Category is required.";
        }
        if (!formData?.organization_ems_training_program_id) {
          errors.organization_ems_training_program_id =
            "Training program is required.";
        }

        const rows = Array.isArray(formData?.module_names)
          ? formData.module_names
          : [];
        const named = rows
          .map((row) =>
            typeof row === "string"
              ? { module_name: row, duration_in_hours: "", duration_in_days: "" }
              : row || {}
          )
          .map((row) => ({
            organization_ems_training_program_module_id:
              row?.organization_ems_training_program_module_id || null,
            module_name: String(row?.module_name || "").trim(),
            duration_in_hours: row?.duration_in_hours,
            duration_in_days: row?.duration_in_days,
          }))
          .filter((row) => row.module_name);
        if (!named.length) {
          errors.module_names = "Add at least one module name.";
        } else if (named.some((row) => row.module_name.length > 150)) {
          errors.module_names = "Module name cannot exceed 150 characters.";
        } else {
          const invalidDuration = named.some((row) => {
            const check = (v) => {
              if (v === "" || v === null || v === undefined) return false;
              const n = Number(v);
              return Number.isNaN(n) || n < 0;
            };
            return check(row.duration_in_hours) || check(row.duration_in_days);
          });
          if (invalidDuration) {
            errors.module_names =
              "Duration in hours and days must be numbers 0 or greater.";
          }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },
      baseUrl: "/api/ems/training-program-modules",
      initialData: {
        organization_entity_id: "",
        organization_ems_training_program_category_id: "",
        organization_ems_training_program_id: "",
        module_name: "",
        duration_in_hours: "",
        duration_in_days: "",
        module_names: Array.from({ length: 10 }, () => ({
          module_name: "",
          duration_in_hours: "",
          duration_in_days: "",
        })),
      },
      successMessages: {
        add: "Training program module(s) added!",
        update: "Training program module(s) updated!",
      },
      redirectPath: "/training-program-modules",
      headerProps: {
        addMessage: "Add Training Program Modules",
        updateMessage: "Update Training Program Modules",
        homeLink: "/training-program-modules",
        homeText: "Training Program Modules",
      },
      Inputs: [
        {
          type: "Select",
          label: "Training Program Category *",
          mainKey: "organization_ems_training_program_category_id",
          OptionMainKey: "ProgramCategory",
          menuKey: "organization_ems_training_program_category_id",
          formatValue: (val) => `${val.training_program_category_name}`,
        },
        {
          type: "Select",
          label: "Training Program *",
          mainKey: "organization_ems_training_program_id",
          OptionMainKey: "TrainingPrograms",
          menuKey: "organization_ems_training_program_id",
          formatValue: (val) => `${val.training_program_name}`,
        },
        {
          type: "RepeatableText",
          label: "Module Names *",
          mainKey: "module_names",
          addLabel: "Add another module",
          defaultCount: 10,
          fields: [
            { key: "module_name", label: "Module Name", type: "text" },
            {
              key: "duration_in_hours",
              label: "Hours",
              heading: "Duration in Hours",
              type: "number",
            },
            {
              key: "duration_in_days",
              label: "Days",
              heading: "Duration in Days",
              type: "number",
            },
          ],
        },
        {
          type: "Text",
          label: "Module Name *",
          mainKey: "module_name",
          hideInAdd: true,
          hideInEdit: true,
        },
        {
          type: "Number",
          label: "Duration in Hours",
          mainKey: "duration_in_hours",
          hideInAdd: true,
          hideInEdit: true,
        },
        {
          type: "Number",
          label: "Duration in Days",
          mainKey: "duration_in_days",
          hideInAdd: true,
          hideInEdit: true,
        },
      ],
      DataFetchers: [
        {
          name: "ProgramCategory",
          Type: "Fetch",
          link: "/api/ems/training-programs-categories",
        },
        {
          name: "TrainingPrograms",
          Type: "Fetch",
          link: "/api/ems/training-programs",
        },
      ],
    },
  },
  {
    route: "/module-questions",
    ListProps: {
      title: "Module Questions",
      apiEndpoint: "/api/ems/module-questions",
      filteredApiEndpoint: "/api/ems/module-questions/filtered",
      filterModule: "ems_module_questions",
      useMetadataFilters: true,
      addRoute: "/module-questions",
      editRouteBase: "/module-questions/edit",
      tablePrimaryKey: "organization_ems_module_question_id",
      tableName: "ems_module_questions",
      datagridKey: "ems_module_questions_grouped_marks_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "category",
        "training_program",
        "question_count",
        "total_marks",
      ],
      config: {
        sort: {
          field: "category",
          direction: "asc",
        },
        defaultVisibleColumns: [
          "category",
          "training_program",
          "question_count",
          "total_marks",
        ],
        columnLabels: {
          category: "Category",
          training_program: "Training Program",
          question_count: "Questions",
          total_marks: "Total Marks",
        },
        columnWidths: {
          category: 240,
          training_program: 320,
          question_count: 120,
          total_marks: 130,
        },
      },
      mapResponseToRows: (data) => {
        const formatMarks = (value) => {
          const n = Number(value);
          if (!Number.isFinite(n)) return "0";
          return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
        };
        const mapQuestion = (item) => ({
          id: item.organization_ems_module_question_id ?? item.id,
          organization_ems_module_question_id:
            item.organization_ems_module_question_id ?? item.id,
          organization_ems_training_program_module_id:
            item.organization_ems_training_program_module_id ??
            item?.module?.organization_ems_training_program_module_id ??
            null,
          module_name:
            item?.module_name ||
            item?.module?.module_name ||
            item?.training_program_module?.module_name ||
            "-",
          module: item?.module,
          question: item.question || "-",
          option_1: item.option_1 || "-",
          option_2: item.option_2 || "-",
          option_3: item.option_3 || "-",
          option_4: item.option_4 || "-",
          correct_answer: item.correct_answer || "",
          marks: formatMarks(item.marks ?? 1),
        });
        const sumMarks = (questions) =>
          formatMarks(
            (Array.isArray(questions) ? questions : []).reduce(
              (sum, row) => sum + (Number(row?.marks) || 0),
              0
            )
          );
        const nestModules = (questions) => {
          const byModule = new Map();
          (Array.isArray(questions) ? questions : []).forEach((item) => {
            const moduleId =
              item.organization_ems_training_program_module_id ??
              item?.module?.organization_ems_training_program_module_id ??
              item.module_name ??
              "0";
            const key = String(moduleId);
            if (!byModule.has(key)) {
              byModule.set(key, {
                organization_ems_training_program_module_id: moduleId,
                module_name: item.module_name || item?.module?.module_name || "-",
                questions: [],
              });
            }
            byModule.get(key).questions.push(item);
          });
          return Array.from(byModule.values()).map((mod) => ({
            ...mod,
            question_count: mod.questions.length,
            total_marks: sumMarks(mod.questions),
          }));
        };
        const rows = Array.isArray(data) ? data : [];
        const grouped = rows.some((item) => Array.isArray(item?.questions) || Array.isArray(item?.modules))
          ? rows.map((item) => {
              const questions = (item.questions || []).map(mapQuestion);
              const modules = Array.isArray(item.modules) && item.modules.length
                ? item.modules.map((mod) => {
                    const moduleQuestions = (mod.questions || []).map(mapQuestion);
                    return {
                      organization_ems_training_program_module_id:
                        mod.organization_ems_training_program_module_id ??
                        mod.id,
                      module_name: mod.module_name || "-",
                      question_count: moduleQuestions.length,
                      total_marks: formatMarks(
                        mod.total_marks ?? sumMarks(moduleQuestions)
                      ),
                      questions: moduleQuestions,
                    };
                  })
                : nestModules(questions);
              return {
                categoryId: item.organization_ems_training_program_category_id,
                programId: item.organization_ems_training_program_id,
                category:
                  item?.category?.training_program_category_name ||
                  item?.category ||
                  "-",
                training_program:
                  item?.training_program?.training_program_name ||
                  item?.training_program ||
                  "-",
                total_marks: formatMarks(item.total_marks ?? sumMarks(questions)),
                modules,
                questions,
              };
            })
          : (() => {
              const byKey = new Map();
              rows.forEach((item) => {
                const categoryId =
                  item.organization_ems_training_program_category_id ??
                  item?.module?.organization_ems_training_program_category_id;
                const programId =
                  item.organization_ems_training_program_id ??
                  item?.module?.organization_ems_training_program_id;
                const key = `${categoryId ?? "0"}:${programId ?? "0"}`;
                if (!byKey.has(key)) {
                  byKey.set(key, {
                    categoryId,
                    programId,
                    category:
                      item?.module?.category?.training_program_category_name ||
                      item?.category?.training_program_category_name ||
                      "-",
                    training_program:
                      item?.module?.training_program?.training_program_name ||
                      item?.training_program?.training_program_name ||
                      "-",
                    questions: [],
                  });
                }
                byKey.get(key).questions.push(mapQuestion(item));
              });
              return Array.from(byKey.values()).map((group) => ({
                ...group,
                modules: nestModules(group.questions),
                total_marks: sumMarks(group.questions),
              }));
            })();

        return grouped.map((group) => {
          const id = `${group.categoryId ?? "0"}:${group.programId ?? "0"}`;
          const count = group.questions.length;
          return {
            id,
            organization_ems_module_question_id: id,
            category: group.category,
            training_program: group.training_program,
            question_count:
              count === 1 ? "1 question" : `${count} questions`,
            total_marks: group.total_marks,
            modules: group.modules || [],
            questions: group.questions,
          };
        });
      },
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const isBulk =
          formData?._form_mode === "add" ||
          Boolean(formData?._edit_all) ||
          !formData?.organization_ems_module_question_id;
        const validAnswers = ["option_1", "option_2", "option_3", "option_4"];

        if (!formData?.organization_id) {
          errors.organization_id = "Organization is required.";
        }
        if (!formData?.organization_ems_training_program_module_id) {
          errors.organization_ems_training_program_module_id =
            "Training program module is required.";
        }

        const isValidMarks = (value) => {
          const n = Number(value);
          return Number.isFinite(n) && n >= 0.01 && n <= 9999.99;
        };

        const validateOne = (row, prefix = "") => {
          const setErr = (key, message) => {
            if (prefix) errors.questions = errors.questions || message;
            else errors[key] = message;
          };
          if (!String(row?.question || "").trim()) {
            setErr("question", "Question is required.");
          } else if (String(row.question).trim().length > 5000) {
            setErr("question", "Question cannot exceed 5000 characters.");
          }
          ["option_1", "option_2", "option_3", "option_4"].forEach((key, idx) => {
            const value = String(row?.[key] || "").trim();
            if (!value) {
              setErr(key, `Option ${idx + 1} is required.`);
            } else if (value.length > 500) {
              setErr(key, `Option ${idx + 1} cannot exceed 500 characters.`);
            }
          });
          if (!row?.correct_answer) {
            setErr("correct_answer", "Correct answer is required.");
          } else if (!validAnswers.includes(row.correct_answer)) {
            setErr("correct_answer", "Select Option 1, 2, 3, or 4.");
          }
          if (!isValidMarks(row?.marks)) {
            setErr("marks", "Marks must be a number greater than 0.");
          }
        };

        if (isBulk) {
          const rows = (Array.isArray(formData?.questions) ? formData.questions : [])
            .map((row) => row || {})
            .filter((row) => String(row.question || "").trim());
          if (!rows.length) {
            errors.questions =
              "Add at least one question, or import questions from a file.";
          } else {
            const incomplete = rows.find((row) => {
              const optionsFilled = ["option_1", "option_2", "option_3", "option_4"].every(
                (key) => String(row?.[key] || "").trim()
              );
              return (
                !optionsFilled ||
                !validAnswers.includes(row.correct_answer) ||
                !isValidMarks(row?.marks) ||
                String(row.question).trim().length > 5000
              );
            });
            if (incomplete) {
              errors.questions =
                "Each question needs 4 options, a correct answer (Option 1-4), and marks.";
            } else {
              const seen = new Set();
              const hasDuplicate = rows.some((row) => {
                const key = String(row.question || "").trim().toLowerCase();
                if (!key) return false;
                if (seen.has(key)) return true;
                seen.add(key);
                return false;
              });
              if (hasDuplicate) {
                errors.questions =
                  "This question already exists in this organization.";
              }
            }
          }
        } else {
          validateOne(formData);
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },
      baseUrl: "/api/ems/module-questions",
      initialData: {
        organization_entity_id: "",
        organization_ems_training_program_category_id: "",
        organization_ems_training_program_id: "",
        organization_ems_training_program_module_id: "",
        question: "",
        option_1: "",
        option_2: "",
        option_3: "",
        option_4: "",
        correct_answer: "",
        marks: "1",
        questions: Array.from({ length: 3 }, () => ({
          question: "",
          option_1: "",
          option_2: "",
          option_3: "",
          option_4: "",
          correct_answer: "",
          marks: "1",
        })),
        questions_import: "",
        questions_import_name: "",
      },
      successMessages: {
        add: "Module question(s) added!",
        update: "Module question(s) updated!",
      },
      redirectPath: "/module-questions",
      headerProps: {
        addMessage: "Add Module Questions",
        updateMessage: "Update Module Questions",
        homeLink: "/module-questions",
        homeText: "Module Questions",
      },
      fieldGridLayout: {
        rows: [
          {
            variant: "threeEqual",
            mainKeys: [
              "organization_ems_training_program_category_id",
              "organization_ems_training_program_id",
              "organization_ems_training_program_module_id",
            ],
          },
          { variant: "full", mainKeys: ["questions_import"] },
          { variant: "full", mainKeys: ["questions"] },
          { variant: "full", mainKeys: ["question"] },
          {
            variant: "twoEqual",
            mainKeys: ["option_1", "option_2"],
          },
          {
            variant: "twoEqual",
            mainKeys: ["option_3", "option_4"],
          },
          {
            variant: "twoEqual",
            mainKeys: ["correct_answer", "marks"],
          },
        ],
        sections: [],
      },
      Inputs: [
        {
          type: "Select",
          label: "Training Program Category",
          mainKey: "organization_ems_training_program_category_id",
          OptionMainKey: "ProgramCategory",
          menuKey: "organization_ems_training_program_category_id",
          formatValue: (val) => `${val.training_program_category_name}`,
        },
        {
          type: "Select",
          label: "Training Program",
          mainKey: "organization_ems_training_program_id",
          OptionMainKey: "TrainingPrograms",
          menuKey: "organization_ems_training_program_id",
          formatValue: (val) => `${val.training_program_name}`,
        },
        {
          type: "Select",
          label: "Module *",
          mainKey: "organization_ems_training_program_module_id",
          OptionMainKey: "TrainingProgramModules",
          menuKey: "organization_ems_training_program_module_id",
          formatValue: (val) => `${val.module_name}`,
        },
        {
          type: "File",
          label: "Import Questions",
          mainKey: "questions_import",
          accept: ".xlsx,.xls,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv",
          visibleWhen: (_formData, mode) => mode === "add",
          parseImport: "moduleQuestions",
          downloadTemplate: "moduleQuestions",
          helperText:
            "Excel or CSV columns: Question, Option 1, Option 2, Option 3, Option 4, Correct Answer (Option 1-4), Marks.",
        },
        {
          type: "RepeatableText",
          label: "Questions *",
          mainKey: "questions",
          addLabel: "Add another question",
          defaultCount: 3,
          rowLayout: "questionCard",
          visibleWhen: (_formData, mode) =>
            mode === "add" || Boolean(_formData?._edit_all),
          fields: [
            { key: "question", label: "Question", type: "textarea" },
            { key: "option_1", label: "Option 1", type: "text" },
            { key: "option_2", label: "Option 2", type: "text" },
            { key: "option_3", label: "Option 3", type: "text" },
            { key: "option_4", label: "Option 4", type: "text" },
            {
              key: "correct_answer",
              label: "Correct Answer",
              type: "select",
              options: [
                { value: "option_1", label: "Option 1" },
                { value: "option_2", label: "Option 2" },
                { value: "option_3", label: "Option 3" },
                { value: "option_4", label: "Option 4" },
              ],
            },
            {
              key: "marks",
              label: "Marks",
              type: "number",
              defaultValue: "1",
              min: 0.01,
              step: "0.5",
            },
          ],
        },
        {
          type: "MultiRow",
          label: "Question *",
          mainKey: "question",
          hideInAdd: true,
          visibleWhen: (_formData, mode) =>
            mode !== "add" && !_formData?._edit_all,
        },
        {
          type: "Text",
          label: "Option 1 *",
          mainKey: "option_1",
          hideInAdd: true,
          visibleWhen: (_formData, mode) =>
            mode !== "add" && !_formData?._edit_all,
        },
        {
          type: "Text",
          label: "Option 2 *",
          mainKey: "option_2",
          hideInAdd: true,
          visibleWhen: (_formData, mode) =>
            mode !== "add" && !_formData?._edit_all,
        },
        {
          type: "Text",
          label: "Option 3 *",
          mainKey: "option_3",
          hideInAdd: true,
          visibleWhen: (_formData, mode) =>
            mode !== "add" && !_formData?._edit_all,
        },
        {
          type: "Text",
          label: "Option 4 *",
          mainKey: "option_4",
          hideInAdd: true,
          visibleWhen: (_formData, mode) =>
            mode !== "add" && !_formData?._edit_all,
        },
        {
          type: "Select",
          label: "Correct Answer *",
          mainKey: "correct_answer",
          OptionMainKey: "CorrectAnswers",
          menuKey: "value",
          formatValue: (val) => val?.label || val,
          hideInAdd: true,
          visibleWhen: (_formData, mode) =>
            mode !== "add" && !_formData?._edit_all,
        },
        {
          type: "Number",
          label: "Marks *",
          mainKey: "marks",
          hideInAdd: true,
          visibleWhen: (_formData, mode) =>
            mode !== "add" && !_formData?._edit_all,
        },
      ],
      DataFetchers: [
        {
          name: "ProgramCategory",
          Type: "Fetch",
          link: "/api/ems/training-programs-categories",
        },
        {
          name: "TrainingPrograms",
          Type: "Fetch",
          link: "/api/ems/training-programs",
        },
        {
          name: "TrainingProgramModules",
          Type: "Fetch",
          link: "/api/ems/training-program-modules",
        },
        {
          name: "CorrectAnswers",
          Type: "NoFetch",
          Options: [
            { value: "option_1", label: "Option 1" },
            { value: "option_2", label: "Option 2" },
            { value: "option_3", label: "Option 3" },
            { value: "option_4", label: "Option 4" },
          ],
        },
      ],
    },
  },
  {
    route: "/training-spaces",
    ListProps: {
      title: "Training Spaces",
      apiEndpoint: "/api/ems/training-spaces",
      filteredApiEndpoint: "/api/ems/training-spaces/filtered",
      filterModule: "ems_training_spaces",
      useMetadataFilters: true,
      addRoute: "/training-spaces",
      editRouteBase: "/training-spaces/edit",
      tablePrimaryKey: "ems_training_space_id",
      tableName: "ems_training_spaces",
      datagridKey: "ems_training_spaces_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "space_name",
        "capacity",
        "floor_number",
        "mode",
        "status",
        "time_slots",
      ],
      config: {
        sort: { field: "space_name", direction: "asc" },
        defaultVisibleColumns: [
          "space_name",
          "floor_number",
          "mode",
          "status",
        ],
        columnLabels: {
          space_name: "Space Name",
          capacity: "Capacity",
          floor_number: "Floor",
          mode: "Mode",
          status: "Status",
          time_slots: "Time Slots",
        },
        columnWidths: {
          space_name: 300,
          capacity: 140,
          floor_number: 110,
          mode: 130,
          status: 120,
        },
      },
      mapResponseToRows: (data) =>
        (Array.isArray(data) ? data : []).map((item) => {
          const explicitPk =
            item?.organization_ems_training_space_id ??
            item?.organization_ems_training_spaces_id ??
            item?.organization_ems_training_spaceID ??
            item?.organization_ems_training_spacesID ??
            item?.training_space_id ??
            item?.training_spaces_id ??
            item?.space_id ??
            item?.id;
          const inferredPk =
            Object.entries(item || {}).find(([k, v]) => {
              const key = String(k || "").toLowerCase();
              if (!key.endsWith("_id")) return false;
              if (!key.includes("training_space")) return false;
              if (v === null || v === undefined || String(v).trim() === "")
                return false;
              return !Number.isNaN(Number(v));
            })?.[1] ?? null;
          const pk =
            item?.ems_training_space_id ??
            explicitPk ??
            inferredPk ??
            null;
          return {
            id: pk,
            ems_training_space_id: pk,
            organization_ems_training_space_id: pk,
            space_name: item?.space_name || "-",
            capacity:
              item?.capacity === null || item?.capacity === undefined
                ? "-"
                : String(item.capacity),
            floor_number:
              item?.floor_number === null || item?.floor_number === undefined
                ? "-"
                : Number(item.floor_number),
            mode: item?.mode || "-",
            status: item?.status || "-",
            training_space_slots: Array.isArray(item?.training_space_slots)
              ? item.training_space_slots
              : [],
            time_slots: Array.isArray(item?.training_space_slots) && item.training_space_slots.length
              ? item.training_space_slots
                  .map(
                    (slot) =>
                      `${formatTime12h(slot?.start_time) || "--:--"} - ${
                        formatTime12h(slot?.end_time) || "--:--"
                      }`
                  )
                  .join(", ")
              : "-",
          };
        }),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          space_name,
          capacity,
          floor_number,
          mode,
          status,
          training_space_slots,
        } = formData;

        if (!organization_id) {
          errors.organization_id = "Organization is required.";
        } else if (isNaN(Number(organization_id))) {
          errors.organization_id = "Organization ID must be a valid number.";
        }

        if (
          organization_entity_id &&
          organization_entity_id !== "" &&
          isNaN(Number(organization_entity_id))
        ) {
          errors.organization_entity_id = "Entity ID must be a valid number.";
        }

        if (!space_name || String(space_name).trim() === "") {
          errors.space_name = "Space name is required.";
        } else if (String(space_name).length > 150) {
          errors.space_name = "Space name cannot exceed 150 characters.";
        }

        if (
          capacity !== "" &&
          capacity !== null &&
          capacity !== undefined &&
          (isNaN(Number(capacity)) || !Number.isInteger(Number(capacity)))
        ) {
          errors.capacity = "Capacity must be a valid integer.";
        }

        if (
          floor_number !== "" &&
          floor_number !== null &&
          floor_number !== undefined &&
          (isNaN(Number(floor_number)) || !Number.isInteger(Number(floor_number)))
        ) {
          errors.floor_number = "Floor number must be a valid integer.";
        }

        if (
          mode &&
          !["Online", "Offline", "Hybrid"].includes(String(mode))
        ) {
          errors.mode = "Mode must be Online, Offline, or Hybrid.";
        }

        if (
          status &&
          !["Active", "Inactive"].includes(String(status))
        ) {
          errors.status = "Status must be Active or Inactive.";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },
      baseUrl: "/api/ems/training-spaces",
      initialData: {
        organization_entity_id: "",
        space_name: "",
        capacity: "",
        floor_number: "",
        mode: "",
        status: "",
        training_space_slots: [{ start_time: "", end_time: "" }],
      },
      successMessages: {
        add: "Training Space added!",
        update: "Training Space updated!",
      },
      redirectPath: "/training-spaces",
      headerProps: {
        addMessage: "Add Training Space",
        updateMessage: "Update Training Space",
        homeLink: "/training-spaces",
        homeText: "Training Spaces",
      },
      fieldGridLayout: {
        rows: [
          { variant: "twoEqual", mainKeys: ["space_name", "floor_number"] },
          { variant: "twoEqual", mainKeys: ["capacity", "mode"] },
          { variant: "full", itemMds: [1, 1], mainKeys: ["status"] },
          { variant: "full", mainKeys: ["training_space_slots"] },
        ],
      },
      Inputs: [
        {
          type: "Text",
          label: "Space Name *",
          mainKey: "space_name",
        },
        {
          type: "Number",
          label: "Capacity",
          mainKey: "capacity",
        },
        {
          type: "TimeSlotsTable",
          label: "Time Slots *",
          mainKey: "training_space_slots",
        },
        {
          type: "Number",
          label: "Floor Number",
          mainKey: "floor_number",
        },
        {
          type: "Select",
          label: "Mode",
          mainKey: "mode",
          OptionMainKey: "TrainingSpaceModes",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "Select",
          label: "Status",
          mainKey: "status",
          OptionMainKey: "TrainingSpaceStatuses",
          menuKey: null,
          formatValue: (val) => val,
        },
      ],
      DataFetchers: [
        {
          name: "TrainingSpaceModes",
          Type: "NoFetch",
          Options: ["Online", "Offline", "Hybrid"],
        },
        {
          name: "TrainingSpaceStatuses",
          Type: "NoFetch",
          Options: ["Active", "Inactive"],
        },
      ],
    },
  },
  {
    route: "/students",
    ListProps: {
      title: "Students",
      apiEndpoint: "/api/ems/students",
      filteredApiEndpoint: "/api/ems/students/filtered",
      filterModule: "ems_students",
      useMetadataFilters: true,
      addRoute: "/students",
      editRouteBase: "/students/edit",
      tablePrimaryKey: "organization_ems_student_id",
      tableName: "ems_students",
      datagridKey: "ems_students_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "student_name",
        "student_id",
        "gender",
        "student_status",
        "country",
        "state",
        "city",
        "phone",
        "email",
        "student_training_program",
        "latest_admission_date",
        "date_of_birth",
        "certificate_name",
        "organization_ems_lead_id",
        "training_program_id",
      ],
      config: {
        sort: { field: "latest_admission_ts", direction: "desc" },
        defaultVisibleColumns: [
          "student_name",
          "gender",
          "country",
          "state",
          "city",
          "phone",
          "email",
        ],
        columnLabels: {
          student_name: "Student Name",
          gender: "Gender",
          country: "Country",
          state: "State",
          city: "City",
          phone: "Phone",
          email: "Email",
        },
        columnWidths: {
          email: 260,
        },
        admissionPeriodOptions: [
          { value: "this_week", label: "This Week" },
          { value: "last_week", label: "Last Week" },
          { value: "next_week", label: "Next Week" },
          { value: "this_month", label: "This Month" },
          { value: "last_month", label: "Last Month" },
          { value: "next_month", label: "Next Month" },
          { value: "this_year", label: "This Year" },
          { value: "last_year", label: "Last Year" },
          { value: "next_year", label: "Next Year" },
        ],
      },
      mapResponseToRows: (data) => {
        // NOTE: rows arrive already ordered by the server (StudentsController::indexWithFilters
        // applies sort.field/sort.direction from the grid's column-sort click). Do not re-sort
        // here — that previously overrode every column sort (e.g. clicking "Student Name")
        // with a hardcoded "latest admission time desc" order.
        const sorted = Array.isArray(data) ? data : [];

        return sorted.map((item) => ({
          student_training_program_id:
            item?.training_program_id ??
            item?.organization_ems_training_program_id ??
            item?.training_program?.organization_ems_training_program_id ??
            item?.training_program?.training_program_id ??
            (Array.isArray(item?.student_admissions) && item.student_admissions.length
              ? item.student_admissions
                  .map(
                    (a) =>
                      a?.training_program_id ??
                      a?.training_program?.organization_ems_training_program_id ??
                      a?.training_program?.training_program_id
                  )
                  .find((v) => v != null && String(v).trim() !== "")
              : "") ??
            "",
          student_training_program: (() => {
            const admissions = Array.isArray(item?.student_admissions) ? item.student_admissions : [];
            const names = [...new Set(admissions.map((a) => a?.training_program?.training_program_name).filter(Boolean))];
            if (names.length > 1) return "Multiple";
            return names[0] ?? item?.training_program?.training_program_name ?? "-";
          })(),
          student_program_count: (() => {
            const admissions = Array.isArray(item?.student_admissions) ? item.student_admissions : [];
            return new Set(admissions.map((a) => a?.training_program?.training_program_name).filter(Boolean)).size;
          })(),
          pending_fee: (() => {
            const installments = Array.isArray(item?.student_installments) ? item.student_installments : [];
            if (!installments.length) return null;
            const pending = installments.filter((inst) => {
              const st = String(inst?.payment_status ?? "").toLowerCase();
              return st !== "paid" && st !== "cleared";
            });
            const total = pending.reduce((sum, inst) => sum + (Number(inst?.fee_inr) || Number(inst?.amount_inr) || Number(inst?.amount) || 0), 0);
            return total > 0 ? total : "Cleared";
          })(),
          id: item.organization_ems_student_id,
          organization_ems_student_id: item.organization_ems_student_id,
          // Optional relations used for "Students -> Admission" action
          organization_ems_lead_id:
            item?.organization_ems_lead_id ??
            item?.lead_id ??
            item?.lead?.organization_ems_lead_id ??
            item?.lead?.lead_id ??
            "",
          training_program_id:
            item?.training_program_id ??
            item?.organization_ems_training_program_id ??
            item?.training_program?.organization_ems_training_program_id ??
            item?.training_program?.training_program_id ??
            "",
          student_name:
            [item?.first_name, item?.middle_name, item?.last_name]
              .filter((n) => n && String(n).trim() !== "")
              .join(" ")
              .trim() || "-",
          gender: item.gender || "-",
          phone: item.phone || "-",
          email: item.email || "-",
          country: item?.country?.country_name || "-",
          state: item?.state?.state_name || "-",
          city: item?.city?.city_name || "-",
          date_of_birth: item.date_of_birth || "-",
          alternate_phone: item.alternate_phone || "-",
          address: item.address || "-",
          certificate_name: item.certificate_name || "-",
          student_status: item.student_status || "-",
          remarks: item.remarks || "-",
          profile_image_url: item.profile_image_url || "-",
          student_id: item.student_id || "-",
          latest_admission_date:
            item?.latest_admission_date ??
            (Array.isArray(item?.student_admissions) && item.student_admissions.length
              ? item.student_admissions
                  .map((a) => a?.admission_date)
                  .find((v) => v != null && String(v).trim() !== "")
              : null) ??
            "-",
          latest_admission_ts: (() => {
            const raw =
              item?.latest_admission_date ??
              (Array.isArray(item?.student_admissions) && item.student_admissions.length
                ? item.student_admissions
                    .map((a) => a?.admission_date ?? a?.created_at)
                    .find((v) => v != null && String(v).trim() !== "")
                : null);
            const t = raw ? new Date(raw).getTime() : Number.NaN;
            return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
          })(),
          // Keep nested relations for Students list accordion details.
          student_admissions: Array.isArray(item?.student_admissions)
            ? item.student_admissions
            : Array.isArray(item?.StudentAdmissions)
              ? item.StudentAdmissions
              : [],
          student_installments: Array.isArray(item?.student_installments)
            ? item.student_installments
            : Array.isArray(item?.StudentInstallments)
              ? item.StudentInstallments
              : [],
          studnet_batches: Array.isArray(item?.studnet_batches)
            ? item.studnet_batches
            : Array.isArray(item?.StudnetBatches)
              ? item.StudnetBatches
              : Array.isArray(item?.student_batches)
                ? item.student_batches
                : [],
          certficates: (() => {
            const raw =
              item?.certficates ??
              item?.certficate ??
              item?.Certficate ??
              item?.certificate ??
              item?.certificates ??
              null;
            if (Array.isArray(raw)) return raw;
            if (raw && typeof raw === "object") return [raw];
            return [];
          })(),
          placements: Array.isArray(item?.placements)
            ? item.placements
            : Array.isArray(item?.Placements)
              ? item.Placements
              : [],
        }));
      },
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          organization_ems_lead_id,
          country_id,
          state_id,
          city_id,
          first_name,
          last_name,
          gender,
          date_of_birth,
          email,
          phone,
          alternate_phone,
          address,
          certificate_name,
          profile_image_url,
          remarks,
        } = formData;

        // --- organization_id (required|integer|exists) ---
        if (!organization_id)
          errors.organization_id = "Organization is required.";
        else if (isNaN(organization_id))
          errors.organization_id = "Organization ID must be a valid number.";

        // --- organization_entity_id (sometimes|nullable|integer) ---
        if (organization_entity_id && isNaN(organization_entity_id))
          errors.organization_entity_id =
            "Organization Entity ID must be a valid number.";

        // --- country_id (sometimes|nullable|integer) ---
        if (country_id && isNaN(country_id))
          errors.country_id = "Country ID must be a valid number.";

        // --- state_id (sometimes|nullable|integer) ---
        if (state_id && isNaN(state_id))
          errors.state_id = "State ID must be a valid number.";

        // --- city_id (sometimes|nullable|integer) ---
        if (city_id && isNaN(city_id))
          errors.city_id = "City ID must be a valid number.";

        // --- organization_ems_lead_id (nullable|integer) ---
        if (
          organization_ems_lead_id !== "" &&
          organization_ems_lead_id != null &&
          isNaN(Number(organization_ems_lead_id))
        ) {
          errors.organization_ems_lead_id = "Lead must be a valid selection.";
        }

        // --- first_name (required|string|max:100) ---
        if (!first_name?.trim()) errors.first_name = "First name is required.";
        else if (first_name.length > 100)
          errors.first_name = "First name cannot exceed 100 characters.";

        // --- last_name (nullable|string|max:50) ---
        if (last_name && last_name.length > 50)
          errors.last_name = "Last name cannot exceed 50 characters.";

        // --- gender (nullable|in:Male,Female,Other) ---
        if (gender && !["Male", "Female", "Other"].includes(gender))
          errors.gender = "Gender must be Male, Female, or Other.";

        // --- date_of_birth (nullable|date) ---
        if (date_of_birth && isNaN(Date.parse(date_of_birth)))
          errors.date_of_birth = "Date of birth must be a valid date.";

        // --- email (required|string|max:100|unique) ---
        if (!email?.trim()) errors.email = "Email is required.";
        else if (email.length > 100)
          errors.email = "Email cannot exceed 100 characters.";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
          errors.email = "Email must be valid.";

        // --- phone (required|string|max:20|unique) ---
        if (!phone?.trim()) errors.phone = "Phone number is required.";
        else if (phone.length > 20)
          errors.phone = "Phone number cannot exceed 20 characters.";
        else if (!/^[0-9+\-\s()]+$/.test(phone))
          errors.phone = "Phone number contains invalid characters.";

        // --- alternate_phone (nullable|string|max:20) ---
        if (alternate_phone && alternate_phone.length > 20)
          errors.alternate_phone =
            "Alternate phone cannot exceed 20 characters.";

        // --- address (nullable|string|max:255) ---
        if (address && address.length > 255)
          errors.address = "Address cannot exceed 255 characters.";

        // --- certificate_name (nullable|string|max:150) ---
        if (certificate_name && certificate_name.length > 150)
          errors.certificate_name =
            "Certificate name cannot exceed 150 characters.";

        // --- profile_image_url (nullable|file|mimes:jpg,jpeg,png|max:2048) ---
        if (profile_image_url) {
          if (
            profile_image_url.size &&
            profile_image_url.size > 2 * 1024 * 1024
          )
            errors.profile_image_url = "Profile image size cannot exceed 2MB.";
          if (
            profile_image_url.type &&
            !["image/jpeg", "image/jpg", "image/png"].includes(
              profile_image_url.type
            )
          )
            errors.profile_image_url =
              "Profile image must be JPG, JPEG, or PNG.";
        }

        // --- remarks (nullable|string) ---
        if (remarks && typeof remarks !== "string")
          errors.remarks = "Remarks must be a valid text.";

        // --- set & return ---
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/students",
      initialData: {
        organization_entity_id: "",
        organization_ems_lead_id: "",
        country_id: "",
        state_id: "",
        city_id: "",
        first_name: "",
        last_name: "",
        gender: "",
        date_of_birth: "",
        email: "",
        phone: "",
        alternate_phone: "",
        address: "",
        certificate_name: "",
        remarks: "",
        profile_image_url: "",
        student_id: "",
      },
      successMessages: {
        add: "Student added!",
        update: "Student updated!",
      },
      redirectPath: "/students",
      headerProps: {
        addMessage: "Add Student",
        updateMessage: "Update Student",
        homeLink: "/students",
        homeText: "Student",
      },
      fieldGridLayout: {
        rows: [

          { variant: "full", mainKeys: ["profile_image_url"] },


          { variant: "full", mainKeys: ["organization_ems_lead_id"] },
          {
            variant: "threeEqual",
            mainKeys: ["first_name", "last_name", "certificate_name"],
          },
          {
            variant: "twoEqual",
            mainKeys: ["gender", "date_of_birth"],
          },
          { variant: "full", mainKeys: ["email"] },
          {
            variant: "twoEqual",
            mainKeys: ["phone", "alternate_phone"],
          },
          {
            variant: "threeEqual",
            mainKeys: ["country_id", "state_id", "city_id"],
          },
          { variant: "full", mainKeys: ["address"] },
          { variant: "full", mainKeys: ["remarks"] },
          // { variant: "full", mainKeys: ["profile_image_url"] },
        ],
        sections: [],
        spacingAfterEveryRows: 3,
      },
      Inputs: [
        {
          type: "Select",
          label: "Lead ",
          mainKey: "organization_ems_lead_id",
          OptionMainKey: "Leads",
          menuKey: "organization_ems_lead_id",
          formatValue: (val) =>
            val?.person_full_name ||
            [val?.first_name, val?.last_name]
              .filter((part) => part && String(part).trim() !== "")
              .join(" ")
              .trim() ||
            (val?.organization_ems_lead_id
              ? `Lead #${val.organization_ems_lead_id}`
              : ""),
        },
        {
          type: "Text",
          label: "First Name *",
          mainKey: "first_name",
        },
        {
          type: "Text",
          label: "Last Name ",
          mainKey: "last_name",
        },
        {
          type: "Text",
          label: "Certificate Name ",
          mainKey: "certificate_name",
        },
        {
          type: "Select",
          label: "Gender ",
          mainKey: "gender",
          OptionMainKey: "Genders",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "Date",
          label: "Date Of Birth ",
          mainKey: "date_of_birth",
        },
        {
          type: "Text",
          label: "Email *",
          mainKey: "email",
        },
        {
          type: "Text",
          label: "Phone *",
          mainKey: "phone",
        },
        {
          type: "Text",
          label: "Alternate Phone ",
          mainKey: "alternate_phone",
        },
        {
          type: "Select",
          label: "Country ",
          mainKey: "country_id",
          OptionMainKey: "Countries",
          menuKey: "general_country_id",
          formatValue: (val) => `${val.country_name}`,
        },
        {
          type: "Select",
          label: "State ",
          mainKey: "state_id",
          OptionMainKey: "States",
          menuKey: "general_state_id",
          formatValue: (val) => `${val.state_name}`,
        },
        {
          type: "Select",
          label: "City ",
          mainKey: "city_id",
          OptionMainKey: "Cities",
          menuKey: "general_city_id",
          formatValue: (val) => `${val.city_name}`,
        },
        {
          type: "MultiRow",
          label: "Address ",
          mainKey: "address",
        },
        {
          type: "MultiRow",
          label: "Remarks ",
          mainKey: "remarks",
        },
        {
          type: "Picture",
          label: "Profile Image",
          mainKey: "profile_image_url",
          align: "left",
        },
      ],
      DataFetchers: [
        { name: "Leads", Type: "Fetch", link: "/api/ems/leads" },
        { name: "Countries", Type: "Fetch", link: "/api/general/countries" },
        {
          name: "States",
          Type: "FetchDepend",
          dependKey: "country_id",
          link: "/api/general/countries/${country_id}/states/v1",
        },
        {
          name: "Cities",
          Type: "FetchDepend",
          dependKey: "state_id",
          link: "/api/general/countries/${country_id}/states/${state_id}/cities",
        },
        {
          name: "Genders",
          Type: "NoFetch",
          Options: ["Male", "Female", "Other"],
        },
      ],
    },
  },
  {
    route: "/students-fees",
    ListProps: {
      title: "Fee Receipts",
      apiEndpoint: "/api/ems/students-fees",
      filteredApiEndpoint: "/api/ems/students-fees/filtered",
      filterModule: "ems_student_fees",
      useMetadataFilters: true,
      addRoute: "/students-fees",
      editRouteBase: "/students-fees/edit",
      tablePrimaryKey: "organization_ems_student_fee_id",
      tableName: "ems_student_fees",
      datagridKey: "ems_student_fees_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "payment_date",
        "payment_number",
        "student",
        "inst_no",
        "fee_inr",
        "curr_code",
        "fee_curr",
        "settlement_date",
        "status",
        "organization_ems_student_id",
        "organization_ems_admission_id",
        "organization_ems_fee_installment_id",
        "payment_mode",
        "payment_status",
        "amount_received_inr",
      ],
      config: {
        sort: { field: "payment_date", direction: "desc" },
        defaultVisibleColumns: [
          "payment_date",
          "payment_number",
          "student",
          "inst_no",
          "fee_inr",
          "curr_code",
          "fee_curr",
          "settlement_date",
          "status",
        ],
        columnLabels: {
          payment_date: "Receipt Date",
          payment_number: "Receipt Number",
          student: "Student",
          inst_no: "Inst.No",
          fee_inr: "Fee (INR)",
          curr_code: "Currency Code",
          fee_curr: "Fee (Currency Code)",
          settlement_date: "Settlement Date",
          status: "Status",
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => {
          const inst = item?.installment ?? item?.fee_installment;
          const receiptNo =
            item.payment_number ??
            item.receipt_number ??
            item.student_fee_payment_number;
          const payment_number =
            receiptNo != null && String(receiptNo).trim() !== ""
              ? String(receiptNo).trim()
              : "-";
          return {
            id: item.organization_ems_student_fee_id,
            organization_ems_student_fee_id: item.organization_ems_student_fee_id,
            organization_ems_student_id:
              item?.organization_ems_student_id ??
              item?.student?.organization_ems_student_id ??
              "",
            organization_ems_admission_id:
              item?.organization_ems_admission_id ??
              item?.admission?.organization_ems_admission_id ??
              "",
            organization_ems_fee_installment_id:
              item?.organization_ems_fee_installment_id ??
              inst?.organization_ems_fee_installment_id ??
              "",
            student_obj: item?.student ?? null,
            admission_obj: item?.admission ?? null,
            installment_obj: inst ?? null,
            payment_date: FormatDateV1(item?.payment_date) || "-",
            payment_number,
            student:
              [item?.student?.first_name, item?.student?.middle_name, item?.student?.last_name]
                .filter(Boolean)
                .join(" ")
                .trim() ||
              `${item?.student?.first_name || ""} ${item?.student?.last_name || ""}`.trim() ||
              "-",
            inst_no: inst?.installment_number ?? item?.installment_number ?? "-",
            fee_inr: item.amount_received_inr ?? "-",
            curr_code: item.student_currency_code || "-",
            fee_curr: item.amount_paid_student_currency ?? "-",
            settlement_date: FormatDateV1(item?.settlement_date) || "-",
            status:
              item.payment_status ??
              item.status ??
              item.receipt_status ??
              item.settlement_status ??
              "-",
          };
        }),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          organization_ems_admission_id,
          organization_ems_student_id,
          organization_ems_fee_installment_id,
          payment_number,
          payment_date,

          student_currency_code,
          amount_paid_student_currency,

          amount_received_inr,

          settlement_currency_code,
          settlement_date,
          gateway_charges,
          forex_difference,

          payment_mode,
          transaction_reference,

          remarks,
        } = formData;

        // ===== REQUIRED FIELDS =====
        if (!organization_id) {
          errors.organization_id = "Organization is required.";
        }

        if (!payment_number?.trim()) {
          errors.payment_number = "Receipt number is required.";
        } else if (payment_number.length > 50) {
          errors.payment_number = "Receipt number cannot exceed 50 characters.";
        }

        if (!payment_date) {
          errors.payment_date = "Receipt date is required.";
        } else if (new Date(payment_date) > new Date()) {
          errors.payment_date = "Receipt date cannot be in the future.";
        }

        if (!student_currency_code?.trim()) {
          errors.student_currency_code = "Currency code is required.";
        } else if (student_currency_code.length > 10) {
          errors.student_currency_code =
            "Currency code cannot exceed 10 characters.";
        }

        if (
          amount_paid_student_currency === "" ||
          amount_paid_student_currency === null
        ) {
          errors.amount_paid_student_currency =
            "Currency code amount is required.";
        } else if (isNaN(amount_paid_student_currency)) {
          errors.amount_paid_student_currency = "Amount paid must be a number.";
        } else if (Number(amount_paid_student_currency) < 0) {
          errors.amount_paid_student_currency =
            "Amount paid cannot be negative.";
        }

        if (amount_received_inr === "" || amount_received_inr === null) {
          errors.amount_received_inr = "Amount received (INR) is required.";
        } else if (isNaN(amount_received_inr)) {
          errors.amount_received_inr = "Amount received must be a number.";
        } else if (Number(amount_received_inr) < 0) {
          errors.amount_received_inr = "Amount received cannot be negative.";
        }

        // ===== OPTIONAL NUMERIC FIELDS =====
        if (
          gateway_charges &&
          (isNaN(gateway_charges) || Number(gateway_charges) < 0)
        ) {
          errors.gateway_charges = "Gateway charges must be a positive number.";
        }

        if (forex_difference && isNaN(forex_difference)) {
          errors.forex_difference = "Forex difference must be a number.";
        }

        if (
          settlement_currency_code &&
          settlement_currency_code.trim().length > 10
        ) {
          errors.settlement_currency_code =
            "Settlement currency code cannot exceed 10 characters.";
        }

        // ===== OPTIONAL DATE =====
        if (settlement_date) {
          const validDate = !isNaN(Date.parse(settlement_date));
          if (!validDate) errors.settlement_date = "Invalid settlement date.";
        }

        // ===== PAYMENT MODE =====
        const validPaymentModes = [
          "UPI",
          "NetBanking",
          "Razorpay",
          "Cash",
          "Cheque",
        ];

        if (!payment_mode) {
          errors.payment_mode = "Payment mode is required.";
        } else if (!validPaymentModes.includes(payment_mode)) {
          errors.payment_mode = "Invalid payment mode selected.";
        }

        // ===== OPTIONAL STRING FIELDS =====
        if (transaction_reference && transaction_reference.length > 100) {
          errors.transaction_reference =
            "Transaction reference cannot exceed 100 characters.";
        }

        // ===== OPTIONAL REMARKS =====
        if (remarks && typeof remarks !== "string") {
          errors.remarks = "Remarks must be a valid string.";
        }

        // ===== OPTIONAL RELATIONS (if required to validate IDs) =====
        const idFields = {
          organization_entity_id,
          organization_ems_admission_id,
          organization_ems_student_id,
          organization_ems_fee_installment_id,
        };
        Object.entries(idFields).forEach(([key, value]) => {
          if (value && isNaN(value)) {
            errors[key] = "Invalid ID format.";
          }
        });

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/students-fees",
      initialData: {
        organization_entity_id: "",
        organization_ems_admission_id: "",
        organization_ems_student_id: "",
        organization_ems_fee_installment_id: "",
        payment_number: "",
        payment_date: "",
        student_currency_code: "",
        amount_paid_student_currency: "",
        amount_received_inr: "",
        settlement_currency_code: "",
        settlement_date: "",
        gateway_charges: "",
        forex_difference: "",
        payment_mode: "",
        transaction_reference: "",
        remarks: "",
      },
      successMessages: {
        add: "Student Receipt added!",
        update: "Student Receipt updated!",
      },
      redirectPath: "/students-fees",
      headerProps: {
        addMessage: "Add Fee Receipt",
        updateMessage: "Update Student Receipt",
        homeLink: "/students-fees",
        homeText: "Fee Receipts",
      },
      fieldGridLayout: {
        rows: [
          {
            variant: "twoEqual",
            mainKeys: ["payment_date", "payment_number"],
          },
          {
            variant: "twoEqual",
            mainKeys: [
              "organization_ems_student_id",
              "organization_ems_admission_id",
            ],
          },
          {
            variant: "full",
            mainKeys: ["organization_ems_fee_installment_id"],
          },
          { variant: "full", mainKeys: ["amount_received_inr"] },
          {
            variant: "twoEqual",
            mainKeys: [
              "student_currency_code",
              "amount_paid_student_currency",
            ],
          },
          { variant: "full", mainKeys: ["payment_mode"] },
          { variant: "full", mainKeys: ["transaction_reference"] },
          {
            variant: "twoEqual",
            mainKeys: ["gateway_charges", "forex_difference"],
          },
          {
            variant: "twoEqual",
            mainKeys: ["settlement_currency_code", "settlement_date"],
          },
          { variant: "full", mainKeys: ["remarks"] },
        ],
        sections: [{ beforeRow: 3, title: "INR" }],
      },
      Inputs: [
        {
          type: "Date",
          label: "Receipt Date *",
          mainKey: "payment_date",
        },
        {
          type: "Number",
          label: "Receipt Number *",
          mainKey: "payment_number",
        },
        {
          type: "Select",
          label: "Student *",
          mainKey: "organization_ems_student_id",
          OptionMainKey: "Students",
          menuKey: "organization_ems_student_id",
          formatValue: (val) =>
            [val?.first_name, val?.middle_name, val?.last_name]
              .filter((s) => s != null && String(s).trim() !== "")
              .join(" ")
              .trim() || "—",
        },
        {
          type: "Select",
          label: "Admission Number *",
          mainKey: "organization_ems_admission_id",
          OptionMainKey: "Admissions",
          menuKey: "organization_ems_admission_id",
          formatValue: (val) => `${val.admission_number}`,
        },
        {
          type: "Select",
          label: "Fee Installment ",
          mainKey: "organization_ems_fee_installment_id",
          OptionMainKey: "FeeInstallments",
          menuKey: "organization_ems_fee_installment_id",
          formatValue: (val) => {
            const num =
              val?.installment_number ??
              val?.inst_no ??
              val?.installmentNumber ??
              "";
            const inrRaw =
              val?.fee_instalment_inr ??
              val?.fee_inr ??
              val?.amount_due;
            const studentAmt =
              val?.fee_instalment_student_student_currency ??
              val?.fee_curr;
            const curr =
              (val?.student_currency_code && String(val.student_currency_code).trim()) ||
              (val?.curr_code && String(val.curr_code).trim()) ||
              "";
            const parts = [];
            if (num !== "" && num != null) parts.push(`#${num}`);
            if (inrRaw !== null && inrRaw !== undefined && inrRaw !== "") {
              parts.push(`INR ${inrRaw}`);
            }
            if (
              studentAmt !== null &&
              studentAmt !== undefined &&
              studentAmt !== ""
            ) {
              parts.push(curr ? `${studentAmt} ${curr}` : String(studentAmt));
            }
            if (parts.length) return parts.join(" · ");
            return num !== "" && num != null ? `Installment ${num}` : "—";
          },
        },
        {
          type: "Number",
          label: "Amount Received INR *",
          mainKey: "amount_received_inr",
          disabledWhen: () => true,
        },
        {
          type: "Select",
          label: "Currency Code *",
          mainKey: "student_currency_code",
          OptionMainKey: "Currencies",
          menuKey: "currency_code",
          formatValue: (val) =>
            `${val?.currency_code || ""}${val?.currency_name ? ` - ${val.currency_name}` : ""}`,
          disabledWhen: () => true,
        },
        {
          type: "Number",
          label: "Currency Code Amount *",
          mainKey: "amount_paid_student_currency",
          disabledWhen: () => true,
        },
        {
          type: "Select",
          label: "Mode *",
          mainKey: "payment_mode",
          OptionMainKey: "PaymentMode",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "Text",
          label: "Transaction Reference ",
          mainKey: "transaction_reference",
        },
        {
          type: "Number",
          label: "Gateway Charges ",
          mainKey: "gateway_charges",
        },
        {
          type: "Number",
          label: "Forex Difference ",
          mainKey: "forex_difference",
        },
        {
          type: "Select",
          label: "Settlement Currency Code ",
          mainKey: "settlement_currency_code",
          OptionMainKey: "Currencies",
          menuKey: "currency_code",
          formatValue: (val) =>
            `${val?.currency_code || ""}${val?.currency_name ? ` - ${val.currency_name}` : ""}`,
        },
        {
          type: "Date",
          label: "Settlement Date ",
          mainKey: "settlement_date",
        },
        {
          type: "MultiRow",
          label: "Remarks ",
          mainKey: "remarks",
        },
      ],
      DataFetchers: [
        { name: "Students", Type: "Fetch", link: "/api/ems/students" },
        { name: "Admissions", Type: "Fetch", link: "/api/ems/admissions" },
        {
          name: "FeeInstallments",
          Type: "Fetch",
          link: "/api/ems/fee-installments",
        },

        {
          name: "PaymentMode",
          Type: "NoFetch",
          Options: [
            "UPI",
            "NetBanking",
            "Razorpay",
            "Cash",
            "Cheque",
          ],
        },
        {
          name: "PaymentStatus",
          Type: "NoFetch",
          Options: ["Pending", "Successful", "Failed", "Refunded", "Settled"],
        },
        { name: "Currencies", Type: "Fetch", link: "/api/ems-currencies" },
      ],
    },
  },
  {
    route: "/lead-activity-report",
    ListProps: {
      title: "Lead Activity Report",
      apiEndpoint: "/api/ems/leads",
      useMetadataFilters: false,
      addRoute: "/lead-activity-report",
      editRouteBase: "/lead-activity-report/edit",
      tablePrimaryKey: "organization_ems_lead_id",
      tableName: "ems_lead_activity_report",
      datagridKey: "ems_lead_activity_report_grid_v7",
      showAddButton: false,
      showEditButton: false,
      showViewButton: false,
      readOnlyList: true,
      hideFilterButton: true,
      hidePersonalizeView: true,
      personalizeColumnKeys: [
        "lead_datetime",
        "person_full_name",
        "training_program",
        "activities_count",
        "last_activity_datetime",
      ],
      config: {
        sort: { field: "lead_datetime", direction: "desc" },
        defaultVisibleColumns: [
          "lead_datetime",
          "person_full_name",
          "training_program",
          "activities_count",
          "last_activity_datetime",
        ],
        columnLabels: {
          lead_datetime: "Lead Date Time",
          person_full_name: "Lead Name",
          training_program: "Program",
          activities_count: "Activities",
          last_activity_datetime: "Last Activity Date",
        },
        columnWidths: {
          lead_datetime: "16%",
          person_full_name: "26%",
          training_program: "16%",
          activities_count: "10%",
          last_activity_datetime: "20%",
        },
      },
      mapResponseToRows: (data) =>
        (Array.isArray(data) ? data : [])
          .filter((item) => leadStageStatusLabel(item) === "Open")
          .sort((a, b) => {
            const at = Date.parse(a?.lead_datetime || 0);
            const bt = Date.parse(b?.lead_datetime || 0);
            return (Number.isNaN(bt) ? 0 : bt) - (Number.isNaN(at) ? 0 : at);
          })
          .map((item) => {
            const timing = item?.lead_contact_timings;
            const countryName = item?.country?.country_name || "";
            const stateName = item?.state?.state_name ?? item?.state_name ?? "";
            const cityName = item?.city?.city_name ?? item?.city_name ?? "";
            const locParts = [countryName, stateName, cityName].filter(
              (part) => part && String(part).trim() !== ""
            );
            let tz =
              timing?.preferred_contact_timezone &&
              String(timing.preferred_contact_timezone).trim();
            if (tz && !isValidIanaTimezone(tz)) tz = "";
            if (!tz) tz = inferPreferredTimezoneFromLead(item);
            const summary = leadActivitySummary(item);
            const latest = summary.latest;
            const leadSourceLabel = readLeadSourceName(item);
            return {
              id: item.organization_ems_lead_id,
              organization_ems_lead_id: item.organization_ems_lead_id,
              lead_datetime: formatDateTime(item?.lead_datetime) || "-",
              lead_source:
                item?.lead_source && typeof item.lead_source === "object"
                  ? item.lead_source
                  : item?.leadSource && typeof item.leadSource === "object"
                    ? item.leadSource
                    : null,
              lead_source_name: leadSourceLabel || "",
              __lead_source_display: leadSourceLabel || "—",
              person_full_name: item?.person_full_name || "-",
              phone: item?.phone || "-",
              email: item?.email || "-",
              training_program:
                item?.training_program?.training_program_name || "-",
              country: item?.country?.country_name || "-",
              location_region: locParts.length ? locParts.join(" → ") : "-",
              contact_status_dot: 0,
              __lct_timezone: tz || "",
              __lct_morning_start: timing?.morning_student_time_start ?? "07:00",
              __lct_morning_end: timing?.morning_student_time_end ?? "09:00",
              __lct_evening_start: timing?.evening_student_time_start ?? "19:00",
              __lct_evening_end: timing?.evening_student_time_end ?? "21:00",
              __lct_morning_ist_start: timing?.morning_ist_time_start || "",
              __lct_evening_ist_start: timing?.evening_ist_time_start || "",
              __lead_status_display: "Open",
              lead_stage_name:
                item?.lead_stage?.lead_stage_name ||
                item?.leadStage?.lead_stage_name ||
                "-",
              lead_activities_count: summary.count,
              last_lead_activity_datetime: summary.lastText,
              activities_count: summary.count,
              last_activity_datetime: summary.lastText,
              last_activity_type_status: latest
                ? `${latest.activity_type || "-"} · ${leadActivityStatusLabel(latest)}`
                : "-",
              __last_activity_type: latest?.activity_type || "-",
              __last_activity_status: leadActivityStatusLabel(latest),
            };
          }),
    },
  },
  {
    route: "/leads",
    ListProps: {
      title: "Leads",
      apiEndpoint: "/api/ems/leads",
      filteredApiEndpoint: "/api/ems/leads/filtered",
      filterModule: "ems_leads",
      useMetadataFilters: true,
      addRoute: "/leads",
      editRouteBase: "/leads/edit",
      tablePrimaryKey: "organization_ems_lead_id",
      tableName: "ems_leads",
      datagridKey: "ems_leads_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "lead_datetime",
        "training_program",
        "person_full_name",
        "assigned_counsellor_name",
        "email",
        "phone",
        "alternate_phone",
        "contact_status_dot",
        "country",
        "location_region",
        "lead_stage_name",
        "__lead_status_display",
        "lead_activities_count",
        "last_lead_activity_datetime",
        "__lead_source_display",
        "remarks",
        "interested_program_remarks",
      ],
      config: {
        defaultVisibleColumns: [
          "lead_datetime",
          "training_program",
          "person_full_name",
          "assigned_counsellor_name",
          "phone",
          "contact_status_dot",
          "country",
          "lead_stage_name",
          "lead_activities_count",
        ],
        columnLabels: {
          lead_datetime: "Lead Date",
          training_program: "Training Program",
          person_full_name: "Person Name",
          assigned_counsellor_name: "Assign to Counselor",
          email: "Email",
          phone: "Phone",
          contact_status_dot: "●",
          country: "Country / region",
          location_region: "Location",
          lead_stage_name: "Lead Stage",
          __lead_status_display: "Lead Status",
          lead_activities_count: "#",
          last_lead_activity_datetime: "Last Activity",
          __lead_source_display: "Lead Source",
          remarks: "Remarks",
          interested_program_remarks: "Program Remarks",
          alternate_phone: "Alternate Phone",
        },
        // Make Training Program wider than other columns.
        columnWidths: {
          lead_datetime: 200,
          training_program: 320,
          person_full_name: 260,
          email: 220,
          phone: 160,
          contact_status_dot: 60,
          country: 260,
          lead_activities_count: 140,
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => {
          const timing = item?.lead_contact_timings;
          const countryName = item?.country?.country_name || "";
          const stateName =
            item?.state?.state_name ?? item?.state_name ?? "";
          const cityName =
            item?.city?.city_name ?? item?.city_name ?? "";
          const locParts = [countryName, stateName, cityName].filter(
            (p) => p && String(p).trim() !== ""
          );
          const location_region =
            locParts.length > 0 ? locParts.join(" → ") : "-";

          let tz =
            timing?.preferred_contact_timezone &&
            String(timing.preferred_contact_timezone).trim();
          if (tz && !isValidIanaTimezone(tz)) {
            tz = "";
          }
          if (!tz) {
            tz = inferPreferredTimezoneFromLead(item);
          }

          const mStart = timing?.morning_student_time_start ?? "07:00";
          const mEnd = timing?.morning_student_time_end ?? "09:00";
          const eStart = timing?.evening_student_time_start ?? "19:00";
          const eEnd = timing?.evening_student_time_end ?? "21:00";
          const mIstStart =
            timing?.morning_ist_time_start != null &&
            String(timing.morning_ist_time_start).trim() !== ""
              ? String(timing.morning_ist_time_start).trim()
              : "";
          const eIstStart =
            timing?.evening_ist_time_start != null &&
            String(timing.evening_ist_time_start).trim() !== ""
              ? String(timing.evening_ist_time_start).trim()
              : "";

          const leadSourceLabel =
            item?.lead_source?.lead_source_name ||
            item?.leadSource?.lead_source_name ||
            item?.lead_source?.lead_source_short_name ||
            item?.leadSource?.lead_source_short_name ||
            "";
          const activities = Array.isArray(item?.lead_activities)
            ? item.lead_activities
            : Array.isArray(item?.leadActivities)
              ? item.leadActivities
              : [];
          const activityTime = (value) => {
            if (!value) return 0;
            const parsed = Date.parse(String(value).trim().replace(" ", "T"));
            return Number.isNaN(parsed) ? 0 : parsed;
          };
          const latestActivity = activities
            .filter((a) => a && a.activity_datetime)
            .sort((a, b) => activityTime(b.activity_datetime) - activityTime(a.activity_datetime))[0];
          const apiActivityCount = Number(item?.lead_activities_count);
          const activityCount = activities.length > 0
            ? activities.length
            : Number.isFinite(apiActivityCount) && apiActivityCount >= 0
              ? apiActivityCount
              : 0;
          const rawLastActivity =
            latestActivity?.activity_datetime ||
            item?.last_lead_activity_at ||
            item?.last_lead_activity_datetime ||
            "";
          const lastActivityText = rawLastActivity
            ? formatDateTime(String(rawLastActivity).trim().replace(" ", "T")) ||
              String(rawLastActivity)
            : "-";
          const leadStatusRaw =
            item?.lead_stage?.lead_status ??
            item?.leadStage?.lead_status ??
            item?.lead_status;
          const leadStatusDisplay =
            leadStatusRaw === 1 ||
            leadStatusRaw === "1" ||
            leadStatusRaw === true ||
            String(leadStatusRaw || "").trim().toLowerCase() === "open"
              ? "Open"
              : leadStatusRaw === 0 ||
                  leadStatusRaw === "0" ||
                  leadStatusRaw === false ||
                  String(leadStatusRaw || "").trim().toLowerCase() === "closed" ||
                  String(leadStatusRaw || "").trim().toLowerCase() === "closed"
                ? "Closed"
                : "-";

          return {
          lead_datetime: formatDateTime(item?.lead_datetime) || "-",
          __lead_source_display:
            leadSourceLabel && String(leadSourceLabel).trim() !== ""
              ? String(leadSourceLabel).trim()
              : "—",
          training_program:
            item?.training_program?.training_program_name || "-",
          person_full_name: item?.person_full_name || "-",
          assigned_counsellor_name: item?.assigned_counsellor_name || "-",
          country: item?.country?.country_name || "-",
          location_region,
          contact_status_dot: 0,
          contact_timing_live: tz ? " " : "-",
          __lct_timezone: tz || "",
          __lct_morning_start: mStart,
          __lct_morning_end: mEnd,
          __lct_evening_start: eStart,
          __lct_evening_end: eEnd,
          __lct_morning_ist_start: mIstStart,
          __lct_evening_ist_start: eIstStart,
          country_id:
            item?.country?.general_country_id ??
            item?.country_id ??
            item?.country?.organization_general_country_id ??
            item?.country?.country_id ??
            item?.country?.id ??
            "",
          state_id:
            item?.state?.general_state_id ??
            item?.state?.state_general_state_id ??
            item?.state?.organization_general_state_id ??
            item?.state_id ??
            item?.state?.state_id ??
            item?.state?.id ??
            item?.state?.generalStateId ??
            item?.general_state_id ??
            item?.organization_general_state_id ??
            item?.state_general_state_id ??
            item?.stateId ??
            "",
          city_id:
            item?.city?.general_city_id ??
            item?.city?.city_general_city_id ??
            item?.city?.organization_general_city_id ??
            item?.city_id ??
            item?.city?.city_id ??
            item?.city?.id ??
            item?.city?.generalCityId ??
            item?.general_city_id ??
            item?.organization_general_city_id ??
            item?.city_general_city_id ??
            item?.cityId ??
            "",
          state_name:
            item?.state?.state_name ??
            item?.state_name ??
            item?.general_state_name ??
            item?.state_name ??
            item?.state?.name ??
            item?.state?.state ??
            "",
          city_name:
            item?.city?.city_name ??
            item?.city_name ??
            item?.general_city_name ??
            item?.city_name ??
            item?.city?.name ??
            item?.city?.city ??
            "",
          phone: item.phone || "-",
          email: item?.email || "-",
          alternate_phone: item?.alternate_phone || "-",
          address: item?.address || "-",
          remarks: item?.remarks || "-",
          interested_program_remarks:
            item?.interested_program_remarks || "-",
          lead_stage_id: item?.lead_stage_id ?? null,
          lead_stage_name:
            item?.lead_stage?.lead_stage_name ||
            item?.leadStage?.lead_stage_name ||
            "-",
          is_spam: item?.is_spam ?? false,
          __lead_status_display: leadStatusDisplay,
          lead_activities_count: activityCount,
          last_lead_activity_datetime: lastActivityText,
          __latest_lead_activity_datetime:
            rawLastActivity || "",
          training_program_fee:
            item?.training_program?.fee_inr ??
            item?.training_program?.fee ??
            item?.training_program?.program_fee ??
            null,
          id: item.organization_ems_lead_id,
          organization_ems_lead_id: item.organization_ems_lead_id,

          // state: item?.state?.state_name || "-",
          // city: item?.city?.city_name || "-",
          // source: item?.leadSource?.lead_source_name || "-",
          // stage: item?.leadStage?.lead_stage_name || "-",
          // email: item.email || "-",
          // alternate_phone: item.alternate_phone || "-",
          // interested_program_remarks: item.interested_program_remarks || "-",
          // remarks: item.remarks || "-",
          // is_spam: item.is_spam || "-",
          // // lead_status: item.lead_status == true ? "Active" : "Closed" || "-",
          // spam_reason: item.spam_reason || "-",
          };
        }),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          lead_source_id,
          country_id,
          state_id,
          city_id,
          training_program_id,
          training_program_category_id,
          lead_stage_id,
          assigned_counsellor_organization_user_id,
          lead_datetime,
          person_full_name,
          email,
          phone,
          alternate_phone,
          interested_program_remarks,
          remarks,
          is_spam,
          // lead_status,
          spam_reason,
        } = formData;

        // ===== REQUIRED FIELDS =====
        if (!organization_id) {
          errors.organization_id = "Organization ID is required.";
        } else if (isNaN(organization_id)) {
          errors.organization_id = "Organization ID must be a number.";
        }

        if (!lead_datetime) {
          errors.lead_datetime = "Lead datetime is required.";
        } else if (isNaN(Date.parse(lead_datetime))) {
          errors.lead_datetime = "Lead datetime is invalid.";
        } else if (new Date(lead_datetime) > new Date()) {
          errors.lead_datetime = "Lead datetime cannot be in the future.";
        }

        if (!person_full_name?.trim()) {
          errors.person_full_name = "Full name of the lead is required.";
        } else if (person_full_name.length > 150) {
          errors.person_full_name = "Full name cannot exceed 150 characters.";
        }

        // ===== OPTIONAL RELATION IDS =====
        const optionalIds = {
          lead_source_id,
          country_id,
          state_id,
          city_id,
          training_program_id,
          training_program_category_id,
          lead_stage_id,
          assigned_counsellor_organization_user_id,
        };
        Object.entries(optionalIds).forEach(([key, value]) => {
          if (value && isNaN(value)) {
            errors[key] = "Invalid ID format.";
          }
        });

        // ===== EMAIL & PHONE (must be valid when provided) =====
        const emailValue = email?.toString().trim() || "";
        const phoneValue = phone?.toString().trim() || "";
        const alternatePhoneValue = alternate_phone?.toString().trim() || "";
        const emailRegex =
          /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
        const phoneRegex = /^\d{10}$/;

        if (emailValue) {
          if (emailValue.length > 100) {
            errors.email = "Email cannot exceed 100 characters.";
          } else if (!emailRegex.test(emailValue)) {
            errors.email = "Enter a valid email address.";
          }
        }

        if (phoneValue) {
          if (!phoneRegex.test(phoneValue)) {
            errors.phone = "Enter a valid 10-digit phone number.";
          }
        }

        if (alternatePhoneValue) {
          if (!phoneRegex.test(alternatePhoneValue)) {
            errors.alternate_phone =
              "Enter a valid 10-digit alternate phone number.";
          }
        }

        if (
          interested_program_remarks &&
          interested_program_remarks.length > 150
        ) {
          errors.interested_program_remarks =
            "Remarks cannot exceed 150 characters.";
        }

        if (remarks && typeof remarks !== "string") {
          errors.remarks = "Remarks must be a valid string.";
        }

        // if (is_spam !== undefined && typeof is_spam !== "boolean") {
        //   errors.is_spam = "Invalid value for spam flag.";
        // }

        if (spam_reason && spam_reason.length > 255) {
          errors.spam_reason = "Spam reason cannot exceed 255 characters.";
        }

        console.log("errors is : ", errors);

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/leads",
      initialData: {
        organization_entity_id: "",
        lead_source_id: "",
        country_id: "",
        state_id: "",
        city_id: "",
        training_program_id: "",
        training_program_category_id: "",
        lead_stage_id: "",
        assigned_counsellor_organization_user_id: "",

        lead_datetime: "",
        person_full_name: "",
        email: "",
        phone: "",
        alternate_phone: "",
        interested_program_remarks: "",
        remarks: "",
        is_spam: false,
        // lead_status: true,
        spam_reason: "",
      },
      successMessages: {
        add: "Lead added!",
        update: "Lead updated!",
      },
      redirectPath: "/leads",
      headerProps: {
        addMessage: "Add Lead",
        updateMessage: "Update Lead",
        homeLink: "/leads",
        homeText: "Lead",
      },
      fieldGridLayout: {
        rows: [
          { variant: "full", mainKeys: ["lead_datetime"] },
          {
            variant: "twoEqual",
            mainKeys: ["person_full_name", "email"],
          },
          {
            variant: "twoEqual",
            mainKeys: ["phone", "alternate_phone"],
          },
          {
            variant: "twoEqual",
            mainKeys: [
              "training_program_category_id",
              "training_program_id",
            ],
          },
          { variant: "full", mainKeys: ["interested_program_remarks"] },
          {
            variant: "twoEqual",
            mainKeys: ["lead_source_id", "lead_stage_id"],
          },
          {
            variant: "full",
            mainKeys: ["assigned_counsellor_organization_user_id"],
          },
          {
            variant: "threeEqual",
            mainKeys: ["country_id", "state_id", "city_id"],
          },
          { variant: "full", mainKeys: ["remarks"] },
          { variant: "full", mainKeys: ["is_spam"] },
          { variant: "full", mainKeys: ["spam_reason"] },
          { variant: "full", mainKeys: ["__lead_activity_embed"] },
        ],
        sections: [],
        spacingAfterEveryRows: 3,
      },
      Inputs: [
        {
          type: "DateTime",
          label: "Lead DateTime",
          mainKey: "lead_datetime",
        },
        {
          type: "Text",
          label: "Person Full Name",
          mainKey: "person_full_name",
        },
        {
          type: "Text",
          label: "Email ",
          mainKey: "email",
        },
        {
          type: "Text",
          label: "Phone ",
          mainKey: "phone",
          digitsOnly: true,
          maxLength: 10,
        },
        {
          type: "Text",
          label: "Alternate Phone ",
          mainKey: "alternate_phone",
          digitsOnly: true,
          maxLength: 10,
        },
        {
          type: "Select",
          label: "Training Program Category",
          mainKey: "training_program_category_id",
          OptionMainKey: "TrainingProgramCategories",
          menuKey: "organization_ems_training_program_category_id",
          formatValue: (val) => `${val.training_program_category_name}`,
        },
        {
          type: "Select",
          label: "Training Program",
          mainKey: "training_program_id",
          OptionMainKey: "TrainingPrograms",
          menuKey: "organization_ems_training_program_id",
          formatValue: (val) => `${val.training_program_name}`,
        },
        {
          type: "MultiRow",
          label: "Lead Remarks ",
          mainKey: "interested_program_remarks",
        },

        {
          type: "Select",
          label: "Lead Source ",
          mainKey: "lead_source_id",
          OptionMainKey: "LeadSources",
          menuKey: "organization_ems_lead_source_id",
          formatValue: (val) => `${val.lead_source_name}`,
        },
        {
          type: "Select",
          label: "Lead Stage ",
          mainKey: "lead_stage_id",
          OptionMainKey: "LeadStages",
          menuKey: "organization_ems_lead_stage_id",
          disableInEdit: true,
          formatValue: (val) => `${val.lead_stage_name}`,
        },
        {
          type: "Select",
          label: "Assign to Counselor",
          mainKey: "assigned_counsellor_organization_user_id",
          OptionMainKey: "CounsellorUsers",
          menuKey: "organization_user_id",
          formatValue: (val) => formatTrainerUserOption(val),
        },

        {
          type: "Select",
          label: "Country ",
          mainKey: "country_id",
          OptionMainKey: "Countries",
          menuKey: "general_country_id",
          formatValue: (val) => `${val.country_name}`,
        },
        {
          type: "Select",
          label: "State ",
          mainKey: "state_id",
          OptionMainKey: "States",
          menuKey: "general_state_id",
          formatValue: (val) => `${val.state_name}`,
        },
        {
          type: "Select",
          label: "City ",
          mainKey: "city_id",
          OptionMainKey: "Cities",
          menuKey: "general_city_id",
          formatValue: (val) => `${val.city_name}`,
        },

        {
          type: "MultiRow",
          label: "Remarks ",
          mainKey: "remarks",
        },
        // {
        //   type: "Checkbox",
        //   label: "Is Lead Active ?",
        //   mainKey: "lead_status",
        // },
        {
          type: "Checkbox",
          label: "Is Spam ",
          mainKey: "is_spam",
        },
        {
          type: "MultiRow",
          label: "Spam Reason ",
          mainKey: "spam_reason",
        },
        {
          type: "LeadActivityEmbed",
          mainKey: "__lead_activity_embed",
        },
      ],
      DataFetchers: [
        { name: "LeadSources", Type: "Fetch", link: "/api/ems/lead-sources" },
        { name: "LeadStages", Type: "Fetch", link: "/api/ems/lead-stages" },
        {
          name: "CounsellorUsers",
          Type: "Fetch",
          link: "/api/ems/leads/counsellor-users",
        },
        {
          name: "TrainingProgramCategories",
          Type: "Fetch",
          link: "/api/ems/training-programs-categories",
        },
        {
          name: "TrainingPrograms",
          Type: "FetchDepend",
          dependKey: "training_program_category_id",
          link: "/api/ems/training-programs-categories/programs/${training_program_category_id}",
        },

        { name: "Countries", Type: "Fetch", link: "/api/general/countries/v1" },
        {
          name: "States",
          Type: "FetchDepend",
          dependKey: "country_id",
          link: "/api/general/countries/${country_id}/states/v1",
        },
        {
          name: "Cities",
          Type: "FetchDepend",
          dependKey: "state_id",
          link: "/api/general/countries/${country_id}/states/${state_id}/cities",
        },
        {
          name: "Employees",
          Type: "Fetch",
          link: "/api/organizations/${org_id}/users-data-all",
        },
        {
          name: "ActivityTypes",
          Type: "NoFetch",
          Options: [
            "Call",
            "Email",
            "WhatsApp",
            "SMS",
            "Telegram",
            "Online Meeting",
            "Visit",
          ],
        },
        {
          name: "CallStatuses",
          Type: "NoFetch",
          Options: ["Attended", "Missed", "Rejected", "No Answer"],
        },
        {
          name: "LeadDirections",
          Type: "NoFetch",
          Options: ["Incoming", "Outgoing"],
        },
      ],
    },
  },
  {
    route: "/lead-stages",
    ListProps: {
      title: "Lead Stages",
      apiEndpoint: "/api/ems/lead-stages",
      filteredApiEndpoint: "/api/ems/lead-stages/filtered",
      filterModule: "ems_lead_stages",
      useMetadataFilters: true,
      addRoute: "/lead-stages",
      editRouteBase: "/lead-stages/edit",
      tablePrimaryKey: "organization_ems_lead_stage_id",
      tableName: "ems_lead_stages",
      datagridKey: "ems_lead_stages_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "lead_stage_name",
        "lead_stage_short_name",
        "lead_status",
        "description",
      ],
      config: {
        sort: { field: "lead_stage_sequence_number", direction: "asc" },
        defaultVisibleColumns: [
          "lead_stage_name",
          "lead_stage_short_name",
          "lead_status",
          "description",
        ],
        columnLabels: {
          lead_stage_name: "Lead Stage Name",
          lead_stage_short_name: "Lead Stage Short Name",
          lead_status: "Lead Status",
          description: "Description",
        },
        columnWidths: {
          lead_stage_name: 220,
          lead_stage_short_name: 160,
          lead_status: 120,
          description: 280,
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => ({
          id: item.organization_ems_lead_stage_id,

          lead_stage_name: item?.lead_stage_name || "-",
          lead_stage_short_name: item.lead_stage_short_name || "-",
          lead_status:
            item?.lead_status === true ||
            item?.lead_status === 1 ||
            String(item?.lead_status).trim() === "1"
              ? "Open"
              : "Closed",
          description: item.description || "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          lead_stage_name,
          lead_stage_short_name,
          description,
        } = formData;

        // ===== REQUIRED NUMERIC IDS =====
        if (!organization_id) {
          errors.organization_id = "Organization ID is required.";
        } else if (isNaN(organization_id)) {
          errors.organization_id = "Organization ID must be a number.";
        }

        // ===== LEAD STAGE NAME =====
        if (!lead_stage_name?.trim()) {
          errors.lead_stage_name = "Lead stage name is required.";
        } else if (lead_stage_name.length > 100) {
          errors.lead_stage_name =
            "Lead stage name cannot exceed 100 characters.";
        }
        // Note: frontend cannot fully validate uniqueness per organization without an API call.

        // ===== OPTIONAL FIELDS =====
        if (lead_stage_short_name && lead_stage_short_name.length > 10) {
          errors.lead_stage_short_name =
            "Short name cannot exceed 10 characters.";
        }

        if (description && description.length > 255) {
          errors.description = "Description cannot exceed 255 characters.";
        }

        console.log("errors is : ", errors);

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/lead-stages",
      initialData: {
        organization_entity_id: "",
        lead_stage_name: "",
        lead_stage_short_name: "",
        description: "",
        // true => Open, false => Closed
        lead_status: true,
      },
      successMessages: {
        add: "Lead Stage added!",
        update: "Lead Stage updated!",
      },
      redirectPath: "/lead-stages",
      headerProps: {
        addMessage: "Add Lead Stage",
        updateMessage: "Update Lead Stage",
        homeLink: "/lead-stages",
        homeText: "Lead Stage",
      },
      Inputs: [
        {
          type: "Text",
          label: "Lead Stage Name *",
          mainKey: "lead_stage_name",
        },
        {
          type: "Text",
          label: "Lead Stage Short Name ",
          mainKey: "lead_stage_short_name",
        },
        {
          type: "MultiRow",
          label: "Description ",
          mainKey: "description",
        },
        {
          type: "RadioGroup",
          label: "Lead Status",
          mainKey: "lead_status",
          OptionMainKey: "LeadStatuses",
          menuKey: "value",
          formatValue: (val) => (val ? val.label : "-"),
        },
      ],
      DataFetchers: [
        {
          name: "LeadStatuses",
          Type: "NoFetch",
          Options: [
            { label: "Open", value: true },
            { label: "Closed", value: false },
          ],
        },
      ],
    },
  },
  {
    route: "/lead-sources",
    ListProps: {
      title: "Lead Sources",
      apiEndpoint: "/api/ems/lead-sources",
      filteredApiEndpoint: "/api/ems/lead-sources/filtered",
      filterModule: "ems_lead_sources",
      useMetadataFilters: true,
      addRoute: "/lead-sources",
      editRouteBase: "/lead-sources/edit",
      tablePrimaryKey: "organization_ems_lead_source_id",
      tableName: "ems_lead_sources",
      datagridKey: "ems_lead_sources_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "lead_source_name",
        "lead_source_short_name",
        "description",
      ],
      config: {
        sort: { field: "lead_source_name", direction: "asc" },
        defaultVisibleColumns: [
          "lead_source_name",
          "lead_source_short_name",
          "description",
        ],
        columnLabels: {
          lead_source_name: "Lead Source Name",
          lead_source_short_name: "Short Name",
          description: "Description",
        },
        columnWidths: {
          lead_source_name: 220,
          lead_source_short_name: 140,
          description: 280,
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => ({
          id: item.organization_ems_lead_source_id,
          lead_source_name: item?.lead_source_name || "-",
          lead_source_short_name: item.lead_source_short_name || "-",
          description: item.description || "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          lead_source_name,
          lead_source_short_name,
          description,
        } = formData;

        // ===== REQUIRED NUMERIC IDS =====
        if (!organization_id) {
          errors.organization_id = "Organization ID is required.";
        } else if (isNaN(organization_id)) {
          errors.organization_id = "Organization ID must be a number.";
        }

        // ===== LEAD STAGE NAME =====
        if (!lead_source_name?.trim()) {
          errors.lead_source_name = "Lead source name is required.";
        } else if (lead_source_name.length > 100) {
          errors.lead_source_name =
            "Lead source name cannot exceed 100 characters.";
        }
        // Note: frontend cannot fully validate uniqueness per organization without an API call.

        // ===== OPTIONAL FIELDS =====
        if (lead_source_short_name && lead_source_short_name.length > 10) {
          errors.lead_source_short_name =
            "Short name cannot exceed 10 characters.";
        }

        if (description && description.length > 255) {
          errors.description = "Description cannot exceed 255 characters.";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/lead-sources",
      initialData: {
        organization_entity_id: "",
        lead_source_name: "",
        lead_source_short_name: "",
        description: "",
      },
      successMessages: {
        add: "Lead Source added!",
        update: "Lead Source updated!",
      },
      redirectPath: "/lead-sources",
      headerProps: {
        addMessage: "Add Lead Source",
        updateMessage: "Update Lead Source",
        homeLink: "/lead-sources",
        homeText: "Lead Source",
      },
      Inputs: [
        {
          type: "Text",
          label: "Lead Source Name *",
          mainKey: "lead_source_name",
        },
        {
          type: "Text",
          label: "Lead Source Short Name ",
          mainKey: "lead_source_short_name",
        },
        {
          type: "MultiRow",
          label: "Description ",
          mainKey: "description",
        },
      ],
      DataFetchers: [],
    },
  },
  {
    route: "/lead-activities",
    ListProps: {
      title: "Lead Activities",
      apiEndpoint: "/api/ems/lead-activities",
      filteredApiEndpoint: "/api/ems/lead-activities/filtered",
      filterModule: "ems_lead_activities",
      useMetadataFilters: true,
      addRoute: "/lead-activities",
      editRouteBase: "/lead-activities/edit",
      tablePrimaryKey: "organization_ems_lead_activity_id",
      tableName: "ems_lead_activities",
      datagridKey: "ems_lead_activities_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "activity_id",
        "date_time",
        "next_follow_up",
        "lead_person_name",
        "activity_type",
        "lead_direction",
        "status",
        "lead_stage",
        "activity_by",
        "activity_summary",
        "remarks",
        "activity_datetime",
        "call_status",
        "was_within_preferred_time",
        "organization_ems_lead_id",
      ],
      config: {
        defaultVisibleColumns: [
          "activity_id",
          "date_time",
          "next_follow_up",
          "lead_person_name",
          "activity_type",
          "lead_direction",
          "status",
          "lead_stage",
          "activity_by",
        ],
        columnLabels: {
          activity_id: "Activity ID",
          date_time: "Date Time",
          next_follow_up: "Next Follow Up",
          lead_person_name: "Lead Person Name",
          activity_type: "Activity Type",
          lead_direction: "Lead direction",
          status: "Status",
          lead_stage: "Lead Stage",
          activity_by: "Activity By",
          activity_summary: "Activity Summary",
          remarks: "Remarks",
          activity_datetime: "Activity Date Time",
          call_status: "Call Status",
          was_within_preferred_time: "Within Preferred Time",
          organization_ems_lead_id: "Lead ID",
        },
        columnWidths: {
          date_time: 200,
          next_follow_up: 200,
          lead_person_name: 220,
          activity_by: 240,
          lead_direction: 130,
          activity_summary: 260,
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => {
          const trainerPerson =
            item?.trainer?.person ||
            item?.trainer?.organizationPerson?.person ||
            item?.trainer?.organization_person?.person ||
            {};
          const trainerFirst = String(trainerPerson?.first_name || "").trim();
          const trainerLast = String(trainerPerson?.last_name || "").trim();
          const trainerCode = String(
            trainerPerson?.organization_person?.person_code ||
              item?.trainer?.organizationPerson?.person_code ||
              item?.trainer?.organization_person?.person_code ||
              ""
          ).trim();
          const trainerName = [trainerFirst, trainerLast]
            .filter(Boolean)
            .join(" ")
            .trim();
          const appUserName = String(
            item?.trainer?.applicationuser?.[0]?.full_name ||
              item?.trainer?.applicationuser?.[0]?.user_name ||
              ""
          ).trim();
          const trainerDisplay = trainerName
            ? `${trainerName}${trainerCode ? ` (${trainerCode})` : ""}`
            : appUserName
              ? `${appUserName}${trainerCode ? ` (${trainerCode})` : ""}`
            : "-";

          const rawDt = item.activity_datetime;
          let dateTimeDisplay = rawDt || "-";
          if (rawDt) {
            const s = String(rawDt).trim();
            // Common API formats: `YYYY-MM-DD HH:mm:ss` or ISO `YYYY-MM-DDTHH:mm:ss...`
            const m = s.match(
              /^(\d{4}-\d{2}-\d{2})[T\s](\d{1,2}:\d{2}(?::\d{2})?)/
            );
            if (m) {
              const datePart = m[1];
              const timePart = m[2];
              const t12 = formatTime12h(timePart);
              // If minutes are 00, show `8 AM` instead of `8:00 AM`
              const compact =
                typeof t12 === "string" ? t12.replace(/:00\s+(AM|PM)$/i, " $1") : t12;
              dateTimeDisplay = `${datePart} ${compact}`;
            }
          }

          const rawFollowUp = item.next_follow_up;
          let nextFollowUpDisplay = rawFollowUp || "-";
          if (rawFollowUp) {
            const s = String(rawFollowUp).trim();
            const m = s.match(
              /^(\d{4}-\d{2}-\d{2})[T\s](\d{1,2}:\d{2}(?::\d{2})?)/
            );
            if (m) {
              const t12 = formatTime12h(m[2]);
              const compact =
                typeof t12 === "string" ? t12.replace(/:00\s+(AM|PM)$/i, " $1") : t12;
              nextFollowUpDisplay = `${m[1]} ${compact}`;
            }
          }

          return {
          id: item.organization_ems_lead_activity_id,
          activity_id: item.organization_ems_lead_activity_id ?? "-",
            organization_ems_lead_id:
              item?.organization_ems_lead_id ??
              item?.lead?.organization_ems_lead_id ??
              "",
            date_time: dateTimeDisplay,
            next_follow_up: nextFollowUpDisplay,
            lead_person_name: item?.lead?.person_full_name || "-",
            activity_by: trainerDisplay,
          lead: item?.lead?.person_full_name || "-",
            employee: trainerDisplay,

          activity_type: item.activity_type || "-",
          activity_datetime: item.activity_datetime || "-",
            lead_direction: item.lead_direction || "-",
          status: (() => {
            const callStatus = String(item.call_status || "").trim();
            if (["Attended", "Missed", "Rejected", "No Answer"].includes(callStatus)) {
              return callStatus;
            }
            return "-";
          })(),
          lead_stage: item?.lead_stage?.lead_stage_name || "-",
          activity_summary: item.activity_summary || "-",
          remarks: item.remarks || "-",
          was_within_preferred_time: item.was_within_preferred_time || "-",
          call_status: item.call_status || "-",
        };
        }),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          
          organization_ems_lead_id,
          organization_user_id,
          organization_ems_lead_stage_id,
          activity_type,
          activity_datetime,
          next_follow_up,
          lead_direction,
          activity_summary,
          remarks,
          was_within_preferred_time,
          call_status,
        } = formData;

        // ===== REQUIRED FIELDS =====
        if (!organization_id) {
          errors.organization_id = "Organization is required.";
        } else if (isNaN(organization_id)) {
          errors.organization_id = "Organization ID must be a number.";
        }

        if (!organization_ems_lead_id) {
          errors.organization_ems_lead_id = "Lead is required.";
        } else if (isNaN(organization_ems_lead_id)) {
          errors.organization_ems_lead_id = "Lead ID must be a number.";
        }

        if (!organization_user_id) {
          errors.organization_user_id = "Activity By user is required.";
        } else if (isNaN(organization_user_id)) {
          errors.organization_user_id = "Activity By user ID must be a number.";
        }

        if (
          organization_ems_lead_stage_id &&
          isNaN(organization_ems_lead_stage_id)
        ) {
          errors.organization_ems_lead_stage_id = "Invalid lead stage selected.";
        }

        // ===== ACTIVITY TYPE =====
        const validActivityTypes = [
          "Call",
          "Email",
          "WhatsApp",
          "SMS",
          "Telegram",
          "Online Meeting",
          "Visit",
        ];

        if (!activity_type) {
          errors.activity_type = "Activity type is required.";
        } else if (!validActivityTypes.includes(activity_type)) {
          errors.activity_type = "Invalid activity type selected.";
        }

        // ===== ACTIVITY DATETIME =====
        if (!activity_datetime) {
          errors.activity_datetime = "Activity date & time is required.";
        } else if (isNaN(Date.parse(activity_datetime))) {
          errors.activity_datetime = "Invalid date & time format.";
        } else {
          const leadDt = formData.lead_datetime || formData._lead_datetime;
          const activityLeadError = validateActivityDatetimeAgainstLead(
            activity_datetime,
            leadDt
          );
          if (activityLeadError) {
            errors.activity_datetime = activityLeadError;
          }
        }

        if (next_follow_up && String(next_follow_up).trim() !== "") {
          if (isNaN(Date.parse(next_follow_up))) {
            errors.next_follow_up = "Invalid next follow up date & time format.";
          }
        }

        const validLeadDirections = ["Incoming", "Outgoing"];
        if (!lead_direction) {
          errors.lead_direction = "Lead direction is required.";
        } else if (!validLeadDirections.includes(lead_direction)) {
          errors.lead_direction =
            "Lead direction must be Incoming or Outgoing.";
        }

        // ===== OPTIONAL FIELDS =====
        if (activity_summary && activity_summary.length > 255) {
          errors.activity_summary =
            "Activity summary cannot exceed 255 characters.";
        }

        if (remarks && typeof remarks !== "string") {
          errors.remarks = "Remarks must be a valid string.";
        }

        // // ===== BOOLEAN FIELDS =====
        // if (typeof was_within_preferred_time !== "boolean") {
        //   errors.was_within_preferred_time =
        //     "Please specify if it was within preferred time.";
        // }

        // const booleanFields = {
        //   email_read_flag,
        //   email_response_flag,
        //   whatsapp_read_flag,
        //   whatsapp_response_flag,
        // };

        // Object.entries(booleanFields).forEach(([key, value]) => {
        //   if (value !== undefined && typeof value !== "boolean") {
        //     errors[key] = "Invalid value.";
        //   }
        // });

        // ===== ENUM FIELD: CALL STATUS =====
        const validCallStatuses = [
          "Attended",
          "Missed",
          "Rejected",
          "No Answer",
        ];
        if (call_status && !validCallStatuses.includes(call_status)) {
          errors.call_status = "Invalid call status selected.";
        }

        // ===== SET ERRORS =====
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/lead-activities",
      initialData: {
        organization_entity_id: "",
        organization_ems_lead_id: "",
        organization_user_id: "",
        organization_ems_lead_stage_id: "",
        activity_type: "",
        activity_datetime: "",
        next_follow_up: "",
        lead_direction: "",
        activity_summary: "",
        remarks: "",
        was_within_preferred_time: false,
        call_status: "",
      },
      successMessages: {
        add: "Lead Activity added!",
        update: "Lead Activity updated!",
      },
      redirectPath: "/lead-activities",
      headerProps: {
        addMessage: "Add Lead Activity",
        updateMessage: "Update Lead Activity",
        homeLink: "/lead-activities",
        homeText: "Lead Activity",
      },
      fieldGridLayout: {
        rows: [
          { variant: "full", mainKeys: ["activity_datetime"] },
          { variant: "full", mainKeys: ["next_follow_up"] },
          { variant: "full", mainKeys: ["lead_direction"] },
          { variant: "full", mainKeys: ["organization_ems_lead_id"] },
          { variant: "full", mainKeys: ["organization_user_id"] },
          { variant: "twoEqual", mainKeys: ["activity_type", "call_status"] },
          { variant: "full", mainKeys: ["activity_summary"] },
          { variant: "full", mainKeys: ["organization_ems_lead_stage_id"] },
          { variant: "full", mainKeys: ["remarks"] },
        ],
      },
      Inputs: [
        {
          type: "DateTime",
          label: "Activity Date Time *",
          mainKey: "activity_datetime",
        },
        {
          type: "DateTime",
          label: "Next Follow Up",
          mainKey: "next_follow_up",
        },
        {
          type: "Select",
          label: "Lead direction *",
          mainKey: "lead_direction",
          OptionMainKey: "LeadDirections",
          menuKey: null,
          disableClearable: true,
          selectReadOnly: true,
          formatValue: (val) => val,
        },
        {
          type: "Select",
          label: "Lead Person Name *",
          mainKey: "organization_ems_lead_id",
          OptionMainKey: "Leads",
          menuKey: "organization_ems_lead_id",
          disableClearable: true,
          formatValue: (val) => `${val.person_full_name}`,
        },
        {
          type: "Select",
          label: "Activity By User *",
          mainKey: "organization_user_id",
          OptionMainKey: "Employees",
          menuKey: "organization_user_id",
          disableClearable: true,
          // Prevent typing/free text in Autocomplete input.
          selectReadOnly: true,
          formatValue: (val) => formatTrainerUserOption(val),
        },

        {
          type: "Select",
          label: "Lead Stage ",
          mainKey: "organization_ems_lead_stage_id",
          OptionMainKey: "LeadStages",
          menuKey: "organization_ems_lead_stage_id",
          disableClearable: true,
          selectReadOnly: true,
          formatValue: (val) => `${val.lead_stage_name}`,
        },



        {
          type: "Select",
          label: "Activity Type *",
          mainKey: "activity_type",
          OptionMainKey: "ActivityTypes",
          menuKey: null,
          disableClearable: true,
          selectReadOnly: true,
          formatValue: (val) => val,
        },
        {
          type: "MultiRow",
          label: "Activity Result ",
          mainKey: "activity_summary",
        },
        {
          type: "MultiRow",
          label: "Remarks by Employee ",
          mainKey: "remarks",
        },
        {
          type: "Select",
          label: "Activity Status ",
          mainKey: "call_status",
          OptionMainKey: "CallStatuses",
          menuKey: null,
          disableClearable: true,
          selectReadOnly: true,
          formatValue: (val) => val,
        },
      ],
      DataFetchers: [
        { name: "Leads", Type: "Fetch", link: "/api/ems/leads" },
        { name: "LeadStages", Type: "Fetch", link: "/api/ems/lead-stages" },
        {
          name: "Employees",
          Type: "Fetch",
          link: "/api/organizations/${org_id}/users-data-all",
        },
        {
          name: "ActivityTypes",
          Type: "NoFetch",
          Options: [
            "Call",
            "Email",
            "WhatsApp",
            "SMS",
            "Telegram",
            "Online Meeting",
            "Visit",
          ],
        },
        {
          name: "CallStatuses",
          Type: "NoFetch",
          Options: ["Attended", "Missed", "Rejected", "No Answer"],
        },
        {
          name: "LeadDirections",
          Type: "NoFetch",
          Options: ["Incoming", "Outgoing"],
        },
      ],
    },
  },
  {
    route: "/lead-contact-timings",
    ListProps: {
      title: "Lead Contact Timing",
      apiEndpoint: "/api/ems/lead-contact-timings",
      filteredApiEndpoint: "/api/ems/lead-contact-timings/filtered",
      filterModule: "ems_lead_contact_timings",
      useMetadataFilters: true,
      addRoute: "/lead-contact-timings",
      editRouteBase: "/lead-contact-timings/edit",
      tablePrimaryKey: "organization_ems_lead_contact_timing_id",
      tableName: "ems_lead_contact_timings",
      datagridKey: "ems_lead_contact_timings_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "lead",
        "organization_ems_lead_id",
        "preferred_contact_mode",
        "preferred_contact_timezone",
        "morning_student_time_start",
        "morning_student_time_end",
        "morning_ist_time_start",
        "morning_ist_time_end",
        "evening_student_time_start",
        "evening_student_time_end",
        "evening_ist_time_start",
        "evening_ist_time_end",
      ],
      config: {
        defaultVisibleColumns: [
          "lead",
          "preferred_contact_mode",
          "preferred_contact_timezone",
          "morning_ist_time_start",
          "morning_ist_time_end",
          "evening_ist_time_start",
          "evening_ist_time_end",
        ],
        columnLabels: {
          lead: "Lead",
          preferred_contact_mode: "Contact Mode",
          preferred_contact_timezone: "Timezone",
          morning_student_time_start: "Morning Student (Start)",
          morning_student_time_end: "Morning Student (End)",
          morning_ist_time_start: "Morning IST (Start)",
          morning_ist_time_end: "Morning IST (End)",
          evening_student_time_start: "Evening Student (Start)",
          evening_student_time_end: "Evening Student (End)",
          evening_ist_time_start: "Evening IST (Start)",
          evening_ist_time_end: "Evening IST (End)",
          organization_ems_lead_id: "Lead ID",
        },
        columnWidths: {
          lead: 220,
          preferred_contact_mode: 140,
          preferred_contact_timezone: 180,
          morning_ist_time_start: 150,
          morning_ist_time_end: 150,
          evening_ist_time_start: 150,
          evening_ist_time_end: 150,
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => ({
          id: item.organization_ems_lead_contact_timing_id,
          organization_ems_lead_id:
            item?.organization_ems_lead_id ??
            item?.lead?.organization_ems_lead_id ??
            "-",
          lead:
            item?.lead?.person_full_name ||
            item?.lead_name ||
            (item?.organization_ems_lead_id
              ? `Lead #${item.organization_ems_lead_id}`
              : "-"),
          preferred_contact_mode: item.preferred_contact_mode || "-",
          preferred_contact_timezone: item.preferred_contact_timezone || "-",
          morning_student_time_start:
            formatTime12h(item.morning_student_time_start) ||
            item.morning_student_time_start ||
            "-",
          morning_student_time_end:
            formatTime12h(item.morning_student_time_end) ||
            item.morning_student_time_end ||
            "-",
          morning_ist_time_start:
            formatTime12h(item.morning_ist_time_start) ||
            item.morning_ist_time_start ||
            "-",
          morning_ist_time_end:
            formatTime12h(item.morning_ist_time_end) ||
            item.morning_ist_time_end ||
            "-",
          evening_student_time_start:
            formatTime12h(item.evening_student_time_start) ||
            item.evening_student_time_start ||
            "-",
          evening_student_time_end:
            formatTime12h(item.evening_student_time_end) ||
            item.evening_student_time_end ||
            "-",
          evening_ist_time_start:
            formatTime12h(item.evening_ist_time_start) ||
            item.evening_ist_time_start ||
            "-",
          evening_ist_time_end:
            formatTime12h(item.evening_ist_time_end) ||
            item.evening_ist_time_end ||
            "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          organization_ems_lead_id,
          preferred_contact_mode,
          preferred_contact_timezone,
          morning_student_time_start,
          morning_student_time_end,
          morning_ist_time_start,
          morning_ist_time_end,
          evening_student_time_start,
          evening_student_time_end,
          evening_ist_time_start,
          evening_ist_time_end,
        } = formData;

        if (!organization_ems_lead_id)
          errors.organization_ems_lead_id = "Lead is required.";

        // ===== PREFERRED CONTACT MODE =====
        const validContactModes = [
          "Call",
          "WhatsApp",
          "Email",
          "SMS",
          "Telegram",
          "Visit",
          "Other",
        ];
        if (
          preferred_contact_mode &&
          !validContactModes.includes(preferred_contact_mode)
        ) {
          errors.preferred_contact_mode =
            "Preferred contact mode must be one of Call, WhatsApp, Email, SMS, Telegram, Visit, or Other.";
        }

        // ===== TIMEZONE (IANA) — required for IST conversion =====
        const normalizedTz = normalizePreferredContactTimezone(
          preferred_contact_timezone
        );
        if (!normalizedTz) {
          errors.preferred_contact_timezone =
            "Preferred contact timezone is required (e.g. America/Toronto).";
        } else if (!isValidIanaTimezone(normalizedTz)) {
          errors.preferred_contact_timezone =
            "Use a valid IANA timezone (e.g. America/Toronto, Asia/Kolkata).";
        }

        // ===== SET ERRORS =====
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/lead-contact-timings",
      initialData: {
        organization_entity_id: "",
        organization_ems_lead_id: "",
        preferred_contact_mode: "",
        preferred_contact_timezone: "",
        morning_student_time_start: "",
        morning_student_time_end: "",
        morning_ist_time_start: "",
        morning_ist_time_end: "",
        evening_student_time_start: "",
        evening_student_time_end: "",
        evening_ist_time_start: "",
        evening_ist_time_end: "",
      },
      successMessages: {
        add: "Lead Contact Timing added!",
        update: "Lead Contact Timing updated!",
      },
      redirectPath: "/lead-contact-timings",
      headerProps: {
        addMessage: "Add Lead Contact Timing",
        updateMessage: "Update Lead Contact Timing",
        homeLink: "/lead-contact-timings",
        homeText: "Lead Contact Timing",
      },
      Inputs: [
        {
          type: "Select",
          label: "Lead *",
          mainKey: "organization_ems_lead_id",
          OptionMainKey: "Leads",
          menuKey: "organization_ems_lead_id",
          formatValue: (val) => `${val.person_full_name}`,
        },
        {
          type: "Select",
          label: "Preferred Contact Mode ",
          mainKey: "preferred_contact_mode",
          OptionMainKey: "PreferredContactMode",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "Text",
          label: "Preferred Contact Timezone (IANA) *",
          mainKey: "preferred_contact_timezone",
        },

        {
          type: "Time",
          label: "Morning Student Time Start ",
          mainKey: "morning_student_time_start",
        },
        {
          type: "Time",
          label: "Morning Student Time End ",
          mainKey: "morning_student_time_end",
        },
        {
          type: "Time",
          label: "Morning IST Time Start *",
          mainKey: "morning_ist_time_start",
          disabled: true,
        },
        {
          type: "Time",
          label: "Morning IST Time End *",
          mainKey: "morning_ist_time_end",
          disabled: true,
        },

        {
          type: "Time",
          label: "Evening Student Time Start ",
          mainKey: "evening_student_time_start",
        },
        {
          type: "Time",
          label: "Evening Student Time End ",
          mainKey: "evening_student_time_end",
        },
        {
          type: "Time",
          label: "Evening IST Time Start *",
          mainKey: "evening_ist_time_start",
          disabled: true,
        },
        {
          type: "Time",
          label: "Evening IST Time End *",
          mainKey: "evening_ist_time_end",
          disabled: true,
        },
      ],
      DataFetchers: [
        { name: "Leads", Type: "Fetch", link: "/api/ems/leads" },
        {
          name: "PreferredContactMode",
          Type: "NoFetch",
          Options: [
            "Call",
            "WhatsApp",
            "Email",
            "SMS",
            "Telegram",
            "Visit",
            "Other",
          ],
        },
      ],
    },
  },

  {
    route: "/assesments",
    ListProps: {
      title: "Assesments",
      apiEndpoint: "/api/ems/assesments",
      filteredApiEndpoint: "/api/ems/assesments/filtered",
      filterModule: "ems_assessments",
      useMetadataFilters: true,
      addRoute: "/assesments",
      editRouteBase: "/assesments/edit",
      tablePrimaryKey: "organization_ems_assessment_id",
      tableName: "ems_assessments",
      datagridKey: "ems_assessments_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "training_program",
        "assessment_name",
        "assessment_type",
        "test",
        "max_marks",
        "passing_marks",
        "attachment",
        "remarks",
      ],
      config: {
        sort: { field: "organization_ems_assessment_id", direction: "desc" },
        defaultVisibleColumns: [
          "training_program",
          "assessment_name",
          "assessment_type",
          "test",
          "max_marks",
          "passing_marks",
          "attachment",
        ],
        columnLabels: {
          training_program: "Training Program",
          assessment_name: "Assessment Name",
          assessment_type: "Assessment Type",
          test: "Test",
          max_marks: "Max Marks",
          passing_marks: "Passing Marks",
          attachment: "Attachment",
          remarks: "Description",
        },
        columnWidths: {
          training_program: 260,
          assessment_name: 200,
          assessment_type: 180,
          test: 180,
          attachment: 180,
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => ({
          id: item.organization_ems_assessment_id,
          training_program:
            item?.training_program?.training_program_name || "-",
          assessment_name: item.assessment_name || "-",
          assessment_type:
            item?.assessment_type_record?.assessment_type_name ||
            (typeof item.assessment_type === "string" ? item.assessment_type : "-"),
          test: item?.test
            ? `${item.test.total_questions ?? 0} Q · ${
                item.test.test_duration_minutes ?? "-"
              } min`
            : "-",
          max_marks: item.max_marks ?? item.max_score ?? "-",
          passing_marks: item.passing_marks ?? item.passing_score ?? "-",
          attachment: item.attachment_url
            ? item.attachment_name || "View file"
            : "-",
          attachment_url: item.attachment_url || "",
          remarks: item.remarks || "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors, mode) => {
        const errors = {};
        const {
          organization_id,
          organization_ems_training_program_id,
          training_program_id,
          assessment_name,
          max_marks,
          max_score,
          passing_marks,
          passing_score,
          remarks,
          attachment_url,
        } = formData;
        const programId = organization_ems_training_program_id || training_program_id;
        const maxMarks = max_marks ?? max_score;
        const passingMarks = passing_marks ?? passing_score;

        if (!organization_id)
          errors.organization_id = "Organization ID is required.";
        if (!programId)
          errors.organization_ems_training_program_id =
            "Training program is required.";
        if (!assessment_name)
          errors.assessment_name = "Assessment name is required.";
        if (!formData?.organization_ems_assessment_type_id)
          errors.organization_ems_assessment_type_id =
            "Assessment type is required.";
        if (maxMarks === "" || maxMarks === null || maxMarks === undefined)
          errors.max_marks = "Maximum marks are required.";

        if (
          maxMarks !== "" &&
          maxMarks != null &&
          (isNaN(maxMarks) ||
            Number(maxMarks) < 0 ||
            Number(maxMarks) > 999999.99)
        ) {
          errors.max_marks =
            "Maximum marks must be a valid number between 0 and 999999.99.";
        }

        if (passingMarks) {
          if (
            isNaN(passingMarks) ||
            Number(passingMarks) < 0 ||
            Number(passingMarks) > 999999.99
          ) {
            errors.passing_marks =
              "Passing marks must be a valid number between 0 and 999999.99.";
          } else if (Number(passingMarks) > Number(maxMarks)) {
            errors.passing_marks =
              "Passing marks must be less than or equal to maximum marks.";
          }
        }

        if (remarks && remarks.length > 1000) {
          errors.remarks = "Description cannot exceed 1000 characters.";
        }

        // ===== VALIDATION: attachment (optional) =====
        if (attachment_url instanceof File) {
          const allowed = [
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/vnd.ms-powerpoint",
            "application/vnd.openxmlformats-officedocument.presentationml.presentation",
            "image/jpeg",
            "image/png",
            "image/webp",
            "image/gif",
          ];
          const extOk = /\.(pdf|doc|docx|ppt|pptx|jpe?g|png|webp|gif)$/i.test(
            attachment_url.name || ""
          );
          if (!allowed.includes(attachment_url.type) && !extOk) {
            errors.attachment_url =
              "Attachment must be PDF, DOC, DOCX, PPT, PPTX, or an image.";
          } else if (attachment_url.size > 10 * 1024 * 1024) {
            errors.attachment_url = "Attachment must not exceed 10 MB.";
          }
        }

        // ===== SET ERRORS =====
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/assesments",
      initialData: {
        organization_entity_id: "",
        organization_ems_training_program_id: "",
        organization_ems_assessment_type_id: "",
        assessment_name: "",
        instructions: "",
        reference_url: "",
        max_marks: "",
        passing_marks: "",
        remarks: "",
        attachment_url: "",
        attachment_name: "",
        test_summary: "",
      },
      successMessages: {
        add: "Assessment added!",
        update: "Assessment updated!",
      },
      redirectPath: "/assesments",
      headerProps: {
        addMessage: "Add Assessment",
        updateMessage: "Update Assessment",
        homeLink: "/assesments",
        homeText: "Assessment",
      },
      fieldGridLayout: {
        rows: [
          {
            variant: "twoEqual",
            mainKeys: [
              "organization_ems_training_program_id",
              "organization_ems_assessment_type_id",
            ],
          },
          { variant: "full", mainKeys: ["assessment_name"] },
          { variant: "full", mainKeys: ["remarks"] },
          {
            variant: "twoEqual",
            mainKeys: ["max_marks", "passing_marks"],
          },
          { variant: "full", mainKeys: ["test_summary"] },
          { variant: "full", mainKeys: ["instructions"] },
          { variant: "full", mainKeys: ["reference_url"] },
          { variant: "full", mainKeys: ["attachment_url"] },
        ],
        sections: [],
      },
      Inputs: [
        {
          type: "Select",
          label: "Training Program *",
          mainKey: "organization_ems_training_program_id",
          OptionMainKey: "TrainingPrograms",
          menuKey: "organization_ems_training_program_id",
          formatValue: (val) => `${val.training_program_name}`,
        },
        {
          type: "Text",
          label: "Assessment Name *",
          mainKey: "assessment_name",
        },
        {
          type: "MultiRow",
          label: "Description ",
          mainKey: "remarks",
        },
        {
          type: "Select",
          label: "Assessment Type *",
          mainKey: "organization_ems_assessment_type_id",
          OptionMainKey: "AssessmentTypeRecords",
          menuKey: "organization_ems_assessment_type_id",
          formatValue: (val) =>
            `${val.assessment_type_name}${
              val.assessment_delivery_mode
                ? ` (${val.assessment_delivery_mode})`
                : ""
            }`,
        },
        {
          type: "Number",
          label: "Max Marks *",
          mainKey: "max_marks",
          helperText:
            "This total is split equally across questions when you add the test (e.g. 50 marks and 10 questions = 5 each).",
        },
        {
          type: "Number",
          label: "Passing Marks ",
          mainKey: "passing_marks",
          helperText: "Must not be more than Max Marks.",
        },
        {
          type: "Text",
          label: "Test",
          mainKey: "test_summary",
          hideInAdd: true,
          disabledWhen: () => true,
        },
        {
          type: "File",
          label: "Attachment",
          mainKey: "attachment_url",
          accept:
            ".pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png,.webp,.gif,application/pdf,image/*",
        },
        {
          type: "MultiRow",
          label: "Instructions ",
          mainKey: "instructions",
        },
        {
          type: "Text",
          label: "Reference URL ",
          mainKey: "reference_url",
        },
      ],
      DataFetchers: [
        {
          name: "TrainingPrograms",
          Type: "Fetch",
          link: "/api/ems/training-programs",
        },
        {
          name: "AssessmentTypeRecords",
          Type: "Fetch",
          link: "/api/ems/assessment-types",
        },
      ],
    },
  },
  {
    route: "/assessment-types",
    ListProps: {
      title: "Assessment Types",
      apiEndpoint: "/api/ems/assessment-types",
      useMetadataFilters: false,
      addRoute: "/assessment-types",
      editRouteBase: "/assessment-types/edit",
      tablePrimaryKey: "organization_ems_assessment_type_id",
      tableName: "ems_assessment_types",
      datagridKey: "ems_assessment_types_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "assessment_type_name",
        "assessment_delivery_mode",
        "description",
      ],
      config: {
        sort: { field: "assessment_type_name", direction: "asc" },
        defaultVisibleColumns: [
          "assessment_type_name",
          "assessment_delivery_mode",
          "description",
        ],
        columnLabels: {
          assessment_type_name: "Type",
          assessment_delivery_mode: "Delivery",
          description: "Description",
        },
        columnWidths: {
          assessment_type_name: 220,
          assessment_delivery_mode: 140,
          description: 360,
        },
      },
      mapResponseToRows: (data) =>
        (Array.isArray(data) ? data : []).map((item) => ({
          id: item.organization_ems_assessment_type_id,
          assessment_type_name: item.assessment_type_name || "-",
          assessment_delivery_mode: item.assessment_delivery_mode || "-",
          description: item.description || "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        if (!formData?.organization_id)
          errors.organization_id = "Organization is required.";
        if (!String(formData?.assessment_type_name || "").trim())
          errors.assessment_type_name = "Type name is required.";
        if (!formData?.assessment_delivery_mode)
          errors.assessment_delivery_mode = "Delivery mode is required.";
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },
      baseUrl: "/api/ems/assessment-types",
      initialData: {
        organization_entity_id: "",
        assessment_type_name: "",
        assessment_delivery_mode: "Submission",
        description: "",
      },
      successMessages: {
        add: "Assessment type created.",
        update: "Assessment type updated.",
      },
      redirectPath: "/assessment-types",
      headerProps: {
        addMessage: "Add Assessment Type",
        updateMessage: "Update Assessment Type",
        homeLink: "/assessment-types",
        homeText: "Assessment Types",
      },
      fieldGridLayout: {
        rows: [
          {
            variant: "twoEqual",
            mainKeys: ["assessment_type_name", "assessment_delivery_mode"],
          },
          { variant: "full", mainKeys: ["description"] },
        ],
        sections: [],
      },
      Inputs: [
        {
          type: "Text",
          label: "Type Name *",
          mainKey: "assessment_type_name",
        },
        {
          type: "Select",
          label: "Delivery Mode *",
          mainKey: "assessment_delivery_mode",
          OptionMainKey: "DeliveryModes",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "MultiRow",
          label: "Description ",
          mainKey: "description",
        },
      ],
      DataFetchers: [
        {
          name: "DeliveryModes",
          Type: "NoFetch",
          Options: ["Online", "Submission", "In Person"],
        },
      ],
    },
  },
  {
    route: "/assessment-tests",
    ListProps: {
      title: "Tests",
      apiEndpoint: "/api/ems/tests",
      useMetadataFilters: false,
      addRoute: "/assessment-tests",
      editRouteBase: "/assessment-tests/edit",
      tablePrimaryKey: "organization_ems_test_id",
      tableName: "ems_tests",
      datagridKey: "ems_tests_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "assessment_name",
        "training_program",
        "modules",
        "total_questions",
        "test_duration_minutes",
        "max_attempts",
      ],
      config: {
        sort: { field: "organization_ems_test_id", direction: "desc" },
        defaultVisibleColumns: [
          "assessment_name",
          "training_program",
          "modules",
          "total_questions",
          "test_duration_minutes",
          "max_attempts",
        ],
        columnLabels: {
          assessment_name: "Assessment",
          training_program: "Training Program",
          modules: "Question Module",
          total_questions: "Questions",
          test_duration_minutes: "Duration (min)",
          max_attempts: "Attempts",
        },
        columnWidths: {
          assessment_name: 240,
          training_program: 240,
          modules: 200,
          total_questions: 120,
          test_duration_minutes: 140,
          max_attempts: 110,
        },
      },
      mapResponseToRows: (data) =>
        (Array.isArray(data) ? data : []).map((item) => ({
          id: item.organization_ems_test_id,
          assessment_name: item?.assessment?.assessment_name || "-",
          training_program:
            item?.assessment?.training_program?.training_program_name || "-",
          modules:
            (item?.modules || [])
              .map((row) => row?.module?.module_name)
              .filter(Boolean)
              .join(", ") || "-",
          total_questions: item.total_questions ?? "-",
          test_duration_minutes: item.test_duration_minutes ?? "-",
          max_attempts: item.max_attempts ?? "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const isAdd =
          formData?._form_mode === "add" ||
          !formData?.organization_ems_test_id;
        if (!formData?.organization_id)
          errors.organization_id = "Organization is required.";
        if (!formData?.organization_ems_assessment_id)
          errors.organization_ems_assessment_id = "Assessment is required.";
        if (isAdd) {
          if (!formData?.organization_ems_training_program_module_id)
            errors.organization_ems_training_program_module_id =
              "Module is required to pick questions.";
          if (!formData?.question_pick_mode)
            errors.question_pick_mode = "Choose how to pick questions.";
          if (formData?.question_pick_mode === "Random") {
            const requested = Number(formData?.total_questions);
            const available = Number(formData?._module_question_count);
            if (!formData?.total_questions || !Number.isFinite(requested) || requested < 1) {
              errors.total_questions =
                "Enter how many questions to pick at random.";
            } else if (
              Number.isFinite(available) &&
              available >= 0 &&
              formData?._module_question_count !== "" &&
              formData?._module_question_count != null &&
              requested > available
            ) {
              errors.total_questions =
                available === 1
                  ? "Question count cannot be greater than the 1 question in this module."
                  : `Question count cannot be greater than the ${available} questions in this module.`;
            }
          }
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },
      baseUrl: "/api/ems/tests",
      initialData: {
        organization_entity_id: "",
        organization_ems_assessment_id: "",
        organization_ems_training_program_module_id: "",
        question_pick_mode: "All",
        test_duration_minutes: 30,
        total_questions: "",
        questions_to_attempt: "",
        max_attempts: 1,
        negative_marking: false,
        negative_marks_per_question: "",
        show_result_immediately: false,
        show_correct_answers: false,
        shuffle_questions: true,
        remarks: "",
        questions: [],
      },
      successMessages: {
        add: "Test saved.",
        update: "Test updated.",
      },
      redirectPath: "/assessment-tests",
      headerProps: {
        addMessage: "Add Test",
        updateMessage: "Update Test",
        homeLink: "/assessment-tests",
        homeText: "Tests",
      },
      fieldGridLayout: {
        rows: [
          { variant: "full", mainKeys: ["organization_ems_assessment_id"] },
          {
            variant: "twoEqual",
            mainKeys: [
              "organization_ems_training_program_module_id",
              "question_pick_mode",
            ],
          },
          { variant: "full", mainKeys: ["questions"] },
          {
            variant: "threeEqual",
            mainKeys: [
              "test_duration_minutes",
              "total_questions",
              "max_attempts",
            ],
          },
          {
            variant: "twoEqual",
            mainKeys: ["negative_marking", "negative_marks_per_question"],
          },
          {
            variant: "twoEqual",
            mainKeys: ["show_result_immediately", "show_correct_answers"],
          },
          { variant: "full", mainKeys: ["shuffle_questions"] },
          { variant: "full", mainKeys: ["remarks"] },
        ],
        sections: [],
      },
      Inputs: [
        {
          type: "Select",
          label: "Assessment *",
          mainKey: "organization_ems_assessment_id",
          OptionMainKey: "Assessments",
          menuKey: "organization_ems_assessment_id",
          formatValue: (val) => val.assessment_name,
          disabledWhen: (formData, mode) =>
            mode !== "add" || Boolean(formData?._lock_assessment),
        },
        {
          type: "Select",
          label: "Question Module *",
          mainKey: "organization_ems_training_program_module_id",
          OptionMainKey: "TrainingProgramModules",
          menuKey: "organization_ems_training_program_module_id",
          formatValue: (val) => val.module_name,
          visibleWhen: (_formData, mode) => mode === "add",
          disabledWhen: (formData) =>
            !formData?.organization_ems_assessment_id,
        },
        {
          type: "Select",
          label: "Pick Questions *",
          mainKey: "question_pick_mode",
          OptionMainKey: "QuestionPickModes",
          menuKey: null,
          formatValue: (val) => val,
          visibleWhen: (_formData, mode) => mode === "add",
        },
        {
          type: "RepeatableText",
          label: "Test questions",
          mainKey: "questions",
          hideInAdd: true,
          hideAddButton: true,
          defaultCount: 0,
          emptyText: "No questions on this test.",
          rowLayout: "questionCard",
          disabledWhen: () => true,
          fields: [
            { key: "question", label: "Question", type: "textarea" },
            { key: "option_1", label: "Option 1", type: "text" },
            { key: "option_2", label: "Option 2", type: "text" },
            { key: "option_3", label: "Option 3", type: "text" },
            { key: "option_4", label: "Option 4", type: "text" },
            {
              key: "correct_answer",
              label: "Correct Answer",
              type: "select",
              options: [
                { value: "option_1", label: "Option 1" },
                { value: "option_2", label: "Option 2" },
                { value: "option_3", label: "Option 3" },
                { value: "option_4", label: "Option 4" },
              ],
            },
            {
              key: "marks",
              label: "Marks",
              type: "number",
              defaultValue: "1",
              min: 0.01,
              step: "0.5",
            },
          ],
        },
        {
          type: "Number",
          label: "Duration (minutes)",
          mainKey: "test_duration_minutes",
        },
        {
          type: "Number",
          label: "Question count (for Random)",
          mainKey: "total_questions",
          disabledWhen: (formData) =>
            String(formData?.question_pick_mode || "All") !== "Random",
          clearWhenDisabled: true,
          helperText: (formData) => {
            const available = Number(formData?._module_question_count);
            const hasCount =
              formData?._module_question_count !== "" &&
              formData?._module_question_count != null &&
              Number.isFinite(available);
            const random =
              String(formData?.question_pick_mode || "") === "Random";
            if (random && hasCount) {
              return `This module has ${available} question${
                available === 1 ? "" : "s"
              }. Random count cannot be greater than that.`;
            }
            return "Used only when Pick Questions is Random. Each of these questions gets Max Score ÷ question count from the linked assessment.";
          },
        },
        {
          type: "Number",
          label: "Max Attempts",
          mainKey: "max_attempts",
        },
        {
          type: "Checkbox",
          label: "Negative marking",
          mainKey: "negative_marking",
        },
        {
          type: "Number",
          label: "Negative marks per wrong answer",
          mainKey: "negative_marks_per_question",
          disabledWhen: (formData) => !formData?.negative_marking,
          clearWhenDisabled: true,
        },
        {
          type: "Checkbox",
          label: "Show result immediately",
          mainKey: "show_result_immediately",
          helperText:
            "After submit, show the student their score, percentage, and Pass/Fail.",
        },
        {
          type: "Checkbox",
          label: "Show correct answers",
          mainKey: "show_correct_answers",
          helperText:
            "As soon as the student picks an answer, mark it green if correct and red if wrong, and highlight the right option.",
        },
        {
          type: "Checkbox",
          label: "Shuffle questions",
          mainKey: "shuffle_questions",
        },
        {
          type: "MultiRow",
          label: "Remarks ",
          mainKey: "remarks",
        },
      ],
      DataFetchers: [
        { name: "Assessments", Type: "Fetch", link: "/api/ems/assesments" },
        {
          name: "TrainingProgramModules",
          Type: "Fetch",
          link: "/api/ems/training-program-modules",
        },
        {
          name: "QuestionPickModes",
          Type: "NoFetch",
          Options: ["All", "Random"],
        },
      ],
    },
  },
  {
    route: "/assessment-schedules",
    ListProps: {
      title: "Assessment Schedules",
      apiEndpoint: "/api/ems/assessment-schedules",
      useMetadataFilters: false,
      addRoute: "/assessment-schedules",
      editRouteBase: "/assessment-schedules/edit",
      tablePrimaryKey: "organization_ems_assessment_schedule_id",
      tableName: "ems_assessment_schedules",
      datagridKey: "ems_assessment_schedules_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "assessment_name",
        "batch",
        "scheduled_date",
        "start_time",
        "end_time",
        "students_count",
        "status",
      ],
      config: {
        sort: { field: "scheduled_date", direction: "desc" },
        defaultVisibleColumns: [
          "assessment_name",
          "batch",
          "scheduled_date",
          "start_time",
          "end_time",
          "students_count",
          "status",
        ],
        columnLabels: {
          assessment_name: "Assessment",
          batch: "Batch",
          scheduled_date: "Date",
          start_time: "Start",
          end_time: "End",
          students_count: "Students",
          status: "Status",
        },
        columnWidths: {
          assessment_name: 240,
          batch: 180,
          scheduled_date: 130,
          start_time: 110,
          end_time: 110,
          students_count: 110,
          status: 120,
        },
      },
      mapResponseToRows: (data) =>
        (Array.isArray(data) ? data : []).map((item) => ({
          id: item.organization_ems_assessment_schedule_id,
          assessment_name: item?.assessment?.assessment_name || "-",
          batch: item?.batch?.batch_name || item?.batch?.batch_code || "-",
          scheduled_date: FormatDateV1(item.scheduled_date) || "-",
          start_time: formatTime12h(item.start_time),
          end_time: formatTime12h(item.end_time),
          students_count: item.students_count ?? item.students?.length ?? 0,
          status: item.status || "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        if (!formData?.organization_id)
          errors.organization_id = "Organization is required.";
        if (!formData?.organization_ems_assessment_id)
          errors.organization_ems_assessment_id = "Assessment is required.";
        if (!formData?.organization_ems_batch_id)
          errors.organization_ems_batch_id = "Batch is required.";
        if (!formData?.scheduled_date)
          errors.scheduled_date = "Scheduled date is required.";
        const assignAll = Boolean(formData?.assign_all_batch_students);
        const selectedStudents = Array.isArray(
          formData?.organization_ems_student_ids
        )
          ? formData.organization_ems_student_ids.filter(
              (id) => id != null && String(id).trim() !== ""
            )
          : [];
        const isCancelled =
          String(formData?.status || "").trim().toLowerCase() === "cancelled";
        if (isCancelled && (assignAll || selectedStudents.length > 0)) {
          errors.status =
            "This assessment is assigned to a student and cannot be cancelled.";
        }
        if (!isCancelled && !assignAll && selectedStudents.length === 0)
          errors.organization_ems_student_ids =
            "Select particular students from this batch, or choose All students.";
        const timeToMinutes = (value) => {
          if (value == null || String(value).trim() === "") return null;
          const match = String(value)
            .trim()
            .match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
          if (!match) return null;
          return Number(match[1]) * 60 + Number(match[2]);
        };
        const startMinutes = timeToMinutes(formData?.start_time);
        const endMinutes = timeToMinutes(formData?.end_time);
        if (
          startMinutes != null &&
          endMinutes != null &&
          endMinutes <= startMinutes
        ) {
          errors.end_time =
            "End time must be after start time on the scheduled date.";
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },
      baseUrl: "/api/ems/assessment-schedules",
      initialData: {
        organization_entity_id: "",
        organization_ems_assessment_id: "",
        organization_ems_batch_id: "",
        organization_ems_student_ids: [],
        assign_all_batch_students: false,
        scheduled_date: "",
        start_time: "",
        end_time: "",
        due_date: "",
        due_time: "",
        status: "Scheduled",
        remarks: "",
      },
      successMessages: {
        add: "Schedule created and students assigned.",
        update: "Schedule updated.",
      },
      redirectPath: "/assessment-schedules",
      headerProps: {
        addMessage: "Add Schedule",
        updateMessage: "Update Schedule",
        homeLink: "/assessment-schedules",
        homeText: "Schedules",
      },
      fieldGridLayout: {
        rows: [
          {
            variant: "twoEqual",
            mainKeys: [
              "organization_ems_assessment_id",
              "organization_ems_batch_id",
            ],
          },
          { variant: "full", mainKeys: ["assign_all_batch_students"] },
          { variant: "full", mainKeys: ["organization_ems_student_ids"] },
          {
            variant: "threeEqual",
            mainKeys: ["scheduled_date", "start_time", "end_time"],
          },
          {
            variant: "twoEqual",
            mainKeys: ["due_date", "due_time"],
          },
          {
            variant: "full",
            mainKeys: ["status"],
          },
          { variant: "full", mainKeys: ["remarks"] },
        ],
        sections: [
          {
            beforeRow: 1,
            title: "Who should get this assignment",
          },
        ],
      },
      Inputs: [
        {
          type: "Select",
          label: "Assessment *",
          mainKey: "organization_ems_assessment_id",
          OptionMainKey: "Assessments",
          menuKey: "organization_ems_assessment_id",
          formatValue: (val) => val.assessment_name,
          disabledWhen: (formData) => Boolean(formData?._lock_assessment),
        },
        {
          type: "Select",
          label: "Batch *",
          mainKey: "organization_ems_batch_id",
          OptionMainKey: "Batches",
          menuKey: "organization_ems_batch_id",
          formatValue: (val) =>
            `${val?.batch_code ?? val?.batch_name ?? "-"}`,
          disabledWhen: (formData) => !formData?.organization_ems_assessment_id,
          helperText: (formData) =>
            formData?.organization_ems_assessment_id
              ? "Only batches of this assessment's training program are listed."
              : "Select an assessment first to see its training program batches.",
        },
        {
          type: "RadioGroup",
          label: "Assign to *",
          mainKey: "assign_all_batch_students",
          OptionMainKey: "AssignmentScopes",
          menuKey: "value",
          row: false,
          formatValue: (val) => val?.label ?? val,
          disabledWhen: (formData) => !formData?.organization_ems_batch_id,
          helperText: (formData) => {
            if (!formData?.organization_ems_batch_id) {
              return "Select a batch first, then choose All students or Particular students.";
            }
            if (formData?.assign_all_batch_students) {
              return "Every student currently in this batch will see this assignment in My Assignments.";
            }
            return "Only the students you pick below will see this assignment.";
          },
        },
        {
          type: "MultiSelect",
          label: "Students from this batch *",
          mainKey: "organization_ems_student_ids",
          OptionMainKey: "BatchStudents",
          menuKey: "organization_ems_student_id",
          placeholder: "Search and select students",
          formatValue: (val) => {
            const student = val?.student || val;
            const name = [student?.first_name, student?.last_name]
              .filter(Boolean)
              .join(" ");
            const code = student?.student_id || val?.organization_ems_student_id || "";
            return name ? `${name}${code ? ` (${code})` : ""}` : String(code || "Student");
          },
          visibleWhen: () => true,
          disabledWhen: (formData) =>
            !formData?.organization_ems_batch_id ||
            Boolean(formData?.assign_all_batch_students),
          helperText: (formData) => {
            if (!formData?.organization_ems_batch_id) {
              return "Select a batch first, then pick particular students or choose All students.";
            }
            if (formData?.assign_all_batch_students) {
              return "All students of this batch are selected. Switch to Particular students to change the list.";
            }
            return "Search and select one or more students from this batch.";
          },
        },
        {
          type: "Date",
          label: "Scheduled Date *",
          mainKey: "scheduled_date",
        },
        {
          type: "Time",
          label: "Start Time ",
          mainKey: "start_time",
        },
        {
          type: "Time",
          label: "End Time ",
          mainKey: "end_time",
        },
        {
          type: "Date",
          label: "Due Date ",
          mainKey: "due_date",
        },
        {
          type: "Time",
          label: "Due Time ",
          mainKey: "due_time",
        },
        {
          type: "Select",
          label: "Status ",
          mainKey: "status",
          OptionMainKey: "ScheduleStatuses",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "MultiRow",
          label: "Remarks ",
          mainKey: "remarks",
        },
      ],
      DataFetchers: [
        { name: "Assessments", Type: "Fetch", link: "/api/ems/assesments" },
        { name: "Batches", Type: "Fetch", link: "/api/ems/batches" },
        { name: "BatchStudents", Type: "Fetch", link: "/api/ems/batch-students" },
        {
          name: "AssignmentScopes",
          Type: "NoFetch",
          Options: [
            { label: "All students in this batch", value: true },
            { label: "Particular students", value: false },
          ],
        },
        {
          name: "ScheduleStatuses",
          Type: "NoFetch",
          Options: ["Scheduled", "Closed", "Cancelled"],
        },
      ],
    },
  },
  {
    route: "/assesment-results",
    ListProps: {
      title: "Assessment Results",
      apiEndpoint: "/api/ems/assesment-results",
      filteredApiEndpoint: "/api/ems/assesment-results/filtered",
      filterModule: "ems_assessment_results",
      useMetadataFilters: true,
      addRoute: "/assesment-results",
      editRouteBase: "/assesment-results/edit",
      tablePrimaryKey: "organization_ems_assessment_result_id",
      tableName: "ems_assessment_results",
      datagridKey: "ems_assessment_results_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "assesment",
        "student",
        "admission",
        "score_obtained",
        "result_status",
        "submission",
        "remarks",
      ],
      config: {
        sort: {
          field: "organization_ems_assessment_result_id",
          direction: "desc",
        },
        defaultVisibleColumns: [
          "assesment",
          "student",
          "admission",
          "score_obtained",
          "result_status",
          "submission",
          "remarks",
        ],
        columnLabels: {
          assesment: "Assessment",
          student: "Student",
          admission: "Admission",
          score_obtained: "Score Obtained",
          result_status: "Result Status",
          submission: "Student File",
          remarks: "Remarks",
        },
        columnWidths: {
          assesment: 220,
          student: 220,
          admission: 160,
          score_obtained: 130,
          result_status: 130,
          submission: 180,
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => ({
          id: item.organization_ems_assessment_result_id,

          assesment: item?.assesment?.assessment_name || "-",
          student:
            `${item?.student?.first_name} (${item?.student?.student_id})` ||
            "-",
          admission: item?.admission?.admission_number || "-",

          score_obtained: (() => {
            const markCount = (value) => {
              if (value === null || value === undefined || value === "") return null;
              const num = Number(value);
              if (!Number.isFinite(num)) return null;
              return String(num);
            };
            const obtained = markCount(item?.score_obtained ?? item?.marks_obtained);
            if (obtained == null) return "—";
            const total = markCount(
              item?.assesment?.max_marks ??
                item?.assesment?.max_score ??
                item?.assessment?.max_marks ??
                item?.assessment?.max_score
            );
            return total == null ? obtained : `${obtained}/${total}`;
          })(),
          result_status: item.result_status || "—",
          submission: item.submission_url
            ? item.submission_name || "View file"
            : "—",
          submission_url: item.submission_url || "",
          remarks: item.remarks || "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          organization_ems_assessment_id,
          organization_ems_student_id,
          organization_ems_admission_id,
          assessment_max_score,
          score_obtained,
          result_status,
          remarks,
        } = formData;

        // ===== REQUIRED FIELDS =====
        if (!organization_id)
          errors.organization_id = "Organization is required.";
        if (!organization_ems_assessment_id)
          errors.organization_ems_assessment_id = "Assessment is required.";
        if (!organization_ems_student_id)
          errors.organization_ems_student_id = "Student is required.";
        if (!organization_ems_admission_id)
          errors.organization_ems_admission_id = "Admission is required.";

        // ===== NUMERIC VALIDATION: score_obtained (optional until graded) =====
        if (
          score_obtained !== "" &&
          score_obtained !== null &&
          score_obtained !== undefined
        ) {
          const num = Number(score_obtained);
          if (isNaN(num)) {
            errors.score_obtained = "Score must be a valid number.";
          } else if (num < 0 || num > 9999.99) {
            errors.score_obtained = "Score must be between 0 and 9999.99.";
          } else if (
            assessment_max_score !== "" &&
            assessment_max_score !== null &&
            assessment_max_score !== undefined &&
            !isNaN(Number(assessment_max_score)) &&
            num > Number(assessment_max_score)
          ) {
            errors.score_obtained = `Score cannot be greater than max score (${assessment_max_score}).`;
          }
        }

        const submission_url = formData?.submission_url;
        if (submission_url instanceof File && submission_url.size > 10 * 1024 * 1024) {
          errors.submission_url = "Student file must not exceed 10 MB.";
        }

        // ===== ENUM VALIDATION: result_status =====
        const validStatuses = ["Pass", "Fail", "Absent", "Pending"];
        if (result_status && !validStatuses.includes(result_status)) {
          errors.result_status =
            "Result status must be Pass, Fail, Absent, or Pending.";
        }

        // ===== LENGTH VALIDATION: remarks =====
        if (remarks && remarks.length > 1000) {
          errors.remarks = "Remarks cannot exceed 1000 characters.";
        }

        // ===== SET ERRORS =====
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/assesment-results",
      initialData: {
        organization_entity_id: "",
        organization_ems_assessment_id: "",
        organization_ems_student_id: "",
        organization_ems_admission_id: "",
        assessment_max_score: "",
        score_obtained: "",
        result_status: "",
        remarks: "",
        submission_url: "",
        submission_name: "",
      },
      successMessages: {
        add: "Assessment Results added!",
        update: "Assessment Results updated!",
      },
      redirectPath: "/assesment-results",
      headerProps: {
        addMessage: "Add Assessment Result",
        updateMessage: "Update Assessment Results",
        homeLink: "/assesment-results",
        homeText: "Assessment Results",
      },
      fieldGridLayout: {
        rows: [
          {
            variant: "twoEqual",
            mainKeys: [
              "organization_ems_assessment_id",
              "organization_ems_student_id",
            ],
          },
          {
            variant: "twoEqual",
            mainKeys: ["organization_ems_admission_id", "score_obtained"],
          },
          { variant: "full", mainKeys: ["result_status"] },
          { variant: "full", mainKeys: ["submission_url"] },
          { variant: "full", mainKeys: ["remarks"] },
        ],
        sections: [],
      },
      Inputs: [
        {
          type: "Select",
          label: "Assessment *",
          mainKey: "organization_ems_assessment_id",
          OptionMainKey: "Assesments",
          menuKey: "organization_ems_assessment_id",
          formatValue: (val) => `${val.assessment_name}`,
        },
        {
          type: "Select",
          label: "Student *",
          mainKey: "organization_ems_student_id",
          OptionMainKey: "Students",
          menuKey: "organization_ems_student_id",
          formatValue: (val) => `${val?.first_name} (${val?.student_id})`,
        },
        {
          type: "Select",
          label: "Admission *",
          mainKey: "organization_ems_admission_id",
          OptionMainKey: "Admissions",
          menuKey: "organization_ems_admission_id",
          formatValue: (val) => `${val?.admission_number}`,
        },
        {
          type: "Number",
          label: "Score Obtained",
          mainKey: "score_obtained",
          helperText:
            "Enter the marks obtained. Max score comes from the assessment.",
        },
        {
          type: "Select",
          label: "Result Status",
          mainKey: "result_status",
          OptionMainKey: "ResultStatuses",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "File",
          label: "Student File",
          mainKey: "submission_url",
        },
        {
          type: "MultiRow",
          label: "Remarks ",
          mainKey: "remarks",
        },
      ],
      DataFetchers: [
        {
          name: "Assesments",
          Type: "Fetch",
          link: "/api/ems/assesments",
        },
        { name: "BatchStudents", Type: "Fetch", link: "/api/ems/batch-students" },
        { name: "Students", Type: "Fetch", link: "/api/ems/students" },
        { name: "Admissions", Type: "Fetch", link: "/api/ems/admissions" },
        {
          name: "ResultStatuses",
          Type: "NoFetch",
          Options: ["Pass", "Fail", "Absent", "Pending"],
        },
      ],
    },
  },

  {
    route: "/batches",
    ListProps: {
      title: "Batches",
      apiEndpoint: "/api/ems/batches",
      filteredApiEndpoint: "/api/ems/batches/filtered",
      filterModule: "ems_batches",
      useMetadataFilters: true,
      addRoute: "/batches",
      editRouteBase: "/batches/edit",
      tablePrimaryKey: "organization_ems_batch_id",
      tableName: "ems_batches",
      datagridKey: "ems_batches_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "batch_code",
        "batch_name",
        "training_program",
        "training_program_id",
        "duration",
        "mode",
        "batch_mode",
        "batch_days",
        "start_date",
        "end_date",
        "students",
        "status",
        "preferred_study_slot",
        "timing_details",
      ],
      config: {
        sort: { field: "start_date", direction: "desc" },
        defaultVisibleColumns: [
          "batch_code",
          "training_program",
          "duration",
          "mode",
          "batch_days",
          "start_date",
          "end_date",
          "students",
          "status",
        ],
        columnLabels: {
          batch_code: "Batch Code",
          training_program: "Training Program",
          duration: "Duration",
          mode: "Mode",
          batch_days: "Batch Days",
          start_date: "Start Date",
          end_date: "End Date",
          students: "Students",
          status: "Status",
        },
        columnWidths: {
          training_program: 420,
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => ({
          trainer_employee_ids: [
            ...(Array.isArray(item?.batch_trainers) ? item.batch_trainers : []),
            ...(Array.isArray(item?.batch_classes) ? item.batch_classes : []),
          ]
            .map((x) =>
              x?.trainer_employee_id ??
              x?.trainer?.organization_user_id ??
              null
            )
            .filter((v) => v != null && String(v).trim() !== "")
            .map((v) => String(v).trim()),
          trainer_application_user_ids: [
            ...new Set(
              [
                ...(Array.isArray(item?.batch_trainers) ? item.batch_trainers : []),
                ...(Array.isArray(item?.batch_classes) ? item.batch_classes : []),
              ]
                .map((x) => getApplicationUserIdFromTrainerObject(x?.trainer))
                .filter((v) => v != null && String(v).trim() !== "")
                .map((v) => String(v).trim())
            ),
          ],
          id: item.organization_ems_batch_id,
          organization_ems_batch_id: item.organization_ems_batch_id,
          duration:
            item?.duration_in_months != null
              ? `${item.duration_in_months} ${Number(item.duration_in_months) === 1 ? "Month" : "Months"}`
              : item?.training_program?.duration_in_months != null
                ? `${item.training_program.duration_in_months} ${Number(item.training_program.duration_in_months) === 1 ? "Month" : "Months"}`
                : item?.duration_in_hours != null
                  ? `${item.duration_in_hours} ${Number(item.duration_in_hours) === 1 ? "Hour" : "Hours"}`
                  : item?.training_program?.duration_in_hours != null
                    ? `${item.training_program.duration_in_hours} ${Number(item.training_program.duration_in_hours) === 1 ? "Hour" : "Hours"}`
                    : "-",
          mode: item.batch_mode || "-",
          batch_days: Array.isArray(item.batch_days)
            ? item.batch_days
                .map((d) => {
                  const day = String(d || "").trim().toLowerCase();
                  if (day === "monday") return "M";
                  if (day === "tuesday") return "T";
                  if (day === "wednesday") return "W";
                  if (day === "thursday") return "TH";
                  if (day === "friday") return "F";
                  if (day === "saturday") return "S";
                  if (day === "sunday") return "SU";
                  return d;
                })
                .join(", ")
            : item.batch_days || "-",
          students:
            (Array.isArray(item?.batch_students) ? item.batch_students.length : null) ??
            item?.students_count ??
            item?.student_count ??
            item?.batch_students_count ??
            0,
          __batch_students_tooltip: Array.isArray(item?.batch_students)
            ? item.batch_students
                .map((bs) => {
                  const studentName = [
                    bs?.student?.first_name,
                    bs?.student?.middle_name,
                    bs?.student?.last_name,
                  ]
                    .filter((x) => x && String(x).trim() !== "")
                    .join(" ")
                    .trim();
                  const admissionNumber =
                    bs?.admission?.admission_number ??
                    bs?.admission_number ??
                    bs?.organization_ems_admission_id ??
                    "-";
                  const left = studentName || bs?.student?.student_id || "Student";
                  return `${left} (Admission: ${admissionNumber})`;
                })
                .join("\n")
            : "",
          training_program:
            item?.training_program?.training_program_name || "-",
          training_program_id:
            item?.training_program_id ??
            item?.training_program?.organization_ems_training_program_id ??
            null,
          training_program_obj: item?.training_program ?? null,
          batch_students: Array.isArray(item?.batch_students)
            ? item.batch_students
            : [],
          batch_classes: Array.isArray(item?.batch_classes)
            ? item.batch_classes
            : [],
          batch_code: item.batch_code || "-",
          batch_name: item.batch_name || "-",
          start_date: FormatDateV1(item.start_date) || "-",
          end_date: FormatDateV1(item.end_date) || "-",
          batch_mode: item.batch_mode || "-",
          status: item.status || "-",
          preferred_study_slot: item.preferred_study_slot || "-",
          timing_details: item.timing_details || "-",
          remarks: item.remarks || "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          training_program_id,
          batch_code,
          batch_name,
          start_date,
          end_date,
          batch_mode,
          status,
        } = formData;

        // Organization ID
        if (!organization_id)
          errors.organization_id = "Organization is required.";

        // // Entity ID (optional, integer check)
        // if (organization_entity_id && isNaN(Number(organization_entity_id))) {
        //   errors.organization_entity_id = "Entity ID must be a valid number.";
        // }

        // Training Program ID
        if (!training_program_id)
          errors.training_program_id = "Training program is required.";

        // Batch Code
        if (!batch_code?.trim()) {
          errors.batch_code = "Batch code is required.";
        } else if (batch_code.length > 50) {
          errors.batch_code = "Batch code cannot exceed 50 characters.";
        }

        // Batch Name
        if (!batch_name?.trim()) {
          errors.batch_name = "Batch name is required.";
        } else if (batch_name.length > 100) {
          errors.batch_name = "Batch name cannot exceed 100 characters.";
        }

        // Start Date
        if (!start_date) {
          errors.start_date = "Start date is required.";
        }

        // End Date
        if (end_date) {
          const start = new Date(start_date);
          const end = new Date(end_date);
          if (end < start)
            errors.end_date = "End date must be same or after start date.";
        }

        // Batch Mode
        const validModes = ["Online", "Offline", "Hybrid"];
        if (!batch_mode) {
          errors.batch_mode = "Batch mode is required.";
        } else if (!validModes.includes(batch_mode)) {
          errors.batch_mode = "Invalid batch mode.";
        }

        // Status
        const validStatuses = [
          "Scheduled",
          "Ongoing",
          "On Hold",
          "Completed",
          "Cancelled",
        ];
        if (!status) {
          errors.status = "Status is required.";
        } else if (!validStatuses.includes(status)) {
          errors.status = "Invalid status.";
        } else if (start_date) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const start = new Date(start_date);
          start.setHours(0, 0, 0, 0);
          if (
            !Number.isNaN(start.getTime()) &&
            start > today &&
            status !== "Scheduled"
          ) {
            errors.status =
              "When start date is in the future, status can only be Scheduled.";
          }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/batches",
      initialData: {
        organization_entity_id: "",
        training_program_id: "",
        batch_code: "",
        batch_name: "",
        start_date: "",
        end_date: "",
        batch_mode: "",
        status: "",
        remarks: "",
      },
      successMessages: {
        add: "Batch added!",
        update: "Batch updated!",
      },
      redirectPath: "/batches",
      headerProps: {
        addMessage: "Add Batch",
        updateMessage: "Update Batch",
        homeLink: "/batches",
        homeText: "Batch",
      },
      fieldGridLayout: {
        rows: [
          { variant: "full", mainKeys: ["training_program_id"] },
          { variant: "twoEqual", mainKeys: ["batch_code", "batch_name"] },
          { variant: "twoEqual", mainKeys: ["start_date", "end_date"] },
          { variant: "twoEqual", mainKeys: ["batch_mode", "status"] },
        ],
      },
      Inputs: [
        {
          type: "Select",
          label: "Training Program *",
          mainKey: "training_program_id",
          OptionMainKey: "TrainingPrograms",
          menuKey: "organization_ems_training_program_id",
          formatValue: (val) => `${val.training_program_name}`,
        },

        {
          type: "Text",
          label: "Batch Code *",
          mainKey: "batch_code",
        },
        {
          type: "Text",
          label: "Batch Name *",
          mainKey: "batch_name",
        },
        {
          type: "Date",
          label: "Start Date *",
          mainKey: "start_date",
        },
        {
          type: "Date",
          label: "End Date ",
          mainKey: "end_date",
        },
        {
          type: "Select",
          label: "Batch Mode *",
          mainKey: "batch_mode",
          OptionMainKey: "BatchModes",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "Select",
          label: "Status *",
          mainKey: "status",
          OptionMainKey: "Statuses",
          menuKey: null,
          formatValue: (val) => val,
        },
      ],
      DataFetchers: [
        {
          name: "TrainingPrograms",
          Type: "Fetch",
          link: "/api/ems/training-programs",
        },

        {
          name: "BatchModes",
          Type: "NoFetch",
          Options: ["Online", "Offline", "Hybrid"],
        },
        {
          name: "Statuses",
          Type: "NoFetch",
          Options: ["Scheduled", "Ongoing", "On Hold", "Completed", "Cancelled"],
        },
        {
          name: "BatchDays",
          Type: "NoFetch",
          Options: [
            "Sunday",
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
          ],
        },
      ],
    },
  },
  {
    route: "/batch-modules",
    ListProps: {
      title: "Batch Modules",
      apiEndpoint: "/api/ems/batch-modules",
      filteredApiEndpoint: "/api/ems/batch-modules/filtered",
      filterModule: "ems_batch_modules",
      useMetadataFilters: true,
      addRoute: "/batch-modules",
      editRouteBase: "/batch-modules/edit",
      tablePrimaryKey: "organization_ems_batch_module_id",
      tableName: "Batch Modules",
      showAddButton: false,
      config: {
        defaultVisibleColumns: ["batch", "module", "sequence_no", "status", "started_at", "completed_at"],
        columnLabels: {
          batch: "Batch",
          module: "Module",
          sequence_no: "Sequence",
          status: "Status",
          started_at: "Started",
          completed_at: "Completed",
        },
      },
      mapResponseToRows: (data) =>
        (Array.isArray(data) ? data : []).map((item) => ({
          id: item.organization_ems_batch_module_id,
          organization_ems_batch_module_id: item.organization_ems_batch_module_id,
          batch:
            (typeof item?.batch === "string" && item.batch) ||
            item?.batch?.batch_name ||
            item?.batch?.batch_code ||
            "-",
          module:
            (typeof item?.module === "string" && item.module) ||
            item?.program_module?.module_name ||
            item?.programModule?.module_name ||
            "-",
          sequence_no: item.sequence_no ?? "-",
          status: item.status || "-",
          started_at: item.started_at || "-",
          completed_at: item.completed_at || "-",
          remarks: item.remarks || "",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const allowed = ["Not Started", "Ongoing", "Completed", "On Hold", "Skipped"];
        if (!formData.status) errors.status = "Status is required.";
        else if (!allowed.includes(formData.status)) errors.status = "Invalid status.";
        if (formData.remarks && String(formData.remarks).length > 500) {
          errors.remarks = "Remarks cannot exceed 500 characters.";
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },
      baseUrl: "/api/ems/batch-modules",
      initialData: { status: "Not Started", remarks: "" },
      successMessages: { add: "Batch module saved!", update: "Batch module updated!" },
      redirectPath: "/batch-modules",
      headerProps: {
        addMessage: "Batch Module",
        updateMessage: "Update Batch Module",
        homeLink: "/batch-modules",
        homeText: "Batch Modules",
      },
      Inputs: [
        {
          type: "Select",
          label: "Status *",
          mainKey: "status",
          OptionMainKey: "BatchModuleStatuses",
          menuKey: null,
          formatValue: (val) => val,
        },
        { type: "MultiRow", label: "Remarks", mainKey: "remarks" },
      ],
      DataFetchers: [
        {
          name: "BatchModuleStatuses",
          Type: "NoFetch",
          Options: ["Not Started", "Ongoing", "Completed", "On Hold", "Skipped"],
        },
      ],
    },
  },
  {
    route: "/batch-trainer-modules",
    ListProps: {
      title: "Trainer Modules",
      apiEndpoint: "/api/ems/batch-trainer-modules",
      addRoute: "/batch-trainer-modules",
      editRouteBase: "/batch-trainer-modules/edit",
      tablePrimaryKey: "organization_ems_batch_trainer_module_id",
      tableName: "Trainer Modules",
      bypassPermissions: true,
      showViewButton: false,
      config: {
        defaultVisibleColumns: ["batch", "trainer", "module", "status"],
        columnLabels: {
          batch: "Batch",
          trainer: "Trainer",
          module: "Module",
          status: "Status",
        },
      },
      mapResponseToRows: (data) =>
        (Array.isArray(data) ? data : []).map((item) => {
          const trainer = item?.batch_trainer?.trainer || item?.batchTrainer?.trainer;
          return {
            id: item.organization_ems_batch_trainer_module_id,
            batch:
              item?.batch_trainer?.batch?.batch_name ||
              item?.batchTrainer?.batch?.batch_name ||
              "-",
            trainer:
              String(trainer?.applicationuser?.[0]?.full_name || "").trim() ||
              String(trainer?.person?.full_name || "").trim() ||
              [trainer?.person?.first_name, trainer?.person?.last_name].filter(Boolean).join(" ") ||
              "-",
            module:
              item?.batch_module?.program_module?.module_name ||
              item?.batchModule?.program_module?.module_name ||
              item?.batchModule?.programModule?.module_name ||
              "-",
            status: item.status || "-",
          };
        }),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        if (!formData.organization_ems_batch_trainer_id) errors.organization_ems_batch_trainer_id = "Batch trainer is required.";
        if (!formData.organization_ems_batch_module_id) errors.organization_ems_batch_module_id = "Module is required.";
        if (!["Active", "Inactive"].includes(formData.status)) errors.status = "Status is required.";
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },
      baseUrl: "/api/ems/batch-trainer-modules",
      initialData: { organization_ems_batch_trainer_id: "", organization_ems_batch_module_id: "", status: "Active", remarks: "" },
      successMessages: { add: "Trainer module added!", update: "Trainer module updated!" },
      redirectPath: "/batch-trainer-modules",
      headerProps: { addMessage: "Add Trainer Module", updateMessage: "Update Trainer Module", homeLink: "/batch-trainer-modules", homeText: "Trainer Modules" },
      Inputs: [
        { type: "Select", label: "Batch Trainer *", mainKey: "organization_ems_batch_trainer_id", OptionMainKey: "BatchTrainers", menuKey: "organization_ems_batch_trainer_id", formatValue: (val) => `${val?.trainer?.applicationuser?.[0]?.full_name || val?.trainer?.person?.full_name || "Trainer"} (${val?.batch?.batch_code || val?.batch?.batch_name || "-"})` },
        { type: "Select", label: "Module *", mainKey: "organization_ems_batch_module_id", OptionMainKey: "BatchModules", menuKey: "organization_ems_batch_module_id", formatValue: (val) => val?.program_module?.module_name || val?.programModule?.module_name || `Module ${val?.sequence_no || ""}`.trim() },
        { type: "Select", label: "Status *", mainKey: "status", OptionMainKey: "ActiveStatuses", menuKey: null, formatValue: (val) => val },
        { type: "MultiRow", label: "Remarks", mainKey: "remarks" },
      ],
      DataFetchers: [
        { name: "BatchTrainers", Type: "Fetch", link: "/api/ems/batch-trainers" },
        { name: "BatchModules", Type: "Fetch", link: "/api/ems/batch-modules" },
        { name: "ActiveStatuses", Type: "NoFetch", Options: ["Active", "Inactive"] },
      ],
    },
  },
  {
    route: "/batch-class-trainers",
    ListProps: {
      title: "Batch Class Trainers",
      apiEndpoint: "/api/ems/batch-class-trainers",
      addRoute: "/batch-class-trainers",
      editRouteBase: "/batch-class-trainers/edit",
      tablePrimaryKey: "organization_ems_batch_class_trainer_id",
      tableName: "Batch Class Trainers",
      bypassPermissions: true,
      showViewButton: false,
      config: {
        defaultVisibleColumns: ["batch", "class_name", "time", "trainer", "trainer_role", "status"],
        columnLabels: {
          batch: "Batch",
          class_name: "Class",
          time: "Time",
          trainer: "Trainer",
          trainer_role: "Role",
          status: "Status",
        },
      },
      mapResponseToRows: (data) =>
        (Array.isArray(data) ? data : []).map((item) => {
          const trainer = item?.trainer;
          const batchClass = item?.batch_class || item?.batchClass;
          const days = formatClassDayLetters(batchClass?.class_days);
          return {
            id: item.organization_ems_batch_class_trainer_id,
            batch: batchClass?.batch?.batch_name || batchClass?.batch?.batch_code || "-",
            class_name: days || batchClass?.class_name || "-",
            time: [batchClass?.start_time, batchClass?.end_time]
              .map((value) => formatTime12h(value))
              .filter((value) => value && value !== "-")
              .join(" – ") || "-",
            trainer:
              String(trainer?.applicationuser?.[0]?.full_name || "").trim() ||
              String(trainer?.person?.full_name || "").trim() ||
              [trainer?.person?.first_name, trainer?.person?.last_name].filter(Boolean).join(" ") ||
              "-",
            trainer_employee_id: item.trainer_employee_id || trainer?.organization_user_id || "",
            trainer_application_user_id:
              trainer?.application_user_id ||
              trainer?.applicationuser?.[0]?.application_user_id ||
              trainer?.Applicationuser?.[0]?.application_user_id ||
              "",
            profile_image_url: trainerProfileImageUrl(trainer),
            trainer_role: item.trainer_role || "-",
            status: item.status || "-",
          };
        }),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        if (!formData.organization_ems_batch_class_id) errors.organization_ems_batch_class_id = "Class is required.";
        if (!formData.trainer_employee_id) errors.trainer_employee_id = "Trainer is required.";
        if (!["Primary", "Co-Trainer", "Backup"].includes(formData.trainer_role)) errors.trainer_role = "Role is required.";
        if (!["Active", "Inactive"].includes(formData.status)) errors.status = "Status is required.";
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },
      baseUrl: "/api/ems/batch-class-trainers",
      initialData: { organization_ems_batch_class_id: "", trainer_employee_id: "", trainer_role: "Primary", status: "Active", remarks: "" },
      successMessages: { add: "Class trainer added!", update: "Class trainer updated!" },
      redirectPath: "/batch-class-trainers",
      headerProps: { addMessage: "Add Batch Class Trainer", updateMessage: "Update Batch Class Trainer", homeLink: "/batch-class-trainers", homeText: "Batch Class Trainers" },
      Inputs: [
        { type: "Select", label: "Class *", mainKey: "organization_ems_batch_class_id", OptionMainKey: "BatchClasses", menuKey: "organization_ems_batch_class_id", formatValue: (val) => `${val?.class_name || "Class"} (${val?.batch?.batch_code || "-"})` },
        { type: "Select", label: "Trainer *", mainKey: "trainer_employee_id", OptionMainKey: "Trainers", menuKey: "organization_user_id", formatValue: (val) => formatTrainerUserOption(val) },
        { type: "Select", label: "Role *", mainKey: "trainer_role", OptionMainKey: "ClassTrainerRoles", menuKey: null, formatValue: (val) => val },
        { type: "Select", label: "Status *", mainKey: "status", OptionMainKey: "ActiveStatuses", menuKey: null, formatValue: (val) => val },
        { type: "MultiRow", label: "Remarks", mainKey: "remarks" },
      ],
      DataFetchers: [
        { name: "BatchClasses", Type: "Fetch", link: "/api/ems/batch-classes" },
        { name: "Trainers", Type: "Fetch", link: "/api/organizations/${org_id}/users-data-all" },
        { name: "ClassTrainerRoles", Type: "NoFetch", Options: ["Primary", "Co-Trainer", "Backup"] },
        { name: "ActiveStatuses", Type: "NoFetch", Options: ["Active", "Inactive"] },
      ],
    },
  },
  {
    route: "/batch-class-record-trainers",
    ListProps: {
      title: "Batch Class Record Trainers",
      apiEndpoint: "/api/ems/batch-class-record-trainers",
      addRoute: "/batch-class-record-trainers",
      editRouteBase: "/batch-class-record-trainers/edit",
      tablePrimaryKey: "organization_ems_batch_class_record_trainer_id",
      tableName: "Batch Class Record Trainers",
      bypassPermissions: true,
      showViewButton: false,
      config: {
        defaultVisibleColumns: ["batch", "class_name", "class_date", "time", "trainer", "trainer_role", "assignment_status"],
        columnLabels: {
          batch: "Batch",
          class_name: "Class",
          class_date: "Date",
          time: "Time",
          trainer: "Trainer",
          trainer_role: "Role",
          assignment_status: "Assignment",
        },
      },
      mapResponseToRows: (data) =>
        (Array.isArray(data) ? data : []).map((item) => {
          const record = item?.class_record || item?.classRecord || {};
          const batchClass = record?.batch_class || record?.batchClass || {};
          const dayLetters =
            formatClassDayLetters(batchClass?.class_days) ||
            formatClassDayLetters(batchClass?.class_name);
          const time = [batchClass?.start_time, batchClass?.end_time]
            .map((value) => formatTime12h(value))
            .filter((value) => value && value !== "-")
            .join(" – ");
          return {
            id: item.organization_ems_batch_class_record_trainer_id,
            batch: record?.batch?.batch_code || record?.batch?.batch_name || "-",
            class_name: dayLetters || batchClass?.class_name || record?.topic || "-",
            class_date: formatDateOnly(record?.start_time),
            time: time || "-",
            trainer: sessionTrainerName(item?.trainer) || "-",
            trainer_employee_id:
              item.trainer_employee_id || item?.trainer?.organization_user_id || "",
            trainer_application_user_id:
              item?.trainer?.application_user_id ||
              item?.trainer?.applicationuser?.[0]?.application_user_id ||
              item?.trainer?.Applicationuser?.[0]?.application_user_id ||
              "",
            profile_image_url: trainerProfileImageUrl(item?.trainer),
            trainer_role: item.trainer_role || "-",
            assignment_status: item.assignment_status || "-",
          };
        }),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        if (!formData.organization_ems_batch_class_record_id) {
          errors.organization_ems_batch_class_record_id = "Class record is required.";
        }
        if (!formData.trainer_employee_id) errors.trainer_employee_id = "Trainer is required.";
        if (!["Primary", "Co-Trainer", "Substitute"].includes(formData.trainer_role)) {
          errors.trainer_role = "Role is required.";
        }
        if (!["Assigned", "Completed", "Replaced", "Cancelled"].includes(formData.assignment_status)) {
          errors.assignment_status = "Assignment status is required.";
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },
      baseUrl: "/api/ems/batch-class-record-trainers",
      initialData: {
        organization_ems_batch_class_record_id: "",
        trainer_employee_id: "",
        trainer_role: "Primary",
        assignment_status: "Assigned",
        remarks: "",
      },
      successMessages: { add: "Batch class record trainer added!", update: "Batch class record trainer updated!" },
      redirectPath: "/batch-class-record-trainers",
      headerProps: {
        addMessage: "Add Batch Class Record Trainer",
        updateMessage: "Update Batch Class Record Trainer",
        homeLink: "/batch-class-record-trainers",
        homeText: "Batch Class Record Trainers",
      },
      Inputs: [
        {
          type: "Select",
          label: "Class Record *",
          mainKey: "organization_ems_batch_class_record_id",
          OptionMainKey: "ClassRecords",
          menuKey: "organization_ems_batch_class_record_id",
          formatValue: (val) => {
            const batch = val?.batch?.batch_code || val?.batch?.batch_name || "Batch";
            const date = formatDateOnly(val?.start_time);
            const topic = val?.topic ? String(val.topic).trim() : "";
            return [batch, date !== "-" ? date : "", topic].filter(Boolean).join(" · ");
          },
        },
        { type: "Select", label: "Trainer *", mainKey: "trainer_employee_id", OptionMainKey: "Trainers", menuKey: "organization_user_id", formatValue: (val) => formatTrainerUserOption(val) },
        { type: "Select", label: "Role *", mainKey: "trainer_role", OptionMainKey: "RecordTrainerRoles", menuKey: null, formatValue: (val) => val },
        { type: "Select", label: "Assignment *", mainKey: "assignment_status", OptionMainKey: "AssignmentStatuses", menuKey: null, formatValue: (val) => val },
        { type: "MultiRow", label: "Remarks", mainKey: "remarks" },
      ],
      DataFetchers: [
        { name: "ClassRecords", Type: "Fetch", link: "/api/ems/class-records" },
        { name: "Trainers", Type: "Fetch", link: "/api/organizations/${org_id}/users-data-all" },
        { name: "RecordTrainerRoles", Type: "NoFetch", Options: ["Primary", "Co-Trainer", "Substitute"] },
        { name: "AssignmentStatuses", Type: "NoFetch", Options: ["Assigned", "Completed", "Replaced", "Cancelled"] },
      ],
    },
  },
  {
    route: "/batch-trainers",
    ListProps: {
      title: "Batch Trainers",
      apiEndpoint: "/api/ems/batch-trainers",
      addRoute: "/batch-trainers",
      editRouteBase: "/batch-trainers/edit",
      tablePrimaryKey: "organization_ems_batch_trainer_id",
      tableName: "Batch Trainers",
      showAddButton: true,
      config: {
        defaultVisibleColumns: ["training_program", "batch_name", "trainer", "trainer_role", "module_scope", "status"],
        columnLabels: {
          training_program: "Training Program",
          batch_name: "Batch Name",
          trainer: "Trainer Name",
          trainer_role: "Role",
          module_scope: "Module Scope",
          status: "Status",
        },
        columnWidths: {
          training_program: 260,
          batch_name: 220,
          trainer: 260,
          module: 260,
        },
      },
      mapResponseToRows: (data) =>
        (Array.isArray(data) ? data : []).map((item) => {
          const pk =
            item?.organization_ems_batch_trainer_id ??
            item?.organization_ems_batch_trainers_id ??
            item?.organization_ems_batch_trainerID;
          return {
            id: pk,
            organization_ems_batch_trainer_id: pk,
            training_program:
              item?.batch?.training_program?.training_program_name || "-",
            batch_name: item?.batch?.batch_name || "-",
            trainer:
              String(item?.trainer?.applicationuser?.[0]?.full_name || "").trim() ||
              String(item?.trainer?.person?.full_name || "").trim() ||
              `${item?.trainer?.person?.first_name || ""} ${item?.trainer?.person?.last_name || ""}`.trim() ||
              item?.trainer_name ||
              (item?.trainer_employee_id != null
                ? String(item.trainer_employee_id)
                : "") ||
              "-",
            profile_image_url:
              item?.trainer?.person?.profile_image_url ||
              item?.trainer?.Person?.profile_image_url ||
              item?.trainer?.organization_person?.person?.profile_image_url ||
              item?.trainer?.organization_person?.Person?.profile_image_url ||
              item?.trainer?.organizationPerson?.person?.profile_image_url ||
              item?.trainer?.organizationPerson?.Person?.profile_image_url ||
              item?.trainer?.profile_image_url ||
              "",
            trainer_role: item?.trainer_role || "-",
            module_scope: item?.module_scope || "-",
            status: item?.status || "-",
            module: item?.module || item?.module_scope || "-",
            batch_module_ids: Array.isArray(item?.batch_module_ids)
              ? item.batch_module_ids
              : [],
          };
        }),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          organization_ems_batch_id,
          trainer_employee_id,
          trainer_role,
          module_scope,
          status,
          batch_module_ids,
        } = formData;

        if (!organization_id) {
          errors.organization_id = "Organization is required.";
        } else if (isNaN(Number(organization_id))) {
          errors.organization_id = "Organization ID must be a valid number.";
        }

        if (!organization_ems_batch_id) {
          errors.organization_ems_batch_id = "Batch is required.";
        } else if (isNaN(Number(organization_ems_batch_id))) {
          errors.organization_ems_batch_id = "Batch ID must be a valid number.";
        }

        if (!trainer_employee_id) {
          errors.trainer_employee_id = "Trainer is required.";
        } else if (isNaN(Number(trainer_employee_id))) {
          errors.trainer_employee_id = "Trainer ID must be a valid number.";
        }

        const roles = ["Primary", "Co-Trainer", "Backup"];
        if (!trainer_role) {
          errors.trainer_role = "Trainer role is required.";
        } else if (!roles.includes(trainer_role)) {
          errors.trainer_role = "Invalid trainer role.";
        }

        const scopes = ["All", "Selected"];
        if (!module_scope) {
          errors.module_scope = "Module scope is required.";
        } else if (!scopes.includes(module_scope)) {
          errors.module_scope = "Invalid module scope.";
        } else if (
          module_scope === "Selected" &&
          (!Array.isArray(batch_module_ids) || batch_module_ids.length === 0)
        ) {
          errors.batch_module_ids = "Select at least one module.";
        }

        if (!["Active", "Inactive"].includes(status)) {
          errors.status = "Status is required.";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/batch-trainers",
      initialData: {
        organization_entity_id: "",
        organization_ems_batch_id: "",
        trainer_employee_id: "",
        trainer_role: "Primary",
        module_scope: "All",
        status: "Active",
        batch_module_ids: [],
        remarks: "",
      },
      successMessages: {
        add: "Batch Trainer added!",
        update: "Batch Trainer updated!",
      },
      redirectPath: "/batch-trainers",
      headerProps: {
        addMessage: "Add Batch Trainer",
        updateMessage: "Update Batch Trainer",
        homeLink: "/batch-trainers",
        homeText: "Batch Trainer",
      },
      Inputs: [
        {
          type: "Select",
          label: "Batch *",
          mainKey: "organization_ems_batch_id",
          OptionMainKey: "Batches",
          menuKey: "organization_ems_batch_id",
          formatValue: (val) => {
            if (!val) return "-";
            const code = val.batch_code ?? val.batch?.batch_code ?? "";
            return code || "-";
          },
        },
        {
          type: "Select",
          label: "Trainer *",
          mainKey: "trainer_employee_id",
          OptionMainKey: "Trainers",
          menuKey: "organization_user_id",
          formatValue: (val) => formatTrainerUserOption(val),
        },
        {
          type: "Select",
          label: "Role *",
          mainKey: "trainer_role",
          OptionMainKey: "TrainerRoles",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "Select",
          label: "Module Scope *",
          mainKey: "module_scope",
          OptionMainKey: "ModuleScopes",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "MultiSelect",
          label: "Modules *",
          mainKey: "batch_module_ids",
          OptionMainKey: "BatchModules",
          menuKey: "organization_ems_batch_module_id",
          formatValue: (val) =>
            val?.program_module?.module_name ||
            val?.programModule?.module_name ||
            `Module ${val?.sequence_no || ""}`.trim(),
          visibleWhen: (formData) => formData?.module_scope === "Selected",
          disabledWhen: (formData) => !formData?.organization_ems_batch_id,
          filterOptionsForForm: (opt, formData) =>
            String(opt?.organization_ems_batch_id || "") ===
            String(formData?.organization_ems_batch_id || ""),
        },
        {
          type: "Select",
          label: "Status *",
          mainKey: "status",
          OptionMainKey: "TrainerStatuses",
          menuKey: null,
          formatValue: (val) => val,
        },
        { type: "MultiRow", label: "Remarks", mainKey: "remarks" },
      ],
      fieldGridLayout: {
        rows: [
          { variant: "full", mainKeys: ["organization_ems_batch_id"] },
          { variant: "full", mainKeys: ["trainer_employee_id"] },
          { variant: "twoEqual", mainKeys: ["trainer_role", "module_scope"] },
          { variant: "full", mainKeys: ["batch_module_ids"] },
          { variant: "twoEqual", mainKeys: ["status", "remarks"] },
        ],
      },
      DataFetchers: [
        {
          name: "Batches",
          Type: "Fetch",
          link: "/api/ems/batches",
        },
        {
          name: "Trainers",
          Type: "Fetch",
          link: "/api/organizations/${org_id}/users-data-all",
        },
        {
          name: "TrainerRoles",
          Type: "NoFetch",
          Options: ["Primary", "Co-Trainer", "Backup"],
        },
        {
          name: "ModuleScopes",
          Type: "NoFetch",
          Options: ["All", "Selected"],
        },
        {
          name: "TrainerStatuses",
          Type: "NoFetch",
          Options: ["Active", "Inactive"],
        },
        {
          name: "BatchModules",
          Type: "Fetch",
          link: "/api/ems/batch-modules",
        },
      ],
    },
  },
  {
    route: "/batch-classes",
    ListProps: {
      title: "Batch Classes",
      apiEndpoint: "/api/ems/batch-classes",
      filteredApiEndpoint: "/api/ems/batch-classes/filtered",
      filterModule: "ems_batch_classes",
      useMetadataFilters: true,
      addRoute: "/batch-classes",
      editRouteBase: "/batch-classes/edit",
      tablePrimaryKey: "organization_ems_batch_class_id",
      tableName: "ems_batch_classes",
      datagridKey: "ems_batch_classes_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "batch",
        "training_program",
        "students",
        "batch_start_time",
        "batch_end_time",
        "batch_days",
        "mode",
        "location",
        "last_record",
        "topic",
        "class_status",
      ],
      config: {
        sort: { field: "start_time", direction: "asc" },
        defaultVisibleColumns: [
          "batch",
          "training_program",
          "students",
          "batch_start_time",
          "batch_end_time",
          "batch_days",
          "mode",
          "location",
          "last_record",
        ],
        columnLabels: {
          batch: "Batch",
          training_program: "Training Program",
          students: "Students",
          batch_start_time: "Start Time",
          batch_end_time: "End Time",
          batch_days: "Batch Days",
          mode: "Mode",
          location: "Location",
          last_record: "Last Record",
        },
        columnWidths: {
          batch: 140,
          training_program: 260,
          students: 110,
          batch_start_time: 130,
          batch_end_time: 130,
          batch_days: 140,
          mode: 110,
          location: 170,
          last_record: 170,
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => ({
          id: item.organization_ems_batch_class_id,
          organization_ems_batch_id:
            item?.organization_ems_batch_id ??
            item?.batch?.organization_ems_batch_id ??
            "",
          ...linkedTrainerIdentity(
            item?.class_trainers ?? item?.classTrainers,
            item?.batch?.batch_trainers ?? item?.batch?.batchTrainers,
            item?.trainer_employee_id || item?.trainer ? [item] : []
          ),
          trainer_employee_id:
            item?.trainer_employee_id ??
            item?.trainer?.organization_user_id ??
            "",
          trainer_application_user_id:
            getApplicationUserIdFromTrainerObject(item?.trainer) || "",
          batch:
            item?.batch?.batch_code ||
            item?.batch?.batch_name ||
            "-",
          batch_status: item?.batch?.status || "",
          training_program:
            item?.batch?.training_program?.training_program_name ||
            item?.batch?.training_program_name ||
            "-",
          students: (() => {
            if (!Array.isArray(item?.batch_students)) {
              return (
                item?.students_count ??
                item?.student_count ??
                item?.batch_students_count ??
                0
              );
            }
            const classDateYmd =
              toYmd(item?.class_date) || toYmd(item?.batch_class?.class_date) || toYmd(new Date());
            const activeStudents = item.batch_students.filter((bs) =>
              isBatchStudentActiveForClassDate(bs, classDateYmd)
            );
            return activeStudents.length;
          })(),
          __batch_students_tooltip: Array.isArray(item?.batch_students)
            ? (() => {
                const classDateYmd =
                  toYmd(item?.class_date) || toYmd(item?.batch_class?.class_date) || toYmd(new Date());
                const activeStudents = item.batch_students.filter((bs) =>
                  isBatchStudentActiveForClassDate(bs, classDateYmd)
                );
                return activeStudents
                  .map((bs) => {
                  const fullName =
                    String(bs?.student?.full_name || "").trim() ||
                    [
                      bs?.student?.first_name,
                      bs?.student?.middle_name,
                      bs?.student?.last_name,
                    ]
                      .filter((x) => x && String(x).trim() !== "")
                      .join(" ")
                      .trim();
                  return fullName || bs?.student?.student_id || "Student";
                })
                  .join("\n");
              })()
            : "",
          batch_start_time:
            formatTime12h(
              item?.start_time ?? item?.batch?.batch_start_time ?? item?.batch_start_time
            ) || "-",
          batch_end_time:
            formatTime12h(
              item?.end_time ?? item?.batch?.batch_end_time ?? item?.batch_end_time
            ) || "-",
          batch_days: Array.isArray(item?.class_days)
            ? item.class_days
                .map((d) => {
                  const day = String(d || "").trim().toLowerCase();
                  if (day === "monday") return "M";
                  if (day === "tuesday") return "T";
                  if (day === "wednesday") return "W";
                  if (day === "thursday") return "TH";
                  if (day === "friday") return "F";
                  if (day === "saturday") return "S";
                  if (day === "sunday") return "SU";
                  return d;
                })
                .join(", ")
            : Array.isArray(item?.batch?.batch_days)
            ? item.batch.batch_days
                .map((d) => {
                  const day = String(d || "").trim().toLowerCase();
                  if (day === "monday") return "M";
                  if (day === "tuesday") return "T";
                  if (day === "wednesday") return "W";
                  if (day === "thursday") return "TH";
                  if (day === "friday") return "F";
                  if (day === "saturday") return "S";
                  if (day === "sunday") return "SU";
                  return d;
                })
                .join(", ")
            : "-",
          mode: item?.mode || item?.batch?.batch_mode || "-",
          location: item?.training_space?.space_name || "-",
          last_record: (() => {
            const latestRecord = item?.latest_batch_class_record;
            const record = item?.batch_class_record;
            return formatDateOnly(
              latestRecord?.start_time ??
                record?.class_date ??
                record?.start_time ??
                record?.updated_at ??
                null
            );
          })(),
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          organization_ems_batch_id,
          ems_training_space_id,
          mode,
          start_time,
          end_time,
          class_name,
          class_days,
          status,
          remarks,
          class_platform,
          class_link,
        } = formData;

        // 1️⃣ Organization ID
        if (!organization_id) {
          errors.organization_id = "Organization is required.";
        } else if (isNaN(Number(organization_id))) {
          errors.organization_id = "Organization ID must be a valid number.";
        }

        // 2️⃣ Organization Entity ID
        // if (organization_entity_id && isNaN(Number(organization_entity_id))) {
        //   errors.organization_entity_id = "Entity ID must be a valid number.";
        // }

        // 3️⃣ Batch ID
        if (!organization_ems_batch_id) {
          errors.organization_ems_batch_id = "Batch is required.";
        } else if (isNaN(Number(organization_ems_batch_id))) {
          errors.organization_ems_batch_id = "Batch ID must be a valid number.";
        }

        if (
          ems_training_space_id !== "" &&
          ems_training_space_id !== null &&
          ems_training_space_id !== undefined &&
          isNaN(Number(ems_training_space_id))
        ) {
          errors.ems_training_space_id = "Training space ID must be a valid number.";
        }

        if (!mode) {
          errors.mode = "Mode is required.";
        } else if (!["Online", "Offline"].includes(String(mode))) {
          errors.mode = "Mode must be Online or Offline.";
        }

        // 5️⃣ Start Time
        if (!start_time) {
          errors.start_time = "Start time is required.";
        } else if (!/^\d{2}:\d{2}$/.test(start_time)) {
          errors.start_time = "Invalid time format (expected HH:mm).";
        }

        // 6️⃣ End Time
        if (!end_time) {
          errors.end_time = "End time is required.";
        } else if (!/^\d{2}:\d{2}$/.test(end_time)) {
          errors.end_time = "Invalid time format (expected HH:mm).";
        } else if (start_time && end_time <= start_time) {
          errors.end_time = "End time must be after start time.";
        }

        if (class_name && String(class_name).length > 255) {
          errors.class_name = "Class name cannot exceed 255 characters.";
        }

        const validDays = [
          "Sunday",
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ];
        if (!Array.isArray(class_days) || class_days.length === 0) {
          errors.class_days = "Select at least one class day.";
        } else if (class_days.some((d) => !validDays.includes(String(d)))) {
          errors.class_days = "Class days contain invalid values.";
        }

        if (status && !["Active", "Inactive"].includes(status)) {
          errors.status = "Status must be Active or Inactive.";
        }

        // 8️⃣ Remarks
        if (remarks && remarks.length > 1000) {
          errors.remarks = "Remarks cannot exceed 1000 characters.";
        }

        if (class_platform && String(class_platform).length > 255) {
          errors.class_platform = "Class platform cannot exceed 255 characters.";
        }

        if (class_link && String(class_link).length > 500) {
          errors.class_link = "Class link cannot exceed 500 characters.";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/batch-classes",
      initialData: {
        organization_entity_id: "",
        organization_ems_batch_id: "",
        ems_training_space_id: "",
        mode: "",
        start_time: "",
        end_time: "",
        class_name: "",
        class_days: [],
        status: "Active",
        remarks: "",
        class_platform: "",
        class_link: "",
      },
      successMessages: {
        add: "Batch Class added!",
        update: "Batch Class updated!",
      },
      redirectPath: "/batch-classes",
      headerProps: {
        addMessage: "Add Batch Class",
        updateMessage: "Update Batch Class",
        homeLink: "/batch-classes",
        homeText: "Batch Class",
      },
      Inputs: [
        {
          type: "Select",
          label: "Batch *",
          mainKey: "organization_ems_batch_id",
          OptionMainKey: "Batches",
          menuKey: "organization_ems_batch_id",
          formatValue: (val) => {
            if (!val) return "-";
            const code = val.batch_code ?? val.batch?.batch_code ?? "";
            return code || "-";
          },
        },

        {
          type: "Text",
          label: "Class Name",
          mainKey: "class_name",
        },
        {
          type: "CheckboxGroup",
          label: "Class Days *",
          mainKey: "class_days",
          OptionMainKey: "BatchDays",
          menuKey: null,
          formatValue: (val) => {
            const day = String(val || "").trim().toLowerCase();
            if (day === "monday") return "M";
            if (day === "tuesday") return "T";
            if (day === "wednesday") return "W";
            if (day === "thursday") return "TH";
            if (day === "friday") return "F";
            if (day === "saturday") return "S";
            if (day === "sunday") return "SU";
            return String(val);
          },
        },
        {
          type: "Select",
          label: "Status",
          mainKey: "status",
          OptionMainKey: "ClassSlotStatuses",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "Select",
          label: "Training Space",
          mainKey: "ems_training_space_id",
          OptionMainKey: "TrainingSpaces",
          // Backend + exists rule use `ems_training_space_id` on organization_ems_training_spaces
          menuKey: "ems_training_space_id",
          formatValue: (val) => {
            if (!val) return "-";
            const name = val.space_name ?? val.training_space_name ?? "";
            return name || "-";
          },
        },
        {
          type: "Select",
          label: "Mode *",
          mainKey: "mode",
          OptionMainKey: "BatchClassModes",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "Time",
          label: "Start Time *",
          mainKey: "start_time",
        },
        {
          type: "Time",
          label: "End Time *",
          mainKey: "end_time",
        },
        {
          type: "MultiRow",
          label: "Remarks ",
          mainKey: "remarks",
        },
        {
          type: "Text",
          label: "Class Platform ",
          mainKey: "class_platform",
        },
        {
          type: "Text",
          label: "Class Link ",
          mainKey: "class_link",
        },
      ],
      fieldGridLayout: {
        sections: [{ title: "Class Details", beforeRow: 6 }],
        rows: [
          { variant: "full", mainKeys: ["organization_ems_batch_id"] },
          { variant: "full", mainKeys: ["class_name"] },
          { variant: "full", mainKeys: ["class_days"] },
          { variant: "full", mainKeys: ["status"] },
          { variant: "full", mainKeys: ["ems_training_space_id"] },
          { variant: "full", mainKeys: ["mode"] },
          { variant: "half", mainKeys: ["start_time", "end_time"] },
          { variant: "full", mainKeys: ["remarks"] },
          { variant: "full", mainKeys: ["class_platform"] },
          { variant: "full", mainKeys: ["class_link"] },
        ],
      },
      DataFetchers: [
        {
          name: "Batches",
          Type: "Fetch",
          link: "/api/ems/batches",
        },
        {
          name: "TrainingSpaces",
          Type: "Fetch",
          link: "/api/ems/training-spaces",
        },
        {
          name: "BatchClassModes",
          Type: "NoFetch",
          Options: ["Online", "Offline"],
        },
        {
          name: "ClassSlotStatuses",
          Type: "NoFetch",
          Options: ["Active", "Inactive"],
        },
        {
          name: "BatchDays",
          Type: "NoFetch",
          Options: [
            "Sunday",
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
          ],
        },
      ],
    },
  },
  {
    route: "/class-records",
    ListProps: {
      title: "Class Records",
      apiEndpoint: "/api/ems/class-records",
      filteredApiEndpoint: "/api/ems/class-records/filtered",
      filterModule: "ems_batch_class_records",
      useMetadataFilters: true,
      addRoute: "/class-records",
      editRouteBase: "/class-records/edit",
      tablePrimaryKey: "organization_ems_batch_class_record_id",
      tableName: "ems_batch_class_records",
      datagridKey: "ems_batch_class_records_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "class_date",
        "batch",
        "training_program",
        "status",
        "trainer",
        "topic",
        "duration",
        "location",
        "mode",
        "start_time",
        "end_time",
        "cancel_reason",
      ],
      config: {
        sort: { field: "start_time", direction: "desc" },
        defaultVisibleColumns: [
          "class_date",
          "batch",
          "training_program",
          "status",
          "trainer",
          "topic",
          "duration",
          "location",
        ],
        columnLabels: {
          class_date: "Date",
          batch: "Batch",
          training_program: "Training Program",
          status: "Status",
          trainer: "Trainer",
          topic: "Topic",
          duration: "Duration",
          location: "Location",
        },
        columnWidths: {
          class_date: 130,
          batch: 200,
          training_program: 320,
          status: 180,
          trainer: 170,
          topic: 200,
          duration: 180,
          location: 190,
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => {
          const pk =
            item.organization_ems_batch_class_record_id ??
            item.organization_ems_class_record_id ??
            item.organization_ems_batch_record_id ??
            item.batch_record_id ??
            item.class_record_id ??
            item.organization_class_record_id ??
            item.organization_ems_recording_id ??
            item.id ??
            null;
          return {
            id: pk,
            organization_ems_batch_class_record_id: pk,
            organization_ems_class_record_id: pk,
            organization_ems_batch_record_id: pk,
            batch_record_id: pk,
            ...(() => {
              const sessionRows = Array.isArray(item?.record_trainers)
                ? item.record_trainers
                : Array.isArray(item?.recordTrainers)
                  ? item.recordTrainers
                  : [];
              const currentSessions = sessionRows.filter((row) => {
                const status = String(row?.assignment_status || "Assigned").trim();
                return status !== "Replaced" && status !== "Cancelled";
              });
              const batchLinks = [
                ...(Array.isArray(item?.batch?.batch_trainers) ? item.batch.batch_trainers : []),
                ...(Array.isArray(item?.batchClass?.class_trainers)
                  ? item.batchClass.class_trainers
                  : Array.isArray(item?.batchClass?.classTrainers)
                    ? item.batchClass.classTrainers
                    : []),
                ...(Array.isArray(item?.batch_class?.class_trainers)
                  ? item.batch_class.class_trainers
                  : []),
              ];
              const source = currentSessions.length
                ? currentSessions
                : sessionRows.length
                  ? sessionRows
                  : batchLinks;
              const sessionIdentity = linkedTrainerIdentity(source);
              const batchIdentity = linkedTrainerIdentity(
                batchLinks,
                item?.batchClass?.trainer_employee_id || item?.batchClass?.trainer
                  ? [item.batchClass]
                  : item?.batch_class?.trainer_employee_id || item?.batch_class?.trainer
                    ? [item.batch_class]
                    : []
              );
              const trainerEmployeeIds = [
                ...new Set([
                  ...sessionIdentity.trainer_employee_ids,
                  ...batchIdentity.trainer_employee_ids,
                ]),
              ];
              const trainerApplicationUserIds = [
                ...new Set([
                  ...sessionIdentity.trainer_application_user_ids,
                  ...batchIdentity.trainer_application_user_ids,
                ]),
              ];
              return {
                batch_trainers: item?.batch?.batch_trainers || item?.batch?.batchTrainers || [],
                trainer_employee_ids: trainerEmployeeIds,
                trainer_application_user_ids: trainerApplicationUserIds,
                trainer_employee_id: trainerEmployeeIds[0] || "",
                trainer_application_user_id: trainerApplicationUserIds[0] || "",
              };
            })(),
            organization_ems_batch_class_id:
              item?.organization_ems_batch_class_id ??
              item?.batchClass?.organization_ems_batch_class_id ??
              item?.batch_class?.organization_ems_batch_class_id ??
              "",
            organization_ems_batch_id:
              item?.organization_ems_batch_id ??
              item?.batch?.organization_ems_batch_id ??
              "",
            training_program_id:
              item?.batch?.training_program_id ??
              item?.batch?.training_program?.organization_ems_training_program_id ??
              "",
            class_start_ts: (() => {
              const raw = item?.start_time ?? item?.batch_class_record?.start_time ?? null;
              const ts = raw ? new Date(raw).getTime() : Number.NaN;
              return Number.isFinite(ts) ? ts : Number.NEGATIVE_INFINITY;
            })(),
            class_date: formatDateOnly(item?.start_time),
            batch:
              `${item?.batch?.batch_name || "-"} (${item?.batch?.batch_code || "-"})`,
            batch_name_display: item?.batch?.batch_name || "-",
            batch_code_display: item?.batch?.batch_code || "-",
            training_program:
              item?.batch?.training_program?.training_program_name ??
              item?.batch?.training_program?.program_name ??
              item?.batch?.training_program_name ??
              "-",
            not_conducted_reason:
              item?.cancel_reason ??
              item?.not_conducted_reason ??
              item?.remarks ??
              (item?.status === "Conducted" || item?.status === "Completed"
                ? "-"
                : "—"),
            trainer: (() => {
              const sessionLabel = formatSessionTrainerRows(
                item?.record_trainers ?? item?.recordTrainers
              );
              if (sessionLabel) return sessionLabel;
              const bc = item?.batchClass ?? item?.batch_class ?? {};
              const classTrainerName = batchClassTrainerLabel(bc);
              if (classTrainerName) return classTrainerName;
              const trainerId = bc?.trainer_employee_id ?? "";
              if (trainerId) return `Trainer ID: ${trainerId}`;
              return "-";
            })(),
            ...(() => {
              const cardTrainer = classRecordCardTrainer(item);
              return {
                trainer_card_name: cardTrainer.name || "",
                trainer_card_role: cardTrainer.role || "",
                ...(cardTrainer.employeeId
                  ? { trainer_employee_id: cardTrainer.employeeId }
                  : {}),
                ...(cardTrainer.appUserId
                  ? { trainer_application_user_id: cardTrainer.appUserId }
                  : {}),
                profile_image_url: cardTrainer.photo || classRecordTrainerPhoto(item),
              };
            })(),
            topic: item.topic || "-",
            status: item.status || "-",
            status_reason_display:
              String(item?.status || "").trim().toLowerCase() === "cancelled"
                ? (item?.cancel_reason ??
                  item?.not_conducted_reason ??
                  item?.remarks ??
                  "-")
                : "",
            duration: formatDurationMinutes(item?.start_time, item?.end_time),
            duration_start_display: formatTimeOnlyFromDateTime(item?.start_time),
            duration_end_display: formatTimeOnlyFromDateTime(item?.end_time),
            start_time: formatTimeOnlyFromDateTime(item?.start_time),
            end_time: formatTimeOnlyFromDateTime(item?.end_time),
            actual_start_time: formatClassRecordActualWhen(item?.actual_start_time),
            actual_end_time: formatClassRecordActualWhen(item?.actual_end_time),
            location:
              item?.training_space?.space_name ??
              item?.training_space?.training_space_name ??
              item?.training_space_name ??
              item?.batch_class?.training_space?.space_name ??
              item?.batch_class?.training_space?.training_space_name ??
              item?.batch_class?.training_space_name ??
              item?.batchClass?.training_space?.space_name ??
              item?.batchClass?.training_space?.training_space_name ??
              item?.batchClass?.training_space_name ??
              item?.batch_class?.TrainingSpace?.space_name ??
              item?.batch_class?.TrainingSpace?.training_space_name ??
              item?.batchClass?.TrainingSpace?.space_name ??
              item?.batchClass?.TrainingSpace?.training_space_name ??
              "-",
            mode: item?.mode ?? item?.batch_class?.mode ?? item?.batchClass?.mode ?? "-",
            location_mode_display:
              item?.mode ?? item?.batch_class?.mode ?? item?.batchClass?.mode ?? "-",
          };
        }),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          organization_ems_batch_id,
          organization_ems_batch_class_id,
          ems_training_space_id,
          mode,
          start_time,
          end_time,
          topic,
          status,
          cancel_reason,
        } = formData;

        if (!organization_id) {
          errors.organization_id = "Organization is required.";
        } else if (isNaN(Number(organization_id))) {
          errors.organization_id = "Organization ID must be a valid number.";
        }

        if (
          organization_entity_id &&
          organization_entity_id !== "" &&
          isNaN(Number(organization_entity_id))
        ) {
          errors.organization_entity_id = "Entity ID must be a valid number.";
        }

        if (!organization_ems_batch_id) {
          errors.organization_ems_batch_id = "Batch is required.";
        } else if (isNaN(Number(organization_ems_batch_id))) {
          errors.organization_ems_batch_id = "Batch ID must be a valid number.";
        }

        if (!organization_ems_batch_class_id) {
          errors.organization_ems_batch_class_id = "Batch class is required.";
        } else if (isNaN(Number(organization_ems_batch_class_id))) {
          errors.organization_ems_batch_class_id =
            "Batch class ID must be a valid number.";
        }

        if (
          ems_training_space_id !== "" &&
          ems_training_space_id !== null &&
          ems_training_space_id !== undefined &&
          isNaN(Number(ems_training_space_id))
        ) {
          errors.ems_training_space_id =
            "Training space ID must be a valid number.";
        }

        const parseLocal = (s) => {
          if (!s) return null;
          const d = new Date(s);
          return Number.isNaN(d.getTime()) ? null : d;
        };

        if (!start_time) {
          errors.start_time = "Start time is required.";
        } else if (!parseLocal(start_time)) {
          errors.start_time = "Invalid start date/time.";
        }

        if (!end_time) {
          errors.end_time = "End time is required.";
        } else if (!parseLocal(end_time)) {
          errors.end_time = "Invalid end date/time.";
        } else if (
          parseLocal(start_time) &&
          parseLocal(end_time) &&
          parseLocal(end_time) <= parseLocal(start_time)
        ) {
          errors.end_time = "End time must be after start time.";
        }

        const validStatuses = [
          "Scheduled",
          "Ongoing",
          "Completed",
          "Cancelled",
        ];
        if (!status) {
          errors.status = "Status is required.";
        } else if (!validStatuses.includes(status)) {
          errors.status = "Invalid status.";
        }
        if (String(status || "").trim() === "Cancelled") {
          const allowedReasons = [
            "Trainer Absent",
            "Students Absent",
            "Technical Issue",
            "Power Failure",
            "Scheduling Error",
            "Festival Off",
          ];
          const reason = String(cancel_reason ?? "").trim();
          if (!reason) {
            errors.cancel_reason = "Cancel reason is required when status is Cancelled.";
          } else if (!allowedReasons.includes(reason)) {
            errors.cancel_reason = "Invalid cancel reason.";
          }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/class-records",
      initialData: {
        organization_entity_id: "",
        organization_ems_batch_id: "",
        organization_ems_batch_class_id: "",
        ems_training_space_id: "",
        mode: "",
        start_time: "",
        end_time: "",
        recording_url: "",
        topic: "",
        status: "",
        cancel_reason: "",
      },
      successMessages: {
        add: "Class record saved!",
        update: "Class record updated!",
      },
      redirectPath: "/class-records",
      headerProps: {
        addMessage: "Add Class Record",
        updateMessage: "Update Class Record",
        homeLink: "/class-records",
        homeText: "Class Records",
      },
      Inputs: [
        {
          type: "Select",
          label: "Batch *",
          mainKey: "organization_ems_batch_id",
          OptionMainKey: "Batches",
          menuKey: "organization_ems_batch_id",
          formatValue: (val) => {
            if (!val) return "-";
            const code = val.batch_code ?? val.batch?.batch_code ?? "";
            return code || "-";
          },
        },
        {
          type: "Select",
          label: "Batch Class *",
          mainKey: "organization_ems_batch_class_id",
          OptionMainKey: "BatchClasses",
          menuKey: "organization_ems_batch_class_id",
          formatValue: (val) => {
            if (!val) return "-";
            const batchName =
              val?.batch?.batch_code ??
              val?.batch?.batch_name ??
              val?.batch_name ??
              "";
            const trainerName = batchClassTrainerLabel(val);
            const className = String(val?.class_name || "").trim();
            const time = [val?.start_time, val?.end_time].filter(Boolean).join("–");
            const parts = [
              batchName ? `Batch: ${batchName}` : "",
              className,
              time,
              trainerName ? `Trainer: ${trainerName}` : "",
            ].filter(Boolean);

            return parts.length > 0 ? parts.join(" | ") : "-";
          },
        },
        {
          type: "Select",
          label: "Status *",
          mainKey: "status",
          OptionMainKey: "ClassRecordStatuses",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "Select",
          label: "Cancel Reason",
          mainKey: "cancel_reason",
          OptionMainKey: "ClassRecordCancelReasons",
          menuKey: null,
          formatValue: (val) => val,
          visibleWhen: (formData) => String(formData?.status || "").trim() === "Cancelled",
        },
        {
          type: "DateTime",
          label: "Start *",
          mainKey: "start_time",
        },
        {
          type: "DateTime",
          label: "End *",
          mainKey: "end_time",
        },
        {
          type: "Select",
          label: "Training Space",
          mainKey: "ems_training_space_id",
          OptionMainKey: "TrainingSpaces",
          menuKey: "ems_training_space_id",
          formatValue: (val) => {
            if (!val) return "-";
            const name = val.space_name ?? val.training_space_name ?? "";
            const cap = val.capacity;
            return cap === null || cap === undefined || cap === ""
              ? name || "-"
              : `${name} (Capacity: ${cap})`;
          },
        },
        {
          type: "Select",
          label: "Mode",
          mainKey: "mode",
          OptionMainKey: "ClassRecordModes",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "Text",
          label: "Recording URL",
          mainKey: "recording_url",
        },
        {
          type: "Text",
          label: "Topic",
          mainKey: "topic",
        },
      ],
      fieldGridLayout: {
        rows: [
          { variant: "full", mainKeys: ["organization_ems_batch_id"] },
          { variant: "full", mainKeys: ["organization_ems_batch_class_id"] },
          { variant: "full", mainKeys: ["status"] },
          { variant: "full", mainKeys: ["cancel_reason"] },
          { variant: "half", mainKeys: ["start_time", "end_time"] },
          { variant: "full", mainKeys: ["ems_training_space_id"] },
          { variant: "full", mainKeys: ["mode"] },
          { variant: "full", mainKeys: ["recording_url"] },
          { variant: "full", mainKeys: ["topic"] },
        ],
      },
      DataFetchers: [
        {
          name: "Batches",
          Type: "Fetch",
          link: "/api/ems/batches",
        },
        {
          name: "BatchClasses",
          Type: "Fetch",
          link: "/api/ems/batch-classes",
        },
        {
          name: "TrainingSpaces",
          Type: "Fetch",
          link: "/api/ems/training-spaces",
        },
        {
          name: "ClassRecordModes",
          Type: "NoFetch",
          Options: ["Online", "Offline"],
        },
        {
          name: "ClassRecordStatuses",
          Type: "NoFetch",
          Options: ["Scheduled", "Ongoing", "Completed", "Cancelled"],
        },
        {
          name: "ClassRecordCancelReasons",
          Type: "NoFetch",
          Options: [
            "Trainer Absent",
            "Students Absent",
            "Technical Issue",
            "Power Failure",
            "Scheduling Error",
            "Festival Off",
          ],
        },
      ],
    },
  },
  {
    route: "/batch-students",
    ListProps: {
      title: "Batch Students",
      apiEndpoint: "/api/ems/batch-students",
      filteredApiEndpoint: "/api/ems/batch-students/filtered",
      filterModule: "ems_batch_students",
      useMetadataFilters: true,
      addRoute: "/batch-students",
      editRouteBase: "/batch-students/edit",
      tablePrimaryKey: "organization_ems_batch_student_id",
      tableName: "ems_batch_students",
      datagridKey: "ems_batch_students_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "batch_code",
        "batch_student",
        "city_country",
        "phone_number",
        "mode",
        "enrollment_date",
        "status",
        "completed_date",
        "admission",
        "training_program",
        "batch_status",
        "remarks",
      ],
      config: {
        sort: { field: "enrollment_date", direction: "desc" },
        defaultVisibleColumns: [
          "batch_code",
          "batch_student",
          "city_country",
          "phone_number",
          "mode",
          "enrollment_date",
          "status",
          "completed_date",
        ],
        columnLabels: {
          batch_code: "Batch Code",
          batch_student: "Batch Student",
          city_country: "Country/City",
          phone_number: "Phone",
          mode: "Mode",
          enrollment_date: "Start Date",
          status: "Status",
          completed_date: "Completed Date",
          remarks: "Remarks",
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => ({
          trainer_employee_ids: [
            ...(Array.isArray(item?.batch?.batch_classes)
              ? item.batch.batch_classes
              : []),
            ...(Array.isArray(item?.batch?.batch_trainers)
              ? item.batch.batch_trainers
              : []),
          ]
            .map((x) =>
              x?.trainer_employee_id ??
              x?.trainer?.organization_user_id ??
              null
            )
            .filter((v) => v != null && String(v).trim() !== "")
            .map((v) => String(v).trim()),
          trainer_application_user_ids: [
            ...new Set(
              [
                ...(Array.isArray(item?.batch?.batch_classes)
                  ? item.batch.batch_classes
                  : []),
                ...(Array.isArray(item?.batch?.batch_trainers)
                  ? item.batch.batch_trainers
                  : []),
              ]
                .map((x) => getApplicationUserIdFromTrainerObject(x?.trainer))
                .filter((v) => v != null && String(v).trim() !== "")
                .map((v) => String(v).trim())
            ),
          ],
          id: item.organization_ems_batch_student_id,
          organization_ems_batch_id:
            item?.organization_ems_batch_id ??
            item?.batch?.organization_ems_batch_id ??
            "",
          batch:
            `${item?.batch?.batch_name || item?.batch_name || "-"} (${item?.batch?.batch_code || item?.batch_code || "-"})`,
          batch_code: `${item?.batch?.batch_code || item?.batch_code || "-"}\n${item?.batch?.training_program?.training_program_name || item?.training_program_name || "-"}`,
          training_program_id:
            item?.batch?.training_program?.organization_ems_training_program_id ??
            item?.batch?.training_program_id ??
            item?.training_program_id ??
            "",
          training_program:
            item?.batch?.training_program?.training_program_name ||
            item?.training_program_name ||
            "-",
          student:
            [item?.student?.first_name, item?.student?.middle_name, item?.student?.last_name]
              .filter(Boolean)
              .join(" ")
              || item?.student?.student_id
              || item?.student_id
              || "-",
          batch_student:
            [item?.student?.first_name, item?.student?.middle_name, item?.student?.last_name]
              .filter(Boolean)
              .join(" ")
              || item?.student?.student_id
              || item?.student_id
              || "-",
          admission: pickBatchStudentAdmissionNumber(item),
          admission_id: pickBatchStudentAdmissionNumber(item),
          city_country: `${
            item?.student?.country?.country_name ||
            item?.student?.country_name ||
            item?.student?.country_id ||
            "-"
          } / ${
            item?.student?.city?.city_name ||
            item?.student?.city_name ||
            item?.student?.city_id ||
            "-"
          }`,
          phone_number: item?.student?.phone || "-",
          mode:
            item?.mode ||
            item?.batch?.batch_mode ||
            item?.batch_mode ||
            "-",

          enrollment_date: FormatDateV1(item.enrollment_date) || "-",
          completion_date: FormatDateV1(item.completion_date) || "-",
          completed_date: FormatDateV1(item.completion_date) || "-",
          batch_status: item.batch_status || "-",
          batch_run_status: item?.batch?.status || "",
          training_status: item.batch_status || "-",
          status: item?.batch_status || "-",
          remarks: item.remarks || "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          organization_ems_batch_id,
          organization_ems_admission_id,
          organization_ems_student_id,
          enrollment_date,
          completion_date,
          mode,
          batch_status,
          remarks,
        } = formData;

        if (!organization_id) {
          errors.organization_id = "Organization is required.";
        } else if (isNaN(Number(organization_id))) {
          errors.organization_id = "Organization ID must be a valid number.";
        }

        // if (organization_entity_id && isNaN(Number(organization_entity_id))) {
        //   errors.organization_entity_id = "Entity ID must be a valid number.";
        // }

        if (!organization_ems_batch_id) {
          errors.organization_ems_batch_id = "Batch is required.";
        } else if (isNaN(Number(organization_ems_batch_id))) {
          errors.organization_ems_batch_id = "Batch ID must be a valid number.";
        }

        if (!organization_ems_admission_id) {
          errors.organization_ems_admission_id = "Admission is required.";
        } else if (isNaN(Number(organization_ems_admission_id))) {
          errors.organization_ems_admission_id =
            "Admission ID must be a valid number.";
        }

        if (!organization_ems_student_id) {
          errors.organization_ems_student_id = "Student is required.";
        } else if (isNaN(Number(organization_ems_student_id))) {
          errors.organization_ems_student_id =
            "Student ID must be a valid number.";
        }

        if (!enrollment_date) {
          errors.enrollment_date = "Enrollment date is required.";
        } else if (isNaN(Date.parse(enrollment_date))) {
          errors.enrollment_date = "Invalid enrollment date.";
        }

        if (completion_date) {
          if (isNaN(Date.parse(completion_date))) {
            errors.completion_date = "Invalid completion date.";
          } else if (
            enrollment_date &&
            new Date(completion_date) < new Date(enrollment_date)
          ) {
            errors.completion_date =
              "Completion date must be after or equal to enrollment date.";
          }
        }

        if (batch_status === "Completed" && !completion_date) {
          errors.completion_date =
            "Completion date is required when training status is Completed.";
        }

        if (
          completion_date &&
          batch_status &&
          batch_status !== "Completed" &&
          batch_status !== "Dropped"
        ) {
          errors.completion_date =
            "Date is allowed only when training status is Completed or Dropped.";
        }

        const validStatuses = ["Pursuing", "Completed", "Dropped", "On Hold"];
        if (batch_status && !validStatuses.includes(batch_status)) {
          errors.batch_status = "Invalid batch status.";
        }

        if (!mode) {
          errors.mode = "Mode is required.";
        } else if (!["Online", "Offline"].includes(String(mode))) {
          errors.mode = "Mode must be Online or Offline.";
        }

        if (remarks && remarks.length > 1000) {
          errors.remarks = "Remarks cannot exceed 1000 characters.";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/batch-students",
      initialData: {
        organization_entity_id: "",
        organization_ems_batch_id: "",
        organization_ems_admission_id: "",
        organization_ems_student_id: "",
        enrollment_date: "",
        completion_date: "",
        mode: "",
        batch_status: "",
        remarks: "",
      },
      successMessages: {
        add: "Batch Student added!",
        update: "Batch Student updated!",
      },
      redirectPath: "/batch-students",
      headerProps: {
        addMessage: "Add Batch Student",
        updateMessage: "Update Batch Student",
        homeLink: "/batch-students",
        homeText: "Batch Student",
      },
      fieldGridLayout: {
        rows: [
          { variant: "full", mainKeys: ["organization_ems_batch_id"] },
          {
            variant: "twoEqual",
            mainKeys: [
              "organization_ems_admission_id",
              "organization_ems_student_id",
            ],
          },
          {
            variant: "twoEqual",
            mainKeys: ["enrollment_date", "mode"],
          },
          { variant: "full", mainKeys: ["batch_status"] },
          { variant: "full", mainKeys: ["completion_date"] },
          { variant: "full", mainKeys: ["remarks"] },
        ],
      },
      Inputs: [
        {
          type: "Select",
          label: "Batch *",
          mainKey: "organization_ems_batch_id",
          OptionMainKey: "Batches",
          menuKey: "organization_ems_batch_id",
          formatValue: (val) => `${val.batch_code || val.batch?.batch_code || "-"}`,
        },
        {
          type: "Select",
          label: "Admission *",
          mainKey: "organization_ems_admission_id",
          OptionMainKey: "Admissions",
          menuKey: "organization_ems_admission_id",
          formatValue: (val) => {
            const s = val?.student;
            const name = s
              ? (s.full_name && String(s.full_name).trim()) ||
                [s.first_name, s.middle_name, s.last_name]
                  .filter(Boolean)
                  .join(" ")
                  .trim()
              : "";
            const num = val?.admission_number != null ? String(val.admission_number) : "";
            if (name && num) return `${name} — ${num}`;
            if (name) return name;
            return num || "-";
          },
        },
        {
          type: "Select",
          label: "Student *",
          mainKey: "organization_ems_student_id",
          OptionMainKey: "Students",
          menuKey: "organization_ems_student_id",
          formatValue: (val) =>
            `${[val.first_name, val.middle_name, val.last_name].filter(Boolean).join(" ")} (${val.student_id || "-"})`,
        },
        {
          type: "Date",
          label: "Start Date *",
          mainKey: "enrollment_date",
        },
        {
          type: "Select",
          label: "Mode *",
          mainKey: "mode",
          OptionMainKey: "BatchModes",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "Select",
          label: "Training Status *",
          mainKey: "batch_status",
          OptionMainKey: "BatchStatuses",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "Date",
          label: "Completion Date ",
          mainKey: "completion_date",
          getLabel: (formData) => {
            const status = String(formData?.batch_status ?? "")
              .trim()
              .toLowerCase();
            if (status === "dropped") return "Dropped Date ";
            return "Completion Date ";
          },
          getHelperText: (formData) => {
            const status = String(formData?.batch_status ?? "")
              .trim()
              .toLowerCase();
            if (status === "dropped") {
              return "Enter date when training status is Dropped.";
            }
            return "Enter date only when training status is Completed.";
          },
        },
        {
          type: "MultiRow",
          label: "Remarks ",
          mainKey: "remarks",
        },
      ],
      DataFetchers: [
        {
          name: "Batches",
          Type: "Fetch",
          link: "/api/ems/batches",
        },
        {
          name: "Admissions",
          Type: "Fetch",
          link: "/api/ems/admissions",
        },
        {
          name: "Students",
          Type: "Fetch",
          link: "/api/ems/students",
        },

        {
          name: "BatchModes",
          Type: "NoFetch",
          Options: ["Online", "Offline"],
        },
        {
          name: "BatchStatuses",
          Type: "NoFetch",
          Options: ["Pursuing", "Completed", "Dropped", "On Hold"],
        },
      ],
    },
  },
  {
    route: "/recruitment-agencies",
    ListProps: {
      title: "Recruitment Agencies",
      apiEndpoint: "/api/ems/recruitment-agencies",
      filteredApiEndpoint: "/api/ems/recruitment-agencies/filtered",
      filterModule: "ems_recruitment_agencies",
      useMetadataFilters: true,
      addRoute: "/recruitment-agencies",
      editRouteBase: "/recruitment-agencies/edit",
      tablePrimaryKey: "organization_ems_recruitment_agency_id",
      tableName: "ems_recruitment_agencies",
      datagridKey: "ems_recruitment_agencies_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "country",
        "state",
        "city",
        "agency_name",
        "contact_person",
        "email",
        "phone",
        "address",
        "status",
        "remarks",
      ],
      config: {
        sort: { field: "agency_name", direction: "asc" },
        defaultVisibleColumns: [
          "country",
          "state",
          "city",
          "agency_name",
          "contact_person",
          "email",
          "phone",
          "status",
        ],
        columnLabels: {
          country: "Country",
          state: "State",
          city: "City",
          agency_name: "Agency Name",
          contact_person: "Contact Person",
          email: "Email",
          phone: "Phone",
          address: "Address",
          status: "Status",
          remarks: "Remarks",
        },
        columnWidths: {
          agency_name: 220,
          contact_person: 180,
          email: 220,
          phone: 140,
          country: 140,
          state: 140,
          city: 140,
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => ({
          id: item.organization_ems_recruitment_agency_id,
          country: item?.country?.country_name || "-",
          state: item?.state?.state_name || "-",
          city: item?.city?.city_name || "-",
          agency_name: item.agency_name || "-",
          contact_person: item.contact_person || "-",
          email: item.email || "-",
          phone: item.phone || "-",
          address: item.address || "-",
          status: item.status || "-",
          remarks: item.remarks || "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          agency_name,
          contact_person,
          email,
          phone,
          address,
          country_id,
          state_id,
          city_id,
          status,
          remarks,
        } = formData;

        // 1️⃣ Organization ID
        if (!organization_id) {
          errors.organization_id = "Organization is required.";
        } else if (isNaN(Number(organization_id))) {
          errors.organization_id = "Organization ID must be a valid number.";
        }

        // 2️⃣ Organization Entity ID
        // if (organization_entity_id && isNaN(Number(organization_entity_id))) {
        //   errors.organization_entity_id = "Entity ID must be a valid number.";
        // }

        // 3️⃣ Agency Name
        if (!agency_name || agency_name.trim() === "") {
          errors.agency_name = "Agency name is required.";
        } else if (agency_name.length > 150) {
          errors.agency_name = "Agency name cannot exceed 150 characters.";
        }

        // 4️⃣ Contact Person
        if (contact_person && contact_person.length > 100) {
          errors.contact_person =
            "Contact person cannot exceed 100 characters.";
        }

        // 5️⃣ Email
        if (email) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(email)) {
            errors.email = "Invalid email format.";
          } else if (email.length > 100) {
            errors.email = "Email cannot exceed 100 characters.";
          }
        }

        // 6️⃣ Phone (optional; when entered, 10 digits only)
        const phoneValue = phone?.toString().trim() || "";
        if (phoneValue && !/^\d{10}$/.test(phoneValue)) {
          errors.phone = "Enter a valid 10-digit phone number.";
        }

        // 7️⃣ Address
        if (address && address.length > 255) {
          errors.address = "Address cannot exceed 255 characters.";
        }

        // 8️⃣ Country ID
        if (country_id && isNaN(Number(country_id))) {
          errors.country_id = "Country ID must be a valid number.";
        }

        // 9️⃣ State ID
        if (state_id && isNaN(Number(state_id))) {
          errors.state_id = "State ID must be a valid number.";
        }

        // 🔟 City ID
        if (city_id && isNaN(Number(city_id))) {
          errors.city_id = "City ID must be a valid number.";
        }

        // 11️⃣ Status (required)
        const validStatuses = ["Active", "Inactive"];
        if (!status) {
          errors.status = "Status is required.";
        } else if (!validStatuses.includes(status)) {
          errors.status = "Status must be Active or Inactive.";
        }

        // 12️⃣ Remarks
        if (remarks && remarks.length > 1000) {
          errors.remarks = "Remarks cannot exceed 1000 characters.";
        }

        // ✅ Finalize
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/recruitment-agencies",
      initialData: {
        organization_entity_id: "",
        agency_name: "",
        contact_person: "",
        email: "",
        phone: "",
        address: "",
        country_id: "",
        state_id: "",
        city_id: "",
        status: "",
        remarks: "",
      },
      successMessages: {
        add: "Recruitment Agency added!",
        update: "Recruitment Agency updated!",
      },
      redirectPath: "/recruitment-agencies",
      headerProps: {
        addMessage: "Add Recruitment Agency",
        updateMessage: "Update Recruitment Agency",
        homeLink: "/recruitment-agencies",
        homeText: "Recruitment Agency",
      },
      fieldGridLayout: {
        rows: [
          {
            variant: "threeEqual",
            mainKeys: ["country_id", "state_id", "city_id"],
          },
          {
            variant: "twoEqual",
            mainKeys: ["agency_name", "contact_person"],
          },
          {
            variant: "twoEqual",
            mainKeys: ["email", "phone"],
          },
          { variant: "full", mainKeys: ["address"] },
          { variant: "full", mainKeys: ["status"] },
          { variant: "full", mainKeys: ["remarks"] },
        ],
        sections: [],
      },
      Inputs: [
        {
          type: "Select",
          label: "Country ",
          mainKey: "country_id",
          OptionMainKey: "Countries",
          menuKey: "general_country_id",
          formatValue: (val) => `${val.country_name}`,
        },
        {
          type: "Select",
          label: "State ",
          mainKey: "state_id",
          OptionMainKey: "States",
          menuKey: "general_state_id",
          formatValue: (val) => `${val.state_name}`,
        },
        {
          type: "Select",
          label: "City ",
          mainKey: "city_id",
          OptionMainKey: "Cities",
          menuKey: "general_city_id",
          formatValue: (val) => `${val.city_name}`,
        },

        {
          type: "Text",
          label: "Agency Name *",
          mainKey: "agency_name",
        },
        {
          type: "Text",
          label: "Contact Person ",
          mainKey: "contact_person",
        },
        {
          type: "Text",
          label: "Email ",
          mainKey: "email",
        },
        {
          type: "Text",
          label: "Phone ",
          mainKey: "phone",
          digitsOnly: true,
          maxLength: 10,
        },
        {
          type: "MultiRow",
          label: "Address ",
          mainKey: "address",
        },

        {
          type: "Select",
          label: "Status *",
          mainKey: "status",
          OptionMainKey: "Statuses",
          menuKey: null,
          formatValue: (val) => val,
        },

        {
          type: "MultiRow",
          label: "Remarks ",
          mainKey: "remarks",
        },
      ],
      DataFetchers: [
        { name: "Countries", Type: "Fetch", link: "/api/general/countries" },
        {
          name: "States",
          Type: "FetchDepend",
          dependKey: "country_id",
          link: "/api/general/countries/${country_id}/states/v1",
        },
        {
          name: "Cities",
          Type: "FetchDepend",
          dependKey: "state_id",
          link: "/api/general/countries/${country_id}/states/${state_id}/cities",
        },
        {
          name: "Statuses",
          Type: "NoFetch",
          Options: ["Active", "Inactive"],
        },
      ],
    },
  },
  {
    route: "/companies",
    ListProps: {
      title: "Companies",
      apiEndpoint: "/api/ems/companies",
      filteredApiEndpoint: "/api/ems/companies/filtered",
      filterModule: "ems_companies",
      useMetadataFilters: true,
      addRoute: "/companies",
      editRouteBase: "/companies/edit",
      tablePrimaryKey: "organization_ems_company_id",
      tableName: "ems_companies",
      datagridKey: "ems_companies_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "company_name",
        "industry",
        "contact_person",
        "email",
        "phone",
        "address",
        "status",
      ],
      config: {
        sort: { field: "company_name", direction: "asc" },
        defaultVisibleColumns: [
          "company_name",
          "industry",
          "contact_person",
          "email",
          "phone",
          "status",
        ],
        columnLabels: {
          company_name: "Company Name",
          industry: "Industry",
          contact_person: "Contact Person",
          email: "Email",
          phone: "Phone",
          address: "Address",
          status: "Status",
        },
        columnWidths: {
          company_name: 220,
          industry: 160,
          contact_person: 180,
          email: 220,
          phone: 140,
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => ({
          id: item.organization_ems_company_id,

          company_name: item.company_name || "-",
          industry: item.industry || "-",
          contact_person: item.contact_person || "-",
          email: item.email || "-",
          phone: item.phone || "-",
          address: item.address || "-",
          status: item.status || "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          company_name,
          industry,
          contact_person,
          email,
          phone,
          address,
          status,
        } = formData;

        if (!organization_id) {
          errors.organization_id = "Organization is required.";
        } else if (isNaN(Number(organization_id))) {
          errors.organization_id = "Organization ID must be a valid number.";
        }

        // if (organization_entity_id && isNaN(Number(organization_entity_id))) {
        //   errors.organization_entity_id = "Entity ID must be a valid number.";
        // }

        if (!company_name || company_name.trim() === "") {
          errors.company_name = "Company name is required.";
        } else if (company_name.length > 150) {
          errors.company_name = "Company name cannot exceed 150 characters.";
        }

        if (industry && industry.length > 100) {
          errors.industry = "Industry cannot exceed 150 characters.";
        }

        if (contact_person && contact_person.length > 100) {
          errors.contact_person =
            "Contact person cannot exceed 100 characters.";
        }

        if (email) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(email)) {
            errors.email = "Invalid email format.";
          } else if (email.length > 100) {
            errors.email = "Email cannot exceed 100 characters.";
          }
        }

        if (phone && phone.length > 20) {
          errors.phone = "Phone number cannot exceed 20 characters.";
        }

        if (address && address.length > 255) {
          errors.address = "Address cannot exceed 255 characters.";
        }

        const validStatuses = ["Active", "Inactive"];
        if (!status) {
          errors.status = "Status is required.";
        } else if (!validStatuses.includes(status)) {
          errors.status = "Status must be Active or Inactive.";
        }

        // ✅ Finalize
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/companies",
      initialData: {
        organization_entity_id: "",
        company_name: "",
        industry: "",
        contact_person: "",
        email: "",
        phone: "",
        address: "",
        status: "",
      },
      successMessages: {
        add: "Company added!",
        update: "Company updated!",
      },
      redirectPath: "/companies",
      headerProps: {
        addMessage: "Add Company",
        updateMessage: "Update Company",
        homeLink: "/companies",
        homeText: "Company",
      },
      fieldGridLayout: {
        rows: [
          {
            variant: "twoEqual",
            mainKeys: ["company_name", "industry"],
          },
          {
            variant: "twoEqual",
            mainKeys: ["contact_person", "phone"],
          },
          { variant: "full", mainKeys: ["email"] },
          { variant: "full", mainKeys: ["address"] },
          { variant: "full", mainKeys: ["status"] },
        ],
        sections: [],
      },
      Inputs: [
        {
          type: "Text",
          label: "Company Name *",
          mainKey: "company_name",
        },
        {
          type: "Text",
          label: "Industry ",
          mainKey: "industry",
        },
        {
          type: "Text",
          label: "Contact Person ",
          mainKey: "contact_person",
        },
        {
          type: "Text",
          label: "Email ",
          mainKey: "email",
        },
        {
          type: "Text",
          label: "Phone ",
          mainKey: "phone",
        },
        {
          type: "MultiRow",
          label: "Address ",
          mainKey: "address",
        },
        {
          type: "Select",
          label: "Status *",
          mainKey: "status",
          OptionMainKey: "Statuses",
          menuKey: null,
          formatValue: (val) => val,
        },
      ],
      DataFetchers: [
        {
          name: "Statuses",
          Type: "NoFetch",
          Options: ["Active", "Inactive"],
        },
      ],
    },
  },

  {
    route: "/admissions",
    ListProps: {
      title: "Admissions",
      apiEndpoint: "/api/ems/admissions",
      filteredApiEndpoint: "/api/ems/admissions/filtered",
      filterModule: "ems_admissions",
      useMetadataFilters: true,
      addRoute: "/admissions",
      editRouteBase: "/admissions/edit",
      tablePrimaryKey: "organization_ems_admission_id",
      tableName: "ems_admissions",
      datagridKey: "ems_admissions_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "admission_number",
        "admission_date",
        "student_name",
        "training_program",
        "fee_inr",
        "currency_code",
        "net_fee_amount_student_currency",
        "admission_status",
        "organization_ems_student_id",
        "training_program_id",
        "organization_ems_lead_id",
        "net_fee_amount",
        "installment_count",
        "preferred_study_slot",
        "duration_track",
        "duration_in_months",
        "duration_in_hours",
      ],
      config: {
        sort: { field: "admission_date", direction: "desc" },
        defaultVisibleColumns: [
          "admission_number",
          "admission_date",
          "student_name",
          "training_program",
          "fee_inr",
          "currency_code",
          "net_fee_amount_student_currency",
        ],
        columnLabels: {
          admission_number: "Adm. no.",
          admission_date: "Admission Date",
          student_name: "Student Name",
          training_program: "Training Program",
          fee_inr: "Fee (INR)",
          currency_code: "Currency",
          net_fee_amount_student_currency: "Net Fee (Currency)",
          duration_track: "Duration",
          duration_in_months: "Duration (in months)",
          duration_in_hours: "Duration (in hours)",
        },
        columnWidths: {
          admission_number: 96,
          student_name: 300,
          training_program: 320,
          fee_inr: 128,
          currency_code: 72,
          net_fee_amount_student_currency: 140,
          duration_track: 140,
          duration_in_months: 160,
          duration_in_hours: 160,
        },
        admissionPeriodOptions: [
          { value: "this_week", label: "This Week" },
          { value: "last_week", label: "Last Week" },
          { value: "next_week", label: "Next Week" },
          { value: "this_month", label: "This Month" },
          { value: "last_month", label: "Last Month" },
          { value: "next_month", label: "Next Month" },
          { value: "this_year", label: "This Year" },
          { value: "last_year", label: "Last Year" },
          { value: "next_year", label: "Next Year" },
        ],
      },
      mapResponseToRows: (data) =>
        data.map((item) => {
          const trainingProgram =
            item?.training_program ?? item?.trainingProgram ?? null;
          return {
            id: item.organization_ems_admission_id,
            organization_ems_admission_id: item.organization_ems_admission_id,
            organization_ems_student_id:
              item?.organization_ems_student_id ??
              item?.student?.organization_ems_student_id ??
              null,
            student_id_code: item?.student?.student_id || "",
            student_name:
              [item?.student?.first_name, item?.student?.middle_name, item?.student?.last_name]
                .filter((n) => n && String(n).trim() !== "")
                .join(" ")
                .trim() || "-",
            training_program:
              trainingProgram?.training_program_name || "-",
            training_program_id:
              item?.training_program_id ??
              trainingProgram?.organization_ems_training_program_id ??
              null,
            training_program_category:
              trainingProgram?.category?.training_program_category_name || "-",
            student: item?.student ?? null,
            training_program_obj: trainingProgram,

            admission_number: item.admission_number || "-",
            admission_date: FormatDateV1(item.admission_date) || "-",
            admission_status: item.admission_status || "-",
            total_fee_amount: item.total_fee_amount || "-",
            discount_amount: item.discount_amount || "-",
            discount_reason: item.discount_reason || "-",
            fee_inr: item.net_fee_amount ?? "-",
            net_fee_amount: item.net_fee_amount || "-",
            installment_count: item.installment_count || "-",
            currency_code: item.currency_code || "-",
            net_fee_amount_student_currency:
              item.net_fee_amount_student_currency != null &&
              item.net_fee_amount_student_currency !== ""
                ? item.net_fee_amount_student_currency
                : "-",
            preferred_study_slot: item.preferred_study_slot || "-",
            preferred_study_times: item.preferred_study_times || "-",
            remarks: item.remarks || "-",
            duration_track: item.duration_track || "-",
            duration_in_months:
              item.duration_in_months != null && item.duration_in_months !== ""
                ? item.duration_in_months
                : "-",
            duration_in_hours:
              item.duration_in_hours != null && item.duration_in_hours !== ""
                ? item.duration_in_hours
                : "-",
          };
        }),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          organization_ems_student_id,
          training_program_id,
          admission_number,
          admission_date,
          total_fee_amount,
          discount_amount,
          net_fee_amount,
          total_fee_amount_student_currency,
          discount_amount_student_currency,
          discount_reason_student_currency,
          net_fee_amount_student_currency,
          installment_count,
          fee_installments,
          currency_code,
          preferred_study_slot,
          preferred_study_times,
          wants_placement,
          placement_required_enabled,
          remarks,
          duration_track,
          duration_in_months,
          duration_in_hours,
        } = formData;

        // --- Required + Integer validations ---
        if (!organization_id)
          errors.organization_id = "Organization is required.";
        else if (isNaN(organization_id))
          errors.organization_id = "Organization ID must be a number.";

        if (!organization_ems_student_id)
          errors.organization_ems_student_id = "Student is required.";
        else if (isNaN(organization_ems_student_id))
          errors.organization_ems_student_id = "Student ID must be a number.";

        if (!training_program_id)
          errors.training_program_id = "Training program is required.";
        else if (isNaN(training_program_id))
          errors.training_program_id = "Program ID must be a number.";

        // --- Admission Number ---
        if (!admission_number)
          errors.admission_number = "Admission number is required.";
        else if (admission_number.length > 50)
          errors.admission_number = "Maximum 50 characters allowed.";

        // --- Admission Date ---
        if (!admission_date)
          errors.admission_date = "Admission date is required.";

        // --- Fee Validations ---
        if (!total_fee_amount && total_fee_amount !== 0)
          errors.total_fee_amount = "Total fee amount is required.";
        else if (isNaN(total_fee_amount) || total_fee_amount < 0)
          errors.total_fee_amount = "Must be a valid non-negative number.";

        if (discount_amount && (isNaN(discount_amount) || discount_amount < 0))
          errors.discount_amount =
            "Discount amount must be a valid non-negative number.";

        const coupon = String(formData?.offer_code || "").trim();
        if (coupon && formData?._offer_applied !== true) {
          errors.offer_code =
            "This coupon is not valid for this student or lead, or is still being checked.";
        }

        if (!net_fee_amount && net_fee_amount !== 0)
          errors.net_fee_amount = "Net fee amount is required.";
        else if (isNaN(net_fee_amount) || net_fee_amount < 0)
          errors.net_fee_amount = "Must be a valid non-negative number.";

        if (
          total_fee_amount_student_currency !== "" &&
          total_fee_amount_student_currency !== null &&
          total_fee_amount_student_currency !== undefined &&
          (isNaN(total_fee_amount_student_currency) ||
            total_fee_amount_student_currency < 0)
        ) {
          errors.total_fee_amount_student_currency =
            "Must be a valid non-negative number.";
        }

        if (
          discount_amount_student_currency !== "" &&
          discount_amount_student_currency !== null &&
          discount_amount_student_currency !== undefined &&
          (isNaN(discount_amount_student_currency) ||
            discount_amount_student_currency < 0)
        ) {
          errors.discount_amount_student_currency =
            "Discount amount must be a valid non-negative number.";
        }

        if (
          discount_reason_student_currency &&
          discount_reason_student_currency.length > 255
        ) {
          errors.discount_reason_student_currency =
            "Maximum 255 characters allowed.";
        }

        if (
          net_fee_amount_student_currency !== "" &&
          net_fee_amount_student_currency !== null &&
          net_fee_amount_student_currency !== undefined &&
          (isNaN(net_fee_amount_student_currency) ||
            net_fee_amount_student_currency < 0)
        ) {
          errors.net_fee_amount_student_currency =
            "Must be a valid non-negative number.";
        }

        // --- Installment Count ---
        if (!installment_count)
          errors.installment_count = "Installment count is required.";
        else if (isNaN(installment_count) || installment_count < 1)
          errors.installment_count = "Installment count must be at least 1.";
        else if (Number(installment_count) > 24)
          errors.installment_count = "Installment count cannot be more than 24.";

        if (installment_count && Array.isArray(fee_installments)) {
          if (fee_installments.length !== Number(installment_count)) {
            errors.fee_installments =
              "Installment table row count must match installment count.";
          } else if (
            fee_installments.some(
              (row) =>
                row?.fee_instalment_inr === "" ||
                row?.fee_instalment_inr === null ||
                row?.fee_instalment_inr === undefined ||
                isNaN(Number(row?.fee_instalment_inr)) ||
                Number(row?.fee_instalment_inr) < 0 ||
                !row?.currency_code_inr ||
                !row?.due_date ||
                !row?.student_currency_code
            )
          ) {
            errors.fee_installments =
              "Fill required fields in all installment rows.";
          } else {
            const netInr = Number(net_fee_amount || 0);
            const netStudent = Number(net_fee_amount_student_currency || 0);
            const amountError = validateFeeInstallmentAmounts({
              installments: fee_installments,
              netInr,
              netStudent,
            });
            if (amountError) {
              errors.fee_installments = amountError;
            } else {
              const dueDateOrderError =
                getInstallmentDueDateOrderError(fee_installments);
              if (dueDateOrderError) {
                errors.fee_installments = dueDateOrderError;
              }
            }
          }
        }

        // --- Currency ---
        if (currency_code && currency_code.length > 10)
          errors.currency_code = "Currency code must not exceed 10 characters.";

        // --- Preferred Study Slot ---
        const validSlots = ["Morning", "Afternoon", "Evening", "Weekend"];
        if (preferred_study_slot && !validSlots.includes(preferred_study_slot))
          errors.preferred_study_slot = "Invalid study slot.";

        if (preferred_study_times && preferred_study_times.length > 100)
          errors.preferred_study_times = "Maximum 100 characters allowed.";

        if (placement_required_enabled) {
          if (!wants_placement || !["Yes", "No"].includes(String(wants_placement))) {
            errors.wants_placement =
              "Please choose whether placement assistance is required.";
          }
        }

        const track = String(duration_track || "").trim();
        if (track && !["Normal Track", "Fast Track"].includes(track)) {
          errors.duration_track = "Select Normal Track or Fast Track.";
        }
        if (track === "Fast Track") {
          const months = Number(duration_in_months);
          const hours = Number(duration_in_hours);
          if (
            duration_in_months === "" ||
            duration_in_months === null ||
            duration_in_months === undefined ||
            Number.isNaN(months) ||
            months <= 0
          ) {
            errors.duration_in_months =
              "Duration in months is required for Fast Track.";
          }
          if (
            duration_in_hours === "" ||
            duration_in_hours === null ||
            duration_in_hours === undefined ||
            Number.isNaN(hours) ||
            hours < 1 ||
            !Number.isInteger(hours)
          ) {
            errors.duration_in_hours =
              "Duration in hours is required for Fast Track.";
          }
        }

        if (remarks && remarks.trim().length > 500)
          errors.remarks = "Remarks too long (max 500 characters).";

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },
      baseUrl: "/api/ems/admissions",
      initialData: {
        organization_entity_id: "",
        organization_ems_student_id: "",
        training_program_id: "",
        admission_number: "",
        admission_date: "",
        total_fee_amount: "",
        discount_amount: "",
        discount_reason: "",
        net_fee_amount: "",
        total_fee_amount_student_currency: "",
        discount_amount_student_currency: "",
        discount_reason_student_currency: "",
        net_fee_amount_student_currency: "",
        installment_count: "",
        fee_installments: [],
        currency_code: "",
        preferred_study_slot: "",
        preferred_study_times: "",
        wants_placement: "",
        placement_required_enabled: false,
        remarks: "",
        duration_track: "",
        duration_in_months: "",
        duration_in_hours: "",
        offer_code: "",
        _offer_applied: false,
        _coupon_message: "",
      },
      successMessages: {
        add: "Admission added!",
        update: "Admission updated!",
      },
      redirectPath: "/admissions",
      headerProps: {
        addMessage: "Add Admission",
        updateMessage: "Update Admission",
        homeLink: "/admissions",
        homeText: "Admission",
      },
      fieldGridLayout: {
        rows: [
          {
            variant: "twoEqual",
            mainKeys: ["admission_number", "admission_date"],
          },
          {
            variant: "twoEqual",
            mainKeys: [
              "organization_ems_student_id",
              "training_program_id",
            ],
          },
          { variant: "full", mainKeys: ["offer_code"] },
          { variant: "full", mainKeys: ["duration_track"] },
          {
            variant: "twoEqual",
            mainKeys: ["duration_in_months", "duration_in_hours"],
          },
          {
            variant: "threeEqual",
            mainKeys: ["total_fee_amount", "discount_amount", "net_fee_amount"],
          },
          { variant: "full", mainKeys: ["currency_code"] },
          {
            variant: "threeEqual",
            mainKeys: [
              "total_fee_amount_student_currency",
              "discount_amount_student_currency",
              "net_fee_amount_student_currency",
            ],
          },
          { variant: "full", mainKeys: ["discount_reason_student_currency"] },
          { variant: "full", mainKeys: ["installment_count"] },
          { variant: "full", mainKeys: ["fee_installments"] },
          {
            variant: "twoEqual",
            mainKeys: ["preferred_study_slot", "preferred_study_times"],
          },
          { variant: "full", mainKeys: ["wants_placement"] },
          { variant: "full", mainKeys: ["remarks"] },
        ],
        sections: [
          { beforeRow: 2, title: "Duration", sx: { mb: 2.5 } },
          { beforeRow: 4, title: "Fee", sx: { mb: 2.5 } },
          { beforeRow: 8, title: "Fee Installments", sx: { mb: 2.5 } },
        ],
      },
      Inputs: [
        {
          type: "Text",
          label: "Admission Number *",
          mainKey: "admission_number",
        },
        {
          type: "Date",
          label: "Admission Date *",
          mainKey: "admission_date",
        },
        {
          type: "Select",
          label: "Student *",
          mainKey: "organization_ems_student_id",
          OptionMainKey: "Students",
          menuKey: "organization_ems_student_id",
          formatValue: (val) =>
            `${[val.first_name, val.middle_name, val.last_name].filter(Boolean).join(" ")} (${val.student_id || ""})`,
        },
        {
          type: "Select",
          label: "Training Program *",
          mainKey: "training_program_id",
          OptionMainKey: "TrainingPrograms",
          menuKey: "organization_ems_training_program_id",
          formatValue: (val) => `${val.training_program_name}`,
          disableInEdit: true,
          disableClearable: true,
          selectReadOnly: true,
        },
        {
          type: "Text",
          label: "Coupon code ",
          mainKey: "offer_code",
          disabledWhen: (formData) =>
            !formData?.organization_ems_student_id ||
            !formData?.training_program_id,
          helperText: (formData) => {
            if (formData?._offer_applied && formData?._coupon_message) {
              return formData._coupon_message;
            }
            if (!formData?.organization_ems_student_id || !formData?.training_program_id) {
              return "Select a student and training program, then enter the offer code.";
            }
            return "If this offer is for this student or lead, the discount is applied to the fee.";
          },
        },
        {
          type: "CheckboxGroup",
          label: "",
          mainKey: "duration_track",
          OptionMainKey: "DurationTrack",
          menuKey: null,
          exclusive: true,
          formatValue: (val) => val,
        },
        {
          type: "Number",
          label: "Duration (in months)",
          mainKey: "duration_in_months",
          visibleWhen: (formData) =>
            String(formData?.duration_track || "").trim() === "Fast Track",
        },
        {
          type: "Number",
          label: "Duration (in hours)",
          mainKey: "duration_in_hours",
          visibleWhen: (formData) =>
            String(formData?.duration_track || "").trim() === "Fast Track",
        },
        {
          type: "Number",
          label: "Total Fee Amount (INR) *",
          mainKey: "total_fee_amount",
          disabledWhen: (formData) => Boolean(formData?._program_has_inr_fee),
        },
        {
          type: "Number",
          label: "Discount Amount (INR) ",
          mainKey: "discount_amount",
          disabledWhen: (formData) => Boolean(formData?._offer_applied),
        },
        {
          type: "Number",
          label: "Net Fee Amount (INR) *",
          mainKey: "net_fee_amount",
          disabledWhen: (formData) => Boolean(formData?._program_has_inr_fee),
        },
        {
          type: "Select",
          label: "Student Currency Code ",
          mainKey: "currency_code",
          OptionMainKey: "Currencies",
          menuKey: "currency_code",
          formatValue: (val) =>
            `${val?.currency_code || ""}${val?.currency_name ? ` - ${val.currency_name}` : ""}`,
        },
        {
          type: "Number",
          label: "Total Fee Amount ",
          getLabel: (formData) => {
            const code = String(formData?.currency_code ?? "").trim();
            return code
              ? `Total Fee Amount (${code}) `
              : "Total Fee Amount ";
          },
          mainKey: "total_fee_amount_student_currency",
          disabledWhen: (formData) =>
            Boolean(formData?._program_has_student_currency_fee),
        },
        {
          type: "Number",
          label: "Discount Amount ",
          getLabel: (formData) => {
            const code = String(formData?.currency_code ?? "").trim();
            return code
              ? `Discount Amount (${code}) `
              : "Discount Amount ";
          },
          mainKey: "discount_amount_student_currency",
          disabledWhen: () => true,
        },
        {
          type: "Text",
          label: "Discount Reason ",
          getLabel: (formData) => {
            const code = String(formData?.currency_code ?? "").trim();
            return code
              ? `Discount Reason (${code}) `
              : "Discount Reason ";
          },
          mainKey: "discount_reason_student_currency",
          disabledWhen: (formData) => {
            const v = formData?.discount_amount_student_currency;
            if (v === "" || v === null || v === undefined) return true;
            const n = Number(v);
            return !Number.isFinite(n) || n <= 0;
          },
        },
        {
          type: "Number",
          label: "Net Fee Amount ",
          getLabel: (formData) => {
            const code = String(formData?.currency_code ?? "").trim();
            return code
              ? `Net Fee Amount (${code}) `
              : "Net Fee Amount ";
          },
          mainKey: "net_fee_amount_student_currency",
          disabledWhen: (formData) =>
            Boolean(formData?._program_has_student_currency_fee),
        },
        {
          type: "Number",
          label: "Installment Count *",
          mainKey: "installment_count",
          helperText: "Maximum 24 installments.",
        },
        {
          type: "InstallmentsTable",
          label: "",
          mainKey: "fee_installments",
        },
        {
          type: "Select",
          label: "Preferred Study Slot ",
          mainKey: "preferred_study_slot",
          OptionMainKey: "StudySlot",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "Text",
          label: "Preferred Study Times ",
          mainKey: "preferred_study_times",
        },
        {
          type: "Select",
          label: "Placement assistance required *",
          mainKey: "wants_placement",
          OptionMainKey: "PlacementChoice",
          menuKey: null,
          formatValue: (val) => val,
          visibleWhen: (formData, _mode, selectOptions) => {
            if (formData?.placement_required_enabled) return true;
            const settings = selectOptions?.InstituteSettings?.[0];
            return !!settings?.placement_required_enabled;
          },
        },
        {
          type: "MultiRow",
          label: "Remarks ",
          mainKey: "remarks",
        },
      ],
      DataFetchers: [
        { name: "Students", Type: "Fetch", link: "/api/ems/students" },
        {
          name: "TrainingPrograms",
          Type: "Fetch",
          link: "/api/ems/training-programs",
        },
        {
          name: "Currencies",
          Type: "Fetch",
          link: "/api/ems-currencies",
        },
        {
          name: "StudySlot",
          Type: "NoFetch",
          Options: ["Morning", "Afternoon", "Evening", "Weekend"],
        },
        {
          name: "DurationTrack",
          Type: "NoFetch",
          Options: ["Normal Track", "Fast Track"],
        },
        {
          name: "PlacementChoice",
          Type: "NoFetch",
          Options: ["Yes", "No"],
        },
        {
          name: "InstituteSettings",
          Type: "Fetch",
          link: "/api/ems/institute-settings",
        },
      ],
    },
  },
  {
    route: "/fee-installments",
    ListProps: {
      title: "Fee Installments",
      apiEndpoint: "/api/ems/fee-installments",
      filteredApiEndpoint: "/api/ems/fee-installments/filtered",
      filterModule: "ems_fee_installments",
      useMetadataFilters: true,
      addRoute: "/fee-installments",
      editRouteBase: "/fee-installments/edit",
      tablePrimaryKey: "organization_ems_fee_installment_id",
      tableName: "ems_fee_installments",
      datagridKey: "ems_fee_installments_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "due_date",
        "student",
        "admission",
        "inst_no",
        "fee_inr",
        "curr_code",
        "fee_curr",
        "status",
        "organization_ems_student_id",
        "organization_ems_admission_id",
        "installment_number",
        "fee_instalment_inr",
        "student_currency_code",
        "remarks",
      ],
      config: {
        sort: { field: "due_date", direction: "asc" },
        defaultVisibleColumns: [
          "due_date",
          "student",
          "inst_no",
          "fee_inr",
          "curr_code",
          "fee_curr",
          "status",
        ],
        columnLabels: {
          due_date: "Due Date",
          student: "Student",
          inst_no: "Inst.No",
          fee_inr: "Fee (INR)",
          curr_code: "Currency Code",
          fee_curr: "Fee (Currency Code)",
          status: "Status",
        },
        duePeriodOptions: [
          { value: "this_week", label: "This Week" },
          { value: "last_week", label: "Last Week" },
          { value: "next_week", label: "Next Week" },
          { value: "this_month", label: "This Month" },
          { value: "last_month", label: "Last Month" },
          { value: "next_month", label: "Next Month" },
          { value: "this_year", label: "This Year" },
          { value: "last_year", label: "Last Year" },
          { value: "next_year", label: "Next Year" },
        ],
      },
      mapResponseToRows: (data) =>
        data.map((item) => ({
          id: item.organization_ems_fee_installment_id,
          student_obj: item?.student ?? null,
          admission_obj: item?.admission ?? null,
          // Extra IDs for "Receipt" prefill in Fee Receipts form
          organization_ems_student_id:
            item?.organization_ems_student_id ??
            item?.student?.organization_ems_student_id ??
            item?.student?.student_id ??
            "",
          organization_ems_admission_id:
            item?.organization_ems_admission_id ??
            item?.admission?.organization_ems_admission_id ??
            item?.admission?.admission_id ??
            "",
          organization_ems_fee_installment_id: item.organization_ems_fee_installment_id,
          student:
            [item?.student?.first_name, item?.student?.middle_name, item?.student?.last_name]
              .filter(Boolean)
              .join(" ")
              || item?.student?.student_id
              || item?.organization_ems_student_id
              || "-",
          admission:
            item?.admission?.admission_number
            || item?.admission_number
            || item?.organization_ems_admission_id
            || "-",

          installment_number: item.installment_number || "-",
          // Align list columns with Fee Receipts (inst_no, fee_inr, curr_code, fee_curr)
          inst_no: item.installment_number || "-",
          due_date: FormatDateV1(item.due_date) || "-",
          due_date_raw:
            item.due_date != null && item.due_date !== ""
              ? String(item.due_date).slice(0, 10)
              : "",
          // YYYY-MM-DD for Fee Receipt prefill (payment_date)
          payment_date_prefill:
            item.due_date != null && item.due_date !== ""
              ? String(item.due_date).slice(0, 10)
              : "",
          fee_instalment_inr:
            item.fee_instalment_inr ?? item.amount_due ?? "-",
          fee_inr: item.fee_instalment_inr ?? item.amount_due ?? "-",
          currency_code_inr:
            item.currency_code_inr ?? item.currency_code ?? "-",
          fee_instalment_student_student_currency:
            item.fee_instalment_student_student_currency ?? "-",
          fee_curr: item.fee_instalment_student_student_currency ?? "-",
          student_currency_code: item.student_currency_code ?? "-",
          curr_code: item.student_currency_code ?? "-",
          status: item.status || "-",
          remarks: item.remarks || "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          organization_ems_admission_id,
          organization_ems_student_id,
          installment_number,
          due_date,
          fee_instalment_inr,
          fee_instalment_student_student_currency,
          student_currency_code,
          status,
          remarks,
        } = formData;

        if (!organization_id) {
          errors.organization_id = "Organization is required.";
        } else if (isNaN(Number(organization_id))) {
          errors.organization_id = "Organization ID must be a number.";
        }

        if (organization_entity_id && isNaN(Number(organization_entity_id))) {
          errors.organization_entity_id = "Entity ID must be a number.";
        }

        if (!organization_ems_admission_id) {
          errors.organization_ems_admission_id = "Admission is required.";
        } else if (isNaN(Number(organization_ems_admission_id))) {
          errors.organization_ems_admission_id =
            "Admission ID must be a number.";
        }

        if (!organization_ems_student_id) {
          errors.organization_ems_student_id = "Student is required.";
        } else if (isNaN(Number(organization_ems_student_id))) {
          errors.organization_ems_student_id = "Student ID must be a number.";
        }

        if (!installment_number) {
          errors.installment_number = "Installment number is required.";
        } else if (isNaN(Number(installment_number))) {
          errors.installment_number = "Installment number must be numeric.";
        } else if (Number(installment_number) < 1) {
          errors.installment_number = "Installment number must be at least 1.";
        }

        if (!due_date) {
          errors.due_date = "Due date is required.";
        } else {
          const parsedDate = new Date(due_date);
          if (isNaN(parsedDate.getTime())) {
            errors.due_date = "Invalid due date format.";
          }
        }

        const existingInstallments = Array.isArray(formData?.existing_fee_installments)
          ? formData.existing_fee_installments
          : [];

        const isAddMode = !formData?.organization_ems_fee_installment_id;
        if (isAddMode && organization_ems_admission_id) {
          const admissionFromForm = {
            organization_ems_admission_id,
            installment_count: formData?._admission_installment_count,
            net_fee_amount: formData?._admission_net_fee_inr,
          };
          const capacityError = getFeeInstallmentCapacityError({
            admission: admissionFromForm,
            installmentNumber: installment_number,
            existingInstallments,
            forCreate: true,
          });
          if (capacityError) {
            errors.organization_ems_admission_id = capacityError;
          }
        } else if (
          !isAddMode &&
          formData?._admission_installment_count &&
          installment_number
        ) {
          const maxCount = Number(formData._admission_installment_count);
          if (Number(installment_number) > maxCount) {
            errors.installment_number = `Installment number cannot exceed ${maxCount} for this admission.`;
          }
        }

        if (
          !errors.due_date &&
          due_date &&
          installment_number &&
          organization_ems_student_id
        ) {
          const dueDateOrderError = getStandaloneFeeInstallmentDueDateError({
            installmentNumber: installment_number,
            dueDate: due_date,
            existingInstallments,
            excludeInstallmentId: formData?.organization_ems_fee_installment_id,
          });
          if (dueDateOrderError) {
            errors.due_date = dueDateOrderError;
          }
        }

        if (
          fee_instalment_inr === "" ||
          fee_instalment_inr === null ||
          fee_instalment_inr === undefined
        ) {
          errors.fee_instalment_inr = "Fee installment (INR) is required.";
        } else if (isNaN(Number(fee_instalment_inr))) {
          errors.fee_instalment_inr = "Fee installment (INR) must be numeric.";
        } else if (Number(fee_instalment_inr) < 0) {
          errors.fee_instalment_inr = "Fee installment (INR) cannot be negative.";
        } else if (
          formData?._admission_installment_count &&
          installment_number
        ) {
          const expectedInr = getExpectedInstallmentAmount(
            Number(formData._admission_net_fee_inr || 0),
            Number(formData._admission_installment_count),
            Number(installment_number)
          );
          if (
            expectedInr !== null &&
            !moneyMatches(fee_instalment_inr, expectedInr)
          ) {
            errors.fee_instalment_inr = `Fee installment (INR) must be ${expectedInr} based on the admission net fee and installment count.`;
          }
        }

        if (
          fee_instalment_student_student_currency !== "" &&
          fee_instalment_student_student_currency !== null &&
          fee_instalment_student_student_currency !== undefined
        ) {
          if (isNaN(Number(fee_instalment_student_student_currency))) {
            errors.fee_instalment_student_student_currency =
              "Student currency installment must be numeric.";
          } else if (Number(fee_instalment_student_student_currency) < 0) {
            errors.fee_instalment_student_student_currency =
              "Student currency installment cannot be negative.";
          } else if (
            formData?._admission_installment_count &&
            installment_number &&
            Number(formData?._admission_net_fee_student || 0) > 0
          ) {
            const expectedStudent = getExpectedInstallmentAmount(
              Number(formData._admission_net_fee_student || 0),
              Number(formData._admission_installment_count),
              Number(installment_number)
            );
            if (
              expectedStudent !== null &&
              !moneyMatches(
                fee_instalment_student_student_currency,
                expectedStudent
              )
            ) {
              errors.fee_instalment_student_student_currency = `Student currency installment must be ${expectedStudent} based on the admission net fee and installment count.`;
            }
          }
        }

        if (
          student_currency_code &&
          student_currency_code.trim().length > 10
        ) {
          errors.student_currency_code =
            "Student currency code cannot exceed 10 characters.";
        }

        const validStatuses = ["Pending", "Paid", "Overdue", "Cancelled"];
        if (!status) {
          errors.status = "Status is required.";
        } else if (!validStatuses.includes(status)) {
          errors.status =
            "Status must be one of: Pending, Paid, Overdue, or Cancelled.";
        } else {
          const statusChangeError = validateFeeInstallmentStatusChange({
            status,
            initialStatus: formData?._initial_status,
            mode: formData?._form_mode || (formData?.organization_ems_fee_installment_id ? "edit" : "add"),
            installmentId: formData?.organization_ems_fee_installment_id,
            paidInstallmentIds: formData?._paid_fee_receipt_installment_ids,
          });
          if (statusChangeError) {
            errors.status = statusChangeError;
          }
        }

        if (remarks && remarks.trim().length > 500) {
          errors.remarks = "Remarks too long (max 500 characters).";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/fee-installments",
      initialData: {
        organization_entity_id: "",
        organization_ems_admission_id: "",
        organization_ems_student_id: "",
        installment_number: "",
        due_date: "",
        fee_instalment_inr: "",
        currency_code_inr: "INR",
        fee_instalment_student_student_currency: "",
        student_currency_code: "",
        status: "",
        remarks: "",
      },
      successMessages: {
        add: "Fee Installment added!",
        update: "Fee Installment updated!",
      },
      redirectPath: "/fee-installments",
      headerProps: {
        addMessage: "Add Fee Installment",
        updateMessage: "Update Fee Installment",
        homeLink: "/fee-installments",
        homeText: "Fee Installment",
      },
      fieldGridLayout: {
        rows: [
          {
            variant: "twoEqual",
            mainKeys: ["organization_ems_student_id", "organization_ems_admission_id"],
          },
          { variant: "full", mainKeys: ["installment_number"] },
          { variant: "twoEqual", mainKeys: ["due_date", "fee_instalment_inr"] },
          {
            variant: "twoEqual",
            mainKeys: [
              "student_currency_code",
              "fee_instalment_student_student_currency",
            ],
          },
          { variant: "full", mainKeys: ["status"] },
          { variant: "full", mainKeys: ["remarks"] },
        ],
      },
      Inputs: [
        {
          type: "Select",
          label: "Admission *",
          mainKey: "organization_ems_admission_id",
          OptionMainKey: "Admissions",
          menuKey: "organization_ems_admission_id",
          formatValue: (val) => `${val.admission_number}`,
        },
        {
          type: "Select",
          label: "Student *",
          mainKey: "organization_ems_student_id",
          OptionMainKey: "Students",
          menuKey: "organization_ems_student_id",
          formatValue: (val) =>
            `${[val.first_name, val.middle_name, val.last_name].filter(Boolean).join(" ")} (${val.student_id || ""})`,
        },

        {
          type: "Number",
          label: "Installment Number *",
          mainKey: "installment_number",
        },
        {
          type: "Date",
          label: "Due Date *",
          mainKey: "due_date",
        },
        {
          type: "Number",
          label: "Fee Instalment (INR) *",
          mainKey: "fee_instalment_inr",
          disabledWhen: (formData) => !!formData?.organization_ems_admission_id,
        },
        {
          type: "Select",
          label: "Student Currency Code ",
          mainKey: "student_currency_code",
          OptionMainKey: "Currencies",
          menuKey: "currency_code",
          formatValue: (val) =>
            `${val?.currency_code || ""}${val?.currency_name ? ` - ${val.currency_name}` : ""}`,
        },
        {
          type: "Number",
          label: "Fee Instalment (Student Currency) ",
          mainKey: "fee_instalment_student_student_currency",
          disabledWhen: (formData) => !!formData?.organization_ems_admission_id,
        },
        {
          type: "Select",
          label: "Status *",
          mainKey: "status",
          OptionMainKey: "Statuses",
          menuKey: null,
          formatValue: (val) => val,
        },

        {
          type: "MultiRow",
          label: "Remarks ",
          mainKey: "remarks",
        },
      ],
      DataFetchers: [
        { name: "Admissions", Type: "Fetch", link: "/api/ems/admissions" },
        { name: "Students", Type: "Fetch", link: "/api/ems/students" },
        { name: "FeeInstallments", Type: "Fetch", link: "/api/ems/fee-installments" },
        { name: "StudentFees", Type: "Fetch", link: "/api/ems/students-fees" },
        { name: "Currencies", Type: "Fetch", link: "/api/ems-currencies" },

        {
          name: "Statuses",
          Type: "NoFetch",
          Options: ["Pending", "Paid", "Overdue", "Cancelled"],
        },
      ],
    },
  },

  {
    route: "/classes-attendance",
    ListProps: {
      title: "Classes Attendance",
      apiEndpoint: "/api/ems/classes-attendance",
      filteredApiEndpoint: "/api/ems/classes-attendance/filtered",
      filterModule: "ems_class_attendance",
      useMetadataFilters: true,
      addRoute: "/classes-attendance",
      editRouteBase: "/classes-attendance/edit",
      tablePrimaryKey: "organization_ems_class_attendance_id",
      tableName: "ems_class_attendance",
      datagridKey: "ems_class_attendance_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "batch_class",
        "batch_class_record",
        "student",
        "attendance_date",
        "attendance_status",
        "remarks",
      ],
      config: {
        sort: { field: "attendance_date", direction: "desc" },
        defaultVisibleColumns: [
          "batch_class",
          "batch_class_record",
          "student",
          "attendance_date",
          "attendance_status",
          "remarks",
        ],
        columnLabels: {
          batch_class: "Batch Class",
          batch_class_record: "Batch Class Record",
          student: "Student",
          attendance_date: "Attendance Date",
          attendance_status: "Attendance Status",
          remarks: "Remarks",
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => ({
          id: item.organization_ems_class_attendance_id,
          ...linkedTrainerIdentity(
            item?.batchClassRecord?.record_trainers ??
              item?.batchClassRecord?.recordTrainers ??
              item?.batch_class_record?.record_trainers ??
              item?.batch_class_record?.recordTrainers,
            item?.batchClass?.class_trainers ??
              item?.batchClass?.classTrainers ??
              item?.batch_class?.class_trainers,
            item?.batchClass?.batch?.batch_trainers ??
              item?.batch_class?.batch?.batch_trainers ??
              item?.batchClassRecord?.batch?.batch_trainers ??
              item?.batch_class_record?.batch?.batch_trainers,
            item?.batchClass?.trainer_employee_id || item?.batchClass?.trainer
              ? [item.batchClass]
              : item?.batch_class?.trainer_employee_id || item?.batch_class?.trainer
                ? [item.batch_class]
                : []
          ),
          trainer_employee_id:
            item?.batchClass?.trainer_employee_id ??
            item?.batch_class?.trainer_employee_id ??
            item?.batchClassRecord?.batchClass?.trainer_employee_id ??
            item?.batch_class_record?.batch_class?.trainer_employee_id ??
            item?.batchClass?.trainer?.organization_user_id ??
            item?.batch_class?.trainer?.organization_user_id ??
            item?.batchClassRecord?.batchClass?.trainer?.organization_user_id ??
            item?.batch_class_record?.batch_class?.trainer?.organization_user_id ??
            "",
          trainer_application_user_id:
            getApplicationUserIdFromTrainerObject(
              item?.batchClass?.trainer ??
                item?.batch_class?.trainer ??
                item?.batchClassRecord?.batchClass?.trainer ??
                item?.batch_class_record?.batch_class?.trainer
            ) || "",
          student:
            `${item?.student?.first_name || ""} ${item?.student?.last_name || ""}`.trim() || "-",
          organization_ems_batch_class_id:
            item?.organization_ems_batch_class_id ??
            item?.batchClass?.organization_ems_batch_class_id ??
            item?.batch_class?.organization_ems_batch_class_id ??
            "",
          organization_ems_student_id:
            item?.organization_ems_student_id ??
            item?.student?.organization_ems_student_id ??
            "",
          organization_ems_batch_class_record_id:
            item?.organization_ems_batch_class_record_id ??
            item?.batchClassRecord?.organization_ems_batch_class_record_id ??
            item?.batch_class_record?.organization_ems_batch_class_record_id ??
            "",
          batch_class: item?.batchClass?.topic || item?.batch_class?.topic || "-",
          batch_class_record:
            item?.batch_class_record?.topic ??
            item?.class_record?.topic ??
            item?.batchClassRecord?.topic ??
            item?.batchClass?.topic ??
            item?.batch_class?.topic ??
            (item?.organization_ems_batch_class_record_id
              ? `Record #${item.organization_ems_batch_class_record_id}`
              : "-"),
          attendance_date: FormatDateV1(item?.attendance_date) || "-",

          attendance_status: item.attendance_status || "-",

          remarks: item.remarks || "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          organization_ems_batch_class_record_id,
          organization_ems_batch_class_id,
          organization_ems_student_id,
          attendance_date,
          attendance_status,

          remarks,
        } = formData;

        if (!organization_id) {
          errors.organization_id = "Organization is required.";
        } else if (isNaN(Number(organization_id))) {
          errors.organization_id = "Organization ID must be a number.";
        }

        if (organization_entity_id && isNaN(Number(organization_entity_id))) {
          errors.organization_entity_id = "Entity ID must be a number.";
        }

        if (!organization_ems_batch_class_id) {
          errors.organization_ems_batch_class_id = "Batch Class is required.";
        } else if (isNaN(Number(organization_ems_batch_class_id))) {
          errors.organization_ems_batch_class_id =
            "Batch Class ID must be a number.";
        }

        if (
          organization_ems_batch_class_record_id !== "" &&
          organization_ems_batch_class_record_id !== null &&
          organization_ems_batch_class_record_id !== undefined &&
          isNaN(Number(organization_ems_batch_class_record_id))
        ) {
          errors.organization_ems_batch_class_record_id =
            "Batch Class Record ID must be a number.";
        }

        if (!organization_ems_student_id) {
          errors.organization_ems_student_id = "Student is required.";
        } else if (isNaN(Number(organization_ems_student_id))) {
          errors.organization_ems_student_id = "Student ID must be a number.";
        }

        if (!attendance_date) {
          errors.attendance_date = "Attendance date is required.";
        } else {
          const d = new Date(attendance_date);
          if (Number.isNaN(d.getTime())) {
            errors.attendance_date = "Attendance date is invalid.";
          } else {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const att = new Date(d);
            att.setHours(0, 0, 0, 0);
            if (att.getTime() > today.getTime()) {
              errors.attendance_date =
                "Attendance date cannot be in the future.";
            }
          }
        }

        const validStatuses = ["Present", "Absent"];
        if (!attendance_status)
          errors.attendance_status = "Attendance status is required.";
        else if (!validStatuses.includes(attendance_status))
          errors.attendance_status = "Invalid attendance status.";

        if (remarks && remarks.trim().length > 500) {
          errors.remarks = "Remarks too long (max 500 characters).";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/classes-attendance",
      initialData: {
        organization_entity_id: "",
        organization_ems_batch_class_record_id: "",
        organization_ems_batch_class_id: "",
        organization_ems_student_id: "",
        attendance_date: "",
        attendance_status: "",
        remarks: "",
      },
      successMessages: {
        add: "Class Attendance added!",
        update: "Class Attendance updated!",
      },
      redirectPath: "/classes-attendance",
      headerProps: {
        addMessage: "Add Class Attendance",
        updateMessage: "Update Class Attendance",
        homeLink: "/classes-attendance",
        homeText: "Class Attendance",
      },
      Inputs: [
        {
          type: "Select",
          label: "Batch Class *",
          mainKey: "organization_ems_batch_class_id",
          OptionMainKey: "BatchClasses",
          menuKey: "organization_ems_batch_class_id",
          formatValue: (val) => {
            if (!val) return "-";
            const batchName =
              val?.batch?.batch_code ??
              val?.batch?.batch_name ??
              val?.batch_name ??
              "";
            const trainerName = batchClassTrainerLabel(val);
            const topic = String(val?.topic ?? "").trim();

            const parts = [
              batchName ? `Batch: ${batchName}` : "",
              trainerName ? `Trainer: ${trainerName}` : "",
              topic ? `Topic: ${topic}` : "",
            ].filter(Boolean);
            return parts.length ? parts.join(" | ") : "-";
          },
        },
        {
          type: "Select",
          label: "Batch Class Record",
          mainKey: "organization_ems_batch_class_record_id",
          OptionMainKey: "ClassRecords",
          menuKey: "organization_ems_batch_class_record_id",
          formatValue: (val) => {
            if (!val) return "-";
            const topic = String(val?.topic ?? val?.batch_class?.topic ?? "").trim();
            const dateLabel = FormatDateV1(val?.start_time);
            if (topic && dateLabel && dateLabel !== "-") {
              return `${topic} (${dateLabel})`;
            }
            if (topic) return topic;
            return dateLabel && dateLabel !== "-" ? dateLabel : "-";
          },
        },
        {
          type: "Select",
          label: "Student *",
          mainKey: "organization_ems_student_id",
          OptionMainKey: "Students",
          menuKey: "organization_ems_student_id",
          formatValue: (val) =>
            `${[val.first_name, val.middle_name, val.last_name].filter(Boolean).join(" ")} (${val.student_id || ""})`,
        },
        {
          type: "Date",
          label: "Attendance Date *",
          mainKey: "attendance_date",
        },
        {
          type: "Select",
          label: "Attendance Status *",
          mainKey: "attendance_status",
          OptionMainKey: "AttendanceStatuses",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "MultiRow",
          label: "Remarks ",
          mainKey: "remarks",
        },
      ],
      DataFetchers: [
        { name: "ClassRecords", Type: "Fetch", link: "/api/ems/class-records" },
        { name: "BatchClasses", Type: "Fetch", link: "/api/ems/batch-classes" },
        { name: "Students", Type: "Fetch", link: "/api/ems/students" },
        {
          name: "AttendanceStatuses",
          Type: "NoFetch",
          Options: ["Present", "Absent"],
        },
      ],
    },
  },

  {
    route: "/certificates",
    ListProps: {
      title: "Certificates",
      apiEndpoint: "/api/ems/certificates",
      filteredApiEndpoint: "/api/ems/certificates/filtered",
      filterModule: "ems_certificates",
      useMetadataFilters: true,
      addRoute: "/certificates",
      editRouteBase: "/certificates/edit",
      tablePrimaryKey: "organization_ems_certificate_id",
      tableName: "ems_certificates",
      datagridKey: "ems_certificates_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "student",
        "admission",
        "batch",
        "training_program",
        "certificate_number",
        "issue_date",
        "valid_until",
        "certificate_status",
        "certificate_name",
        "certificate_url",
        "remarks",
      ],
      config: {
        sort: { field: "issue_date", direction: "desc" },
        defaultVisibleColumns: [
          "student",
          "admission",
          "batch",
          "training_program",
          "certificate_number",
          "issue_date",
          "valid_until",
          "certificate_status",
        ],
        columnLabels: {
          student: "Student",
          admission: "Admission",
          batch: "Batch",
          training_program: "Training Program",
          certificate_number: "Certificate Number",
          issue_date: "Issue Date",
          valid_until: "Valid Until",
          certificate_status: "Status",
          certificate_name: "Certificate Name",
          remarks: "Remarks",
        },
        columnWidths: {
          student: 220,
          admission: 160,
          batch: 180,
          training_program: 280,
          certificate_number: 200,
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => ({
          id: item.organization_ems_certificate_id,

          student:
            `${item?.student?.first_name} (${item?.student?.student_id})` ||
            "-",
          admission: `${item?.admission?.admission_number}` || "-",
          batch:
            `${item?.batch?.batch_name} (${item?.batch?.batch_code})` || "-",
          training_program:
            `${item?.training_program?.training_program_name} (${item?.training_program?.training_program_code})` ||
            "",

          certificate_number: item?.certificate_number || "-",
          issue_date: FormatDateV1(item.issue_date) || "-",
          valid_until: FormatDateV1(item.valid_until) || "-",
          certificate_status: item.certificate_status || "-",
          certificate_name: item.certificate_name || "-",
          certificate_url: item.certificate_url || "-",
          qr_code_url: item.qr_code_url || "-",
          remarks: item.remarks || "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          organization_ems_student_id,
          organization_ems_admission_id,
          organization_ems_batch_id,
          training_program_id,
          issue_date,
          valid_until,
          certificate_status,
          certificate_name,
          certificate_url,
          qr_code_url,
          grade,
          remarks,
          _certificate_eligible,
          _admission_has_certificate,
        } = formData;

        // --- organization_id (required|integer|exists) ---
        if (!organization_id)
          errors.organization_id = "Organization is required.";
        else if (isNaN(organization_id))
          errors.organization_id = "Organization ID must be a valid number.";

        // --- organization_entity_id (sometimes|nullable|integer) ---
        if (organization_entity_id && isNaN(organization_entity_id))
          errors.organization_entity_id =
            "Organization Entity ID must be a valid number.";

        // --- organization_ems_student_id (required|integer|exists) ---
        if (!organization_ems_student_id)
          errors.organization_ems_student_id = "Student is required.";
        else if (isNaN(organization_ems_student_id))
          errors.organization_ems_student_id =
            "Student ID must be a valid number.";

        // --- organization_ems_admission_id (required|integer|exists) ---
        if (!organization_ems_admission_id)
          errors.organization_ems_admission_id = "Admission is required.";
        else if (isNaN(organization_ems_admission_id))
          errors.organization_ems_admission_id =
            "Admission ID must be a valid number.";

        // --- organization_ems_batch_id (sometimes|nullable|integer|exists) ---
        if (organization_ems_batch_id && isNaN(organization_ems_batch_id))
          errors.organization_ems_batch_id = "Batch ID must be a valid number.";

        // --- training_program_id (required|integer|exists) ---
        if (!training_program_id)
          errors.training_program_id = "Training program is required.";
        else if (isNaN(training_program_id))
          errors.training_program_id =
            "Training program ID must be a valid number.";

        // --- One certificate per admission ---
        if (_admission_has_certificate && organization_ems_admission_id) {
          errors.organization_ems_admission_id =
            "A certificate already exists for this admission number.";
        }

        // --- Certificate: admission completed + all fee installments paid (UI flag from CommonForm) ---
        if (
          _certificate_eligible === false &&
          organization_ems_student_id &&
          organization_ems_admission_id &&
          !_admission_has_certificate
        ) {
          errors.organization_ems_student_id =
            "The fee is not paid. Admission must be completed and all fee installments must be paid before adding a certificate.";
        }

        // --- issue_date (required|date) ---
        if (!issue_date) errors.issue_date = "Issue date is required.";
        else if (isNaN(Date.parse(issue_date)))
          errors.issue_date = "Issue date must be a valid date.";

        // --- valid_until (nullable|date|after_or_equal:issue_date) ---
        if (valid_until) {
          if (isNaN(Date.parse(valid_until)))
            errors.valid_until = "Valid until must be a valid date.";
          else if (issue_date && new Date(valid_until) < new Date(issue_date))
            errors.valid_until =
              "Valid until must be after or equal to the issue date.";
        }

        // --- certificate_status (sometimes|in:Issued,Revoked,Expired,Reissued) ---
        const validStatuses = ["Issued", "Revoked", "Expired", "Reissued"];
        if (!certificate_status) {
          errors.certificate_status = "Certificate Status is required.";
        } else if (
          certificate_status &&
          !validStatuses.includes(certificate_status)
        )
          errors.certificate_status = "Invalid certificate status selected.";

        // --- certificate_name (nullable|string|max:150) ---
        if (certificate_name) {
          if (typeof certificate_name !== "string")
            errors.certificate_name = "Certificate name must be text.";
          else if (certificate_name.trim().length > 150)
            errors.certificate_name =
              "Certificate name too long (max 150 chars).";
        }

        if (grade && String(grade).trim().length > 20) {
          errors.grade = "Grade is too long (max 20 characters).";
        }

        // --- certificate_url (file upload check) ---
        // Allow existing saved URL strings on edit; only validate new File uploads.
        if (certificate_url instanceof File) {
          if (
            !certificate_url.type.startsWith("image/") &&
            !certificate_url.type.startsWith("application/pdf")
          )
            errors.certificate_url =
              "Certificate file must be an image or PDF.";
        }

        // --- qr_code_url (file upload check) ---
        if (qr_code_url instanceof File) {
          if (!qr_code_url.type.startsWith("image/"))
            errors.qr_code_url =
              "QR code must be an image file (PNG, JPG, etc.).";
        }

        // --- remarks (nullable|string|max:500) ---
        if (remarks) {
          if (typeof remarks !== "string")
            errors.remarks = "Remarks must be text.";
          else if (remarks.trim().length > 500)
            errors.remarks = "Remarks too long (max 500 chars).";
        }

        // --- Set and return ---
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/certificates",
      initialData: {
        organization_entity_id: "",
        organization_ems_student_id: "",
        organization_ems_admission_id: "",
        organization_ems_batch_id: "",
        training_program_id: "",
        _certificate_eligible: true,
        _admission_has_certificate: false,
        issue_date: "",
        valid_until: "",
        certificate_status: "",
        certificate_name: "",
        grade: "",
        remarks: "",
      },
      successMessages: {
        add: "Certificate added!",
        update: "Certificate updated!",
      },
      redirectPath: "/certificates",
      headerProps: {
        addMessage: "Add Certificate",
        updateMessage: "Update Certificate",
        homeLink: "/certificates",
        homeText: "Certificate",
      },
      fieldGridLayout: {
        rows: [
          {
            variant: "twoEqual",
            mainKeys: [
              "organization_ems_student_id",
              "organization_ems_admission_id",
            ],
          },
          {
            variant: "twoEqual",
            mainKeys: ["organization_ems_batch_id", "training_program_id"],
          },
          {
            variant: "twoEqual",
            mainKeys: ["issue_date", "valid_until"],
          },
          { variant: "full", mainKeys: ["certificate_status"] },
          {
            variant: "twoEqual",
            mainKeys: ["certificate_name", "grade"],
          },
          {
            variant: "twoEqual",
            mainKeys: ["certificate_url", "qr_code_url"],
          },
          { variant: "full", mainKeys: ["remarks"] },
        ],
      },
      Inputs: [
        {
          type: "Select",
          label: "Student *",
          mainKey: "organization_ems_student_id",
          OptionMainKey: "Students",
          menuKey: "organization_ems_student_id",
          formatValue: (val) => `${val.first_name} (${val.student_id})`,
        },
        {
          type: "Select",
          label: "Admission *",
          mainKey: "organization_ems_admission_id",
          OptionMainKey: "Admissions",
          menuKey: "organization_ems_admission_id",
          formatValue: (val) => `${val.admission_number}`,
        },
        {
          type: "Select",
          label: "Batch ",
          mainKey: "organization_ems_batch_id",
          OptionMainKey: "Batches",
          menuKey: "organization_ems_batch_id",
          formatValue: (val) => `${val.batch_code || val.batch?.batch_code || "-"}`,
        },
        {
          type: "Select",
          label: "Training Program ",
          mainKey: "training_program_id",
          OptionMainKey: "TrainingPrograms",
          menuKey: "organization_ems_training_program_id",
          formatValue: (val) =>
            `${val.training_program_name} (${val.training_program_code})`,
        },
        {
          type: "Date",
          label: "Issue Date *",
          mainKey: "issue_date",
        },
        {
          type: "Date",
          label: "Valid Until Date ",
          mainKey: "valid_until",
        },
        {
          type: "Select",
          label: "Certificate Statuses *",
          mainKey: "certificate_status",
          OptionMainKey: "CertificateStatuses",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "Text",
          label: "Certificate Name ",
          mainKey: "certificate_name",
        },
        {
          type: "Text",
          label: "Grade",
          mainKey: "grade",
        },
        {
          type: "File",
          label: "Certificate File",
          mainKey: "certificate_url",
          hideInAdd: true,
        },
        {
          type: "File",
          label: "QR Code",
          mainKey: "qr_code_url",
          hideInAdd: true,
        },
        {
          type: "MultiRow",
          label: "Remarks ",
          mainKey: "remarks",
        },
      ],
      DataFetchers: [
        { name: "Students", Type: "Fetch", link: "/api/ems/students" },
        { name: "Admissions", Type: "Fetch", link: "/api/ems/admissions" },
        { name: "Batches", Type: "Fetch", link: "/api/ems/batches" },
        {
          name: "TrainingPrograms",
          Type: "Fetch",
          link: "/api/ems/training-programs",
        },
        {
          name: "FeeInstallments",
          Type: "Fetch",
          link: "/api/ems/fee-installments",
        },
        {
          name: "BatchStudents",
          Type: "Fetch",
          link: "/api/ems/batch-students",
        },
        {
          name: "Certificates",
          Type: "Fetch",
          link: "/api/ems/certificates",
        },
        {
          name: "CertificateStatuses",
          Type: "NoFetch",
          Options: ["Issued", "Revoked", "Expired", "Reissued"],
        },
      ],
    },
  },
  {
    route: "/placement-pool",
    ListProps: {
      title: "Placement Pool",
      apiEndpoint: "/api/ems/placement-pool",
      useMetadataFilters: false,
      addRoute: "/placement-pool",
      editRouteBase: "/placement-pool/edit",
      tablePrimaryKey: "organization_ems_admission_id",
      tableName: "ems_placement_pool",
      datagridKey: "",
      showAddButton: false,
      readOnlyList: true,
      hideFilterButton: true,
      hidePersonalizeView: true,
      personalizeColumnKeys: [
        "admission_number",
        "student_name",
        "course",
        "batch_start_date",
        "batch_end_date",
        "batch_student_status",
      ],
      config: {
        sort: { field: "admission_number", direction: "desc" },
        defaultVisibleColumns: [
          "admission_number",
          "student_name",
          "course",
          "batch_start_date",
          "batch_end_date",
          "batch_student_status",
        ],
        columnLabels: {
          admission_number: "Adm. no.",
          student_name: "Student Name",
          course: "Course",
          batch_start_date: "Batch Start Date",
          batch_end_date: "Batch End Date",
          batch_student_status: "Batch Student Status",
        },
        columnWidths: {
          admission_number: 100,
          student_name: 220,
          course: 280,
          batch_start_date: 140,
          batch_end_date: 140,
          batch_student_status: 160,
        },
      },
      mapResponseToRows: (data) =>
        (Array.isArray(data) ? data : []).map((item) => {
          const trainingProgram =
            item?.training_program ?? item?.trainingProgram ?? null;
          const student = item?.student ?? null;
          const batchStudents = Array.isArray(item?.batch_students)
            ? item.batch_students
            : Array.isArray(item?.batchStudents)
              ? item.batchStudents
              : [];

          // Prefer the latest batch enrollment for this admission.
          const latestBatchStudent = [...batchStudents].sort((a, b) => {
            const aDate = new Date(
              a?.enrollment_date || a?.batch?.start_date || 0
            ).getTime();
            const bDate = new Date(
              b?.enrollment_date || b?.batch?.start_date || 0
            ).getTime();
            return bDate - aDate;
          })[0];

          const batch =
            latestBatchStudent?.batch ||
            latestBatchStudent?.Batch ||
            null;

          return {
            id: item.organization_ems_admission_id,
            organization_ems_admission_id: item.organization_ems_admission_id,
            admission_number: item.admission_number || "-",
            student_name:
              [student?.first_name, student?.middle_name, student?.last_name]
                .filter((n) => n && String(n).trim() !== "")
                .join(" ")
                .trim() || "-",
            course:
              trainingProgram?.training_program_name ||
              batch?.training_program?.training_program_name ||
              "-",
            batch_start_date: FormatDateV1(batch?.start_date) || "-",
            batch_end_date: FormatDateV1(batch?.end_date) || "-",
            batch_student_status:
              latestBatchStudent?.batch_status ||
              latestBatchStudent?.status ||
              "-",
          };
        }),
    },
    FormProps: {
      validateForm: () => true,
      baseUrl: "/api/ems/placement-pool",
      initialData: {},
      successMessages: { add: "", update: "" },
      redirectPath: "/placement-pool",
      headerProps: {
        addMessage: "Placement Pool",
        updateMessage: "Placement Pool",
        homeLink: "/placement-pool",
        homeText: "Placement Pool",
      },
      Inputs: [],
      DataFetchers: [],
    },
  },
  {
    route: "/placement-referrals",
    ListProps: {
      title: "Placement Referrals",
      apiEndpoint: "/api/ems/placement-referrals",
      filteredApiEndpoint: "/api/ems/placement-referrals/filtered",
      filterModule: "ems_placement_referrals",
      useMetadataFilters: true,
      addRoute: "/placement-referrals",
      editRouteBase: "/placement-referrals/edit",
      tablePrimaryKey: "organization_ems_placement_referral_id",
      tableName: "ems_placement_referrals",
      datagridKey: "ems_placement_referrals_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "student",
        "admission",
        "training_program",
        "agency",
        "company",
        "referral_date",
        "referral_status",
        "job_role",
        "package_amount",
        "currency_code",
        "joining_date",
        "remarks",
      ],
      config: {
        sort: { field: "referral_date", direction: "desc" },
        defaultVisibleColumns: [
          "student",
          "admission",
          "training_program",
          "agency",
          "company",
          "referral_date",
          "referral_status",
          "job_role",
        ],
        columnLabels: {
          student: "Student",
          admission: "Admission",
          training_program: "Training Program",
          agency: "Agency",
          company: "Company",
          referral_date: "Referral Date",
          referral_status: "Status",
          job_role: "Job Role",
          package_amount: "Package Amount",
          currency_code: "Currency",
          joining_date: "Joining Date",
          remarks: "Remarks",
        },
        columnWidths: {
          student: 220,
          admission: 160,
          training_program: 280,
          agency: 180,
          company: 180,
          referral_date: 140,
          referral_status: 150,
          job_role: 160,
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => ({
          id: item.organization_ems_placement_referral_id,

          student:
            `${item?.student?.first_name} (${item?.student?.student_id})` ||
            "-",
          admission: `${item?.admission?.admission_number}` || "-",
          training_program:
            `${item?.training_program?.training_program_name} (${item?.training_program?.training_program_code})` ||
            "",
          agency: `${item?.agency?.agency_name}` || "-",
          company: `${item?.company?.company_name}` || "-",

          referral_date: FormatDateV1(item.referral_date) || "-",
          referral_status: item?.referral_status || "-",
          job_role: item?.job_role || "-",
          package_amount: item?.package_amount || "-",
          currency_code: item?.currency_code || "-",
          joining_date: FormatDateV1(item.joining_date) || "-",
          remarks: item.remarks || "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          organization_ems_student_id,
          organization_ems_admission_id,
          training_program_id,
          organization_ems_recruitment_agency_id,
          organization_ems_company_id,
          referral_date,
          referral_status,
          job_role,
          package_amount,
          currency_code,
          joining_date,
          remarks,
        } = formData;

        // --- organization_id (required|integer|exists) ---
        if (!organization_id)
          errors.organization_id = "Organization is required.";
        else if (isNaN(organization_id))
          errors.organization_id = "Organization ID must be a valid number.";

        // --- organization_entity_id (sometimes|nullable|integer) ---
        if (organization_entity_id && isNaN(organization_entity_id))
          errors.organization_entity_id =
            "Organization Entity ID must be a valid number.";

        // --- organization_ems_student_id (required|integer|exists) ---
        if (!organization_ems_student_id)
          errors.organization_ems_student_id = "Student is required.";
        else if (isNaN(organization_ems_student_id))
          errors.organization_ems_student_id =
            "Student ID must be a valid number.";

        // --- organization_ems_admission_id (required|integer|exists) ---
        if (!organization_ems_admission_id)
          errors.organization_ems_admission_id = "Admission is required.";
        else if (isNaN(organization_ems_admission_id))
          errors.organization_ems_admission_id =
            "Admission ID must be a valid number.";

        // --- training_program_id (required|integer|exists) ---
        if (!training_program_id)
          errors.training_program_id = "Training program is required.";
        else if (isNaN(training_program_id))
          errors.training_program_id =
            "Training program ID must be a valid number.";

        // --- organization_ems_recruitment_agency_id (sometimes|nullable|integer) ---
        if (
          organization_ems_recruitment_agency_id &&
          isNaN(organization_ems_recruitment_agency_id)
        )
          errors.organization_ems_recruitment_agency_id =
            "Recruitment agency ID must be a valid number.";

        // --- organization_ems_company_id (sometimes|nullable|integer) ---
        if (organization_ems_company_id && isNaN(organization_ems_company_id))
          errors.organization_ems_company_id =
            "Company ID must be a valid number.";

        // --- referral_date (required|date) ---
        if (!referral_date) errors.referral_date = "Referral date is required.";
        else if (isNaN(Date.parse(referral_date)))
          errors.referral_date = "Referral date must be a valid date.";

        // --- referral_status (sometimes|in:Referred,In Process,Interview Scheduled,Offer Received,Rejected,Placed) ---
        const validStatuses = [
          "Referred",
          "In Process",
          "Interview Scheduled",
          "Offer Received",
          "Rejected",
          "Placed",
        ];
        if (referral_status && !validStatuses.includes(referral_status))
          errors.referral_status = "Invalid referral status selected.";

        // --- job_role (nullable|string|max:100) ---
        if (job_role) {
          if (typeof job_role !== "string")
            errors.job_role = "Job role must be text.";
          else if (job_role.trim().length > 100)
            errors.job_role = "Job role too long (max 100 characters).";
        }

        // --- package_amount (nullable|numeric|min:0) ---
        if (package_amount) {
          if (isNaN(package_amount))
            errors.package_amount = "Package amount must be numeric.";
          else if (Number(package_amount) < 0)
            errors.package_amount = "Package amount cannot be negative.";
        }

        // --- currency_code (required|string|max:10) ---
        if (!currency_code) errors.currency_code = "Currency code is required.";
        else if (typeof currency_code !== "string")
          errors.currency_code = "Currency code must be text.";
        else if (currency_code.trim().length > 10)
          errors.currency_code = "Currency code too long (max 10 characters).";

        // --- joining_date (nullable|date|after_or_equal:referral_date) ---
        if (joining_date) {
          if (isNaN(Date.parse(joining_date)))
            errors.joining_date = "Joining date must be a valid date.";
          else if (
            referral_date &&
            new Date(joining_date) < new Date(referral_date)
          )
            errors.joining_date =
              "Joining date must be after or equal to referral date.";
        }

        // --- remarks (nullable|string|max:500) ---
        if (remarks) {
          if (typeof remarks !== "string")
            errors.remarks = "Remarks must be text.";
          else if (remarks.trim().length > 500)
            errors.remarks = "Remarks too long (max 500 chars).";
        }

        // --- Set & Return ---
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/placement-referrals",
      initialData: {
        organization_entity_id: "",
        organization_ems_student_id: "",
        organization_ems_admission_id: "",
        training_program_id: "",
        organization_ems_recruitment_agency_id: "",
        organization_ems_company_id: "",
        referral_date: "",
        referral_status: "",
        job_role: "",
        package_amount: "",
        currency_code: "",
        valid_until: "",
        joining_date: "",
        remarks: "",
      },
      successMessages: {
        add: "Placement Referral added!",
        update: "Placement Referral updated!",
      },
      redirectPath: "/placement-referrals",
      headerProps: {
        addMessage: "Add Placement Referral",
        updateMessage: "Update Placement Referral",
        homeLink: "/placement-referrals",
        homeText: "Placement Referral",
      },
      fieldGridLayout: {
        rows: [
          { variant: "full", mainKeys: ["organization_ems_student_id"] },
          {
            variant: "twoEqual",
            mainKeys: [
              "organization_ems_admission_id",
              "training_program_id",
            ],
          },
          {
            variant: "twoEqual",
            mainKeys: [
              "organization_ems_recruitment_agency_id",
              "organization_ems_company_id",
            ],
          },
          {
            variant: "twoEqual",
            mainKeys: ["referral_date", "referral_status"],
          },
          {
            variant: "twoEqual",
            mainKeys: ["job_role", "package_amount"],
          },
          { variant: "full", mainKeys: ["valid_until"] },
          {
            variant: "twoEqual",
            mainKeys: ["currency_code", "joining_date"],
          },
          { variant: "full", mainKeys: ["remarks"] },
        ],
        sections: [],
      },
      Inputs: [
        {
          type: "Select",
          label: "Student *",
          mainKey: "organization_ems_student_id",
          OptionMainKey: "Students",
          menuKey: "organization_ems_student_id",
          formatValue: (val) => `${val.first_name} (${val.student_id})`,
        },
        {
          type: "Select",
          label: "Admission *",
          mainKey: "organization_ems_admission_id",
          OptionMainKey: "Admissions",
          menuKey: "organization_ems_admission_id",
          formatValue: (val) => `${val.admission_number}`,
          disabledWhen: () => true,
        },
        {
          type: "Select",
          label: "Training Program *",
          mainKey: "training_program_id",
          OptionMainKey: "TrainingPrograms",
          menuKey: "organization_ems_training_program_id",
          formatValue: (val) =>
            `${val.training_program_name} (${val.training_program_code})`,
          disabledWhen: () => true,
        },
        {
          type: "Select",
          label: "Recruitment Agency ",
          mainKey: "organization_ems_recruitment_agency_id",
          OptionMainKey: "RecruitmentAgencies",
          menuKey: "organization_ems_recruitment_agency_id",
          formatValue: (val) => `${val.agency_name}`,
        },
        {
          type: "Select",
          label: "Company ",
          mainKey: "organization_ems_company_id",
          OptionMainKey: "Companies",
          menuKey: "organization_ems_company_id",
          formatValue: (val) => `${val.company_name}`,
        },

        {
          type: "Date",
          label: "Referral Date *",
          mainKey: "referral_date",
        },

        {
          type: "Select",
          label: "Referral Status ",
          mainKey: "referral_status",
          OptionMainKey: "ReferralStatuses",
          menuKey: null,
          formatValue: (val) => val,
        },

        {
          type: "Text",
          label: "Job Role ",
          mainKey: "job_role",
        },
        {
          type: "Number",
          label: "Package Amount ",
          mainKey: "package_amount",
        },
        {
          type: "Date",
          label: "Valid Until Date ",
          mainKey: "valid_until",
        },
        {
          type: "Select",
          label: "Currency Code *",
          mainKey: "currency_code",
          OptionMainKey: "Currencies",
          menuKey: "currency_code",
          formatValue: (val) =>
            `${val?.currency_code || ""}${val?.currency_name ? ` - ${val.currency_name}` : ""}`,
        },
        {
          type: "Date",
          label: "Joining Date ",
          mainKey: "joining_date",
        },

        {
          type: "MultiRow",
          label: "Remarks ",
          mainKey: "remarks",
        },
      ],
      DataFetchers: [
        { name: "Students", Type: "Fetch", link: "/api/ems/students" },
        { name: "Admissions", Type: "Fetch", link: "/api/ems/admissions" },

        {
          name: "TrainingPrograms",
          Type: "Fetch",
          link: "/api/ems/training-programs",
        },
        {
          name: "RecruitmentAgencies",
          Type: "Fetch",
          link: "/api/ems/recruitment-agencies",
        },
        {
          name: "Companies",
          Type: "Fetch",
          link: "/api/ems/companies",
        },
        {
          name: "ReferralStatuses",
          Type: "NoFetch",
          Options: [
            "Referred",
            "In Process",
            "Interview Scheduled",
            "Offer Received",
            "Rejected",
            "Placed",
          ],
        },
        { name: "Currencies", Type: "Fetch", link: "/api/ems-currencies" },
      ],
    },
  },
  {
    route: "/demo-sessions",
    ListProps: {
      title: "Demo Sessions",
      apiEndpoint: "/api/ems/demo-sessions",
      filteredApiEndpoint: "/api/ems/demo-sessions/filtered",
      filterModule: "ems_demo_sessions",
      useMetadataFilters: true,
      addRoute: "/demo-sessions",
      editRouteBase: "/demo-sessions/edit",
      tablePrimaryKey: "organization_ems_demo_session_id",
      tableName: "ems_demo_sessions",
      datagridKey: "ems_demo_sessions_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "demo_date",
        "start_time_ist",
        "end_time_ist",
        "training_program",
        "demo_mode",
        "trainer",
        "participant_count",
        "status",
        "client_timezone",
        "trainer_location",
        "meeting_link",
        "demo_duration_minutes",
        "organization_ems_training_program_id",
        "trainer_employee_id",
      ],
      config: {
        defaultVisibleColumns: [
          "demo_date",
          "start_time_ist",
          "end_time_ist",
          "training_program",
          "demo_mode",
          "trainer",
          "participant_count",
          "status",
        ],
        columnLabels: {
          demo_date: "Demo Date",
          start_time_ist: "Time Start",
          end_time_ist: "Time End",
          training_program: "Program",
          demo_mode: "Mode",
          trainer: "Trainer",
          participant_count: "Participants / Enrolled",
          status: "Status",
        },
        columnWidths: {
          demo_date: 148,
          start_time_ist: 148,
          end_time_ist: 148,
          demo_mode: 148,
          status: 148,
          participant_count: 160,
          training_program: 320,
          trainer: 260,
        },
        sort: { field: "demo_date", direction: "desc" },
      },
      mapResponseToRows: (data) =>
        data.map((item) => ({
          id: item.organization_ems_demo_session_id,
          trainer_employee_id:
            item?.trainer_employee_id ??
            item?.trainer?.organization_user_id ??
            "",
          trainer_application_user_id:
            getApplicationUserIdFromTrainerObject(item?.trainer) || "",
          trainer:
            String(item?.trainer?.applicationuser?.[0]?.full_name || "").trim() ||
            `${item?.trainer?.person?.first_name || ""} ${item?.trainer?.person?.last_name || ""}${item?.trainer?.person?.organization_person?.person_code ? ` (${item?.trainer?.person?.organization_person?.person_code})` : ""}`.trim() ||
            "",
          training_program:
            `${item?.training_program?.training_program_name} (${item?.training_program?.training_program_code})` ||
            "",
          demo_date: FormatDateV1(item.demo_date) || "-",
          start_time_ist: formatTime12h(item?.start_time_ist),
          end_time_ist: formatTime12h(item?.end_time_ist),
          start_time_client: item?.start_time_client || "-",
          end_time_client: item?.end_time_client || "-",
          client_timezone: item?.client_timezone || "-",
          demo_notes: item?.demo_notes || "-",
          demo_mode: item?.demo_mode || "-",
          trainer_location: item?.trainer_location || "-",
          meeting_link: item?.meeting_link || "-",
          student_remarks: item?.student_remarks || "-",
          trainer_remarks: item?.trainer_remarks || "-",
          counsellor_remarks: item?.counsellor_remarks || "-",
          status: item?.status || "-",
          demo_duration_minutes: item?.demo_duration_minutes || "-",
          participant_count: (() => {
            const parts =
              item?.demo_participants ?? item?.demoParticipants ?? [];
            const total = Array.isArray(parts) ? parts.length : 0;
            const enrolled = countEnrolledDemoParticipants(parts);
            return `${total} / ${enrolled}`;
          })(),
          __participant_names_tooltip: buildDemoParticipantNamesTooltip(
            item?.demo_participants ?? item?.demoParticipants ?? []
          ),
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          trainer_employee_id,
          organization_ems_training_program_id,
          demo_date,
          start_time_ist,
          end_time_ist,
          demo_mode,
          trainer_location,
          meeting_link,
          counsellor_remarks,
          status,
          demo_duration_minutes,
          trainer_remarks,
        } = formData;

        // --- organization_id ---
        if (!organization_id)
          errors.organization_id = "Organization is required.";
        else if (isNaN(organization_id))
          errors.organization_id = "Organization ID must be a valid number.";

        // --- organization_entity_id ---
        if (organization_entity_id && isNaN(organization_entity_id))
          errors.organization_entity_id =
            "Organization Entity ID must be a valid number.";

        // --- trainer_employee_id ---
        if (!trainer_employee_id)
          errors.trainer_employee_id = "Trainer is required.";
        else if (isNaN(trainer_employee_id))
          errors.trainer_employee_id = "Trainer ID must be a valid number.";

        // --- training_program_id ---
        if (!organization_ems_training_program_id)
          errors.organization_ems_training_program_id =
            "Training program is required.";
        else if (isNaN(organization_ems_training_program_id))
          errors.organization_ems_training_program_id =
            "Training program ID must be a valid number.";

        // --- demo_date ---
        if (!demo_date) errors.demo_date = "Demo date is required.";

        // --- start_time_ist & end_time_ist ---
        if (!start_time_ist)
          errors.start_time_ist = "Time Start (IST) is required.";
        if (!end_time_ist) errors.end_time_ist = "End Time (IST) is required.";
        if (start_time_ist && end_time_ist && end_time_ist <= start_time_ist)
          errors.end_time_ist =
            "End Time (IST) must be after Time Start (IST).";

        // --- demo_mode ---
        const validDemoModes = ["Online", "Offline", "Hybrid"];
        if (!demo_mode) errors.demo_mode = "Demo mode is required.";
        else if (!validDemoModes.includes(demo_mode))
          errors.demo_mode = "Demo mode must be online, offline, or hybrid.";

        // --- trainer_location ---
        const validLocations = ["office", "home"];
        if (!trainer_location)
          errors.trainer_location = "Trainer location is required.";
        else if (!validLocations.includes(trainer_location))
          errors.trainer_location = "Trainer location must be office or home.";

        // --- counsellor_remarks ---
        if (counsellor_remarks && String(counsellor_remarks).length > 255)
          errors.counsellor_remarks = "Maximum 255 characters allowed.";

        // --- meeting_link ---
        if (meeting_link && meeting_link.length > 255)
          errors.meeting_link = "Meeting link cannot exceed 255 characters.";

        // --- status ---
        const validStatuses = [
          "Scheduled",
          "Completed",
          "Cancelled",
          "Postponed",
        ];
        if (!status) errors.status = "Status is required.";
        else if (!validStatuses.includes(status))
          errors.status = "Invalid status selected.";

        // --- demo_duration_minutes (optional; shown on edit) ---
        if (demo_duration_minutes !== "" && demo_duration_minutes != null) {
          if (isNaN(demo_duration_minutes))
            errors.demo_duration_minutes = "Duration must be a number.";
          else if (Number(demo_duration_minutes) < 1)
            errors.demo_duration_minutes =
              "Duration must be at least 1 minute.";
          else if (Number(demo_duration_minutes) > 600)
            errors.demo_duration_minutes =
              "Duration cannot exceed 600 minutes.";
        }

        if (trainer_remarks && String(trainer_remarks).length > 255)
          errors.trainer_remarks = "Trainer remarks cannot exceed 255 characters.";

        // --- Finalize ---
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      embeddedParticipantValidateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          organization_ems_demo_session_id,
          organization_ems_lead_id,
          attendance_status,
          student_remarks,
          trainer_remarks: pTrainerRemarks,
          counsellor_remarks: pCounsellorRemarks,
        } = formData;

        if (!organization_id)
          errors.organization_id = "Organization is required.";
        else if (isNaN(Number(organization_id)))
          errors.organization_id = "Organization ID must be a number.";

        if (organization_entity_id && isNaN(Number(organization_entity_id)))
          errors.organization_entity_id = "Entity ID must be a number.";

        if (!organization_ems_demo_session_id)
          errors.organization_ems_demo_session_id = "Demo session is required.";
        else if (isNaN(Number(organization_ems_demo_session_id)))
          errors.organization_ems_demo_session_id =
            "Demo session ID must be a number.";

        if (
          organization_ems_lead_id === "" ||
          organization_ems_lead_id === null ||
          organization_ems_lead_id === undefined
        ) {
          errors.organization_ems_lead_id = "Lead is required.";
        } else if (isNaN(Number(organization_ems_lead_id)))
          errors.organization_ems_lead_id = "Lead ID must be a number.";

        const validAttendance = [
          "Scheduled",
          "Attended",
          "Absent",
          "Cancelled",
        ];
        if (
          attendance_status === "" ||
          attendance_status === null ||
          attendance_status === undefined
        ) {
          errors.attendance_status = "Attendance status is required.";
        } else if (!validAttendance.includes(attendance_status)) {
          errors.attendance_status = "Invalid attendance status.";
        }

        if (student_remarks && String(student_remarks).length > 255)
          errors.student_remarks = "Maximum 255 characters allowed.";
        if (pTrainerRemarks && String(pTrainerRemarks).length > 255)
          errors.trainer_remarks = "Maximum 255 characters allowed.";
        if (pCounsellorRemarks && String(pCounsellorRemarks).length > 255)
          errors.counsellor_remarks = "Maximum 255 characters allowed.";

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },

      baseUrl: "/api/ems/demo-sessions",
      initialData: {
        organization_entity_id: "",
        trainer_employee_id: "",
        organization_ems_training_program_id: "",
        demo_date: "",
        start_time_ist: "",
        end_time_ist: "",
        start_time_client: "",
        end_time_client: "",
        client_timezone: "IST",
        demo_notes: "",
        demo_mode: "",
        trainer_location: "",
        meeting_link: "",
        counsellor_remarks: "",
        status: "scheduled",
        demo_duration_minutes: "",
        trainer_remarks: "",
        student_remarks: "",
        /** Nested rows sent with POST/PUT; same shape the backend expects for demo_participants. */
        demo_participants: [],
      },
      successMessages: {
        add: "Demo Session added!",
        update: "Demo Session updated!",
      },
      redirectPath: "/demo-sessions",
      headerProps: {
        addMessage: "Add Demo Session",
        updateMessage: "Update Demo Session",
        homeLink: "/demo-sessions",
        homeText: "Demo Session",
      },
      fieldGridLayout: {
        sections: [
          { title: "Demo participants", beforeRow: 8 },
        ],
        rows: [
          { mainKeys: ["demo_date"], variant: "full" },
          {
            mainKeys: ["start_time_ist", "end_time_ist"],
            variant: "twoEqual",
          },
          {
            mainKeys: ["organization_ems_training_program_id"],
            variant: "full",
          },
          { mainKeys: ["demo_mode"], variant: "full" },
          { mainKeys: ["trainer_employee_id"], variant: "full" },
          { mainKeys: ["trainer_location"], variant: "full" },
          { mainKeys: ["counsellor_remarks"], variant: "full" },
          { mainKeys: ["meeting_link"], variant: "full" },
          { mainKeys: ["__demo_participants_embed"], variant: "full" },
          { mainKeys: ["demo_duration_minutes"], variant: "full" },
          { mainKeys: ["trainer_remarks"], variant: "full" },
          { mainKeys: ["status"], variant: "full" },
        ],
      },
      embeddedParticipantInputs: [
        {
          type: "Select",
          label: "Lead *",
          mainKey: "organization_ems_lead_id",
          OptionMainKey: "Leads",
          menuKey: "organization_ems_lead_id",
          formatValue: (val) => `${val.person_full_name ?? ""}`,
        },
        {
          type: "Select",
          label: "Attendance status *",
          mainKey: "attendance_status",
          OptionMainKey: "AttendanceStatuses",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "MultiRow",
          label: "Student remarks ",
          mainKey: "student_remarks",
        },
        {
          type: "MultiRow",
          label: "Trainer remarks ",
          mainKey: "trainer_remarks",
        },
        {
          type: "MultiRow",
          label: "Counsellor remarks ",
          mainKey: "counsellor_remarks",
        },
      ],
      Inputs: [
        {
          type: "Date",
          label: "Demo Date *",
          mainKey: "demo_date",
          demoSessionDdMmYyyyHelper: true,
        },
        {
          type: "Time",
          label: "Time Start (IST) *",
          mainKey: "start_time_ist",
        },
        {
          type: "Time",
          label: "End Time (IST) *",
          mainKey: "end_time_ist",
        },
        {
          type: "Select",
          label: "Training Program *",
          mainKey: "organization_ems_training_program_id",
          OptionMainKey: "TrainingPrograms",
          menuKey: "organization_ems_training_program_id",
          formatValue: (val) =>
            `${val.training_program_name} (${val.training_program_code})`,
        },
        {
          type: "Select",
          label: "Demo Mode *",
          mainKey: "demo_mode",
          OptionMainKey: "DemoModes",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "Select",
          label: "Trainer *",
          mainKey: "trainer_employee_id",
          OptionMainKey: "Trainers",
          menuKey: "organization_user_id",
          formatValue: (val) => formatTrainerUserOption(val),
        },
        {
          type: "Select",
          label: "Trainer Location *",
          mainKey: "trainer_location",
          OptionMainKey: "TrainerLocations",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "MultiRow",
          label: "Counsellor Remarks ",
          mainKey: "counsellor_remarks",
        },
        {
          type: "Text",
          label: "Meeting Link ",
          mainKey: "meeting_link",
        },
        {
          type: "DemoParticipantsEmbed",
          label: "Demo participants",
          mainKey: "__demo_participants_embed",
        },
        {
          type: "Number",
          label: "Demo Duration Minutes ",
          mainKey: "demo_duration_minutes",
          hideInAdd: true,
        },
        {
          type: "MultiRow",
          label: "Trainer Remarks ",
          mainKey: "trainer_remarks",
          hideInAdd: true,
        },
        {
          type: "Select",
          label: "Status *",
          mainKey: "status",
          OptionMainKey: "Statuses",
          menuKey: null,
          formatValue: (val) => val,
        },
      ],
      DataFetchers: [
        {
          name: "Trainers",
          Type: "Fetch",
          link: "/api/organizations/${org_id}/users-data-all",
        },
        {
          name: "TrainingPrograms",
          Type: "Fetch",
          link: "/api/ems/training-programs",
        },
        {
          name: "DemoModes",
          Type: "NoFetch",
          Options: ["Online", "Offline", "Hybrid"],
        },
        {
          name: "TrainerLocations",
          Type: "NoFetch",
          Options: ["office", "home"],
        },
        {
          name: "Statuses",
          Type: "NoFetch",
          Options: ["Scheduled", "Completed", "Cancelled", "Postponed"],
        },
        {
          name: "Leads",
          Type: "Fetch",
          link: "/api/ems/leads",
        },
        {
          name: "AttendanceStatuses",
          Type: "NoFetch",
          Options: ["Scheduled", "Attended", "Absent", "Cancelled"],
        },
      ],
    },
  },

  {
    route: "/demo-participants",
    ListProps: {
      title: "Demo Participants",
      apiEndpoint: "/api/ems/demo-participants",
      filteredApiEndpoint: "/api/ems/demo-participants/filtered",
      filterModule: "ems_demo_participants",
      useMetadataFilters: true,
      addRoute: "/demo-participants",
      editRouteBase: "/demo-participants/edit",
      tablePrimaryKey: "organization_ems_demo_participant_id",
      tableName: "ems_demo_participants",
      datagridKey: "ems_demo_participants_grid_v2",
      showAddButton: true,
      personalizeColumnKeys: [
        "demo_session",
        "lead",
        "training_program",
        "organization_ems_demo_session_id",
        "organization_ems_lead_id",
        "attendance_status",
        "student_remarks",
        "trainer_remarks",
        "counsellor_remarks",
        "trainer_employee_id",
      ],
      config: {
        defaultVisibleColumns: [
          "demo_session",
          "lead",
          "training_program",
          "attendance_status",
          "student_remarks",
          "trainer_remarks",
          "counsellor_remarks",
        ],
        columnLabels: {
          demo_session: "Demo Session",
          lead: "Lead",
          training_program: "Program",
          attendance_status: "Attendance",
          student_remarks: "Student Remarks",
          trainer_remarks: "Trainer Remarks",
          counsellor_remarks: "Counsellor Remarks",
          organization_ems_demo_session_id: "Session ID",
          organization_ems_lead_id: "Lead ID",
        },
        columnWidths: {
          demo_session: 148,
          lead: 220,
          training_program: 280,
          attendance_status: 140,
          student_remarks: 200,
          trainer_remarks: 200,
          counsellor_remarks: 200,
        },
        sort: {
          field: "organization_ems_demo_participant_id",
          direction: "desc",
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => {
          const session = item?.demo_session ?? item?.demoSession ?? null;
          const lead = item?.lead ?? null;
          const leadNameFromPerson = [
            lead?.person?.first_name,
            lead?.person?.last_name,
          ]
            .filter(Boolean)
            .join(" ")
            .trim();
          const leadName =
            (lead?.person_full_name || leadNameFromPerson) || "-";
          const program = session?.training_program ?? session?.trainingProgram ?? null;
          const programName = String(program?.training_program_name || "").trim();
          const programCode = String(program?.training_program_code || "").trim();
          const trainingProgram = programName
            ? programCode
              ? `${programName} (${programCode})`
              : programName
            : "-";
          return {
            id: item.organization_ems_demo_participant_id,
            organization_ems_demo_participant_id:
              item.organization_ems_demo_participant_id,
            trainer_employee_id:
              session?.trainer_employee_id ??
              session?.trainer?.organization_user_id ??
              "",
            trainer_application_user_id:
              getApplicationUserIdFromTrainerObject(session?.trainer) || "",
            demo_session: FormatDateV1(session?.demo_date) || "-",
            lead: leadName,
            training_program: trainingProgram,
            attendance_status: item.attendance_status || "-",
            student_remarks: item.student_remarks || "-",
            trainer_remarks: item.trainer_remarks || "-",
            counsellor_remarks: item.counsellor_remarks || "-",
          };
        }),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          organization_ems_demo_session_id,
          organization_ems_lead_id,
          attendance_status,
          student_remarks,
          trainer_remarks,
          counsellor_remarks,
        } = formData;

        if (!organization_id)
          errors.organization_id = "Organization is required.";
        else if (isNaN(Number(organization_id)))
          errors.organization_id = "Organization ID must be a number.";

        if (organization_entity_id && isNaN(Number(organization_entity_id)))
          errors.organization_entity_id = "Entity ID must be a number.";

        if (!organization_ems_demo_session_id)
          errors.organization_ems_demo_session_id = "Demo session is required.";
        else if (isNaN(Number(organization_ems_demo_session_id)))
          errors.organization_ems_demo_session_id =
            "Demo session ID must be a number.";

        if (
          organization_ems_lead_id === "" ||
          organization_ems_lead_id === null ||
          organization_ems_lead_id === undefined
        ) {
          errors.organization_ems_lead_id = "Lead is required.";
        } else if (isNaN(Number(organization_ems_lead_id)))
          errors.organization_ems_lead_id = "Lead ID must be a number.";

        const validAttendance = [
          "Scheduled",
          "Attended",
          "Absent",
          "Cancelled",
        ];
        if (
          attendance_status === "" ||
          attendance_status === null ||
          attendance_status === undefined
        ) {
          errors.attendance_status = "Attendance status is required.";
        } else if (!validAttendance.includes(attendance_status)) {
          errors.attendance_status = "Invalid attendance status.";
        }

        if (student_remarks && String(student_remarks).length > 255)
          errors.student_remarks = "Maximum 255 characters allowed.";
        if (trainer_remarks && String(trainer_remarks).length > 255)
          errors.trainer_remarks = "Maximum 255 characters allowed.";
        if (counsellor_remarks && String(counsellor_remarks).length > 255)
          errors.counsellor_remarks = "Maximum 255 characters allowed.";

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },
      baseUrl: "/api/ems/demo-participants",
      initialData: {
        organization_entity_id: "",
        organization_ems_demo_session_id: "",
        organization_ems_lead_id: "",
        attendance_status: "",
        student_remarks: "",
        trainer_remarks: "",
        counsellor_remarks: "",
      },
      successMessages: {
        add: "Demo participant added!",
        update: "Demo participant updated!",
      },
      redirectPath: "/demo-participants",
      headerProps: {
        addMessage: "Add Demo Participant",
        updateMessage: "Update Demo Participant",
        homeLink: "/demo-participants",
        homeText: "Demo Participant",
      },
      Inputs: [
        {
          type: "Select",
          label: "Demo Session *",
          mainKey: "organization_ems_demo_session_id",
          OptionMainKey: "DemoSessions",
          menuKey: "organization_ems_demo_session_id",
          formatValue: (val) =>
            `${FormatDateV1(val?.demo_date)}${val?.demo_mode ? ` (${val.demo_mode})` : ""}`,
        },
        {
          type: "Select",
          label: "Lead *",
          mainKey: "organization_ems_lead_id",
          OptionMainKey: "Leads",
          menuKey: "organization_ems_lead_id",
          formatValue: (val) => `${val.person_full_name ?? ""}`,
        },
        {
          type: "Select",
          label: "Attendance status *",
          mainKey: "attendance_status",
          OptionMainKey: "AttendanceStatuses",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "MultiRow",
          label: "Student remarks ",
          mainKey: "student_remarks",
        },
        {
          type: "MultiRow",
          label: "Trainer remarks ",
          mainKey: "trainer_remarks",
        },
        {
          type: "MultiRow",
          label: "Counsellor remarks ",
          mainKey: "counsellor_remarks",
        },
      ],
      DataFetchers: [
        {
          name: "DemoSessions",
          Type: "Fetch",
          link: "/api/ems/demo-sessions",
        },
        { name: "Leads", Type: "Fetch", link: "/api/ems/leads" },
        {
          name: "AttendanceStatuses",
          Type: "NoFetch",
          Options: ["Scheduled", "Attended", "Absent", "Cancelled"],
        },
      ],
    },
  },
  {
    route: "/student-review-platforms",
    ListProps: {
      title: "Review Platforms",
      apiEndpoint: "/api/ems/student-review-platforms",
      filteredApiEndpoint: "/api/ems/student-review-platforms/filtered",
      filterModule: "ems_student_review_platforms",
      useMetadataFilters: true,
      addRoute: "/student-review-platforms",
      editRouteBase: "/student-review-platforms/edit",
      tablePrimaryKey: "organization_ems_student_review_platform_id",
      tableName: "ems_student_review_platforms",
      datagridKey: "ems_student_review_platforms_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "student_review_platform_name",
        "student_review_platform_url",
      ],
      config: {
        sort: { field: "student_review_platform_name", direction: "asc" },
        defaultVisibleColumns: [
          "student_review_platform_name",
          "student_review_platform_url",
        ],
        columnLabels: {
          student_review_platform_name: "Platform Name",
          student_review_platform_url: "Platform URL",
        },
        columnWidths: {
          student_review_platform_name: 260,
          student_review_platform_url: 360,
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => ({
          id: item.organization_ems_student_review_platform_id,
          student_review_platform_name:
            item?.student_review_platform_name || "-",
          student_review_platform_url:
            item?.student_review_platform_url || "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          student_review_platform_name,
          student_review_platform_url,
        } = formData;

        if (!organization_id) {
          errors.organization_id = "Organization ID is required.";
        } else if (isNaN(organization_id)) {
          errors.organization_id = "Organization ID must be a number.";
        }

        if (!student_review_platform_name?.trim()) {
          errors.student_review_platform_name = "Platform name is required.";
        } else if (student_review_platform_name.length > 100) {
          errors.student_review_platform_name =
            "Platform name cannot exceed 100 characters.";
        }

        if (
          student_review_platform_url &&
          student_review_platform_url.length > 500
        ) {
          errors.student_review_platform_url =
            "Platform URL cannot exceed 500 characters.";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },
      baseUrl: "/api/ems/student-review-platforms",
      initialData: {
        organization_entity_id: "",
        student_review_platform_name: "",
        student_review_platform_url: "",
      },
      successMessages: {
        add: "Review platform added!",
        update: "Review platform updated!",
      },
      redirectPath: "/student-review-platforms",
      headerProps: {
        addMessage: "Add Review Platform",
        updateMessage: "Update Review Platform",
        homeLink: "/student-review-platforms",
        homeText: "Review Platforms",
      },
      Inputs: [
        {
          type: "Text",
          label: "Platform Name *",
          mainKey: "student_review_platform_name",
        },
        {
          type: "Text",
          label: "Platform URL",
          mainKey: "student_review_platform_url",
        },
      ],
      DataFetchers: [],
    },
  },
  {
    route: "/student-reviews",
    ListProps: {
      title: "Student Reviews",
      apiEndpoint: "/api/ems/student-reviews",
      filteredApiEndpoint: "/api/ems/student-reviews/filtered",
      filterModule: "ems_student_reviews",
      useMetadataFilters: true,
      addRoute: "/student-reviews",
      editRouteBase: "/student-reviews/edit",
      tablePrimaryKey: "organization_ems_student_review_id",
      tableName: "ems_student_reviews",
      datagridKey: "ems_student_reviews_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "student",
        "admission",
        "platform",
        "review_status",
        "review_rating",
        "review_title",
        "review_requested_date",
        "review_submitted_date",
        "review_verified_date",
      ],
      config: {
        sort: { field: "review_requested_date", direction: "desc" },
        defaultVisibleColumns: [
          "student",
          "admission",
          "platform",
          "review_status",
          "review_rating",
          "review_title",
          "review_requested_date",
        ],
        columnLabels: {
          student: "Student",
          admission: "Admission",
          platform: "Platform",
          review_status: "Status",
          review_rating: "Rating",
          review_title: "Review Title",
          review_requested_date: "Requested Date",
          review_submitted_date: "Submitted Date",
          review_verified_date: "Verified Date",
        },
        columnWidths: {
          student: 220,
          admission: 160,
          platform: 180,
          review_status: 130,
          review_rating: 100,
          review_title: 220,
          review_requested_date: 140,
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => ({
          id: item.organization_ems_student_review_id,
          student:
            `${item?.student?.first_name || ""} (${item?.student?.student_id || "-"})`.trim() ||
            "-",
          admission: item?.admission?.admission_number || "-",
          platform:
            item?.student_review_platform?.student_review_platform_name ||
            "-",
          review_status: item?.review_status || "-",
          review_rating: formatReviewRatingLabel(item?.review_rating),
          __review_rating_value: item?.review_rating,
          review_title: item?.review_title || "-",
          review_requested_date: FormatDateV1(item.review_requested_date),
          review_submitted_date: FormatDateV1(item.review_submitted_date),
          review_verified_date: FormatDateV1(item.review_verified_date),
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const {
          organization_id,
          organization_entity_id,
          organization_ems_student_id,
          organization_ems_admission_id,
          organization_ems_student_review_platform_id,
          review_status,
          review_rating,
          review_title,
          review_content,
          review_url,
          review_requested_date,
          review_submitted_date,
          review_verified_date,
          remarks,
        } = formData;

        if (!organization_id) {
          errors.organization_id = "Organization is required.";
        } else if (isNaN(organization_id)) {
          errors.organization_id = "Organization ID must be a valid number.";
        }

        if (organization_entity_id && isNaN(organization_entity_id)) {
          errors.organization_entity_id =
            "Organization Entity ID must be a valid number.";
        }

        if (!organization_ems_student_id) {
          errors.organization_ems_student_id = "Student is required.";
        } else if (isNaN(organization_ems_student_id)) {
          errors.organization_ems_student_id =
            "Student ID must be a valid number.";
        }

        if (!organization_ems_admission_id) {
          errors.organization_ems_admission_id = "Admission is required.";
        } else if (isNaN(organization_ems_admission_id)) {
          errors.organization_ems_admission_id =
            "Admission ID must be a valid number.";
        }

        if (!organization_ems_student_review_platform_id) {
          errors.organization_ems_student_review_platform_id =
            "Review platform is required.";
        } else if (isNaN(organization_ems_student_review_platform_id)) {
          errors.organization_ems_student_review_platform_id =
            "Review platform ID must be a valid number.";
        }

        const validStatuses = [
          "Pending",
          "Requested",
          "Submitted",
          "Verified",
          "Rejected",
        ];
        if (review_status && !validStatuses.includes(review_status)) {
          errors.review_status = "Invalid review status selected.";
        }

        const validRatings = [1, 2, 3, 4, 5];
        if (
          review_rating !== "" &&
          review_rating != null &&
          !validRatings.includes(Number(review_rating))
        ) {
          errors.review_rating = "Please select a valid rating.";
        }

        if (review_title && review_title.length > 255) {
          errors.review_title = "Review title cannot exceed 255 characters.";
        }

        if (review_url && review_url.length > 500) {
          errors.review_url = "Review URL cannot exceed 500 characters.";
        }

        const dateFields = [
          ["review_requested_date", review_requested_date],
          ["review_submitted_date", review_submitted_date],
          ["review_verified_date", review_verified_date],
        ];
        dateFields.forEach(([key, value]) => {
          if (value && isNaN(Date.parse(value))) {
            errors[key] = "Invalid date.";
          }
        });

        if (remarks && remarks.length > 500) {
          errors.remarks = "Remarks cannot exceed 500 characters.";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },
      baseUrl: "/api/ems/student-reviews",
      initialData: {
        organization_entity_id: "",
        organization_ems_student_id: "",
        organization_ems_admission_id: "",
        organization_ems_student_review_platform_id: "",
        review_status: "Pending",
        review_rating: "",
        review_title: "",
        review_content: "",
        review_url: "",
        review_requested_date: "",
        review_submitted_date: "",
        review_verified_date: "",
        remarks: "",
      },
      successMessages: {
        add: "Student review added!",
        update: "Student review updated!",
      },
      redirectPath: "/student-reviews",
      headerProps: {
        addMessage: "Add Student Review",
        updateMessage: "Update Student Review",
        homeLink: "/student-reviews",
        homeText: "Student Reviews",
      },
      fieldGridLayout: {
        rows: [
          { variant: "full", mainKeys: ["organization_ems_student_id"] },
          {
            variant: "twoEqual",
            mainKeys: [
              "organization_ems_admission_id",
              "organization_ems_student_review_platform_id",
            ],
          },
          {
            variant: "twoEqual",
            mainKeys: ["review_status", "review_rating"],
          },
          { variant: "full", mainKeys: ["review_title"] },
          { variant: "full", mainKeys: ["review_content"] },
          { variant: "full", mainKeys: ["review_url"] },
          {
            variant: "threeEqual",
            mainKeys: [
              "review_requested_date",
              "review_submitted_date",
              "review_verified_date",
            ],
          },
          { variant: "full", mainKeys: ["remarks"] },
        ],
        sections: [],
      },
      Inputs: [
        {
          type: "Select",
          label: "Student *",
          mainKey: "organization_ems_student_id",
          OptionMainKey: "Students",
          menuKey: "organization_ems_student_id",
          formatValue: (val) => `${val.first_name} (${val.student_id})`,
        },
        {
          type: "Select",
          label: "Admission *",
          mainKey: "organization_ems_admission_id",
          OptionMainKey: "Admissions",
          menuKey: "organization_ems_admission_id",
          formatValue: (val) => `${val.admission_number}`,
        },
        {
          type: "Select",
          label: "Review Platform *",
          mainKey: "organization_ems_student_review_platform_id",
          OptionMainKey: "StudentReviewPlatforms",
          menuKey: "organization_ems_student_review_platform_id",
          formatValue: (val) => val.student_review_platform_name,
        },
        {
          type: "Select",
          label: "Review Status",
          mainKey: "review_status",
          OptionMainKey: "ReviewStatuses",
          menuKey: null,
          formatValue: (val) => val,
        },
        {
          type: "Select",
          label: "Rating",
          mainKey: "review_rating",
          OptionMainKey: "ReviewRatings",
          menuKey: "value",
          useOptionEmoji: true,
          formatValue: (val) =>
            val?.label ?? formatReviewRatingLabel(val?.value ?? val),
        },
        {
          type: "Text",
          label: "Review Title",
          mainKey: "review_title",
        },
        {
          type: "MultiRow",
          label: "Review Content",
          mainKey: "review_content",
        },
        {
          type: "Text",
          label: "Review URL",
          mainKey: "review_url",
        },
        {
          type: "Date",
          label: "Requested Date",
          mainKey: "review_requested_date",
        },
        {
          type: "Date",
          label: "Submitted Date",
          mainKey: "review_submitted_date",
        },
        {
          type: "Date",
          label: "Verified Date",
          mainKey: "review_verified_date",
        },
        {
          type: "MultiRow",
          label: "Remarks",
          mainKey: "remarks",
        },
      ],
      DataFetchers: [
        { name: "Students", Type: "Fetch", link: "/api/ems/students" },
        { name: "Admissions", Type: "Fetch", link: "/api/ems/admissions" },
        {
          name: "StudentReviewPlatforms",
          Type: "Fetch",
          link: "/api/ems/student-review-platforms",
        },
        {
          name: "ReviewStatuses",
          Type: "NoFetch",
          Options: [
            "Pending",
            "Requested",
            "Submitted",
            "Verified",
            "Rejected",
          ],
        },
        {
          name: "ReviewRatings",
          Type: "NoFetch",
          Options: REVIEW_RATING_OPTIONS,
        },
      ],
    },
  },
  {
    route: "/offers",
    ListProps: {
      title: "Offers",
      apiEndpoint: "/api/ems/offers",
      useMetadataFilters: false,
      addRoute: "/offers",
      editRouteBase: "/offers/edit",
      tablePrimaryKey: "organization_ems_offer_id",
      tableName: "ems_offers",
      datagridKey: "ems_offers_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "offer_name",
        "offer_code",
        "program_name",
        "discount_label",
        "valid_from",
        "valid_until",
        "description",
      ],
      config: {
        sort: { field: "organization_ems_offer_id", direction: "desc" },
        defaultVisibleColumns: [
          "offer_name",
          "offer_code",
          "program_name",
          "discount_label",
          "valid_from",
          "valid_until",
        ],
        columnLabels: {
          offer_name: "Offer Name",
          offer_code: "Offer Code",
          program_name: "Training Program",
          discount_label: "Discount",
          valid_from: "Valid From",
          valid_until: "Valid Until",
          description: "Description",
        },
        columnWidths: {
          offer_name: 240,
          offer_code: 140,
          program_name: 280,
          discount_label: 140,
          valid_from: 140,
          valid_until: 140,
          description: 280,
        },
      },
      mapResponseToRows: (data) =>
        (Array.isArray(data) ? data : []).map((item) => ({
          id: item.organization_ems_offer_id,
          organization_ems_offer_id: item.organization_ems_offer_id,
          offer_name: item.offer_name || "-",
          offer_code: item.offer_code || "-",
          organization_ems_training_program_id:
            item.organization_ems_training_program_id ?? "",
          program_name: item.program_name || "-",
          discount_label: item.discount_label || "-",
          valid_from: FormatDateV1(item.valid_from),
          valid_until: FormatDateV1(item.valid_until),
          description: item.description || "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const name = String(formData?.offer_name || "").trim();
        const code = String(formData?.offer_code || "").trim();
        const description = String(formData?.description || "").trim();
        const discountType = String(formData?.discount_type || "").trim();
        const discountValue = formData?.discount_value;
        const validFrom = String(formData?.valid_from || "").trim();
        const validUntil = String(formData?.valid_until || "").trim();

        if (!name) errors.offer_name = "Offer name is required.";
        if (!code) errors.offer_code = "Offer code is required.";
        if (!discountType) errors.discount_type = "Discount type is required.";
        if (discountValue === "" || discountValue === null || discountValue === undefined) {
          errors.discount_value = "Discount value is required.";
        } else if (Number.isNaN(Number(discountValue)) || Number(discountValue) < 0) {
          errors.discount_value = "Discount value must be 0 or more.";
        } else if (discountType === "PERCENT" && Number(discountValue) > 100) {
          errors.discount_value = "Percent discount cannot be more than 100.";
        }
        if (!validFrom) errors.valid_from = "Valid from is required.";
        if (!validUntil) errors.valid_until = "Valid until is required.";
        if (validFrom && validUntil && new Date(validUntil) < new Date(validFrom)) {
          errors.valid_until = "Valid until must be on or after valid from.";
        }
        if (!description) errors.description = "Description is required.";

        const photo = formData?.photo_url;
        if (photo instanceof File) {
          const maxBytes = 2 * 1024 * 1024;
          if (!String(photo.type || "").startsWith("image/")) {
            errors.photo_url = "Photo must be an image file.";
          } else if (photo.size > maxBytes) {
            errors.photo_url = "Photo must be 2 MB or smaller.";
          }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },
      baseUrl: "/api/ems/offers",
      initialData: {
        offer_name: "",
        offer_code: "",
        description: "",
        organization_ems_training_program_id: "",
        discount_type: "",
        discount_value: "",
        valid_from: "",
        valid_until: "",
        offer_status: "ACTIVE",
        photo_url: "",
      },
      successMessages: {
        add: "Offer added!",
        update: "Offer updated!",
      },
      redirectPath: "/offers",
      headerProps: {
        addMessage: "Add Offer",
        updateMessage: "Update Offer",
        homeLink: "/offers",
        homeText: "Offers",
      },
      fieldGridLayout: {
        rows: [
          { variant: "twoEqual", mainKeys: ["offer_name", "offer_code"] },
          { variant: "full", mainKeys: ["description"] },
          { variant: "twoEqual", mainKeys: ["discount_type", "discount_value"] },
          { variant: "twoEqual", mainKeys: ["valid_from", "valid_until"] },
          { variant: "full", mainKeys: ["organization_ems_training_program_id"] },
          { variant: "full", mainKeys: ["photo_url"] },
        ],
      },
      Inputs: [
        { type: "Text", label: "Offer Name *", mainKey: "offer_name" },
        { type: "Text", label: "Offer Code *", mainKey: "offer_code" },
        {
          type: "Select",
          label: "Program you are selling",
          mainKey: "organization_ems_training_program_id",
          OptionMainKey: "TrainingPrograms",
          menuKey: "organization_ems_training_program_id",
          formatValue: (val) => val?.training_program_name || "",
        },
        {
          type: "Select",
          label: "Discount Type *",
          mainKey: "discount_type",
          OptionMainKey: "OfferDiscountTypes",
          menuKey: "value",
          formatValue: (val) => val?.label || "",
        },
        { type: "Number", label: "Discount Value *", mainKey: "discount_value" },
        { type: "DateTime", label: "Valid From *", mainKey: "valid_from" },
        { type: "DateTime", label: "Valid Until *", mainKey: "valid_until" },
        { type: "MultiRow", label: "Description *", mainKey: "description" },
        {
          type: "Picture",
          label: "Offer Photo",
          mainKey: "photo_url",
          align: "left",
          showFieldLabel: true,
          uploadButtonLabel: "Upload Image",
        },
      ],
      DataFetchers: [
        { name: "TrainingPrograms", Type: "Fetch", link: "/api/ems/training-programs" },
        {
          name: "OfferDiscountTypes",
          Type: "NoFetch",
          Options: [
            { label: "Percent", value: "PERCENT" },
            { label: "Flat amount (INR)", value: "FLAT" },
          ],
        },
      ],
    },
  },
  {
    route: "/offer-audiences",
    ListProps: {
      title: "Offer Audiences",
      apiEndpoint: "/api/ems/offer-audiences",
      useMetadataFilters: false,
      addRoute: "/offer-audiences",
      editRouteBase: "/offer-audiences/edit",
      tablePrimaryKey: "organization_ems_offer_audience_id",
      tableName: "ems_offer_audiences",
      datagridKey: "ems_offer_audiences_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "offer_name",
        "offer_code",
        "recipient_label",
        "audience_label",
        "target_name",
      ],
      config: {
        sort: { field: "organization_ems_offer_audience_id", direction: "desc" },
        defaultVisibleColumns: [
          "offer_name",
          "recipient_label",
          "audience_label",
          "target_name",
        ],
        columnLabels: {
          offer_name: "Offer",
          offer_code: "Offer Code",
          recipient_label: "Offer scope",
          audience_label: "Audience",
          target_name: "Target",
        },
        columnWidths: {
          offer_name: 240,
          offer_code: 140,
          recipient_label: 120,
          audience_label: 180,
          target_name: 260,
        },
      },
      mapResponseToRows: (data) => {
        const audienceLabels = {
          PUBLIC: "Public",
          ALL_LEADS: "All leads",
          LEAD: "One lead",
          ALL_STUDENTS: "All students",
          TRAINING_CATEGORY: "Training category",
          TRAINING_PROGRAM: "Training program",
          BATCH: "Batch",
          STUDENT: "One student",
          ALUMNI: "Alumni",
        };
        return (Array.isArray(data) ? data : []).map((item) => ({
          id: item.organization_ems_offer_audience_id,
          organization_ems_offer_audience_id: item.organization_ems_offer_audience_id,
          offer_name: item.offer_name || "-",
          offer_code: item.offer_code || "-",
          recipient_label: item.recipient_type === "INTERNAL" ? "Internal" : "External",
          audience_label: audienceLabels[item.audience_type] || item.audience_type || "-",
          target_name: item.target_name || "-",
        }));
      },
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const recipient = String(formData?.recipient_type || "").trim();
        const audience = String(formData?.audience_type || "").trim();
        const external = ["PUBLIC", "ALL_LEADS", "LEAD"];
        const internal = [
          "ALL_STUDENTS",
          "TRAINING_CATEGORY",
          "TRAINING_PROGRAM",
          "BATCH",
          "STUDENT",
          "ALUMNI",
        ];
        const has = (key) =>
          formData?.[key] !== "" && formData?.[key] !== null && formData?.[key] !== undefined;

        if (!formData?.organization_ems_offer_id) {
          errors.organization_ems_offer_id = "Offer is required.";
        }
        if (!recipient) errors.recipient_type = "Offer scope is required.";
        if (!audience) errors.audience_type = "Audience is required.";
        if (recipient === "EXTERNAL" && audience && !external.includes(audience)) {
          errors.audience_type = "Pick a public or lead audience for an external side.";
        }
        if (recipient === "INTERNAL" && audience && !internal.includes(audience)) {
          errors.audience_type = "Pick a student audience for an internal side.";
        }
        if (audience === "LEAD" && !has("organization_ems_lead_id")) {
          errors.organization_ems_lead_id = "Lead is required for this audience.";
        }
        if (audience === "STUDENT" && !has("organization_ems_student_id")) {
          errors.organization_ems_student_id = "Student is required for this audience.";
        }
        if (audience === "ALUMNI" && !has("organization_ems_student_id")) {
          errors.organization_ems_student_id =
            "Select a student whose batch status is Completed.";
        }
        if (audience === "TRAINING_CATEGORY" && !has("organization_ems_training_program_category_id")) {
          errors.organization_ems_training_program_category_id = "Category is required for this audience.";
        }
        if (audience === "TRAINING_PROGRAM" && !has("organization_ems_training_program_id")) {
          errors.organization_ems_training_program_id = "Program is required for this audience.";
        }
        if (audience === "BATCH" && !has("organization_ems_batch_id")) {
          errors.organization_ems_batch_id = "Batch is required for this audience.";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },
      baseUrl: "/api/ems/offer-audiences",
      initialData: {
        organization_ems_offer_id: "",
        recipient_type: "",
        audience_type: "",
        organization_ems_lead_id: "",
        organization_ems_student_id: "",
        organization_ems_training_program_category_id: "",
        organization_ems_training_program_id: "",
        organization_ems_batch_id: "",
      },
      successMessages: {
        add: "Offer audience added!",
        update: "Offer audience updated!",
      },
      redirectPath: "/offer-audiences",
      headerProps: {
        addMessage: "Add Offer Audience",
        updateMessage: "Update Offer Audience",
        homeLink: "/offer-audiences",
        homeText: "Offer Audiences",
      },
      fieldGridLayout: {
        rows: [
          {
            variant: "twoEqual",
            mainKeys: ["organization_ems_offer_id", "recipient_type"],
          },
          {
            variant: "twoEqual",
            mainKeys: ["audience_type", "organization_ems_lead_id"],
          },
          {
            variant: "twoEqual",
            mainKeys: [
              "organization_ems_student_id",
              "organization_ems_training_program_category_id",
            ],
          },
          {
            variant: "twoEqual",
            mainKeys: ["organization_ems_training_program_id", "organization_ems_batch_id"],
          },
        ],
        sections: [{ beforeRow: 1, title: "Who receives it" }],
      },
      Inputs: [
        {
          type: "Select",
          label: "Offer *",
          mainKey: "organization_ems_offer_id",
          OptionMainKey: "Offers",
          menuKey: "organization_ems_offer_id",
          formatValue: (val) =>
            [val?.offer_name, val?.offer_code].filter(Boolean).join(" · ") || "",
          disabledWhen: (formData) => Boolean(formData?._lock_offer),
        },
        {
          type: "Select",
          label: "Offer scope *",
          mainKey: "recipient_type",
          OptionMainKey: "OfferRecipientTypes",
          menuKey: "value",
          formatValue: (val) => val?.label || "",
        },
        {
          type: "Select",
          label: "Audience *",
          mainKey: "audience_type",
          OptionMainKey: "OfferAudienceTypes",
          menuKey: "value",
          formatValue: (val) => val?.label || "",
          disabledWhen: (formData) => !formData?.recipient_type,
          clearWhenDisabled: true,
        },
        {
          type: "Select",
          label: "Lead",
          mainKey: "organization_ems_lead_id",
          OptionMainKey: "Leads",
          menuKey: "organization_ems_lead_id",
          formatValue: (val) =>
            val?.person_full_name ||
            [val?.first_name, val?.last_name].filter(Boolean).join(" ") ||
            "",
          disabledWhen: (formData) => formData?.audience_type !== "LEAD",
          clearWhenDisabled: true,
        },
        {
          type: "Select",
          label: "Student",
          mainKey: "organization_ems_student_id",
          OptionMainKey: "Students",
          menuKey: "organization_ems_student_id",
          formatValue: (val) =>
            [val?.first_name, val?.middle_name, val?.last_name]
              .filter((part) => part && String(part).trim() !== "")
              .join(" ")
              .trim(),
          disabledWhen: (formData) =>
            !["STUDENT", "ALUMNI"].includes(formData?.audience_type),
          clearWhenDisabled: true,
        },
        {
          type: "Select",
          label: "Training Category",
          mainKey: "organization_ems_training_program_category_id",
          OptionMainKey: "ProgramCategory",
          menuKey: "organization_ems_training_program_category_id",
          formatValue: (val) => val?.training_program_category_name || "",
          disabledWhen: (formData) => formData?.audience_type !== "TRAINING_CATEGORY",
          clearWhenDisabled: true,
        },
        {
          type: "Select",
          label: "Training Program",
          mainKey: "organization_ems_training_program_id",
          OptionMainKey: "TrainingPrograms",
          menuKey: "organization_ems_training_program_id",
          formatValue: (val) => val?.training_program_name || "",
          disabledWhen: (formData) => formData?.audience_type !== "TRAINING_PROGRAM",
          clearWhenDisabled: true,
        },
        {
          type: "Select",
          label: "Batch",
          mainKey: "organization_ems_batch_id",
          OptionMainKey: "Batches",
          menuKey: "organization_ems_batch_id",
          formatValue: (val) => val?.batch_code || val?.batch_name || "",
          disabledWhen: (formData) => formData?.audience_type !== "BATCH",
          clearWhenDisabled: true,
        },
      ],
      DataFetchers: [
        { name: "Offers", Type: "Fetch", link: "/api/ems/offers" },
        { name: "Leads", Type: "Fetch", link: "/api/ems/leads" },
        { name: "Students", Type: "Fetch", link: "/api/ems/students" },
        {
          name: "ProgramCategory",
          Type: "Fetch",
          link: "/api/ems/training-programs-categories",
        },
        { name: "TrainingPrograms", Type: "Fetch", link: "/api/ems/training-programs" },
        { name: "Batches", Type: "Fetch", link: "/api/ems/batches" },
        {
          name: "OfferRecipientTypes",
          Type: "NoFetch",
          Options: [
            { label: "External", value: "EXTERNAL" },
            { label: "Internal", value: "INTERNAL" },
          ],
        },
        {
          name: "OfferAudienceTypes",
          Type: "NoFetch",
          Options: [
            { label: "Public", value: "PUBLIC" },
            { label: "All leads", value: "ALL_LEADS" },
            { label: "One lead", value: "LEAD" },
            { label: "All students", value: "ALL_STUDENTS" },
            { label: "Training category", value: "TRAINING_CATEGORY" },
            { label: "Training program", value: "TRAINING_PROGRAM" },
            { label: "Batch", value: "BATCH" },
            { label: "One student", value: "STUDENT" },
            { label: "Alumni", value: "ALUMNI" },
          ],
        },
      ],
    },
  },
  {
    route: "/offer-redemptions",
    ListProps: {
      title: "Offer Redemptions Report",
      apiEndpoint: "/api/ems/offer-redemptions",
      useMetadataFilters: false,
      addRoute: "/offer-redemptions",
      editRouteBase: "/offer-redemptions/edit",
      tablePrimaryKey: "organization_ems_offer_redemption_id",
      tableName: "ems_offer_redemptions",
      datagridKey: "ems_offer_redemptions_grid",
      showAddButton: false,
      readOnlyList: true,
      hideFilterButton: true,
      hidePersonalizeView: true,
      personalizeColumnKeys: [
        "offer_name",
        "offer_code",
        "person_name",
        "recipient_label",
        "admission_number",
        "redemption_label",
        "discount_amount",
        "final_amount",
        "claimed_at",
        "redeemed_at",
      ],
      config: {
        sort: { field: "organization_ems_offer_redemption_id", direction: "desc" },
        defaultVisibleColumns: [
          "offer_name",
          "offer_code",
          "person_name",
          "recipient_label",
          "admission_number",
          "redemption_label",
          "discount_amount",
          "final_amount",
          "claimed_at",
        ],
        columnLabels: {
          offer_name: "Offer",
          offer_code: "Coupon",
          person_name: "Person",
          recipient_label: "Audience",
          admission_number: "Adm. No.",
          redemption_label: "Status",
          discount_amount: "Discount (INR)",
          final_amount: "Net (INR)",
          claimed_at: "Claimed",
          redeemed_at: "Applied",
        },
        columnWidths: {
          offer_name: 220,
          offer_code: 120,
          person_name: 240,
          recipient_label: 120,
          admission_number: 120,
          redemption_label: 140,
          discount_amount: 140,
          final_amount: 140,
          claimed_at: 140,
          redeemed_at: 140,
        },
      },
      mapResponseToRows: (data) => {
        const statusLabels = {
          CLAIMED: "Claimed",
          APPLIED: "Applied",
          REDEEMED: "Redeemed",
          CANCELLED: "Cancelled",
          EXPIRED: "Expired",
        };
        return (Array.isArray(data) ? data : []).map((item) => ({
          id: item.organization_ems_offer_redemption_id,
          organization_ems_offer_redemption_id: item.organization_ems_offer_redemption_id,
          offer_name: item.offer_name || "-",
          offer_code: item.offer_code || "-",
          person_name: item.person_name || "-",
          recipient_label: item.recipient_type === "INTERNAL" ? "Internal" : "External",
          admission_number: item.admission_number || "-",
          redemption_label: statusLabels[item.redemption_status] || item.redemption_status || "-",
          discount_amount: item.discount_amount ?? "-",
          final_amount: item.final_amount ?? "-",
          claimed_at: FormatDateV1(item.claimed_at),
          redeemed_at: FormatDateV1(item.redeemed_at),
        }));
      },
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        const recipient = String(formData?.recipient_type || "").trim();
        const has = (key) =>
          formData?.[key] !== "" && formData?.[key] !== null && formData?.[key] !== undefined;

        if (!formData?.organization_ems_offer_id) {
          errors.organization_ems_offer_id = "Offer is required.";
        }
        if (!recipient) errors.recipient_type = "Side is required.";
        if (!formData?.redemption_status) {
          errors.redemption_status = "Status is required.";
        }
        if (recipient === "EXTERNAL" && !has("organization_ems_lead_id")) {
          errors.organization_ems_lead_id = "An external redemption needs a lead.";
        }
        if (recipient === "INTERNAL" && !has("organization_ems_student_id")) {
          errors.organization_ems_student_id = "An internal redemption needs a student.";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },
      baseUrl: "/api/ems/offer-redemptions",
      initialData: {
        organization_ems_offer_id: "",
        recipient_type: "",
        organization_ems_student_id: "",
        organization_ems_lead_id: "",
        organization_ems_admission_id: "",
        redemption_status: "CLAIMED",
        original_amount: "",
        discount_amount: "",
        final_amount: "",
        original_amount_student_currency: "",
        discount_amount_student_currency: "",
        final_amount_student_currency: "",
        claimed_at: "",
        redeemed_at: "",
      },
      successMessages: {
        add: "Offer redemption added!",
        update: "Offer redemption updated!",
      },
      redirectPath: "/offer-redemptions",
      headerProps: {
        addMessage: "Add Offer Redemption",
        updateMessage: "Update Offer Redemption",
        homeLink: "/offer-redemptions",
        homeText: "Offer Redemptions",
      },
      fieldGridLayout: {
        rows: [
          {
            variant: "twoEqual",
            mainKeys: ["organization_ems_offer_id", "recipient_type"],
          },
          {
            variant: "twoEqual",
            mainKeys: ["organization_ems_lead_id", "organization_ems_student_id"],
          },
          {
            variant: "twoEqual",
            mainKeys: ["organization_ems_admission_id", "redemption_status"],
          },
          {
            variant: "threeEqual",
            mainKeys: ["original_amount", "discount_amount", "final_amount"],
          },
          {
            variant: "threeEqual",
            mainKeys: [
              "original_amount_student_currency",
              "discount_amount_student_currency",
              "final_amount_student_currency",
            ],
          },
          { variant: "twoEqual", mainKeys: ["claimed_at", "redeemed_at"] },
        ],
        sections: [{ beforeRow: 3, title: "Fee" }],
      },
      Inputs: [
        {
          type: "Select",
          label: "Offer *",
          mainKey: "organization_ems_offer_id",
          OptionMainKey: "Offers",
          menuKey: "organization_ems_offer_id",
          formatValue: (val) =>
            [val?.offer_name, val?.offer_code].filter(Boolean).join(" · ") || "",
        },
        {
          type: "Select",
          label: "Side *",
          mainKey: "recipient_type",
          OptionMainKey: "OfferRecipientTypes",
          menuKey: "value",
          formatValue: (val) => val?.label || "",
        },
        {
          type: "Select",
          label: "Lead",
          mainKey: "organization_ems_lead_id",
          OptionMainKey: "Leads",
          menuKey: "organization_ems_lead_id",
          formatValue: (val) =>
            val?.person_full_name ||
            [val?.first_name, val?.last_name].filter(Boolean).join(" ") ||
            "",
        },
        {
          type: "Select",
          label: "Student",
          mainKey: "organization_ems_student_id",
          OptionMainKey: "Students",
          menuKey: "organization_ems_student_id",
          formatValue: (val) =>
            [val?.first_name, val?.middle_name, val?.last_name]
              .filter((part) => part && String(part).trim() !== "")
              .join(" ")
              .trim(),
        },
        {
          type: "Select",
          label: "Admission",
          mainKey: "organization_ems_admission_id",
          OptionMainKey: "Admissions",
          menuKey: "organization_ems_admission_id",
          formatValue: (val) => val?.admission_number || "",
        },
        {
          type: "Select",
          label: "Status *",
          mainKey: "redemption_status",
          OptionMainKey: "OfferRedemptionStatuses",
          menuKey: "value",
          formatValue: (val) => val?.label || "",
        },
        { type: "Number", label: "Total Fee Amount (INR)", mainKey: "original_amount" },
        { type: "Number", label: "Discount Amount (INR)", mainKey: "discount_amount" },
        { type: "Number", label: "Net Fee Amount (INR)", mainKey: "final_amount" },
        {
          type: "Number",
          label: "Total Fee (Student Currency)",
          mainKey: "original_amount_student_currency",
        },
        {
          type: "Number",
          label: "Discount (Student Currency)",
          mainKey: "discount_amount_student_currency",
        },
        {
          type: "Number",
          label: "Net Fee (Student Currency)",
          mainKey: "final_amount_student_currency",
        },
        { type: "DateTime", label: "Claimed At", mainKey: "claimed_at" },
        { type: "DateTime", label: "Redeemed At", mainKey: "redeemed_at" },
      ],
      DataFetchers: [
        { name: "Offers", Type: "Fetch", link: "/api/ems/offers" },
        { name: "Leads", Type: "Fetch", link: "/api/ems/leads" },
        { name: "Students", Type: "Fetch", link: "/api/ems/students" },
        { name: "Admissions", Type: "Fetch", link: "/api/ems/admissions" },
        {
          name: "OfferRecipientTypes",
          Type: "NoFetch",
          Options: [
            { label: "External", value: "EXTERNAL" },
            { label: "Internal", value: "INTERNAL" },
          ],
        },
        {
          name: "OfferRedemptionStatuses",
          Type: "NoFetch",
          Options: [
            { label: "Claimed", value: "CLAIMED" },
            { label: "Applied", value: "APPLIED" },
            { label: "Redeemed", value: "REDEEMED" },
            { label: "Cancelled", value: "CANCELLED" },
            { label: "Expired", value: "EXPIRED" },
          ],
        },
      ],
    },
  },
  {
    route: "/placement-assistance",
    ListProps: {
      title: "Placement Assistance",
      apiEndpoint: "/api/ems/placement-assistance",
      filteredApiEndpoint: "/api/ems/placement-assistance/filtered",
      filterModule: "ems_placement_assistance",
      useMetadataFilters: true,
      addRoute: "/placement-assistance",
      editRouteBase: "/placement-assistance/edit",
      tablePrimaryKey: "organization_ems_placement_assistance_id",
      tableName: "ems_placement_assistance",
      datagridKey: "ems_placement_assistance_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "student",
        "admission",
        "cv_status",
        "cv_prepared_by",
        "cv_completed_at",
        "linkedin_status",
        "linkedin_prepared_by",
        "linkedin_completed_at",
        "questionnaires_status",
        "questionnaires_completed_at",
        "mock_interviews_status",
        "mock_interviews_completed_at",
        "placement_network_status",
        "network_guide",
        "placement_network_completed_at",
        "overall_status",
      ],
      config: {
        sort: { field: "organization_ems_placement_assistance_id", direction: "desc" },
        defaultVisibleColumns: [
          "student",
          "admission",
          "cv_status",
          "cv_prepared_by",
          "cv_completed_at",
          "linkedin_status",
          "linkedin_prepared_by",
          "linkedin_completed_at",
          "questionnaires_status",
          "questionnaires_completed_at",
          "mock_interviews_status",
          "mock_interviews_completed_at",
          "placement_network_status",
          "network_guide",
          "placement_network_completed_at",
          "overall_status",
        ],
        columnLabels: {
          student: "Student",
          admission: "Admission",
          cv_status: "CV",
          cv_prepared_by: "CV Prepared By",
          cv_completed_at: "CV Completed",
          linkedin_status: "LinkedIn",
          linkedin_prepared_by: "LinkedIn Prepared By",
          linkedin_completed_at: "LinkedIn Completed",
          network_guide: "Network Guided By",
          questionnaires_status: "Questionnaires",
          questionnaires_completed_at: "Questionnaires Completed",
          mock_interviews_status: "Mock Interviews",
          mock_interviews_completed_at: "Mock Interviews Completed",
          placement_network_status: "Placement Network",
          placement_network_completed_at: "Network Completed",
          overall_status: "Overall",
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => ({
          id: item.organization_ems_placement_assistance_id,
          student: `${item?.admission?.student?.first_name || ""} ${item?.admission?.student?.last_name || ""}`.trim() || "-",
          admission: item?.admission?.admission_number || "-",
          cv_status: item.cv_status || "-",
          cv_prepared_by: item?.cv_preparer?.full_name || [item?.cv_preparer?.first_name, item?.cv_preparer?.last_name].filter(Boolean).join(" ") || "-",
          cv_completed_at: formatDateTime(item.cv_completed_at) || "-",
          linkedin_status: item.linkedin_status || "-",
          linkedin_completed_at: formatDateTime(item.linkedin_completed_at) || "-",
          questionnaires_completed_at: formatDateTime(item.questionnaires_completed_at) || "-",
          mock_interviews_completed_at: formatDateTime(item.mock_interviews_completed_at) || "-",
          placement_network_completed_at: formatDateTime(item.placement_network_completed_at) || "-",
          linkedin_prepared_by: item?.linkedin_preparer?.full_name || [item?.linkedin_preparer?.first_name, item?.linkedin_preparer?.last_name].filter(Boolean).join(" ") || "-",
          network_guide: item?.network_guide?.full_name || [item?.network_guide?.first_name, item?.network_guide?.last_name].filter(Boolean).join(" ") || "-",
          questionnaires_status: item.questionnaires_status || "-",
          mock_interviews_status: item.mock_interviews_status || "-",
          placement_network_status: item.placement_network_status || "-",
          overall_status: item.overall_status || "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        if (!formData.organization_ems_admission_id) {
          errors.organization_ems_admission_id = "Admission is required.";
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },
      baseUrl: "/api/ems/placement-assistance",
      initialData: {
        organization_ems_admission_id: "",
        cv_status: "Pending",
        cv_prepared_by: "",
        cv_file_url: "",
        cv_completed_at: "",
        linkedin_status: "Pending",
        linkedin_prepared_by: "",
        linkedin_profile_url: "",
        linkedin_completed_at: "",
        questionnaires_status: "Pending",
        questionnaires_completed_at: "",
        mock_interviews_status: "Pending",
        mock_interviews_completed_at: "",
        placement_network_status: "Pending",
        placement_network_guided_by: "",
        placement_network_completed_at: "",
        overall_status: "Pending",
        remarks: "",
      },
      successMessages: {
        add: "Placement assistance added!",
        update: "Placement assistance updated!",
      },
      redirectPath: "/placement-assistance",
      headerProps: {
        addMessage: "Add Placement Assistance",
        updateMessage: "Update Placement Assistance",
        homeLink: "/placement-assistance",
        homeText: "Placement Assistance",
      },
      fieldGridLayout: {
        sections: [
          { beforeRow: 1, title: "CV" },
          { beforeRow: 4, title: "LinkedIn" },
          { beforeRow: 7, title: "Questionnaires" },
          { beforeRow: 8, title: "Mock Interviews" },
          { beforeRow: 9, title: "Placement Network" },
          { beforeRow: 11, title: "Overall" },
        ],
        rows: [
          { variant: "full", mainKeys: ["organization_ems_admission_id"] },
          { variant: "twoEqual", mainKeys: ["cv_status", "cv_completed_at"] },
          { variant: "full", mainKeys: ["cv_prepared_by"] },
          { variant: "full", mainKeys: ["cv_file_url"] },
          { variant: "twoEqual", mainKeys: ["linkedin_status", "linkedin_completed_at"] },
          { variant: "full", mainKeys: ["linkedin_prepared_by"] },
          { variant: "full", mainKeys: ["linkedin_profile_url"] },
          { variant: "twoEqual", mainKeys: ["questionnaires_status", "questionnaires_completed_at"] },
          { variant: "twoEqual", mainKeys: ["mock_interviews_status", "mock_interviews_completed_at"] },
          { variant: "twoEqual", mainKeys: ["placement_network_status", "placement_network_completed_at"] },
          { variant: "full", mainKeys: ["placement_network_guided_by"] },
          { variant: "twoEqual", mainKeys: ["overall_status"], itemMds: [1, 1] },
          { variant: "full", mainKeys: ["remarks"] },
        ],
      },
      Inputs: [
        { type: "Select", label: "Admission *", mainKey: "organization_ems_admission_id", OptionMainKey: "Admissions", menuKey: "organization_ems_admission_id", formatValue: (val) => {
          const student = val?.student || {};
          const name = [student.first_name, student.last_name].filter(Boolean).join(" ") || val?.lead?.person_full_name || "";
          const number = val?.admission_number || val?.organization_ems_admission_id || "";
          return name ? `${number} - ${name}` : String(number);
        } },
        { type: "Select", label: "CV Status", mainKey: "cv_status", OptionMainKey: "ServiceStatuses", menuKey: null, formatValue: (val) => val },
        { type: "Select", label: "CV Prepared By", mainKey: "cv_prepared_by", OptionMainKey: "Employees", menuKey: "organization_user_id", formatValue: (val) => val.full_name || `${val.first_name || ""} ${val.last_name || ""}`.trim() },
        { type: "File", label: "CV File", mainKey: "cv_file_url", accept: ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" },
        { type: "DateTime", label: "CV Completed At", mainKey: "cv_completed_at" },
        { type: "Select", label: "LinkedIn Status", mainKey: "linkedin_status", OptionMainKey: "ServiceStatuses", menuKey: null, formatValue: (val) => val },
        { type: "Select", label: "LinkedIn Prepared By", mainKey: "linkedin_prepared_by", OptionMainKey: "Employees", menuKey: "organization_user_id", formatValue: (val) => val.full_name || `${val.first_name || ""} ${val.last_name || ""}`.trim() },
        { type: "Text", label: "LinkedIn Profile URL", mainKey: "linkedin_profile_url" },
        { type: "DateTime", label: "LinkedIn Completed At", mainKey: "linkedin_completed_at" },
        { type: "Select", label: "Questionnaires Status", mainKey: "questionnaires_status", OptionMainKey: "ServiceStatuses", menuKey: null, formatValue: (val) => val },
        { type: "DateTime", label: "Questionnaires Completed At", mainKey: "questionnaires_completed_at" },
        { type: "Select", label: "Mock Interviews Status", mainKey: "mock_interviews_status", OptionMainKey: "ServiceStatuses", menuKey: null, formatValue: (val) => val },
        { type: "DateTime", label: "Mock Interviews Completed At", mainKey: "mock_interviews_completed_at" },
        { type: "Select", label: "Placement Network Status", mainKey: "placement_network_status", OptionMainKey: "ServiceStatuses", menuKey: null, formatValue: (val) => val },
        { type: "Select", label: "Network Guided By", mainKey: "placement_network_guided_by", OptionMainKey: "Employees", menuKey: "organization_user_id", formatValue: (val) => val.full_name || `${val.first_name || ""} ${val.last_name || ""}`.trim() },
        { type: "DateTime", label: "Network Guidance Completed At", mainKey: "placement_network_completed_at" },
        { type: "Select", label: "Overall Status", mainKey: "overall_status", OptionMainKey: "ServiceStatuses", menuKey: null, formatValue: (val) => val },
        { type: "MultiRow", label: "Remarks", mainKey: "remarks" },
      ],
      DataFetchers: [
        { name: "Admissions", Type: "Fetch", link: "/api/ems/admissions" },
        { name: "Employees", Type: "Fetch", link: "/api/organizations/${org_id}/users-data-all" },
        { name: "ServiceStatuses", Type: "NoFetch", Options: ["Pending", "In Progress", "Completed"] },
      ],
    },
  },
  {
    route: "/placement-mock-interviews",
    ListProps: {
      title: "Mock Interviews",
      apiEndpoint: "/api/ems/placement-mock-interviews",
      filteredApiEndpoint: "/api/ems/placement-mock-interviews/filtered",
      filterModule: "ems_placement_mock_interviews",
      useMetadataFilters: true,
      addRoute: "/placement-mock-interviews",
      editRouteBase: "/placement-mock-interviews/edit",
      tablePrimaryKey: "organization_ems_placement_mock_interview_id",
      tableName: "ems_placement_mock_interviews",
      datagridKey: "ems_placement_mock_interviews_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "student",
        "admission",
        "interview_number",
        "interview_type",
        "scheduled_at",
        "conducted_at",
        "interviewer",
        "overall_score",
        "result",
        "status",
      ],
      config: {
        sort: { field: "scheduled_at", direction: "desc" },
        defaultVisibleColumns: [
          "student",
          "admission",
          "interview_number",
          "interview_type",
          "scheduled_at",
          "conducted_at",
          "interviewer",
          "overall_score",
          "result",
          "status",
        ],
        columnLabels: {
          student: "Student",
          admission: "Admission",
          interview_number: "Number",
          interview_type: "Type",
          scheduled_at: "Scheduled",
          conducted_at: "Conducted",
          interviewer: "Interviewer",
          overall_score: "Overall Score",
          result: "Result",
          status: "Status",
        },
      },
      mapResponseToRows: (data) =>
        data.map((item) => ({
          id: item.organization_ems_placement_mock_interview_id,
          student: `${item?.admission?.student?.first_name || ""} ${item?.admission?.student?.last_name || ""}`.trim() || "-",
          admission: item?.admission?.admission_number || "-",
          interview_number: item.interview_number || "-",
          interview_type: item.interview_type || "-",
          scheduled_at: formatDateTime(item.scheduled_at) || "-",
          conducted_at: formatDateTime(item.conducted_at) || "-",
          interviewer: item?.interviewer?.full_name || [item?.interviewer?.first_name, item?.interviewer?.last_name].filter(Boolean).join(" ") || "-",
          overall_score: item.overall_score ?? "-",
          result: item.result || "-",
          status: item.status || "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        if (!formData.organization_ems_admission_id) errors.organization_ems_admission_id = "Admission is required.";
        if (!formData.interview_number) errors.interview_number = "Interview number is required.";
        if (!formData.interview_type) errors.interview_type = "Interview type is required.";
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },
      baseUrl: "/api/ems/placement-mock-interviews",
      initialData: {
        organization_ems_admission_id: "",
        interview_number: "",
        interview_type: "",
        scheduled_at: "",
        conducted_at: "",
        interviewer_employee_id: "",
        duration_minutes: "",
        technical_score: "",
        practical_score: "",
        project_score: "",
        communication_score: "",
        confidence_score: "",
        overall_score: "",
        result: "",
        feedback: "",
        improvement_areas: "",
        status: "Scheduled",
      },
      successMessages: {
        add: "Mock interview added!",
        update: "Mock interview updated!",
      },
      redirectPath: "/placement-mock-interviews",
      headerProps: {
        addMessage: "Add Mock Interview",
        updateMessage: "Update Mock Interview",
        homeLink: "/placement-mock-interviews",
        homeText: "Mock Interviews",
      },
      fieldGridLayout: {
        rows: [
          { variant: "full", mainKeys: ["organization_ems_admission_id"] },
          { variant: "twoEqual", mainKeys: ["interview_number", "interview_type"] },
          { variant: "twoEqual", mainKeys: ["scheduled_at", "conducted_at"] },
          { variant: "twoEqual", mainKeys: ["interviewer_employee_id", "duration_minutes"] },
          { variant: "twoEqual", mainKeys: ["technical_score", "practical_score"] },
          { variant: "twoEqual", mainKeys: ["project_score", "communication_score"] },
          { variant: "twoEqual", mainKeys: ["confidence_score", "overall_score"] },
          { variant: "twoEqual", mainKeys: ["result", "status"] },
          { variant: "full", mainKeys: ["feedback"] },
          { variant: "full", mainKeys: ["improvement_areas"] },
        ],
      },
      Inputs: [
        { type: "Select", label: "Admission *", mainKey: "organization_ems_admission_id", OptionMainKey: "Admissions", menuKey: "organization_ems_admission_id", formatValue: (val) => {
          const student = val?.student || {};
          const name = [student.first_name, student.last_name].filter(Boolean).join(" ") || val?.lead?.person_full_name || "";
          const number = val?.admission_number || val?.organization_ems_admission_id || "";
          return name ? `${number} - ${name}` : String(number);
        } },
        { type: "Number", label: "Interview Number *", mainKey: "interview_number" },
        { type: "Select", label: "Interview Type *", mainKey: "interview_type", OptionMainKey: "InterviewTypes", menuKey: null, formatValue: (val) => val },
        { type: "DateTime", label: "Scheduled At", mainKey: "scheduled_at" },
        { type: "DateTime", label: "Conducted At", mainKey: "conducted_at" },
        { type: "Select", label: "Interviewer", mainKey: "interviewer_employee_id", OptionMainKey: "Employees", menuKey: "organization_user_id", formatValue: (val) => val.full_name || `${val.first_name || ""} ${val.last_name || ""}`.trim() },
        { type: "Number", label: "Duration (Minutes)", mainKey: "duration_minutes" },
        { type: "Number", label: "Technical Score", mainKey: "technical_score" },
        { type: "Number", label: "Practical Score", mainKey: "practical_score" },
        { type: "Number", label: "Project Score", mainKey: "project_score" },
        { type: "Number", label: "Communication Score", mainKey: "communication_score" },
        { type: "Number", label: "Confidence Score", mainKey: "confidence_score" },
        { type: "Number", label: "Overall Score", mainKey: "overall_score" },
        { type: "Select", label: "Result", mainKey: "result", OptionMainKey: "InterviewResults", menuKey: null, formatValue: (val) => val },
        { type: "Select", label: "Status", mainKey: "status", OptionMainKey: "InterviewStatuses", menuKey: null, formatValue: (val) => val },
        { type: "MultiRow", label: "Feedback", mainKey: "feedback" },
        { type: "MultiRow", label: "Improvement Areas", mainKey: "improvement_areas" },
      ],
      DataFetchers: [
        { name: "Admissions", Type: "Fetch", link: "/api/ems/admissions" },
        { name: "Employees", Type: "Fetch", link: "/api/organizations/${org_id}/users-data-all" },
        { name: "InterviewTypes", Type: "NoFetch", Options: ["Technical Fundamentals", "Advanced Technical", "Final Job Readiness"] },
        { name: "InterviewResults", Type: "NoFetch", Options: ["Passed", "Needs Improvement"] },
        { name: "InterviewStatuses", Type: "NoFetch", Options: ["Scheduled", "Completed", "Cancelled", "Rescheduled"] },
      ],
    },
  },
];

/** Activity datetime must be on or after the parent lead's datetime. */
export function validateActivityDatetimeAgainstLead(
  activityDatetime,
  leadDatetime
) {
  if (!activityDatetime || !leadDatetime) return null;
  const activityMs = Date.parse(activityDatetime);
  const leadMs = Date.parse(leadDatetime);
  if (Number.isNaN(activityMs) || Number.isNaN(leadMs)) return null;
  if (activityMs < leadMs) {
    return "Activity date & time must be on or after the lead date & time.";
  }
  return null;
}

/** Validation for lead-form embedded activity (no employee / summary / remarks fields). */
export function validateLeadActivityEmbedForm(formData, setFormErrors) {
  const errors = {};
  const {
    organization_id,
    organization_ems_lead_id,
    organization_user_id,
    organization_ems_lead_stage_id,
    activity_type,
    activity_datetime,
    next_follow_up,
    lead_direction,
    lead_datetime,
    call_status,
  } = formData;

  if (!organization_id) {
    errors.organization_id = "Organization is required.";
  } else if (isNaN(organization_id)) {
    errors.organization_id = "Organization ID must be a number.";
  }

  if (
    organization_ems_lead_id !== undefined &&
    organization_ems_lead_id !== null &&
    String(organization_ems_lead_id).trim() !== "" &&
    isNaN(organization_ems_lead_id)
  ) {
    errors.organization_ems_lead_id = "Lead ID must be a number.";
  }

  if (
    organization_ems_lead_stage_id &&
    isNaN(organization_ems_lead_stage_id)
  ) {
    errors.organization_ems_lead_stage_id = "Invalid lead stage selected.";
  }

  const validActivityTypes = [
    "Call",
    "Email",
    "WhatsApp",
    "SMS",
    "Telegram",
    "Online Meeting",
    "Visit",
  ];
  if (!activity_type) {
    errors.activity_type = "Activity type is required.";
  } else if (!validActivityTypes.includes(activity_type)) {
    errors.activity_type = "Invalid activity type selected.";
  }

  if (!activity_datetime) {
    errors.activity_datetime = "Activity date & time is required.";
  } else if (isNaN(Date.parse(activity_datetime))) {
    errors.activity_datetime = "Invalid date & time format.";
  } else {
    const leadDt = lead_datetime || formData._lead_datetime;
    const activityLeadError = validateActivityDatetimeAgainstLead(
      activity_datetime,
      leadDt
    );
    if (activityLeadError) {
      errors.activity_datetime = activityLeadError;
    }
  }

  if (next_follow_up && String(next_follow_up).trim() !== "") {
    if (isNaN(Date.parse(next_follow_up))) {
      errors.next_follow_up = "Invalid next follow up date & time format.";
    }
  }

  const validLeadDirections = ["Incoming", "Outgoing"];
  if (!lead_direction) {
    errors.lead_direction = "Lead direction is required.";
  } else if (!validLeadDirections.includes(lead_direction)) {
    errors.lead_direction = "Lead direction must be Incoming or Outgoing.";
  }

  const validCallStatuses = [
    "Attended",
    "Missed",
    "Rejected",
    "No Answer",
  ];
  if (call_status && !validCallStatuses.includes(call_status)) {
    errors.call_status = "Invalid call status selected.";
  }

  if (!organization_user_id) {
    errors.organization_user_id = "Activity By user is required.";
  } else if (isNaN(organization_user_id)) {
    errors.organization_user_id = "Activity By user ID must be a number.";
  }

  setFormErrors(errors);
  return Object.keys(errors).length === 0;
}

const LEAD_ACTIVITY_EMBED_OMIT_MAIN_KEYS = new Set([
  "organization_ems_lead_id",
  "activity_summary",
  "remarks",
]);

/** Lead form embed: subset of `/lead-activities` (no lead picker, activity result, or remarks). */
export function getLeadActivityEmbedConfig() {
  const item = Configurations.find((c) => c.route === "/lead-activities");
  const fp = item?.FormProps;
  if (!fp) return null;
  const rows = (fp.fieldGridLayout?.rows || [])
    .map((row) => ({
      ...row,
      mainKeys: (row.mainKeys || []).filter(
        (k) => !LEAD_ACTIVITY_EMBED_OMIT_MAIN_KEYS.has(k)
      ),
    }))
    .filter((row) => (row.mainKeys || []).length > 0);
  return {
    validateForm: validateLeadActivityEmbedForm,
    initialData: { ...fp.initialData },
    fieldGridLayout: { ...fp.fieldGridLayout, rows },
    Inputs: fp.Inputs.filter(
      (i) => !LEAD_ACTIVITY_EMBED_OMIT_MAIN_KEYS.has(i.mainKey)
    ),
  };
}
